import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
// 2026-09-24: the fold buttons draw SVG icons, not characters — a literal
// ☰ has no glyph in this font stack and painted an empty 40x40 button.
import { MenuIcon } from '@shopify/polaris-icons';
// The same collapse glyph the main nav uses (Eric, 2026-09-24) — one icon,
// one definition, so the two sidebars cannot drift apart.
import { SidebarGlyph } from './AppShell';
import { useParams, useNavigate, useLocation, Navigate, useSearchParams, Link } from 'react-router-dom';
import { useStore, getStoreSlugFromPath } from './StoreContext';
import {
    Page, Card, Box, BlockStack, InlineStack, Text, Button, ButtonGroup,
    ChoiceList, Popover, Spinner, Banner, TextField, DatePicker, Badge, Select,
    Modal, Tooltip, Checkbox, Icon,
} from '@shopify/polaris';
import { BarChart, PolarisVizProvider } from '@shopify/polaris-viz';
import '@shopify/polaris-viz/build/esm/styles.css';
import * as echarts from 'echarts';
import ReactECharts from 'echarts-for-react';
import BinLocationsReport from './reports/BinLocationsReport';
import ZeroSalesReport from './reports/ZeroSalesReport';
import ClubMemberAnalyticsReport from './reports/ClubMemberAnalyticsReport';
import RefundAnalyticsReport from './reports/RefundAnalyticsReport';
import SalesAnalyticsReport from './reports/SalesAnalyticsReport';
import DiscountAnalyticsReport from './reports/DiscountAnalyticsReport';
import GiftCardAnalyticsReport from './reports/GiftCardAnalyticsReport';
import PickupsVsGiftCardsReport from './reports/PickupsVsGiftCardsReport';
import InventoryDashboardReport from './reports/InventoryDashboardReport';
import CrossSiteSalesReport from './reports/CrossSiteSalesReport';
import ClubRebatesReport from './reports/ClubRebatesReport';
import { polarisEchartsTheme } from './polarisEchartsTheme';
import { useAuth, useAuthFetch } from './AuthComponent';
import SourceBadge, { sourceLabel } from './SourceBadge';
import SmartProductImage from './SmartProductImage';
import { ordersCumulativeTotals } from './ordersCumulativeTotals';
import BulkTransfersReport from './reports/BulkTransfersReport';
import StoreVelocityReport from './reports/StoreVelocityReport';
import StoreBriefingReport from './reports/StoreBriefingReport';
import ChargebacksReport from './reports/ChargebacksReport';

import { hasRole, allRolesIn, userRoles, hasPermission } from './constants/roles';
import FBMReport from './reports/FBMReport';

// Register the Polaris-tuned theme once per bundle.
echarts.registerTheme('polaris', polarisEchartsTheme);

/* ---------- helpers ----------------------------------------------------- */

// Tiny CSV helper. Each row is a flat array (no nesting). Strings with commas /
// quotes / newlines get RFC-4180 quoted + double-quoted. Triggers a browser
// download via a Blob URL — no server round trip.
// Height a scrolling table pane must have so its scroll container (the app's
// `.page-content`, or the document) does not scroll at all: the container's
// visible height minus everything above the pane and everything below it
// (card padding, page gutter). 2026-09-21 (Eric).
export function paneHeightToFitScroller(el, floor = 240) {
    if (!el) return null;
    let sc = el.parentElement;
    while (sc && sc !== document.body) {
        const cs = getComputedStyle(sc);
        if (/(auto|scroll)/.test(cs.overflowY)) break;
        sc = sc.parentElement;
    }
    const r = el.getBoundingClientRect();
    if (!sc || sc === document.body) {
        const below = Math.max(0, document.documentElement.scrollHeight - (r.bottom + window.scrollY));
        return Math.max(floor, Math.floor(window.innerHeight - r.top - below));
    }
    const sr = sc.getBoundingClientRect();
    const topIn = r.top - sr.top + sc.scrollTop;
    const bottomIn = r.bottom - sr.top + sc.scrollTop;
    // "Below" = the real content under the pane — following siblings up the
    // ancestor chain (the pager) plus each ancestor's bottom padding/border —
    // NOT `scrollHeight - bottom`: the scroller's inner wrapper and the
    // reporting grid are stretched to the viewport, so that counted the blank
    // area and collapsed the pane to its floor (Eric, 2026-09-22: Zero Sales
    // sat at 240px with half the screen empty).
    let below = 0;
    for (let n = el; n && n !== sc; n = n.parentElement) {
        const nb = n.getBoundingClientRect().bottom;
        let sibBottom = nb;
        for (let s = n.nextElementSibling; s; s = s.nextElementSibling) {
            // 2026-09-25 (Eric): out-of-flow siblings are decorations, not
            // content under the pane. `.report-card-seam` is position: fixed
            // and ends wherever the grid ends; counting it made "below" grow
            // every time the pane shrank, so the pane fed on itself down to
            // the 240px floor (Revenue By Club (Including Promo), help open).
            if (/^(fixed|absolute|sticky)$/.test(getComputedStyle(s).position)) continue;
            sibBottom = Math.max(sibBottom, s.getBoundingClientRect().bottom);
        }
        below += Math.max(0, sibBottom - nb);
        const pcs = getComputedStyle(n.parentElement);
        below += (parseFloat(pcs.paddingBottom) || 0) + (parseFloat(pcs.borderBottomWidth) || 0);
    }
    return Math.max(floor, Math.floor(sc.clientHeight - topIn - below));
}

export function downloadCsv(filename, rows) {
    if (!rows || rows.length === 0) return;
    const escape = (cell) => {
        if (cell == null) return '';
        let s = String(cell);
        // CSV formula-injection defense (H11): a cell beginning with = + - @
        // (or tab / CR) is executed as a formula by Excel / Google Sheets, so a
        // crafted club name / note / product title in a report could run code on
        // whoever opens the export. Prefix a single quote to neutralize it — but
        // leave plain numbers (including negatives like refund amounts) alone so
        // numeric columns still compute.
        if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) {
            s = "'" + s;
        }
        if (/[",\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
        return s;
    };
    const csv = rows.map(r => r.map(escape).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

function localIsoDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
export function isoDaysAgo(n) {
    const d = new Date(); d.setDate(d.getDate() - n);
    return localIsoDate(d);
}
export function todayIso() { return localIsoDate(new Date()); }

// Parse 'YYYY-MM-DD' into a Date at local midnight. Avoids the UTC drift you get
// from new Date('2026-05-19') in negative-offset zones.
function parseIsoLocal(s) {
    if (!s) return new Date();
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
}
const _MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function _fmtRangeLabel(fromIso, toIso) {
    if (!fromIso && !toIso) return 'Select dates';
    const f = parseIsoLocal(fromIso);
    const t = parseIsoLocal(toIso || fromIso);
    const sameYear = f.getFullYear() === t.getFullYear();
    const sameMonth = sameYear && f.getMonth() === t.getMonth();
    const sameDay = sameMonth && f.getDate() === t.getDate();
    if (sameDay) {
        return `${_MONTH_NAMES_SHORT[f.getMonth()]} ${f.getDate()}, ${f.getFullYear()}`;
    }
    if (sameMonth) {
        return `${_MONTH_NAMES_SHORT[f.getMonth()]} ${f.getDate()}–${t.getDate()}, ${f.getFullYear()}`;
    }
    if (sameYear) {
        return `${_MONTH_NAMES_SHORT[f.getMonth()]} ${f.getDate()} – ${_MONTH_NAMES_SHORT[t.getMonth()]} ${t.getDate()}, ${f.getFullYear()}`;
    }
    return `${_MONTH_NAMES_SHORT[f.getMonth()]} ${f.getDate()}, ${f.getFullYear()} – ${_MONTH_NAMES_SHORT[t.getMonth()]} ${t.getDate()}, ${t.getFullYear()}`;
}

// Sidebar layout — top-level items sit at root; `group` entries render as a
// collapsible header with indented children (auto-expands whichever group
// contains the currently-selected report). Groups have `groupId` used as the
// localStorage key for remembering user's expand/collapse choice.
// Per-role report visibility. store_manager is admitted to Reporting for
// EXACTLY ONE report (Store Fulfillments) — everything else stays hidden,
// and the backend's report endpoints reject store_manager independently
// (role gates check every layer).
// `requireStore` (2026-09-18, Eric): a report shown only to users who can
// reach that store (any of its locations) — Revenue By Club (Including Promo)
// is Surf's. `stores` is StoreContext's accessibleStores; the backend
// endpoint refuses callers without that store in scope independently.
// `who` is the user (multi-role, 2026-10-05): a report hidden for some roles stays
// visible while ANY of the person's roles may see it; the store-manager short list
// applies only when store_manager is their only role.
function _visibleReportForRole(r, who, stores) {
    if (r.permission && !hasPermission(who, r.permission)) return false;
    const roles = userRoles(who);
    if (r.hideForRoles && roles.length && roles.every(x => r.hideForRoles.includes(x))) return false;
    if (r.requireStore && !(stores || []).includes(r.requireStore)) return false;
    if (roles.length && roles.every(x => x === 'store_manager')) {
        if (r.children) return r.children.some(c => _visibleReportForRole(c, who, stores));
        // store_manager: Store Fulfillments + Bulk Transfers (boxes going TO their stores, 2026-09-04)
        return r.slug === 'store-fulfillments' || r.slug === 'bulk-transfers' || r.slug === 'store-ordering' || r.slug === 'store-briefing' || r.slug === 'fbm-report';
    }
    return true;
}

// Reports that exist only on operations-test.* (hostname gate, same as Bulk Print Labels).

const REPORTS = [
    // Today's Overview (2026-07-24) — fleet-wide "today" snapshot across all
    // non-franchise locations, no filters, franchise role hidden. See
    // TodaysOverviewReport + /api/reports/todays_overview.
    { slug: 'todays-overview',    label: "Today's Overview",         component: TodaysOverviewReport, hideForRoles: ['franchise', 'external'] },
    // Renamed from "Overview" (kept the URL slug for bookmark stability).
    // Fulfillment ramp chart moved to Today's Overview.
    { slug: 'overview',           label: 'Top Days',                 component: TopDaysReport },
    { slug: 'orders',             label: 'Orders',                   component: OrdersReport },
    // Location Summary (2026-08-21) — location-facing top portion of the Club
    // Summary one-pager (KPIs / bucket table / duration / weekly chart, no
    // per-order lists). Location access enforced by _scoped_store_slocs.
    { slug: 'location-summary',   label: 'Location Summary',         component: LocationSummaryReport },
    // 2026-09-28 (Eric): Store Reports section — the store-facing reports live
    // together above the club ones.
    { groupId: 'store', label: 'Store Reports', children: [
        // Store Briefing (2026-09-30) — nightly per-store briefing for the store manager (store_briefing.py). First in the group (Eric).
        { slug: 'store-briefing',     label: 'Store Briefing',           component: StoreBriefingReport, hideForRoles: ['franchise'] },   // corporate stores only (Eric, 2026-10-05)
        // Store Fulfillments (2026-08-27) — pickup-cycle status per retail store.
        // The ONLY report store_manager can see (see _visibleReportForRole).
        // Club Member Analytics (2026-10-16) — replaces the Grafana dashboard; Team Portal members + Lightspeed orders + OpsHub pickups, scoped to the caller's stores.
        { slug: 'club-member-analytics', label: 'Club Member Analytics', component: ClubMemberAnalyticsReport },
        { slug: 'store-fulfillments', label: 'Store Fulfillments',       component: StoreFulfillmentsReport },
        // Bulk Transfers (2026-09-03) — bulk boxes shipped from the Teams fulfillment
        // locations to the stores; scoped by the box's from-location.
        { slug: 'bulk-transfers',     label: 'Bulk Transfers',           component: BulkTransfersReport },
        { slug: 'store-ordering',     label: 'Store Ordering',           component: StoreVelocityReport, hideForRoles: ['franchise'] },   // was store-velocity / Store Velocity & Stats until 2026-10-05 (David Thomasson); the old address redirects   // Lightspeed = corporate stores only (Eric, 2026-10-05)
        // Backend Reporting (2026-10-18, Eric): a sub-header whose reports (and the header itself) need the
        // Extra Permission reports.backend — finance and admin by default. Put new backend reports in
        // `children` and gate their endpoints with require_permission(request, 'reports.backend').
        { subheader: true, groupId: 'backend', label: 'Backend Reporting', permission: 'reports.backend', children: [
            // Refund Analytics (2026-10-18, Eric) — replaces the Grafana Lightspeed Refund Analytics dashboard. First in the group.
            { slug: 'refund-analytics', label: 'Refund Analytics', component: RefundAnalyticsReport, permission: 'reports.backend' },
            // Sales Analytics (2026-10-18, Eric) — replaces the Grafana Lightspeed Sales Analytics dashboard, with year-over-year and week-of-year.
            { slug: 'sales-analytics', label: 'Sales Analytics', component: SalesAnalyticsReport, permission: 'reports.backend' },
            // Discount Analytics (2026-10-18, Eric) — replaces the Grafana Lightspeed Discount Analytics dashboard.
            { slug: 'discount-analytics', label: 'Discount Analytics', component: DiscountAnalyticsReport, permission: 'reports.backend' },
            // Gift Card Analytics (2026-10-18, Eric) — replaces the Grafana Lightspeed Gift Card Analytics dashboard.
            { slug: 'gift-card-analytics', label: 'Gift Card Analytics', component: GiftCardAnalyticsReport, permission: 'reports.backend' },
            // Pickups vs Gift Cards (2026-10-18, Eric) — replaces the Grafana Order Pickups vs TeamPortalOrder Gift Card Usage dashboard.
            { slug: 'pickups-vs-gift-cards', label: 'Pickups vs Gift Cards', component: PickupsVsGiftCardsReport, permission: 'reports.backend' },
        ] },
    ] },
    { groupId: 'club', label: 'Club Reports', children: [
        { slug: 'club-summary',               label: 'Club Summary',                                component: ClubSummaryReport },
        { slug: 'orders-by-club',             label: 'Orders By Club',                              component: OrdersByClubReport },
        { slug: 'revenue-by-club',            label: 'Revenue By Club',                             component: RevenueByClubReport },
        // 2026-09-17 (Eric): live everywhere (was test-only from 2026-09-11 to 2026-09-17).
        // 2026-09-18 (Eric): only for users with access to a Surf location.
        { slug: 'club-revenue-summary',       label: 'Revenue By Club (Including Promo)',           component: ClubRevenueSummaryReport, requireStore: 'surf' },
        // Club Rebates (2026-09-20, Eric) — the Team Portal's rebate report with OpsHub's count beside the TP's.
        // 2026-09-21 (Eric): backend on prod, the report itself TEST-ONLY while the counting rule was worked out.
        // 2026-09-26 (Eric): live everywhere — the rule ('count what the customer is getting') shipped in v20260926.3.
        { slug: 'club-rebates',               label: 'Club Rebates',                                component: ClubRebatesReport },
        { slug: 'revenue-by-club-vendor',     label: 'Revenue By Club & Vendor',                    component: RevenueByClubVendorReport },
        { slug: 'discounts-by-club',          label: 'Discounts By Club',                           component: DiscountsByClubReport },
        { slug: 'addon-retail',               label: 'Add-On & Retail Items By Club',               component: AddOnRetailReport },
        { slug: 'products-by-club',           label: 'Products Sold By Club',                       component: ProductsByClubReport },
        { slug: 'embellishment-report',       label: 'Embellishment Report',                        component: EmbellishmentReport },
        { slug: 'inventory-by-club',          label: 'Inventory Status By Location / Club',        component: InventoryByClubReport },
        { slug: 'product-revenue-by-club',    label: 'Product Revenue By Club (MSRP)',              component: ProductRevenueByClubReport },
        { slug: 'numbers-names-by-club',      label: 'Numbers & Names By Club',                     component: NumbersNamesByClubReport },
        { slug: 'product-analysis-by-club',   label: 'Product / Player / Number Analysis By Club',  component: ProductAnalysisByClubReport },
        { slug: 'product-fulfillment-by-club', label: 'Partial Analysis By Club',                   component: ProductFulfillmentByClubReport },
        { slug: 'pickup-location-by-club',    label: 'Pickup Location By Club',                     component: PickupLocationByClubReport },
        { slug: 'annapolis-pickup-analysis',  label: 'Soccer Post Annapolis Pickup Analysis',       component: AnnapolisPickupAnalysisReport, hideForRoles: ['franchise'] },
    ]},
    // Location Reports (2026-09-13) — warehouse-level reports. Bin Locations
    // replaces the Grafana Lancaster/Brookfield bin dashboards (spec
    // docs/superpowers/specs/2026-09-13-bin-locations-report-design.md).
    { groupId: 'location', label: 'Location Reports',
      hideForRoles: ['franchise', 'store_manager', 'crm', 'customer_service', 'finance', 'external'],
      children: [
        // Inventory Dashboard (2026-09-22, Eric): OpsHub-native replacement for
        // the Grafana "Teams Las Vegas Inventory" dashboard, any Teams / Warehouse
        // location (spec docs/superpowers/specs/2026-09-22-inventory-dashboard-report-design.md).
        { slug: 'inventory-dashboard', label: 'Inventory Dashboard', component: InventoryDashboardReport,
          hideForRoles: ['franchise', 'store_manager', 'crm', 'customer_service', 'finance', 'external'] },
        // Cross Site Sales (2026-10-28, Eric): one inventory mirrored onto several stores ("(Virtual)" copies);
        // stock once, Committed across every copy, Units Sold per store + Total. Any store at the top.
        { slug: 'cross-site-sales', label: 'Cross Site Sales', component: CrossSiteSalesReport,
          hideForRoles: ['franchise', 'store_manager', 'crm', 'customer_service', 'finance', 'external'] },
        { slug: 'bin-locations', label: 'Bin Locations Report', component: BinLocationsReport,
          hideForRoles: ['franchise', 'store_manager', 'crm', 'customer_service', 'finance', 'external'] },
        // Zero Sales (2026-09-15, Eric): inventory at Teams / Warehouse
        // locations with no sales YTD from that location, per SKU.
        { slug: 'zero-sales', label: 'Zero Sales', component: ZeroSalesReport,
          hideForRoles: ['franchise', 'store_manager', 'crm', 'customer_service', 'finance', 'external'] },
    ] },
    { slug: 'products-by-vendor',    label: 'Products Sold By Vendor',  component: ProductsByVendorReport },
    { slug: 'custom-crm-products',   label: 'Custom CRM Products',      component: CustomCRMProductsReport },
    { slug: 'fbm-report',            label: 'FBM Report',               component: FBMReport, requireStore: 'soccerpost', permission: 'reports.fbm' },
    { slug: 'jumbled-bundles',       label: 'Jumbled Bundles',          component: JumbledBundlesReport },
    { slug: 'split-team-fulfillment', label: 'Split Team Fulfillment',   component: SplitTeamFulfillmentReport },
    // Chargebacks (2026-10-06, Eric) — every Shopify Payments dispute on the store, live, scoped by location.
    // Sits above Team Portal Franchise Reconciliation (Eric).
    { slug: 'chargebacks',           label: 'Chargebacks',              component: ChargebacksReport },
    // TPFR (2026-08-12): admin + franchise; finance added 2026-10-21 (Eric), unscoped like admin. Backend enforces the same
    // gate via role check → 403 for other roles. `hideForRoles` here just
    // suppresses the sidebar entry so store_manager / crm / user don't see
    // a link that would 403 on click.
    { slug: 'tp-franchise-reconciliation',
      label: 'Team Portal Franchise Reconciliation',
      component: TPFranchiseReconciliationReport,
      hideForRoles: ['store_manager', 'user', 'site_manager', 'crm', 'external'] },   // crm removed 2026-09-11 (Eric); site_manager hidden (API refuses it); finance allowed 2026-10-21
    { groupId: 'operator', label: 'Operator Reports', children: [
        { slug: 'picking',                    label: 'Picking',              component: PickingReport },
        { slug: 'picking-by-category',        label: 'Picking By Category',  component: PickingByCategoryReport },
        { slug: 'picking-times',              label: 'Picking Times',        component: PickingTimesReport },
        { slug: 'pressing',                   label: 'Pressing',          component: PressingReport },
        { slug: 'pressing-reworks',           label: 'Pressing Reworks',  component: PressingReworksReport },
        { slug: 'pressing-times',             label: 'Pressing Times',    component: PressingTimesReport },
        { slug: 'packing',                    label: 'Packing',           component: PackingReport },
        { slug: 'packing-times',              label: 'Packing Times',     component: PackingTimesReport },
        { slug: 'qc',                         label: 'QC',                component: QCReport },
        { slug: 'ppp-overview',               label: 'PPP Overview',      component: PPPOverviewReport },
        { slug: 'operator',                   label: 'Operator Report',   component: OperatorReport },
    ]},
];

// Flat list of every routeable report — for slug → component resolution and
// for finding which group (if any) an active slug belongs to.
// Every group (top level and nested sub-headers) that has a collapse state.
const _flattenGroups = (list) => list.flatMap(r => (r.groupId ? [r] : []).concat(r.children ? _flattenGroups(r.children) : []));
const _flattenReports = (list) => list.flatMap(r => r.children ? _flattenReports(r.children) : [r]);
const REPORTS_FLAT = _flattenReports(REPORTS).filter(r => r.slug);

function _fmtUSD(n) {
    const v = Number(n || 0);
    return v.toLocaleString('en-US', { style: 'currency', currency: 'USD',
        maximumFractionDigits: 0 });
}
// Same, with cents (Discounts By Club, Eric 2026-10-16).
function _fmtUSDc(n) {
    const v = Number(n || 0);
    return v.toLocaleString('en-US', { style: 'currency', currency: 'USD',
        minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function _fmtDuration(s) {
    s = Math.round(s || 0);
    if (s < 60) return `${s}s`;
    if (s < 3600) {
        const m = Math.floor(s / 60), r = s % 60;
        return r ? `${m}m ${r}s` : `${m}m`;
    }
    const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
}

/* ---------- shared in-flight spinner overlay ----------------------------
   Shows a Polaris Spinner centered in the viewport when a report's loading
   state stays true past `delayMs` (default 400ms). Short loads do nothing —
   no spinner flash. Dropped into each report's BlockStack so re-fetches
   (e.g. toggling Team Portal / Retail) surface a clear loading indicator
   without replacing existing data on screen. Polaris Spinner reference:
   https://polaris-react.shopify.com/components/feedback-indicators/spinner */
function DelayedSpinner({ loading, delayMs = 400 }) {
    const [show, setShow] = useState(false);
    useEffect(() => {
        if (!loading) { setShow(false); return; }
        const t = setTimeout(() => setShow(true), delayMs);
        return () => clearTimeout(t);
    }, [loading, delayMs]);
    if (!show) return null;
    // Render to document.body via portal so this overlay is never a sibling
    // of the report's own children. If it were, its rendered div would
    // become :first-child of the parent BlockStack and steal the sticky-
    // positioning the filter card relies on via that selector — the user
    // saw the top of the page disappear during re-fetches as a result.
    return createPortal(
        <div style={{
            position: 'fixed',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 50,
            pointerEvents: 'none',
            background: 'rgba(255,255,255,0.92)',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        }}>
            <Spinner accessibilityLabel="Loading report" size="large" />
        </div>,
        document.body,
    );
}

/* ---------- shared date-range control ---------------------------------- */
// 2026-09-22 (Eric): the Popover calendar + preset buttons every report's
// filter bar uses, extracted so single-purpose reports (Zero Sales,
// Inventory Dashboard) get the same control instead of raw <input type=date>.
// `presets` picks which preset buttons show (default: all of them).
export const REPORT_DATE_PRESETS = ['yesterday', 'today', '7d', '30d', '90d', 'ytd', 'last_ytd'];
export function ReportDateRange({ dateFrom, setDateFrom, dateTo, setDateTo, presets = REPORT_DATE_PRESETS }) {
    const [dateOpen, setDateOpen] = useState(false);
    // The calendar's visible month/year — initialized from dateFrom, then
    // user-controlled via prev/next arrows. We re-sync to dateFrom whenever the
    // popover is (re)opened so jumping via preset buttons brings the calendar
    // along.
    const _initFrom = useMemo(() => parseIsoLocal(dateFrom || todayIso()), [dateFrom]);
    const [{ month, year }, setMonthYear] = useState(
        () => ({ month: _initFrom.getMonth(), year: _initFrom.getFullYear() })
    );
    useEffect(() => {
        if (dateOpen) {
            const d = parseIsoLocal(dateFrom || todayIso());
            setMonthYear({ month: d.getMonth(), year: d.getFullYear() });
        }
    }, [dateOpen, dateFrom]);
    const show = k => presets.includes(k);
    return (
        <InlineStack gap="200" wrap blockAlign="center">
            <Popover
                active={dateOpen}
                activator={
                    <Button onClick={() => setDateOpen(v => !v)}
                            disclosure={dateOpen ? 'up' : 'down'}>
                        {_fmtRangeLabel(dateFrom, dateTo)}
                    </Button>
                }
                onClose={() => setDateOpen(false)}
                preferredAlignment="left"
                fluidContent
            >
                <div style={{ padding: 12,
                              width: 'min(660px, calc(100vw - 40px))' }}>
                    <DatePicker
                        month={month}
                        year={year}
                        onMonthChange={(m, y) => setMonthYear({ month: m, year: y })}
                        selected={{
                            start: parseIsoLocal(dateFrom || todayIso()),
                            end: parseIsoLocal(dateTo || dateFrom || todayIso()),
                        }}
                        onChange={({ start, end }) => {
                            setDateFrom(localIsoDate(start));
                            setDateTo(localIsoDate(end || start));
                        }}
                        allowRange
                        multiMonth
                    />
                </div>
            </Popover>
            <ButtonGroup segmented>
                {show('yesterday') && <Button onClick={() => { setDateFrom(isoDaysAgo(1));   setDateTo(isoDaysAgo(1)); }}>Yesterday</Button>}
                {show('today') && <Button onClick={() => { setDateFrom(todayIso());      setDateTo(todayIso()); }}>Today</Button>}
                {show('7d') && <Button onClick={() => { setDateFrom(isoDaysAgo(6));   setDateTo(todayIso()); }}>7d</Button>}
                {show('30d') && <Button onClick={() => { setDateFrom(isoDaysAgo(29));  setDateTo(todayIso()); }}>30d</Button>}
                {show('90d') && <Button onClick={() => { setDateFrom(isoDaysAgo(89));  setDateTo(todayIso()); }}>90d</Button>}
                {show('ytd') && <Button onClick={() => { setDateFrom(localIsoDate(new Date(new Date().getFullYear(), 0, 1))); setDateTo(todayIso()); }}>YTD</Button>}
                {/* 2026-09-11 (Eric): the same window one year earlier —
                    Jan 1 of last year through today's month/day last year,
                    e.g. on 2026-09-11 → 2025-01-01 … 2025-09-11. */}
                {show('last_ytd') && <Button onClick={() => {
                    const now = new Date();
                    setDateFrom(localIsoDate(new Date(now.getFullYear() - 1, 0, 1)));
                    setDateTo(localIsoDate(new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())));
                }}>Last YTD</Button>}
            </ButtonGroup>
        </InlineStack>
    );
}

/* ---------- shared filter bar ------------------------------------------ */

// A filter dropdown in the house style (2026-09-22, Eric: "I want all
// dropdowns on all reports to be in that style") — the same Popover + search
// + ChoiceList picker ReportFilters uses for Location and Club, packaged so
// every report can use it in place of a plain Polaris <Select>.
//   single  : value is a string; picking a choice closes the popover.
//   multiple: value is an array of strings; the activator carries the count.
// The search field appears once the list is long enough to need it (or when
// `searchable` says so). An option with value '' is the "all" choice: it is
// rendered as a real choice in single mode and drives the Clear button.
export function searchMatches(text, query) {
    const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return true;
    const hay = String(text || '').toLowerCase();
    return words.every(w => hay.includes(w));
}

export function ReportPicker({
    label, options = [], value, onChange, multiple = false, searchable = null,
    placeholder = null, minWidth = 280, maxHeight = 420, disabled = false, groups = null,
    nowrap = false,   // 2026-10-28 (Eric): long labels stay on one line; the popover grows to fit
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const sel = multiple ? (value || []) : (value ?? '');
    const all = useMemo(() => (groups || [{ title: null, options }]).flatMap(g => g.options || []), [groups, options]);
    const showSearch = searchable === null ? all.length >= 8 : !!searchable;
    const match = useCallback((o) => {
        const q = search.trim().toLowerCase();
        return searchMatches(o.label, q);
    }, [search]);
    // The '' ("all") choice never hides behind a search term in single mode.
    const shown = useMemo(() => (groups || [{ title: null, options }]).map(g => ({
        title: g.title,
        options: (g.options || []).filter(o => (!multiple && o.value === '') || match(o)),
    })).filter(g => g.options.length > 0), [groups, options, match, multiple]);
    const selectedLabel = useMemo(() => {
        if (multiple) return null;
        const hit = all.find(o => String(o.value) === String(sel));
        return hit ? hit.label : label;
    }, [all, sel, multiple, label]);
    const isSet = multiple ? sel.length > 0 : (sel !== '' && sel != null);
    const activatorText = multiple
        ? `${label}${sel.length ? ` (${sel.length})` : ''}`
        : selectedLabel;
    const pick = useCallback((vals) => {
        if (multiple) { onChange(vals); return; }
        onChange(vals[0] ?? '');
        setOpen(false);
        setSearch('');
    }, [multiple, onChange]);
    const clear = useCallback(() => {
        onChange(multiple ? [] : '');
        if (!multiple) setOpen(false);
    }, [multiple, onChange]);
    return (
        <Popover
            active={open && !disabled}
            activator={
                <Button onClick={() => setOpen(v => !v)} disclosure={open ? 'up' : 'down'}
                        pressed={isSet} disabled={disabled}>
                    {activatorText}
                </Button>
            }
            onClose={() => setOpen(false)}
            preferredAlignment="left"
            fluidContent={nowrap}
        >
            <div style={{ minWidth, maxHeight, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {showSearch && (
                    <TextField labelHidden label={`Search ${label.toLowerCase()}`} type="search"
                        value={search} onChange={setSearch} autoComplete="off"
                        placeholder={placeholder || `Search ${label.toLowerCase()}…`}
                        clearButton onClearButtonClick={() => setSearch('')} />
                )}
                <button type="button" className="filter-pill-clear" disabled={!isSet} onClick={clear}>Clear</button>
                <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                    {shown.length === 0 ? (
                        <div style={{ color: 'var(--p-color-text-secondary)', fontSize: 13, padding: '8px 4px' }}>
                            No matches for "{search}"
                        </div>
                    ) : shown.map((g, i) => (
                        <React.Fragment key={g.title || i}>
                            {i > 0 && <hr style={{ border: 0, borderTop: '1px solid var(--color-border)', margin: '8px 0' }} />}
                            {g.title && <Text as="p" variant="headingXs" tone="subdued">{g.title}</Text>}
                            <ChoiceList titleHidden title={g.title || label} allowMultiple={multiple}
                                choices={g.options.map(o => ({ label: nowrap ? <span style={{ whiteSpace: 'nowrap' }}>{o.label}</span> : o.label, value: String(o.value) }))}
                                selected={multiple ? sel.map(String) : [String(sel)]}
                                onChange={pick} />
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </Popover>
    );
}


// The report title with its column definitions tucked behind a link
// (2026-09-23, Eric: the same treatment Team Portal Franchise Reconciliation
// has, on Revenue By Club and Revenue By Club (Including Promo)). Closed by
// default, and each report remembers the reader's choice in this browser.
//   items  — [label, definition] pairs; the definition may be JSX.
//   footer — one subdued line under them for whole-report caveats.
// The seam between the pinned filter card and the report below it
// (2026-09-23, Eric). The 16px gap used to be filled by a box-shadow on the
// card, which takes the card's border-radius — so the strip carried 12px
// bottom curves and the table box rising underneath showed its own curves
// through the notches, the two arcs crossing into little points ("horns").
// Drawing the strip as its own fixed element instead lets it be a clean
// rectangle with a concave flare at each end, so the card's white sweeps
// outward and the box below nests into it. It is positioned off the card's
// own rect, so it follows any report's card height, and it never touches the
// card's `overflow: clip` — which is what keeps Polaris' white inside the
// rounded border.
function ReportCardSeam() {
    const [box, setBox] = useState(null);
    useLayoutEffect(() => {
        let raf = 0;
        const measure = () => {
            const c = document.querySelector(
                '.reporting-content > .Polaris-BlockStack > .Polaris-ShadowBevel');
            if (!c) { setBox(prev => (prev === null ? prev : null)); return; }
            const r = c.getBoundingClientRect();
            // Round: getBoundingClientRect returns floats, and a sub-pixel
            // difference between renders used to make the guard below miss —
            // every render scheduled another state update (React #185, blank
            // page on client-side navigation until a reload). 2026-09-23.
            const next = { left: Math.round(r.left), width: Math.round(r.width),
                           top: Math.round(r.bottom) - 12 };
            setBox(prev => (prev && prev.left === next.left && prev.width === next.width
                            && prev.top === next.top) ? prev : next);
        };
        const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
        measure();
        // Mount-once: the card element is replaced when the reader switches
        // report, so watch the content root rather than the card itself.
        const root = document.querySelector('.reporting-content');
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
        const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(schedule) : null;
        if (root && ro) ro.observe(root);
        if (root && mo) mo.observe(root, { childList: true, subtree: true });
        const scroller = document.querySelector('.page-content');
        window.addEventListener('resize', schedule);
        if (scroller) scroller.addEventListener('scroll', schedule, { passive: true });
        return () => {
            cancelAnimationFrame(raf);
            if (ro) ro.disconnect();
            if (mo) mo.disconnect();
            window.removeEventListener('resize', schedule);
            if (scroller) scroller.removeEventListener('scroll', schedule);
        };
    }, []);
    if (!box) return null;
    return <div className="report-card-seam"
                style={{ left: box.left, width: box.width, top: box.top, height: 28 }} />;
}


export function ReportColumnHelp({ title, storageKey, items = [], footer = null,
                                   linkLabel = 'How each column is calculated',
                                   defaultOpen = false, children = null }) {
    // 2026-09-24 (Eric): `defaultOpen` for blocks that should be READ, not hunted
    // for — the Partial Analysis view definitions explain four views whose names
    // alone don't say what they count, so they open expanded and collapse away
    // once the reader knows them. A reader's own choice still wins: whatever they
    // last did is remembered per report, and only an untouched one falls back to
    // this default.
    const [open, setOpen] = useState(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            return stored === null ? defaultOpen : stored === '1';
        } catch (_e) { return defaultOpen; }
    });
    const set = (v) => {
        setOpen(v);
        try { localStorage.setItem(storageKey, v ? '1' : '0'); } catch (_e) { /* private window */ }
    };
    return (
        <>
            {(title || !open) && (
                <InlineStack align="space-between" blockAlign="center" wrap>
                    {title ? <Text variant="headingMd" as="h2">{title}</Text> : <span />}
                    {!open && <Button variant="plain" onClick={() => set(true)}>{linkLabel}</Button>}
                </InlineStack>
            )}
            {open && (
                <Banner tone="info" title={linkLabel} onDismiss={() => set(false)}>
                    <BlockStack gap="150">
                        {children}
                        {items.map(([label, tip], i) => (
                            <Text as="p" key={label || i}><b>{label}</b> — {tip}</Text>
                        ))}
                        {footer && <Text as="p" tone="subdued">{footer}</Text>}
                    </BlockStack>
                </Banner>
            )}
        </>
    );
}


export function ReportFilters({ dateFrom, setDateFrom, dateTo, setDateTo,
                         availableLocations, locationFilter, setLocationFilter,
                         availableClubs, clubFilter, setClubFilter,
                         onExport, exportDisabled, exportLabel,
                         // Optional buttons rendered right before Export (Custom MSRPs).
                         extraActions,
                         mergeTeamsLancaster, setMergeTeamsLancaster,
                         mergeNjWarehouses, setMergeNjWarehouses,
                         // Optional trailing content injected right after the
                         // Club dropdown (e.g. the Team Products picker on
                         // Product Analysis By Club). Kept inside the same
                         // InlineStack so it wraps + aligns with the other
                         // filters instead of dropping onto its own line.
                         trailing = null,
                         multiClub = false }) {
    const [locOpen, setLocOpen] = useState(false);
    const [locSearch, setLocSearch] = useState('');
    // 2026-09-14 (Eric): the picker lists every store's locations, but the
    // page (URL slug, header store, order links, `store=` on some queries)
    // stays on whatever store the user was viewing. Picking a location that
    // belongs to ONE other store now switches the app to that store — same
    // as choosing it in the header dropdown — with the report path, filters
    // and query string preserved. A mixed-store selection leaves the store
    // alone; the "Clear" button never switches.
    const { activeStore: _pickStore, setActiveStore: _setPickStore,
            accessibleStores: _pickStores } = useStore();
    const _pickNav = useNavigate();
    const _pickRoute = useLocation();
    const pickLocations = useCallback((ids) => {
        setLocationFilter(ids);
        const keys = [...new Set((ids || []).map(id =>
            (availableLocations.find(l => String(l.shopify_location_id) === String(id)) || {}).store_key
        ).filter(Boolean))];
        if (keys.length !== 1) return;
        const k = keys[0];
        if (k === _pickStore || !_pickStores.includes(k)) return;
        if (!getStoreSlugFromPath(_pickRoute.pathname)) return;
        _setPickStore(k);
        const suffix = _pickRoute.pathname.replace(/^\/store\/[^/]+/, '');
        _pickNav(`/store/${k}${suffix}${_pickRoute.search}`, { replace: true });
    }, [setLocationFilter, availableLocations, _pickStore, _pickStores, _setPickStore, _pickRoute, _pickNav]);
    const [clubOpen, setClubOpen] = useState(false);
    const [clubSearch, setClubSearch] = useState('');
    const showLocPicker = availableLocations.length > 1;
    // Single-option auto-select (2026-08-31, gary.fisher): when the user can
    // only access ONE location the picker is hidden, but several reports
    // hard-gate on an explicit selection — leaving "Pick a location first"
    // with no way to pick. Select the sole location automatically.
    useEffect(() => {
        if (availableLocations.length === 1
            && (locationFilter || []).length === 0
            && typeof setLocationFilter === 'function') {
            setLocationFilter([availableLocations[0].shopify_location_id]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableLocations, locationFilter]);
    const showClubPicker = Array.isArray(availableClubs) && availableClubs.length > 0
                            && typeof setClubFilter === 'function';
    const filteredClubs = useMemo(() => {
        const q = clubSearch.trim().toLowerCase();
        if (!q) return availableClubs;
        return availableClubs.filter(c => searchMatches(c, q));
    }, [availableClubs, clubSearch]);
    // Soccer Post locations first (alphabetical), then divider, then franchises
    // (alphabetical) — same convention used by the Orders Location pill.
    // Filtered by `locSearch` substring (case-insensitive on the location name).
    const locFiltered = useMemo(() => {
        const q = locSearch.trim().toLowerCase();
        if (!q) return availableLocations;
        return availableLocations.filter(l => searchMatches(l.name, q));
    }, [availableLocations, locSearch]);
    const sp = useMemo(() => locFiltered.filter(l => !l.is_franchise)
        .slice().sort((a, b) => a.name.localeCompare(b.name)), [locFiltered]);
    const fr = useMemo(() => locFiltered.filter(l =>  l.is_franchise)
        .slice().sort((a, b) => a.name.localeCompare(b.name)), [locFiltered]);
    const toChoice = l => ({ label: l.name, value: l.shopify_location_id });

    return (
        <InlineStack gap="200" wrap blockAlign="end">
            <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
            {showLocPicker && (
                <Popover
                    active={locOpen}
                    activator={
                        <Button onClick={() => setLocOpen(v => !v)}
                                disclosure={locOpen ? 'up' : 'down'}
                                pressed={locationFilter.length > 0}>
                            Location{locationFilter.length ? ` (${locationFilter.length})` : ''}
                        </Button>
                    }
                    onClose={() => setLocOpen(false)}
                    preferredAlignment="left"
                >
                    <div style={{ minWidth: 280, maxHeight: 420, padding: 10,
                                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <TextField labelHidden label="Search locations" type="search"
                            value={locSearch} onChange={setLocSearch}
                            placeholder="Search locations…" autoComplete="off"
                            clearButton onClearButtonClick={() => setLocSearch('')} />
                        <button type="button" className="filter-pill-clear"
                                disabled={locationFilter.length === 0}
                                onClick={() => setLocationFilter([])}>Clear</button>
                        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                            {sp.length === 0 && fr.length === 0 ? (
                                <div style={{ color: 'var(--p-color-text-secondary)', fontSize: 13,
                                              padding: '8px 4px' }}>
                                    No matches for "{locSearch}"
                                </div>
                            ) : (
                                <>
                                    {sp.length > 0 && (
                                        <ChoiceList titleHidden title="Soccer Post" allowMultiple
                                            choices={sp.map(toChoice)} selected={locationFilter}
                                            onChange={pickLocations} />
                                    )}
                                    {sp.length > 0 && fr.length > 0 && (
                                        <hr style={{ border: 0, borderTop: '1px solid var(--color-border)', margin: '8px 0' }} />
                                    )}
                                    {fr.length > 0 && (
                                        <ChoiceList titleHidden title="Franchises" allowMultiple
                                            choices={fr.map(toChoice)} selected={locationFilter}
                                            onChange={pickLocations} />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </Popover>
            )}
            {showClubPicker && (
                <Popover
                    active={clubOpen}
                    activator={
                        <Button onClick={() => setClubOpen(v => !v)}
                                disclosure={clubOpen ? 'up' : 'down'}
                                pressed={clubFilter.length > 0}>
                            Club{clubFilter.length ? ` (${clubFilter.length})` : ''}
                        </Button>
                    }
                    onClose={() => setClubOpen(false)}
                    preferredAlignment="left"
                >
                    <div style={{ minWidth: 280, maxHeight: 420, padding: 10,
                                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <TextField labelHidden label="Search clubs" type="search"
                            value={clubSearch} onChange={setClubSearch}
                            placeholder="Search clubs…" autoComplete="off"
                            clearButton onClearButtonClick={() => setClubSearch('')} />
                        <button type="button" className="filter-pill-clear"
                                disabled={clubFilter.length === 0}
                                onClick={() => setClubFilter([])}>Clear</button>
                        {/* 2026-09-13 (Eric): tick every club the search matches. */}
                        {filteredClubs.length > 0 && (
                            <Checkbox
                                label={`Select all ${filteredClubs.length.toLocaleString()} ${clubSearch.trim() ? 'matching' : 'clubs'}`}
                                checked={filteredClubs.every(c => clubFilter.includes(c))}
                                onChange={(on) => setClubFilter(on
                                    ? Array.from(new Set([...clubFilter, ...filteredClubs]))
                                    : clubFilter.filter(c => !filteredClubs.includes(c)))} />
                        )}
                        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                            {filteredClubs.length === 0 ? (
                                <div style={{ color: 'var(--p-color-text-secondary)', fontSize: 13,
                                              padding: '8px 4px' }}>
                                    No matches for "{clubSearch}"
                                </div>
                            ) : (
                                <ChoiceList titleHidden title="Clubs" allowMultiple
                                    choices={filteredClubs.map(c => ({
                                        label: c, value: c,
                                        // Default behavior: single-club — once one is picked, grey
                                        // out the rest. Reports that pass multiClub={true} (e.g.
                                        // Numbers & Names) allow free multi-select.
                                        disabled: !multiClub && clubFilter.length > 0 && !clubFilter.includes(c),
                                    }))}
                                    selected={clubFilter}
                                    onChange={setClubFilter} />
                            )}
                        </div>
                    </div>
                </Popover>
            )}
            {trailing}
            {typeof setMergeTeamsLancaster === 'function' && (
                <Button
                    onClick={() => setMergeTeamsLancaster(v => !v)}
                    pressed={!!mergeTeamsLancaster}
                >
                    {mergeTeamsLancaster ? 'UnMerge Teams Lancaster' : 'Merge Teams Lancaster'}
                </Button>
            )}
            {typeof setMergeNjWarehouses === 'function' && (
                <Button
                    onClick={() => setMergeNjWarehouses(v => !v)}
                    pressed={!!mergeNjWarehouses}
                >
                    {mergeNjWarehouses ? 'UnMerge NJ Warehouses' : 'Merge NJ Warehouses'}
                </Button>
            )}
            {onExport && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    {extraActions || null}
                    <Button onClick={onExport} disabled={!!exportDisabled}>
                        {exportLabel || 'Export to csv'}
                    </Button>
                </div>
            )}
        </InlineStack>
    );
}

/* ---------- Orders report --------------------------------------------- */

// Right-side stacked bar (event-on-date buckets). Order = bottom-up in the stack.
const ORDERS_EVENT_BUCKETS = [
    { key: 'fulfilled',         label: 'Fulfilled',         color: '#10b981' },
    { key: 'completed_partial', label: 'Completed Partial', color: '#a855f7' },
    { key: 'partial',           label: 'Partial',           color: '#f59e0b' },
    { key: 'cancelled',         label: 'Cancelled',         color: '#ef4444' },
    { key: 'unfulfilled',       label: 'Unfulfilled',       color: '#94a3b8' },
];
const ORDERS_PLACED_COLOR = '#6366f1';

/* ---------- Top Days (was: Overview) ----------------------------------
   Renamed 2026-07-24. Same top-20 tables; the Teams Fulfillment Ramp chart
   moved to the new Today's Overview page since it's inherently single-day
   only. URL slug 'overview' preserved for bookmark stability. */

function TopDaysReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const { storeSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ panels: [], date_from: '', date_to: '' });
    const [err, setErr] = useState(null);

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc', l);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            const res = await fetchAuth(`/api/reports/orders_summary?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter]);

    useEffect(() => { load(); }, [load]);

    const handleExport = useCallback(() => {
        // Mirror the on-screen tables. Top 20 Days (aggregate-by-date) and
        // Top 20 Days by Site (location-day pairs) — both use the same
        // Modified metric: fulfilled + cancelled + partial + completed_partial.
        const panels = data.panels || [];

        // ── Top 20 Days (mirrors TopModifiedDays component) ─────────────
        const byDate = new Map();
        for (const panel of panels) {
            if (panel.is_combined) continue;
            for (const r of (panel.rows || [])) {
                const m = (r.fulfilled || 0) + (r.cancelled || 0)
                        + (r.partial || 0) + (r.completed_partial || 0);
                if (!m) continue;
                const slot = byDate.get(r.date) || { date: r.date, modified: 0, byLoc: [] };
                slot.modified += m;
                slot.byLoc.push({ name: panel.name, n: m });
                byDate.set(r.date, slot);
            }
        }
        const topDays = Array.from(byDate.values())
            .sort((a, b) => b.modified - a.modified || a.date.localeCompare(b.date))
            .slice(0, 20);

        // ── Top 20 Days by Site (mirrors TopModifiedDaysBySite) ─────────
        const bySite = [];
        for (const panel of panels) {
            if (panel.is_combined) continue;
            for (const r of (panel.rows || [])) {
                const m = (r.fulfilled || 0) + (r.cancelled || 0)
                        + (r.partial || 0) + (r.completed_partial || 0);
                if (!m) continue;
                bySite.push({
                    name: panel.name,
                    is_franchise: panel.is_franchise,
                    date: r.date,
                    modified: m,
                });
            }
        }
        const topBySite = bySite
            .sort((a, b) => b.modified - a.modified
                       || a.name.localeCompare(b.name)
                       || a.date.localeCompare(b.date))
            .slice(0, 20);

        const rows = [];
        rows.push(['Top 20 Days']);
        rows.push(['#', 'Date', 'Modified', 'Top Locations']);
        topDays.forEach((row, idx) => {
            const breakdown = row.byLoc
                .slice()
                .sort((a, b) => b.n - a.n)
                .slice(0, 3)
                .map(l => `${l.name} (${l.n})`)
                .join(' · ');
            rows.push([idx + 1, row.date, row.modified, breakdown]);
        });
        rows.push([]);  // blank separator
        rows.push(['Top 20 Days by Site']);
        rows.push(['#', 'Location', 'Franchise', 'Date', 'Modified']);
        topBySite.forEach((row, idx) => {
            rows.push([
                idx + 1, row.name,
                row.is_franchise ? 'yes' : 'no',
                row.date, row.modified,
            ]);
        });

        downloadCsv(`reporting_overview_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Top Days</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.panels || []).length}
                    />
                    <Text variant="bodySm" tone="subdued">
                        Window: {data.date_from || '—'} → {data.date_to || '—'} · {data.panels?.length || 0} location{data.panels?.length === 1 ? '' : 's'}
                    </Text>
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            {loading && (data.panels || []).length === 0 ? null : (
                <>
                    <TopModifiedDays panels={data.panels || []} />
                    <TopModifiedDaysBySite panels={data.panels || []} />
                </>
            )}
        </BlockStack>
    );
}

/* ---------- Today's Overview (2026-07-24) -----------------------------
   Fleet-wide snapshot for today. No filters. Backend enumerates all
   non-franchise locations across every store and computes each loc's own
   "today" in its own timezone (Vegas today vs Lancaster today) so ops teams
   can compare a full day's work per site regardless of clock offset.
   Renders one per-loc panel plus the Teams Fulfillment Ramp chart. */

function TodaysOverviewReport({ availableLocations: _unused }) {
    const fetchAuth = useAuthFetch();
    const { storeSlug } = useParams();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ panels: [] });
    const [err, setErr] = useState(null);
    // 2026-08-06: Today ↔ Yesterday toggle. When mode==='yesterday' the
    // backend swaps every panel to that loc's local yesterday (per-loc TZ
    // preserved) and the ramp chart re-scopes to the previous day.
    const [mode, setMode] = useState('today');
    // Sort state — mirror Orders-By-Club: click header to toggle asc/desc.
    // Default sort is Modified descending (busiest sites at the top).
    const [sortKey, setSortKey] = useState('modified');
    const [sortAsc, setSortAsc] = useState(false);
    const onSort = (k) => {
        if (sortKey === k) setSortAsc(v => !v);
        else { setSortKey(k); setSortAsc(false); }
    };

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const res = await fetchAuth(`/api/reports/todays_overview?date=${mode}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, mode]);

    useEffect(() => { load(); }, [load]);

    // Derive a shared date-range for the ramp chart. Each loc has its own
    // local_date; the chart currently takes a single dateFrom/dateTo pair
    // and self-scopes per-loc via its own endpoint. When mode==='yesterday'
    // pass yesterday's ISO date so the chart re-scopes; otherwise today.
    // The chart only uses the date bounds to fetch events, and event
    // timestamps are TZ-adjusted server-side per loc.
    const _rampDate = mode === 'yesterday' ? isoDaysAgo(1) : todayIso();

    // Flatten each panel to one row for the sortable table. The Modified
    // metric mirrors the definition used across Reporting: fulfilled +
    // cancelled + partial_entries + completed_partial. See Top Days /
    // TopModifiedDays for the canonical shape.
    //
    // Avg orders/hr (2026-08-06): numerator = fulfilled + partial +
    // completed_partial (excludes cancelled — user request). Denominator =
    // hours between first and last PPP event at this loc today. Renders as
    // '—' when the loc has no PPP events (nothing to divide by).
    const flatRows = useMemo(() => {
        return (data.panels || []).map(p => {
            const r = (p.rows || [])[0] || {};
            const partial = r.partial_entries || r.partial || 0;
            const modified = (r.fulfilled || 0) + (r.cancelled || 0)
                            + partial + (r.completed_partial || 0);
            const productivity = (r.fulfilled || 0) + partial + (r.completed_partial || 0);
            let elapsedHours = 0;
            if (p.first_ppp_utc && p.last_ppp_utc) {
                const t0 = Date.parse(p.first_ppp_utc);
                const t1 = Date.parse(p.last_ppp_utc);
                if (Number.isFinite(t0) && Number.isFinite(t1) && t1 > t0) {
                    elapsedHours = (t1 - t0) / 3_600_000;
                }
            }
            const avgOrdersPerHr = elapsedHours > 0
                ? productivity / elapsedHours
                : null;
            return {
                key:               `${p.store_key}:${p.shopify_location_id}`,
                location:          p.name || '',
                local_today:       p.local_date || p.local_today || '',
                timezone:          p.timezone || '',
                orders:            r.orders || 0,
                fulfilled:         r.fulfilled || 0,
                cancelled:         r.cancelled || 0,
                partial,
                completed_partial: r.completed_partial || 0,
                modified,
                avg_orders_per_hr: avgOrdersPerHr,
            };
        });
    }, [data]);

    const cols = [
        { key: 'location',          label: 'Location',          num: false },
        { key: 'local_today',       label: 'Local date',        num: false },
        { key: 'orders',            label: 'Placed',            num: true  },
        { key: 'fulfilled',         label: 'Fulfilled',         num: true  },
        { key: 'cancelled',         label: 'Cancelled',         num: true  },
        { key: 'partial',           label: 'Partial',           num: true  },
        { key: 'completed_partial', label: 'Completed Partial', num: true  },
        { key: 'modified',          label: 'Modified',          num: true  },
        // 2026-08-06 Avg orders/hr. Tooltip on the header explains the
        // numerator (Fulfilled + Partial + Completed Partial) — cancelled is
        // deliberately excluded because it isn't fulfillment work.
        { key: 'avg_orders_per_hr', label: 'Avg orders/hr',     num: true,
          tooltip: 'Avg orders/hr = (Fulfilled + Partial + Completed Partial) '
                 + '÷ hours between the first and last pick/press/pack event '
                 + 'at this location during the day\'s business window '
                 + '(6 AM local → 2 AM the next day local).' },
    ];

    // Only show locations with at least one Placed or Modified order today.
    // Zero-activity sites clutter the report — the operator only cares about
    // where work is actually happening. Requested 2026-07-25.
    const activeRows = useMemo(
        () => flatRows.filter(r => (r.orders || 0) > 0 || (r.modified || 0) > 0),
        [flatRows],
    );

    const sortedRows = useMemo(() => {
        const numeric = new Set(cols.filter(c => c.num).map(c => c.key));
        const out = activeRows.slice();
        out.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            if (numeric.has(sortKey)) {
                // Avg orders/hr uses null to mean "no PPP events yet, can't
                // compute". Force nulls to the bottom regardless of dir so
                // real numbers always cluster together.
                const anum = av == null ? -Infinity : Number(av);
                const bnum = bv == null ? -Infinity : Number(bv);
                return (sortAsc ? 1 : -1) * (anum - bnum);
            }
            return (sortAsc ? 1 : -1) * String(av || '').localeCompare(String(bv || ''));
        });
        return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeRows, sortKey, sortAsc]);

    const isYesterday = mode === 'yesterday';
    const headingSuffix = isYesterday ? '(yesterday)' : '(today)';

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <InlineStack align="space-between" blockAlign="start" gap="300" wrap={false}>
                    <BlockStack gap="200">
                        <Text variant="headingMd">Today's Overview</Text>
                        <Text variant="bodySm" tone="subdued">
                            Fleet-wide snapshot for {isYesterday ? 'yesterday' : 'today'}.
                            Each location's numbers are computed in its own
                            timezone so a full day of work per site compares
                            apples-to-apples. Franchise locations excluded.
                        </Text>
                    </BlockStack>
                    <ButtonGroup segmented>
                        <Button pressed={!isYesterday} onClick={() => setMode('today')}>
                            Today
                        </Button>
                        <Button pressed={isYesterday}  onClick={() => setMode('yesterday')}>
                            Yesterday
                        </Button>
                    </ButtonGroup>
                </InlineStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load Today's Overview">{err}</Banner>}

            {(data.panels || []).length > 0 && (
                <Card>
                    <BlockStack gap="300">
                        <Text variant="headingMd">Per-location activity {headingSuffix}</Text>
                        <div className="orders-summary-table-scroll">
                            <table className="orders-summary-table">
                                <thead>
                                    <tr>
                                        {cols.map(c => (
                                            <th key={c.key}
                                                title={c.tooltip || undefined}
                                                className={`sortable${c.num ? ' num' : ''}`}
                                                onClick={() => onSort(c.key)}>
                                                {c.label}
                                                {sortKey === c.key ? (sortAsc ? ' ▲' : ' ▼') : ''}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.length === 0 && (
                                        <tr><td colSpan={cols.length} style={{ textAlign: 'center', padding: 16, color: '#6d7175' }}>
                                            No activity {isYesterday ? 'yesterday' : 'today'} at any non-franchise location.
                                        </td></tr>
                                    )}
                                    {sortedRows.map(r => (
                                        <tr key={r.key}>
                                            <td>{r.location}</td>
                                            <td>
                                                <Text variant="bodySm" tone="subdued">
                                                    {r.local_today}{r.timezone ? ` (${r.timezone})` : ''}
                                                </Text>
                                            </td>
                                            <td className="num">{r.orders}</td>
                                            <td className="num">{r.fulfilled}</td>
                                            <td className="num">{r.cancelled}</td>
                                            <td className="num">{r.partial}</td>
                                            <td className="num">{r.completed_partial}</td>
                                            <td className="num"><strong>{r.modified}</strong></td>
                                            <td className="num">
                                                {r.avg_orders_per_hr == null
                                                    ? <Text as="span" tone="subdued">—</Text>
                                                    : r.avg_orders_per_hr.toFixed(1)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </BlockStack>
                </Card>
            )}

            {/* Teams Fulfillment Ramp chart — moved here from Top Days.
                Each loc's line starts at its own T=0 (first pick/press/pack
                event), so Vegas at 9 AM PT compares apples-to-apples with
                Lancaster at 6 AM ET on "hour N of shift". Ramp date follows
                the Today/Yesterday toggle. */}
            <TeamsFulfillmentRampChart
                dateFrom={_rampDate}
                dateTo={_rampDate}
                storeSlug={storeSlug}
            />
        </BlockStack>
    );
}

// ── Teams Fulfillment Ramp (Overview, single-day only) ──────────────────
// Compares Teams locations' fulfillment output over the course of the day.
// Each location's line starts at their own T=0 — first pick/press/pack
// event that day — so lines are apples-to-apples on "hour N of your shift"
// rather than wall-clock (which would penalize later-starting sites).
// Y is cumulative count of order_cache rows at this loc whose fulfilled_at
// (or first_partial_at) falls on the day.
function TeamsFulfillmentRampChart({ dateFrom, dateTo, storeSlug }) {
    const fetchAuth = useAuthFetch();
    const [loading, setLoading] = useState(false);
    const [data, setData]       = useState(null);
    const [err, setErr]         = useState(null);

    // Single-day gate. If a range is selected, render nothing.
    const isSingleDay = !!(dateFrom && dateTo && dateFrom === dateTo);

    useEffect(() => {
        if (!isSingleDay) { setData(null); return; }
        let cancelled = false;
        setLoading(true); setErr(null);
        const p = new URLSearchParams({
            date_from: dateFrom, date_to: dateTo, store: storeSlug || 'soccerpost',
        });
        fetchAuth(`/api/reports/teams_fulfillment_ramp?${p.toString()}`)
            .then(r => r.ok ? r.json() : r.text().then(t => { throw new Error(t || r.statusText); }))
            .then(j => { if (!cancelled) setData(j); })
            .catch(e => { if (!cancelled) setErr(e.message || String(e)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isSingleDay, dateFrom, dateTo, storeSlug, fetchAuth]);

    const option = useMemo(() => {
        if (!data || !(data.locations || []).length) return null;
        // X domain: 0 → largest elapsed_min across any series (padded to 5).
        let maxMin = 0;
        for (const loc of data.locations) {
            const lastPoint = loc.points[loc.points.length - 1];
            if (lastPoint && lastPoint.elapsed_min > maxMin) maxMin = lastPoint.elapsed_min;
        }
        // Round up to next hour and add 30 min padding
        const xMax = Math.max(60, Math.ceil((maxMin + 30) / 60) * 60);
        const xTicks = [];
        for (let h = 0; h * 60 <= xMax; h++) xTicks.push(h * 60);

        return {
            title: {
                text: 'Teams Locations — Fulfillment Over Shift',
                subtext: `${data.date} · Each line starts at that location's first fulfillment event (T=0)`,
                left: 'center',
                textStyle: { fontSize: 15, fontWeight: 600 },
                subtextStyle: { fontSize: 11 },
            },
            grid: { left: 60, right: 32, top: 90, bottom: 80 },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'line' },
                formatter: (params) => {
                    if (!params || !params.length) return '';
                    const mins = params[0].value[0];
                    const h = Math.floor(mins / 60);
                    const m = mins % 60;
                    const timeStr = `${h}h ${String(m).padStart(2, '0')}m`;
                    const rows = params
                        .filter(p => p.value && p.value[1] != null)
                        .sort((a, b) => b.value[1] - a.value[1])
                        .map(p => `<div style="display:flex;justify-content:space-between;gap:12px">
                            <span>${p.marker}${p.seriesName}</span>
                            <b>${p.value[1]}</b>
                        </div>`)
                        .join('');
                    return `<div style="font-weight:600;margin-bottom:4px">Elapsed: ${timeStr}</div>${rows}`;
                },
            },
            legend: {
                data: data.locations.map(l => l.location_name),
                bottom: 4, type: 'scroll',
            },
            xAxis: {
                type: 'value', name: 'Hours since first fulfillment event', nameLocation: 'middle', nameGap: 30,
                min: 0, max: xMax,
                interval: 60,
                axisLabel: {
                    formatter: (v) => `${Math.round(v / 60)}h`,
                },
                splitLine: { show: true, lineStyle: { color: '#eee' } },
            },
            yAxis: {
                type: 'value', name: 'Cumulative orders fulfilled', nameLocation: 'middle', nameGap: 44,
                min: 0,
                splitLine: { show: true, lineStyle: { color: '#eee' } },
            },
            series: data.locations.map(loc => ({
                name: loc.location_name,
                type: 'line',
                showSymbol: false,
                smooth: 0.2,
                lineStyle: { width: 2 },
                emphasis: { focus: 'series', lineStyle: { width: 3 } },
                data: loc.points.map(pt => [pt.elapsed_min, pt.cum_count]),
            })),
        };
    }, [data]);

    if (!isSingleDay) return null;

    return (
        <Card>
            <BlockStack gap="300">
                <Text variant="headingMd" as="h3">Teams Locations — Fulfillment Ramp</Text>
                <Text variant="bodySm" tone="subdued">
                    Y-axis counts orders at each Teams location whose fulfilled_at
                    or first_partial_at falls in the day's business window
                    (6 AM local → 2 AM the next day local) — so post-midnight
                    fulfillments roll UP into the previous day's ramp.
                </Text>
                {loading && <DelayedSpinner loading={loading} />}
                {err && <Banner tone="critical" title="Couldn't load chart">{err}</Banner>}
                {!loading && !err && data && (data.locations || []).length === 0 && (
                    <Banner tone="info">No Teams locations had any fulfillment activity on {data.date}.</Banner>
                )}
                {option && (
                    <ReactECharts
                        theme="polaris"
                        option={option}
                        notMerge={true}
                        style={{ height: 480, width: '100%' }}
                        opts={{ renderer: 'svg' }}
                    />
                )}
            </BlockStack>
        </Card>
    );
}


function TopModifiedDaysBySite({ panels }) {
    // Top 20 (location, date) pairs by Modified total — same metric as Top 20
    // Days but kept per-location, so a single big day for one site doesn't
    // crowd out other sites' standout days. Excludes the combined "All
    // Locations" panel.
    const top = useMemo(() => {
        const out = [];
        for (const panel of panels) {
            if (panel.is_combined) continue;
            for (const r of (panel.rows || [])) {
                const m = (r.fulfilled || 0) + (r.cancelled || 0)
                        + (r.partial || 0) + (r.completed_partial || 0);
                if (!m) continue;
                out.push({
                    name: panel.name, store_key: panel.store_key,
                    shopify_location_id: panel.shopify_location_id,
                    is_franchise: panel.is_franchise,
                    date: r.date, modified: m,
                });
            }
        }
        return out
            .sort((a, b) => b.modified - a.modified
                       || a.name.localeCompare(b.name)
                       || a.date.localeCompare(b.date))
            .slice(0, 20);
    }, [panels]);

    if (!top.length) return null;

    return (
        <Card>
            <BlockStack gap="200">
                <Text variant="headingMd">Top 20 Days by Site</Text>
                <Text variant="bodySm" tone="subdued">
                    Highest 20 single-day Modified totals across every location-day pair in the current window. The same site can appear more than once.
                </Text>
                <div className="orders-summary-table-scroll">
                    <table className="orders-summary-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Location</th>
                                <th>Date</th>
                                <th className="num">Modified</th>
                            </tr>
                        </thead>
                        <tbody>
                            {top.map((row, idx) => (
                                <tr key={`${row.store_key}:${row.shopify_location_id}:${row.date}`}>
                                    <td>{idx + 1}</td>
                                    <td>{row.name}{row.is_franchise ? (
                                        <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 5px',
                                                       background: '#fef3c7', color: '#92400e',
                                                       borderRadius: 3 }}>Franchise</span>
                                    ) : null}</td>
                                    <td>{row.date}</td>
                                    <td className="num"><strong>{row.modified}</strong></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </BlockStack>
        </Card>
    );
}

/* ---------- Orders report --------------------------------------------- */

// Cumulative transform shared by the Orders report and the embedded panels on
// Location Summary: per panel, sort rows ASC and walk accumulating from
// prior_state. Stack-correct (still-partial = ever-partial − completed_partial;
// unfulfilled = orders − fulfilled − ever-partial − cancelled). Pure — inputs
// untouched. See the OrdersReport displayPanels comment for the full rationale.
function _ordersCumulativePanels(panels) {
    return (panels || []).map(panel => {
        const asc = (panel.rows || []).slice().sort((a, b) =>
            String(a.date).localeCompare(String(b.date)));
        const ps = panel.prior_state || {};
        let cOrd = ps.orders || 0, cFul = ps.fulfilled || 0, cCan = ps.cancelled || 0,
            cPar = ps.partial || 0, cCp = ps.completed_partial || 0;
        const cumRows = asc.map(r => {
            cOrd += r.orders || 0; cFul += r.fulfilled || 0; cCan += r.cancelled || 0;
            cPar += r.partial || 0; cCp += r.completed_partial || 0;
            const stillPartial = Math.max(0, cPar - cCp);
            const unfulfilled  = Math.max(0, cOrd - cFul - cPar - cCan);
            return { ...r, orders: cOrd, fulfilled: cFul, cancelled: cCan,
                     partial: stillPartial, completed_partial: cCp, unfulfilled };
        });
        return { ...panel, rows: cumRows };
    });
}

function OrdersReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(14));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    // kind = order-classification filter. '' (All), 'team_portal', 'retail'.
    // Add-On orders are counted in BOTH TP and Retail (per spec) — so the
    // SQL filter is the inverse (source <> the opposite pure bucket).
    const [kind, setKind] = useState(() => {
        const v = searchParams.get('kind') || '';
        return (v === 'team_portal' || v === 'retail') ? v : '';
    });
    // view = 'by_day' (default) or 'cumulative'. Toggles the chart + table
    // from per-day counts to running-total values across the date range.
    // Transform is client-side — endpoint always returns daily rows.
    const [view, setView] = useState(() => {
        const v = searchParams.get('view') || '';
        return v === 'cumulative' ? 'cumulative' : 'by_day';
    });
    // Chart legend hide-set: series names the user clicked off in the
    // ECharts legend. Persisted as `?chart_off=Orders,Fulfilled,...` so
    // the choice survives date/location/kind/view changes AND page
    // reload. Same set applies to every panel on the page.
    const [chartHidden, setChartHidden] = useState(() => {
        const raw = searchParams.get('chart_off') || '';
        return new Set(raw.split(',').map(s => s.trim()).filter(Boolean));
    });
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ panels: [], date_from: '', date_to: '' });
    const [err, setErr] = useState(null);

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc', l);
        if (kind) p.set('kind', kind);
        if (view === 'cumulative') p.set('view', view);
        if (chartHidden.size > 0) p.set('chart_off', [...chartHidden].join(','));
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, kind, view, chartHidden, setSearchParams]);

    // Cumulative transform: for each panel, sort rows by date ASC and walk
    // through accumulating orders / fulfilled / cancelled / partial /
    // completed_partial. Returns NEW rows; original `data.panels` untouched
    // (so toggling back to By Day is instant and lossless).
    const displayPanels = useMemo(() => {
        if (view !== 'cumulative') return data.panels || [];
        return (data.panels || []).map(panel => {
            const asc = (panel.rows || []).slice().sort((a, b) =>
                String(a.date).localeCompare(String(b.date)));
            // Seed running totals from prior_state (cumulative state at EOD
            // df-1 across the entire materialized history). Without this the
            // final cumulative would only reflect in-range deltas — the "Orders
            // at 6/18" value would shrink whenever the user narrowed the
            // window. Cumulative is meant to track the snapshot of state, so
            // wider/narrower windows must converge to the same final number.
            //
            // Stack-correct derivation: each placed order is in EXACTLY one of
            // {fulfilled, completed_partial, still-partial, cancelled,
            // unfulfilled} at any point in time. Raw `partial` events double-
            // count orders that later transition to completed_partial, and the
            // backend's per-day `unfulfilled` is a NOW-snapshot per-placed-day
            // (not a cumulative event). Derive both at display time so the
            // stack always sums to the Orders line:
            //   still-partial = ever-partial − completed_partial
            //   unfulfilled   = orders − fulfilled − ever-partial − cancelled
            // (The `ever-partial − completed_partial + completed_partial`
            // pair collapses to just `ever-partial` in the unfulfilled formula,
            // which is why the subtraction looks asymmetric.)
            const ps = panel.prior_state || {};
            let cOrd = ps.orders || 0;
            let cFul = ps.fulfilled || 0;
            let cCan = ps.cancelled || 0;
            let cPar = ps.partial || 0;       // ever-partial (includes those that later completed)
            let cCp  = ps.completed_partial || 0;
            const cumRows = asc.map(r => {
                cOrd += r.orders || 0;
                cFul += r.fulfilled || 0;
                cCan += r.cancelled || 0;
                cPar += r.partial || 0;
                cCp  += r.completed_partial || 0;
                const stillPartial = Math.max(0, cPar - cCp);
                const unfulfilled  = Math.max(0, cOrd - cFul - cPar - cCan);
                return {
                    ...r,
                    orders: cOrd, fulfilled: cFul, cancelled: cCan,
                    partial: stillPartial, completed_partial: cCp,
                    unfulfilled: unfulfilled,
                };
            });
            return { ...panel, rows: cumRows };
        });
    }, [data.panels, view]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            if (kind) p.set('kind', kind);
            const res = await fetchAuth(`/api/reports/orders_summary?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            const j = await res.json();
            // Defensive client-side sort: Soccer Post (non-franchise) first
            // alphabetical, then franchises alphabetical at the bottom.
            j.panels = (j.panels || []).slice().sort((a, b) => {
                // "All Locations" combined panel always sits at the top.
                const ca = a.is_combined ? 1 : 0;
                const cb = b.is_combined ? 1 : 0;
                if (ca !== cb) return cb - ca;
                const fa = a.is_franchise ? 1 : 0;
                const fb = b.is_franchise ? 1 : 0;
                if (fa !== fb) return fa - fb;
                return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
            });
            setData(j);
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, kind]);

    useEffect(() => { load(); }, [load]);

    const handleExport = useCallback(() => {
        const isCum = view === 'cumulative';
        // Column set matches what's shown in the table for the current view.
        const header = ['Location', 'Store', 'Franchise', 'Date',
                        'Orders', 'Fulfilled', 'Cancelled', 'Partial',
                        'Completed partial', ...(isCum ? ['Unfulfilled'] : ['Unfulfilled', 'Modified'])];
        const rows = [header];
        const panelsToExport = isCum ? (displayPanels || data.panels || []) : (data.panels || []);
        for (const panel of panelsToExport) {
            for (const r of (panel.rows || [])) {
                const partialCell = isCum ? (r.partial || 0) : (r.partial_entries || 0);
                const lastCell    = isCum ? (r.unfulfilled || 0)
                                          : ((r.fulfilled || 0) + (r.cancelled || 0)
                                             + (r.partial_entries || 0) + (r.completed_partial || 0));
                rows.push([
                    panel.name, panel.store_key,
                    panel.is_franchise ? 'yes' : 'no', r.date,
                    r.orders || 0,
                    r.fulfilled || 0, r.cancelled || 0,
                    partialCell, r.completed_partial || 0,
                    ...(isCum ? [lastCell] : [r.unfulfilled || 0, lastCell]),
                ]);
            }
        }
        const kindSlug = kind ? `_${kind}` : '';
        const viewSlug = isCum ? '_cumulative' : '';
        downloadCsv(`reporting_orders${kindSlug}${viewSlug}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, displayPanels, kind, view]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Orders</Text>
                    <InlineStack gap="400" blockAlign="center">
                        <ButtonGroup variant="segmented">
                            <Button pressed={kind === ''}            onClick={() => setKind('')}>All</Button>
                            <Button pressed={kind === 'team_portal'} onClick={() => setKind('team_portal')}>Team Portal</Button>
                            <Button pressed={kind === 'retail'}      onClick={() => setKind('retail')}>Retail</Button>
                        </ButtonGroup>
                        <ButtonGroup variant="segmented">
                            <Button pressed={view === 'by_day'}     onClick={() => setView('by_day')}>By Day</Button>
                            <Button pressed={view === 'cumulative'} onClick={() => setView('cumulative')}>Cumulative</Button>
                        </ButtonGroup>
                    </InlineStack>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.panels || []).length}
                    />
                    <Text variant="bodySm" tone="subdued">
                        Window: {data.date_from || '—'} → {data.date_to || '—'} · {data.panels?.length || 0} location{data.panels?.length === 1 ? '' : 's'}
                    </Text>
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            {loading && (data.panels || []).length === 0 ? null
             : (data.panels || []).length === 0 ? (
                <Card>
                    <div style={{ padding: 32, textAlign: 'center', color: '#6d7175' }}>
                        No locations match the current filter.
                    </div>
                </Card>
            ) : (
                displayPanels.map(panel => (
                    <OrdersPanel key={`${panel.store_key}:${panel.shopify_location_id}`}
                                 panel={panel} view={view}
                                 chartHidden={chartHidden}
                                 setChartHidden={setChartHidden} />
                ))
            )}
        </BlockStack>
    );
}

// Top 20 Days — used on the Overview landing page. Shows the days with the
// largest "Modified" totals (fulfilled + partial + completed-partial +
// cancelled) summed across every location in the current window. The combined
// "All Locations" panel is excluded so we sum from the per-location panels
// (which the backend de-duplicates split-FO orders for).
function TopModifiedDays({ panels }) {
    const top = useMemo(() => {
        const byDate = new Map();
        for (const panel of panels) {
            if (panel.is_combined) continue;
            for (const r of (panel.rows || [])) {
                const m = (r.fulfilled || 0) + (r.cancelled || 0)
                        + (r.partial || 0) + (r.completed_partial || 0);
                if (!m) continue;
                const slot = byDate.get(r.date) || { date: r.date, modified: 0, byLoc: [] };
                slot.modified += m;
                slot.byLoc.push({ name: panel.name, n: m });
                byDate.set(r.date, slot);
            }
        }
        return Array.from(byDate.values())
            .sort((a, b) => b.modified - a.modified || a.date.localeCompare(b.date))
            .slice(0, 20);
    }, [panels]);

    if (!top.length) return null;

    return (
        <Card>
            <BlockStack gap="200">
                <Text variant="headingMd">Top 20 Days</Text>
                <Text variant="bodySm" tone="subdued">
                    Days with the largest Modified total (fulfilled + partially fulfilled + completed-fulfillment + cancelled) summed across every location in the current window.
                </Text>
                <div className="orders-summary-table-scroll">
                    <table className="orders-summary-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Date</th>
                                <th className="num">Modified</th>
                                <th>Top Locations</th>
                            </tr>
                        </thead>
                        <tbody>
                            {top.map((row, idx) => {
                                const breakdown = row.byLoc
                                    .slice()
                                    .sort((a, b) => b.n - a.n)
                                    .slice(0, 3)
                                    .map(l => `${l.name} (${l.n})`)
                                    .join(' · ');
                                return (
                                    <tr key={row.date}>
                                        <td>{idx + 1}</td>
                                        <td>{row.date}</td>
                                        <td className="num"><strong>{row.modified}</strong></td>
                                        <td style={{ color: '#6d7175' }}>{breakdown}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </BlockStack>
        </Card>
    );
}

function PairedBarChart({ rows, view = 'by_day', chartHidden, setChartHidden }) {
    // Always sort ASC by date (not just reverse) — when the parent has
    // already produced ASC rows (e.g. cumulative-view transform), a blind
    // `.reverse()` would flip them back to DESC and put today on the LEFT.
    const asc = useMemo(() => (rows || []).slice().sort((a, b) =>
        String(a.date).localeCompare(String(b.date))), [rows]);
    const dates = useMemo(() => asc.map(r => r.date), [asc]);
    const isCumulative = view === 'cumulative';

    const option = useMemo(() => {
        const seriesNames = ['Orders', ...ORDERS_EVENT_BUCKETS
            .filter(b => isCumulative || b.key !== 'unfulfilled')
            .map(b => b.label)];
        // legend.selected: {name → boolean}. False = user turned it off.
        // Missing = default (on). Persisting the OFF set (not the ON set)
        // means new/renamed series come on by default — future-proof.
        const selected = {};
        for (const n of seriesNames) selected[n] = !(chartHidden && chartHidden.has(n));
        return ({
        animation: false,
        grid: { left: 8, right: 12, top: 28, bottom: 60, containLabel: true },
        legend: {
            top: 0,
            data: seriesNames,
            selected,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                if (!params?.length) return '';
                const date = params[0].axisValueLabel;
                const lines = params
                    .filter(p => p.value > 0)
                    .map(p => {
                        const swatch = `<span style="display:inline-block;width:10px;height:10px;background:${p.color};border-radius:2px;margin-right:6px"></span>`;
                        return `<div>${swatch}${p.seriesName}: <strong>${p.value}</strong></div>`;
                    })
                    .join('');
                return `<div style="font-weight:600;margin-bottom:4px">${date}</div>${lines || '<div style="color:#6d7175">No activity</div>'}`;
            },
        },
        xAxis: {
            type: 'category',
            data: dates,
            axisLabel: {
                formatter: v => v?.slice(5),  // MM-DD
                hideOverlap: true,
            },
            axisTick: { alignWithLabel: true },
        },
        yAxis: {
            type: 'value',
            minInterval: 1,
        },
        // Slider auto-shows when there are too many days to fit comfortably.
        dataZoom: dates.length > 30 ? [{
            type: 'slider',
            xAxisIndex: 0,
            start: Math.max(0, 100 - (30 / Math.max(dates.length, 1)) * 100),
            end:   100,
        }] : [],
        series: isCumulative ? [
            // Outcome series stacked — Fulfilled+Completed Partial+Partial+
            // Cancelled+Unfulfilled = current state of every placed order, so
            // the stacked total tracks the Orders line. Orders itself is NOT
            // stacked — it's drawn on top as a reference line. Smoothed area
            // fills give Grafana-parity look.
            ...ORDERS_EVENT_BUCKETS.map(b => ({
                name: b.label,
                type: 'line',
                stack: 'outcomes',
                data: asc.map(r => r[b.key] || 0),
                smooth: true,
                symbol: 'none',
                lineStyle: { width: 1, color: b.color, opacity: 0.5 },
                areaStyle: { color: b.color, opacity: 0.55 },
                itemStyle: { color: b.color },
            })),
            {
                name: 'Orders',
                type: 'line',
                data: asc.map(r => r.orders || 0),
                smooth: true,
                symbol: 'none',
                z: 10,
                lineStyle: { width: 2.5, color: ORDERS_PLACED_COLOR },
                itemStyle: { color: ORDERS_PLACED_COLOR },
            },
        ] : [
            // By-day view: original 4-bucket event stack (Fulfilled, Completed
            // Partial, Partial entries, Cancelled). Orders shown as separate
            // companion bar. NO Unfulfilled in by-day — that's a state, not an
            // event.
            {
                name: 'Orders',
                type: 'bar',
                data: asc.map(r => r.orders || 0),
                barGap: '20%',
                barCategoryGap: '40%',
                itemStyle: { color: ORDERS_PLACED_COLOR },
            },
            ...ORDERS_EVENT_BUCKETS.filter(b => b.key !== 'unfulfilled').map(b => ({
                name: b.label,
                type: 'bar',
                stack: 'events',
                // For Partial, use entries-only (orders that went partial that
                // day) rather than the net delta — matches the original Orders
                // Summary semantics.
                data: asc.map(r => (b.key === 'partial' ? (r.partial_entries || 0) : (r[b.key] || 0))),
                itemStyle: { color: b.color },
            })),
        ],
    }); }, [asc, dates, isCumulative, chartHidden]);

    // Capture legend clicks so the OFF set persists across date/loc/view
    // changes AND page reload (see chartHidden in OrdersReport).
    const onEvents = useMemo(() => ({
        legendselectchanged: (evt) => {
            if (!setChartHidden || !evt || !evt.selected) return;
            setChartHidden(prev => {
                const next = new Set(prev || []);
                for (const [name, on] of Object.entries(evt.selected)) {
                    if (on) next.delete(name);
                    else next.add(name);
                }
                return next;
            });
        },
    }), [setChartHidden]);

    return (
        <ReactECharts
            theme="polaris"
            option={option}
            // notMerge: discard the previous series array before applying the
            // new option. Without this, switching from Cumulative (5 stacked
            // area lines + Orders line) to By Day (bars) leaves the old area
            // lines rendered behind the new bars — series are merged by index,
            // and the line-series objects never get torn down.
            notMerge={true}
            onEvents={onEvents}
            style={{ height: 600, width: '100%' }}
            opts={{ renderer: 'svg' }}
        />
    );
}

function OrdersPanel({ panel, view = 'by_day', chartHidden, setChartHidden }) {
    const isCum = view === 'cumulative';
    const totals = useMemo(() => {
        if (isCum) {
            // 2026-09-02: backlog-as-of-end-date + whole-window flows.
            // See ordersCumulativeTotals.js for why last−first was wrong.
            return ordersCumulativeTotals(panel);
        }
        const acc = { orders: 0, fulfilled: 0, cancelled: 0, partial: 0,
                      partial_entries: 0, completed_partial: 0,
                      unfulfilled: 0, modified: 0 };
        for (const r of (panel.rows || [])) {
            acc.orders            += r.orders            || 0;
            acc.fulfilled         += r.fulfilled         || 0;
            acc.cancelled         += r.cancelled         || 0;
            acc.partial           += r.partial           || 0;
            acc.partial_entries   += r.partial_entries   || 0;
            acc.completed_partial += r.completed_partial || 0;
            acc.unfulfilled       += r.unfulfilled       || 0;
        }
        // Modified = total day-by-day events (fulfilled + cancelled +
        // partial-entries + completed_partial) — entries only, not net.
        acc.modified = acc.fulfilled + acc.cancelled + acc.partial_entries + acc.completed_partial;
        return acc;
    }, [panel.rows, isCum]);

    const allZero = (panel.rows || []).every(r => !r.orders && !r.fulfilled && !r.cancelled
                                                && !r.partial && !r.completed_partial);

    // Sortable columns. Default: date descending (newest first).
    const [sortKey, setSortKey] = useState('date');
    const [sortDir, setSortDir] = useState('desc');
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir('desc'); }
    };
    const sortedRows = useMemo(() => {
        const rows = (panel.rows || []).slice();
        const modOf = (r) => (r.fulfilled || 0) + (r.cancelled || 0)
                            + (r.partial_entries || 0) + (r.completed_partial || 0);
        rows.sort((a, b) => {
            const av = sortKey === '_modified' ? modOf(a) : a[sortKey];
            const bv = sortKey === '_modified' ? modOf(b) : b[sortKey];
            // String compare for date (ISO sorts chronologically); numeric for the rest.
            const cmp = sortKey === 'date'
                ? String(av).localeCompare(String(bv))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [panel.rows, sortKey, sortDir]);
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    return (
        <Card>
            <BlockStack gap="300">
                <div className="orders-panel-sticky-header">
                    <InlineStack gap="200" blockAlign="baseline">
                        <Text variant="headingMd">▾ {panel.name}</Text>
                        {panel.is_franchise && (
                            <span style={{ fontSize: 11, padding: '2px 6px',
                                           background: '#fef3c7', color: '#92400e',
                                           borderRadius: 3 }}>Franchise</span>
                        )}
                        <Text variant="bodySm" tone="subdued">
                            {isCum ? (
                                <>
                                    <span className="sf-tip sf-tip--left" data-tip="Orders placed between the start and end dates (both days included) at the selected location(s). Each order counts once, however many locations or fulfillments it spans.">{totals.orders} orders</span> · <span className="sf-tip sf-tip--left" data-tip="Orders that became fully fulfilled (every item shipped or picked up) between the start and end dates — including orders placed before the start date.">{totals.fulfilled} fulfilled</span> · <span className="sf-tip sf-tip--left" data-tip="Orders cancelled between the start and end dates.">{totals.cancelled} cancelled</span> · <span className="sf-tip sf-tip--left" data-tip="Partial-fulfillment events between the start and end dates: orders that became partially shipped, plus partials that then finished. An order that did both counts in each.">{totals.partial + totals.completed_partial} partial activity</span> · <span className="sf-tip sf-tip--left" data-tip="Open backlog on the end date: every order placed on or before the end date — including ones placed before the start date — that is still not fulfilled and not partially fulfilled. This is the latest row of the table, not a change over the window. It can read slightly lower than the Orders list's Unfulfilled chip, which also counts in-progress orders."><strong>{totals.unfulfilled} unfulfilled</strong></span>
                                </>
                            ) : (
                                <>
                                    <span className="sf-tip sf-tip--left" data-tip="Distinct orders placed in this window at the selected location(s). Each order counts once no matter how many locations or fulfillments it spans.">{totals.orders} orders</span> · <span className="sf-tip sf-tip--left" data-tip="Fulfillment EVENTS in the window (order fully shipped/picked up), summed day by day. An order fulfilled in two waves counts twice — this is activity, not a distinct-order count.">{totals.fulfilled} fulfilled</span> · <span className="sf-tip sf-tip--left" data-tip="Cancellation events in the window.">{totals.cancelled} cancelled</span> · <span className="sf-tip sf-tip--left" data-tip="Partial-fulfillment events in the window (a wave shipped, or a partial completed), summed day by day.">{totals.partial_entries + totals.completed_partial} partial activity</span> · <span className="sf-tip sf-tip--left" data-tip="Orders placed in this window that are still untouched right now — not fulfilled, not partially shipped, not cancelled. Same orders as the Orders list's Unfulfilled chip for these dates."><strong>{totals.unfulfilled} unfulfilled</strong></span> · <span className="sf-tip sf-tip--left" data-tip="Total day-by-day fulfillment EVENTS = fulfilled + cancelled + partial + completed-partial. An order can be modified several times, so do NOT subtract this from Orders to get unfulfilled — switch to the Cumulative view for that."><strong>{totals.modified} modified</strong></span>
                                </>
                            )}
                        </Text>
                    </InlineStack>
                </div>
                {allZero ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#6d7175' }}>
                        No order activity in this window.
                    </div>
                ) : (
                    <div className="reporting-chart-table-grid"
                         style={{ display: 'grid',
                                  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                                  gap: 24, alignItems: 'start' }}>
                        <div>
                            <Text variant="bodySm" tone="subdued">
                                {panel.name} — Orders placed (left) vs events (right) per day
                            </Text>
                            <PairedBarChart rows={panel.rows} view={view}
                                            chartHidden={chartHidden}
                                            setChartHidden={setChartHidden} />
                        </div>
                        <div>
                            <Text variant="bodySm" tone="subdued">{panel.name} — All Orders</Text>
                            <div className="orders-summary-table-scroll">
                                <table className="orders-summary-table">
                                    <thead>
                                        <tr>
                                            <th className="sortable" onClick={() => handleSort('date')}>Date{arrow('date')}</th>
                                            <th className="num sortable sf-tip" data-tip="Orders PLACED on this date (cumulative view: total placed through this date). Distinct orders." onClick={() => handleSort('orders')}>Orders{arrow('orders')}</th>
                                            <th className="num sortable sf-tip" data-tip="Orders fully fulfilled (cumulative view: total fulfilled through this date)." onClick={() => handleSort('fulfilled')}>Fulfilled{arrow('fulfilled')}</th>
                                            <th className="num sortable sf-tip" data-tip="Orders cancelled (cumulative view: total cancelled through this date)." onClick={() => handleSort('cancelled')}>Cancelled{arrow('cancelled')}</th>
                                            <th className="num sortable sf-tip" data-tip="Partially-fulfilled orders — some but not all of the order shipped." onClick={() => handleSort(isCum ? 'partial' : 'partial_entries')}>Partial{arrow(isCum ? 'partial' : 'partial_entries')}</th>
                                            <th className="num sortable sf-tip" data-tip="Orders that were partially fulfilled earlier and have now been fully completed." onClick={() => handleSort('completed_partial')}>Completed Partial{arrow('completed_partial')}</th>
                                            {isCum
                                                ? <th className="num sortable sf-tip" data-tip="CUMULATIVE backlog as of this date: orders placed on or before this date that are still not fulfilled and not partial. This column runs against the ALL-TIME placed total on the left (not the window total in the header), which is why the row Orders figure is larger than the panel header." onClick={() => handleSort('unfulfilled')}>Unfulfilled{arrow('unfulfilled')}</th>
                                                : <>
                                                    <th className="num sortable sf-tip" data-tip="Orders placed on this date that are still untouched right now — not fulfilled, not partially shipped, not cancelled." onClick={() => handleSort('unfulfilled')}>Unfulfilled{arrow('unfulfilled')}</th>
                                                    <th className="num sortable sf-tip" data-tip="Fulfillment events on this date = fulfilled + cancelled + partial + completed-partial. Activity count, not distinct orders." onClick={() => handleSort('_modified')}>Modified{arrow('_modified')}</th>
                                                  </>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedRows.map(r => {
                                            // By-day: Partial column = entries-only count of orders that went partial that day.
                                            // Cumulative: Partial column = net count currently in partial state (running total of delta).
                                            const partialCell = isCum ? r.partial : (r.partial_entries || 0);
                                            const lastCell    = isCum ? (r.unfulfilled || 0)
                                                                      : ((r.fulfilled || 0) + (r.cancelled || 0)
                                                                         + (r.partial_entries || 0) + (r.completed_partial || 0));
                                            return (
                                                <tr key={r.date}>
                                                    <td>{r.date}</td>
                                                    <td className="num">{r.orders}</td>
                                                    <td className="num">{r.fulfilled}</td>
                                                    <td className="num">{r.cancelled}</td>
                                                    <td className="num">{partialCell}</td>
                                                    <td className="num">{r.completed_partial}</td>
                                                    {!isCum && <td className="num">{r.unfulfilled || 0}</td>}
                                                    <td className="num">{lastCell}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </BlockStack>
        </Card>
    );
}

/* ---------- Picking report -------------------------------------------- */

function PickingReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    // URL is the source of truth so a report can be shared via link. Seed
    // state from search params, then mirror state → URL on every change.
    const [searchParams, setSearchParams] = useSearchParams();
    const urlFrom    = searchParams.get('from');
    const urlTo      = searchParams.get('to');
    const urlMetric  = searchParams.get('metric');
    const urlLocs    = searchParams.getAll('loc');
    const VALID_METRICS = ['items', 'apparel_items', 'embellishment_items', 'line_items', 'orders'];

    const [dateFrom, setDateFrom] = useState(urlFrom || todayIso());
    const [dateTo,   setDateTo]   = useState(urlTo   || todayIso());
    const [locationFilter, setLocationFilter] = useState(urlLocs);
    const [metric, setMetric] = useState(VALID_METRICS.includes(urlMetric) ? urlMetric : 'items');
    const [mergeTeamsLancaster, setMergeTeamsLancaster] = useState(false);
    const [mergeNjWarehouses, setMergeNjWarehouses] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('items');
    const [sortDir, setSortDir] = useState('desc');

    // State → URL sync. `replace` so refining filters doesn't pollute back-history.
    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        if (metric && metric !== 'items') p.set('metric', metric);
        for (const l of locationFilter) p.append('loc', l);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, metric, locationFilter, setSearchParams]);

    // `tip` = the header tooltip (2026-10-02, Eric): what each column counts, in plain words.
    const PICKING_COLUMNS = [
        { key: 'picker',              label: 'Picker',     sortType: 'string',
          tip: 'The person who picked, by their portal PIN. One row per picker per location.' },
        { key: 'store',               label: 'Store',      sortType: 'string',
          tip: 'The Shopify store the orders belong to.' },
        { key: 'location',            label: 'Location',   sortType: 'string',
          tip: 'The warehouse or Teams location where the picking was done.' },
        { key: 'items',               label: 'Items',      sortType: 'number',
          tip: 'Units picked in this window: every unit marked picked, minus any that were un-picked. Apparel plus embellishments.' },
        { key: 'apparel_items',       label: 'Apparel',    sortType: 'number',
          tip: 'Units picked that are products: jerseys, shorts, socks, balls and so on. Everything that is not an embellishment.' },
        { key: 'embellishment_items', label: 'Embel.',     sortType: 'number',
          tip: 'Units picked that are embellishments: logos, numbers and names that get pressed onto a garment.' },
        { key: 'scan_items',          label: 'Scan',       sortType: 'number',
          tip: 'Units picked by scanning a barcode.' },
        { key: 'manual_items',        label: 'Manual',     sortType: 'number',
          tip: 'Units picked by tapping instead of scanning.' },
        { key: 'pct_scanned',         label: '% Scanned',  sortType: 'number',
          tip: 'Scanned units as a share of all units picked. Higher means fewer picks were tapped by hand.' },
        { key: 'line_items',          label: 'Line items', sortType: 'number',
          tip: 'Different order lines the picker picked from. Five units of one item on one order is one line item.' },
        { key: 'orders',              label: 'Orders',     sortType: 'number',
          tip: 'Different orders the picker picked at least one unit on in this window, finished or not.' },
        // Average order picking time (2026-10-02, Eric): opened → fully picked, stored with each order.
        { key: 'avg_order_seconds',   label: 'Avg order time', sortType: 'number',
          tip: 'For orders that became fully picked in this window: this picker\u2019s time on each, from opening the order in the Picking Portal to their last pick, averaged. Looking without picking adds nothing, and a gap of over 30 minutes is not counted.' },
        // Average total picking time per day: the picker's session time ÷ the days they picked.
        { key: 'avg_seconds_per_day', label: 'Avg time / day', sortType: 'number',
          tip: 'The picker\u2019s total picking time (first pick to closing each order) divided by the days they picked in this window. It covers every order they worked on, finished or not.' },
    ];
    const filteredRows = useMemo(
        () => (data.rows || []).filter(r => (r.lifetime_orders || 0) >= 10),
        [data.rows]
    );
    const sortedRows = useMemo(() => {
        const col = PICKING_COLUMNS.find(c => c.key === sortKey) || PICKING_COLUMNS[0];
        const rows = filteredRows.slice();
        rows.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const cmp = col.sortType === 'string'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [filteredRows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = PICKING_COLUMNS.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            if (mergeTeamsLancaster) p.set('merge_teams_lancaster', 'true');
            if (mergeNjWarehouses) p.set('merge_nj_warehouses', 'true');
            // No store filter — backend defaults to all stores (admins only;
            // site_managers are auto-scoped server-side anyway).
            const res = await fetchAuth(`/api/reports/picking?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, mergeTeamsLancaster, mergeNjWarehouses]);

    useEffect(() => { load(); }, [load]);

    const metricKeyToLabel = {
        items: 'Items',
        apparel_items: 'Apparel',
        embellishment_items: 'Embellishments',
        line_items: 'Line items',
        orders: 'Orders',
    };
    const echartOption = useMemo(() => {
        const sorted = filteredRows.slice().sort((a, b) => b[metric] - a[metric]);
        const labels = sorted.map(r => r.location ? `${r.picker} — ${r.location}` : r.picker);
        const values = sorted.map(r => r[metric] || 0);
        return {
            grid: { left: 60, right: 30, top: 30, bottom: 140, containLabel: false },
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: {
                    rotate: 45,
                    interval: 0,
                    fontSize: 11,
                    align: 'right',
                    verticalAlign: 'top',
                },
                axisTick: { alignWithLabel: true },
            },
            yAxis: { type: 'value', minInterval: 1 },
            series: [{
                name: metricKeyToLabel[metric],
                type: 'bar',
                data: values,
                itemStyle: { color: '#5BA0E5' },
            }],
        };
    }, [filteredRows, metric]);

    const totals = useMemo(() => filteredRows.reduce((acc, r) => {
        acc.items               += r.items;
        acc.apparel_items       += r.apparel_items || 0;
        acc.embellishment_items += r.embellishment_items || 0;
        acc.line_items          += r.line_items;
        acc.orders              += r.orders;
        acc.picking_seconds     += r.picking_seconds || 0;
        acc.timed_orders        += r.timed_orders || 0;
        return acc;
    }, { items: 0, apparel_items: 0, embellishment_items: 0, line_items: 0, orders: 0, picking_seconds: 0, timed_orders: 0 }), [filteredRows]);
    // Overall average order picking time = opened → fully picked, over every order finished in the
    // window (the backend counts a shared order once; not an average of the pickers' averages).
    const orderTime = data.order_time || {};
    const avgOrderSeconds = orderTime.orders ? orderTime.seconds / orderTime.orders : null;
    // Average total picking time per day = everyone's picking-session time ÷ the days anyone picked.
    const pickingDays = useMemo(() => {
        const days = new Set();
        for (const r of filteredRows) for (const d of (r.picking_dates || [])) days.add(d);
        return days.size;
    }, [filteredRows]);
    const avgSecondsPerDay = pickingDays ? totals.picking_seconds / pickingDays : null;

    const handleExport = useCallback(() => {
        const rows = [['Picker', 'Store', 'Location', 'Items', 'Apparel', 'Embel.', 'Scan', 'Manual', '% Scanned', 'Line items', 'Orders', 'Avg order time (seconds)', 'Avg time per day (seconds)', 'Days picked']];
        for (const r of sortedRows) rows.push([
            r.picker, r.store || '', r.location || '', r.items,
            r.apparel_items || 0, r.embellishment_items || 0,
            r.scan_items || 0, r.manual_items || 0,
            Number(r.pct_scanned || 0).toFixed(1),
            r.line_items, r.orders,
            r.avg_order_seconds == null ? '' : Math.round(r.avg_order_seconds),
            r.avg_seconds_per_day == null ? '' : Math.round(r.avg_seconds_per_day),
            (r.picking_dates || []).length,
        ]);
        downloadCsv(`reporting_picking_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Picking</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        mergeTeamsLancaster={mergeTeamsLancaster}
                        setMergeTeamsLancaster={setMergeTeamsLancaster}
                        mergeNjWarehouses={mergeNjWarehouses}
                        setMergeNjWarehouses={setMergeNjWarehouses}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                    />
                    <ButtonGroup segmented>
                        <Button pressed={metric === 'items'}               onClick={() => setMetric('items')}>Items</Button>
                        <Button pressed={metric === 'apparel_items'}       onClick={() => setMetric('apparel_items')}>Apparel</Button>
                        <Button pressed={metric === 'embellishment_items'} onClick={() => setMetric('embellishment_items')}>Embel.</Button>
                        <Button pressed={metric === 'line_items'}          onClick={() => setMetric('line_items')}>Line items</Button>
                        <Button pressed={metric === 'orders'}              onClick={() => setMetric('orders')}>Orders</Button>
                    </ButtonGroup>
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Totals: {totals.items} items ({totals.apparel_items} apparel + {totals.embellishment_items} embel.) · {totals.line_items} line items · {totals.orders} orders
                        </Text>
                        <Tooltip content="For each order that became fully picked in this window: the time from a picker opening it in the Picking Portal to its last pick. If a picker came back to an order, each visit is added; looking without picking adds nothing. This time is stored with the order." dismissOnMouseOut>
                            <Text variant="bodySm" fontWeight="semibold">
                                Average order picking time: {avgOrderSeconds == null ? '—' : _fmtDuration(avgOrderSeconds)}
                                {orderTime.orders ? ` (${orderTime.orders.toLocaleString()} orders fully picked${orderTime.orders_with_open < orderTime.orders ? `, ${(orderTime.orders - orderTime.orders_with_open).toLocaleString()} timed from the first pick because no open was recorded` : ''})` : ''}
                            </Text>
                        </Tooltip>
                        <Tooltip content="All the time every picker shown spent picking (first pick to closing the order), divided by the number of days anyone picked in this window. For a single day it is that day's total picking time. It covers every order worked on, finished or not." dismissOnMouseOut>
                            <Text variant="bodySm" fontWeight="semibold">
                                Average total picking time per day: {avgSecondsPerDay == null ? '—' : _fmtDuration(avgSecondsPerDay)}
                                {pickingDays > 1 ? ` (over ${pickingDays} days)` : ''}
                            </Text>
                        </Tooltip>
                        <Text variant="bodySm" tone="subdued">
                            Only includes operators with 10 or more lifetime orders.
                        </Text>
                    </InlineStack>
                    <div style={{ height: 500, position: 'relative' }}>
                        {loading ? null : (data.rows || []).length === 0 ? (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex',
                                          alignItems: 'center', justifyContent: 'center',
                                          color: '#6d7175' }}>
                                No picking activity in this window.
                            </div>
                        ) : (
                            <ReactECharts
                                theme="polaris"
                                option={echartOption}
                                style={{ height: '100%', width: '100%' }}
                                opts={{ renderer: 'svg' }}
                            />
                        )}
                    </div>

                    {(data.rows || []).length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ marginTop: 12 }}>
                            <thead>
                                <tr>
                                    {PICKING_COLUMNS.map(c => (
                                        <th key={c.key}
                                            className={c.sortType === 'number' ? 'col-right' : undefined}
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleSort(c.key)}>
                                            {/* Hover the header for what the column counts; clicking still sorts. */}
                                            <Tooltip content={c.tip} dismissOnMouseOut>
                                                <span>{c.label}{arrow(c.key)}</span>
                                            </Tooltip>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((r, i) => (
                                    <tr key={`${r.picker}|${r.store_key || ''}|${r.location || ''}|${i}`}>
                                        <td className="td-cell">{r.picker}</td>
                                        <td className="td-cell">{r.store || '—'}</td>
                                        <td className="td-cell">{r.location || '—'}</td>
                                        <td className="td-cell col-right">{r.items}</td>
                                        <td className="td-cell col-right">{r.apparel_items || 0}</td>
                                        <td className="td-cell col-right">{r.embellishment_items || 0}</td>
                                        <td className="td-cell col-right">{r.scan_items || 0}</td>
                                        <td className="td-cell col-right">{r.manual_items || 0}</td>
                                        <td className="td-cell col-right">{Number(r.pct_scanned || 0).toFixed(1)}%</td>
                                        <td className="td-cell col-right">{r.line_items}</td>
                                        <td className="td-cell col-right">{r.orders}</td>
                                        <td className="td-cell col-right">{r.avg_order_seconds == null ? '—' : _fmtDuration(r.avg_order_seconds)}</td>
                                        <td className="td-cell col-right">{r.avg_seconds_per_day == null ? '—' : _fmtDuration(r.avg_seconds_per_day)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- Picking By Category report -------------------------------- */
// Wide-table variant of the Picking report: each row is a picker, each
// column (past the fixed Picker/Store/Location/Total ones) is a Shopify
// productType selected top-N by units picked in the window. Extra picks
// outside the top-N roll into "Other". Useful for Tinton Falls / Eatontown
// where the product mix (jerseys vs numbers vs trading cards vs bags) is
// what the ops team wants to slice — the plain Picking report just sums
// units and can't answer "who picked all the Panini today".

function PickingByCategoryReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlFrom = searchParams.get('from');
    const urlTo   = searchParams.get('to');
    const urlLocs = searchParams.getAll('loc');
    const urlSortKey = searchParams.get('sort');
    const urlSortDir = searchParams.get('dir');

    const [dateFrom, setDateFrom] = useState(urlFrom || todayIso());
    const [dateTo,   setDateTo]   = useState(urlTo   || todayIso());
    const [locationFilter, setLocationFilter] = useState(urlLocs);
    const [mergeTeamsLancaster, setMergeTeamsLancaster] = useState(false);
    const [mergeNjWarehouses,   setMergeNjWarehouses]   = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ columns: [], rows: [], grand_total: null, date_from: '', date_to: '' });
    const [err,  setErr]  = useState(null);
    const [sortKey, setSortKey] = useState(urlSortKey || 'total');
    const [sortDir, setSortDir] = useState(urlSortDir === 'asc' ? 'asc' : 'desc');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc', l);
        if (sortKey && sortKey !== 'total') p.set('sort', sortKey);
        if (sortDir && sortDir !== 'desc') p.set('dir', sortDir);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, sortKey, sortDir, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            if (mergeTeamsLancaster) p.set('merge_teams_lancaster', 'true');
            if (mergeNjWarehouses)   p.set('merge_nj_warehouses', 'true');
            const res = await fetchAuth(`/api/reports/picking_by_category?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, mergeTeamsLancaster, mergeNjWarehouses]);
    useEffect(() => { load(); }, [load]);

    // Same 10-lifetime-orders threshold as PickingReport (avoids one-off
    // pickers cluttering the ranking).
    const filteredRows = useMemo(
        () => (data.rows || []).filter(r => (r.lifetime_orders || 0) >= 10),
        [data.rows]
    );

    // Column definitions. Fixed columns first, then one per top-N productType,
    // then Other. Each dynamic column sorts by its .units value.
    const columns = useMemo(() => {
        const dyn = (data.columns || []).map(t => ({ key: `type:${t}`, label: t, dynamic: true, ptype: t }));
        return [
            { key: 'picker',   label: 'Picker',   sortType: 'string' },
            { key: 'store',    label: 'Store',    sortType: 'string' },
            { key: 'location', label: 'Location', sortType: 'string' },
            { key: 'total',    label: 'Total',    sortType: 'number' },
            ...dyn,
            { key: 'other',    label: 'Other',    sortType: 'number' },
        ];
    }, [data.columns]);

    const cellUnits = (row, key) => {
        if (key === 'total') return row.total?.units || 0;
        if (key === 'other') return row.other?.units || 0;
        if (key.startsWith('type:')) {
            const t = key.slice(5);
            return row.per_type?.[t]?.units || 0;
        }
        return 0;
    };
    const cellLI = (row, key) => {
        if (key === 'total') return row.total?.line_items || 0;
        if (key === 'other') return row.other?.line_items || 0;
        if (key.startsWith('type:')) {
            const t = key.slice(5);
            return row.per_type?.[t]?.line_items || 0;
        }
        return 0;
    };

    const sortedRows = useMemo(() => {
        const rows = filteredRows.slice();
        const col = columns.find(c => c.key === sortKey);
        rows.sort((a, b) => {
            if (col && col.sortType === 'string') {
                const av = String(a[sortKey] || '');
                const bv = String(b[sortKey] || '');
                const cmp = av.localeCompare(bv);
                return sortDir === 'asc' ? cmp : -cmp;
            }
            const av = cellUnits(a, sortKey);
            const bv = cellUnits(b, sortKey);
            return sortDir === 'asc' ? av - bv : bv - av;
        });
        return rows;
    }, [filteredRows, sortKey, sortDir, columns]);

    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = columns.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const handleExport = useCallback(() => {
        const header = ['Picker', 'Store', 'Location', 'Total units', 'Total line items'];
        for (const t of (data.columns || [])) { header.push(`${t} units`, `${t} line items`); }
        header.push('Other units', 'Other line items');
        const rows = [header];
        for (const r of sortedRows) {
            const row = [
                r.picker, r.store || '', r.location || '',
                r.total?.units || 0, r.total?.line_items || 0,
            ];
            for (const t of (data.columns || [])) {
                row.push(r.per_type?.[t]?.units || 0, r.per_type?.[t]?.line_items || 0);
            }
            row.push(r.other?.units || 0, r.other?.line_items || 0);
            rows.push(row);
        }
        // Grand-total row at the very bottom of the CSV, matching the UI.
        if (data.grand_total) {
            const gt = ['GRAND TOTAL', '', '',
                        data.grand_total.total?.units || 0, data.grand_total.total?.line_items || 0];
            for (const t of (data.columns || [])) {
                gt.push(data.grand_total.per_type?.[t]?.units || 0,
                        data.grand_total.per_type?.[t]?.line_items || 0);
            }
            gt.push(data.grand_total.other?.units || 0, data.grand_total.other?.line_items || 0);
            rows.push(gt);
        }
        downloadCsv(`reporting_picking_by_category_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows]);

    const renderCell = (units, li) => (
        <div style={{ lineHeight: 1.15 }}>
            <div style={{ fontWeight: units ? 600 : 400 }}>{units}</div>
            <div style={{ fontSize: 11, color: '#6d7175' }}>{li} li</div>
        </div>
    );

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Picking By Category</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo}     setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        mergeTeamsLancaster={mergeTeamsLancaster}
                        setMergeTeamsLancaster={setMergeTeamsLancaster}
                        mergeNjWarehouses={mergeNjWarehouses}
                        setMergeNjWarehouses={setMergeNjWarehouses}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            {(data.columns || []).length} top product types (+ Other). Cells show units / line items.
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Only includes operators with 10 or more lifetime orders.
                        </Text>
                    </InlineStack>

                    {(sortedRows.length === 0) ? (
                        <div style={{ padding: 32, color: '#6d7175', textAlign: 'center' }}>
                            {loading ? '' : 'No picking activity in this window.'}
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="data-table" style={{ marginTop: 12 }}>
                                <thead>
                                    <tr>
                                        {columns.map(c => (
                                            <th key={c.key}
                                                className={c.sortType === 'number' || c.dynamic ? 'col-right' : undefined}
                                                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                onClick={() => handleSort(c.key)}>
                                                {c.label}{arrow(c.key)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map((r, i) => (
                                        <tr key={`${r.picker}|${r.store_key || ''}|${r.location || ''}|${i}`}>
                                            <td className="td-cell">{r.picker}</td>
                                            <td className="td-cell">{r.store || '—'}</td>
                                            <td className="td-cell">{r.location || '—'}</td>
                                            <td className="td-cell col-right">{renderCell(r.total?.units || 0, r.total?.line_items || 0)}</td>
                                            {(data.columns || []).map(t => (
                                                <td key={t} className="td-cell col-right">
                                                    {renderCell(r.per_type?.[t]?.units || 0, r.per_type?.[t]?.line_items || 0)}
                                                </td>
                                            ))}
                                            <td className="td-cell col-right">{renderCell(r.other?.units || 0, r.other?.line_items || 0)}</td>
                                        </tr>
                                    ))}
                                    {data.grand_total && (
                                        <tr style={{ background: '#f6f6f7', fontWeight: 600 }}>
                                            <td className="td-cell">Grand Total</td>
                                            <td className="td-cell">—</td>
                                            <td className="td-cell">—</td>
                                            <td className="td-cell col-right">{renderCell(data.grand_total.total?.units || 0, data.grand_total.total?.line_items || 0)}</td>
                                            {(data.columns || []).map(t => (
                                                <td key={t} className="td-cell col-right">
                                                    {renderCell(data.grand_total.per_type?.[t]?.units || 0, data.grand_total.per_type?.[t]?.line_items || 0)}
                                                </td>
                                            ))}
                                            <td className="td-cell col-right">{renderCell(data.grand_total.other?.units || 0, data.grand_total.other?.line_items || 0)}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- Pressing report ------------------------------------------- */

function PressingReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const VALID_METRICS = ['embellishments', 'items', 'orders'];

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || todayIso());
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const initMetric = searchParams.get('metric');
    const [metric, setMetric] = useState(VALID_METRICS.includes(initMetric) ? initMetric : 'embellishments');
    const [mergeTeamsLancaster, setMergeTeamsLancaster] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('embellishments');
    const [sortDir, setSortDir] = useState('desc');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        if (metric && metric !== 'embellishments') p.set('metric', metric);
        for (const l of locationFilter) p.append('loc', l);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, metric, locationFilter, setSearchParams]);

    const PRESSING_COLUMNS = [
        { key: 'presser',        label: 'Presser',        sortType: 'string' },
        { key: 'store',          label: 'Store',          sortType: 'string' },
        { key: 'location',       label: 'Location',       sortType: 'string' },
        { key: 'embellishments', label: 'Embellishments', sortType: 'number' },
        { key: 'items',          label: 'Garments',       sortType: 'number' },
        { key: 'items_scanned',  label: 'Scanned',        sortType: 'number' },
        { key: 'items_manual',   label: 'Manual',         sortType: 'number' },
        { key: 'orders',         label: 'Orders',         sortType: 'number' },
    ];
    const filteredRows = useMemo(
        () => (data.rows || []).filter(r => (r.lifetime_orders || 0) >= 10),
        [data.rows]
    );
    const sortedRows = useMemo(() => {
        const col = PRESSING_COLUMNS.find(c => c.key === sortKey) || PRESSING_COLUMNS[0];
        const rows = filteredRows.slice();
        rows.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const cmp = col.sortType === 'string'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [filteredRows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = PRESSING_COLUMNS.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            if (mergeTeamsLancaster) p.set('merge_teams_lancaster', 'true');
            const res = await fetchAuth(`/api/reports/pressing?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, mergeTeamsLancaster]);
    useEffect(() => { load(); }, [load]);

    const labelFor = { embellishments: 'Embellishments', items: 'Garments', orders: 'Orders' };
    const echartOption = useMemo(() => {
        const sorted = filteredRows.slice().sort((a, b) => b[metric] - a[metric]);
        const labels = sorted.map(r => r.location ? `${r.presser} — ${r.location}` : r.presser);
        const values = sorted.map(r => r[metric] || 0);
        return {
            grid: { left: 60, right: 30, top: 30, bottom: 140, containLabel: false },
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: {
                    rotate: 45,
                    interval: 0,
                    fontSize: 11,
                    align: 'right',
                    verticalAlign: 'top',
                },
                axisTick: { alignWithLabel: true },
            },
            yAxis: { type: 'value', minInterval: 1 },
            series: [{
                name: labelFor[metric],
                type: 'bar',
                data: values,
                itemStyle: { color: '#5BA0E5' },
            }],
        };
    }, [filteredRows, metric]);

    const totals = useMemo(() => filteredRows.reduce((acc, r) => {
        acc.embellishments += r.embellishments;
        acc.items          += r.items;
        acc.items_scanned  += (r.items_scanned || 0);
        acc.items_manual   += (r.items_manual  || 0);
        acc.orders         += r.orders;
        return acc;
    }, { embellishments: 0, items: 0, items_scanned: 0, items_manual: 0, orders: 0 }), [filteredRows]);

    const handleExport = useCallback(() => {
        const rows = [['Presser', 'Store', 'Location', 'Embellishments', 'Garments', 'Scanned', 'Manual', 'Orders']];
        for (const r of sortedRows) rows.push([r.presser, r.store || '', r.location || '',
            r.embellishments, r.items, r.items_scanned || 0, r.items_manual || 0, r.orders]);
        downloadCsv(`reporting_pressing_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Pressing</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        mergeTeamsLancaster={mergeTeamsLancaster}
                        setMergeTeamsLancaster={setMergeTeamsLancaster}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                    />
                    <ButtonGroup segmented>
                        <Button pressed={metric === 'embellishments'} onClick={() => setMetric('embellishments')}>Embellishments</Button>
                        <Button pressed={metric === 'items'}          onClick={() => setMetric('items')}>Garments</Button>
                        <Button pressed={metric === 'orders'}         onClick={() => setMetric('orders')}>Orders</Button>
                    </ButtonGroup>
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Totals: {totals.embellishments} embellishments · {totals.items} garments ({totals.items_scanned} scanned, {totals.items_manual} manual) · {totals.orders} orders
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Only includes operators with 10 or more lifetime orders.
                        </Text>
                    </InlineStack>
                    <div style={{ height: 500, position: 'relative' }}>
                        {loading ? null : (data.rows || []).length === 0 ? (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex',
                                          alignItems: 'center', justifyContent: 'center',
                                          color: '#6d7175' }}>
                                No pressing activity in this window.
                            </div>
                        ) : (
                            <ReactECharts
                                theme="polaris"
                                option={echartOption}
                                style={{ height: '100%', width: '100%' }}
                                opts={{ renderer: 'svg' }}
                            />
                        )}
                    </div>

                    {(data.rows || []).length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ marginTop: 12 }}>
                            <thead>
                                <tr>
                                    {PRESSING_COLUMNS.map(c => (
                                        <th key={c.key}
                                            className={c.sortType === 'number' ? 'col-right' : undefined}
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleSort(c.key)}>
                                            {c.label}{arrow(c.key)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((r, i) => (
                                    <tr key={`${r.presser}|${r.store_key || ''}|${r.location || ''}|${i}`}>
                                        <td className="td-cell">{r.presser}</td>
                                        <td className="td-cell">{r.store || '—'}</td>
                                        <td className="td-cell">{r.location || '—'}</td>
                                        <td className="td-cell col-right">{r.embellishments}</td>
                                        <td className="td-cell col-right">{r.items}</td>
                                        <td className="td-cell col-right">{r.items_scanned || 0}</td>
                                        <td className="td-cell col-right">{r.items_manual || 0}</td>
                                        <td className="td-cell col-right">{r.orders}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- Pressing Reworks report ----------------------------------- */

function PressingReworksReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { storeSlug } = useParams();
    const VALID_METRICS = ['garments', 'orders', 'pressed', 'fail_pct'];

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(6));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const initMetric = searchParams.get('metric');
    const [metric, setMetric] = useState(VALID_METRICS.includes(initMetric) ? initMetric : 'garments');
    const [mergeTeamsLancaster, setMergeTeamsLancaster] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('garments_failed');
    const [sortDir, setSortDir] = useState('desc');
    const [expanded, setExpanded] = useState(() => new Set());
    const [expandedIssues, setExpandedIssues] = useState(() => new Set());

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        if (metric && metric !== 'garments') p.set('metric', metric);
        for (const l of locationFilter) p.append('loc', l);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, metric, locationFilter, setSearchParams]);

    const COLUMNS = [
        { key: 'presser',          label: 'Presser',           sortType: 'string' },
        { key: 'store',            label: 'Store',             sortType: 'string' },
        { key: 'location',         label: 'Location',          sortType: 'string' },
        { key: 'garments_pressed', label: 'Garments Pressed',  sortType: 'number' },
        { key: 'garments_failed',  label: 'Garments Failed',   sortType: 'number' },
        { key: 'orders_affected',  label: 'Orders Affected',   sortType: 'number' },
        { key: 'fail_pct',         label: '% Failed',          sortType: 'number' },
    ];
    const sortedRows = useMemo(() => {
        const col = COLUMNS.find(c => c.key === sortKey) || COLUMNS[3];
        const rows = (data.rows || []).slice();
        rows.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const cmp = col.sortType === 'string'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = COLUMNS.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const rowKey = (r) => `${r.presser}|${r.store_key || ''}|${r.shopify_location_id || ''}`;
    const toggleExpanded = (r) => {
        const k = rowKey(r);
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };
    const issueKey = (r, issue) => `${rowKey(r)}|${issue}`;
    const toggleExpandedIssue = (r, issue) => {
        const k = issueKey(r, issue);
        setExpandedIssues(prev => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };
    const fmtFailedAt = (iso) => {
        if (!iso) return '—';
        const s = String(iso);
        return s.slice(0, 16).replace('T', ' ');
    };

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            if (mergeTeamsLancaster) p.set('merge_teams_lancaster', 'true');
            const res = await fetchAuth(`/api/reports/pressing_reworks?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, mergeTeamsLancaster]);
    useEffect(() => { load(); }, [load]);

    const labelFor = { garments: 'Garments Failed', orders: 'Orders Affected', pressed: 'Garments Pressed', fail_pct: '% Failed' };
    const metricField = { garments: 'garments_failed', orders: 'orders_affected', pressed: 'garments_pressed', fail_pct: 'fail_pct' };
    const echartOption = useMemo(() => {
        const field = metricField[metric];
        const sorted = (data.rows || []).slice().sort((a, b) => (b[field] || 0) - (a[field] || 0));
        const labels = sorted.map(r => r.location ? `${r.presser} — ${r.location}` : r.presser);
        const values = sorted.map(r => r[field] || 0);
        return {
            grid: { left: 60, right: 30, top: 30, bottom: 140, containLabel: false },
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            xAxis: {
                type: 'category', data: labels,
                axisLabel: { rotate: 45, interval: 0, fontSize: 11, align: 'right', verticalAlign: 'top' },
                axisTick: { alignWithLabel: true },
            },
            yAxis: { type: 'value', minInterval: 1 },
            series: [{ name: labelFor[metric], type: 'bar', data: values,
                       itemStyle: { color: '#D9534F' } }],
        };
    }, [data.rows, metric]);

    const totals = useMemo(() => (data.rows || []).reduce((acc, r) => {
        acc.pressed  += r.garments_pressed || 0;
        acc.garments += r.garments_failed  || 0;
        acc.orders   += r.orders_affected  || 0;
        return acc;
    }, { pressed: 0, garments: 0, orders: 0 }), [data.rows]);
    const totalsFailPct = totals.pressed
        ? (totals.garments / totals.pressed * 100).toFixed(3) + '%'
        : '0.000%';
    const fmtPct = (v) => `${(Number(v) || 0).toFixed(3)}%`;

    const handleExport = useCallback(() => {
        const rows = [['Presser', 'Store', 'Location', 'Issue',
                       'Garments Pressed', 'Garments Failed', 'Orders Affected', '% Failed']];
        for (const r of sortedRows) {
            rows.push([r.presser, r.store || '', r.location || '', '',
                       r.garments_pressed, r.garments_failed, r.orders_affected,
                       fmtPct(r.fail_pct)]);
            for (const bi of (r.by_issue || [])) {
                rows.push(['', '', '', bi.issue || '',
                           '', bi.garments_failed, bi.orders_affected, '']);
            }
        }
        downloadCsv(`reporting_pressing_reworks_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Pressing Reworks</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        mergeTeamsLancaster={mergeTeamsLancaster}
                        setMergeTeamsLancaster={setMergeTeamsLancaster}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                    />
                    <ButtonGroup segmented>
                        <Button pressed={metric === 'garments'} onClick={() => setMetric('garments')}>Garments Failed</Button>
                        <Button pressed={metric === 'orders'}   onClick={() => setMetric('orders')}>Orders Affected</Button>
                        <Button pressed={metric === 'pressed'}  onClick={() => setMetric('pressed')}>Garments Pressed</Button>
                        <Button pressed={metric === 'fail_pct'} onClick={() => setMetric('fail_pct')}>% Failed</Button>
                    </ButtonGroup>
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Totals: {totals.pressed} pressed · {totals.garments} failed · {totals.orders} orders affected · {totalsFailPct} overall fail rate
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Attribution: presser of the most recent press strictly before each failure (per bundle).
                        </Text>
                    </InlineStack>
                    <div style={{ height: 500, position: 'relative' }}>
                        {loading ? null : (data.rows || []).length === 0 ? (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex',
                                          alignItems: 'center', justifyContent: 'center',
                                          color: '#6d7175' }}>
                                No reworks in this window.
                            </div>
                        ) : (
                            <ReactECharts
                                theme="polaris"
                                option={echartOption}
                                style={{ height: '100%', width: '100%' }}
                                opts={{ renderer: 'svg' }}
                                notMerge={true}
                            />
                        )}
                    </div>

                    {(data.rows || []).length > 0 && (
                        <table className="data-table" style={{ marginTop: 12 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 32 }} />
                                    {COLUMNS.map(c => (
                                        <th key={c.key}
                                            className={c.sortType === 'number' ? 'col-right' : undefined}
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleSort(c.key)}>
                                            {c.label}{arrow(c.key)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((r, i) => {
                                    const k = rowKey(r);
                                    const isOpen = expanded.has(k);
                                    return (
                                        <React.Fragment key={`${k}|${i}`}>
                                            <tr>
                                                <td className="td-cell" style={{ width: 36 }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleExpanded(r)}
                                                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                                                        style={{ background: 'transparent', border: 'none',
                                                                 cursor: 'pointer', fontSize: 22,
                                                                 fontWeight: 700, color: '#5c5f62',
                                                                 padding: '2px 8px', lineHeight: 1 }}
                                                    >
                                                        {isOpen ? '▾' : '▸'}
                                                    </button>
                                                </td>
                                                <td className="td-cell" style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => toggleExpanded(r)}>
                                                    {r.presser}
                                                </td>
                                                <td className="td-cell">{r.store || '—'}</td>
                                                <td className="td-cell">{r.location || '—'}</td>
                                                <td className="td-cell col-right">{r.garments_pressed}</td>
                                                <td className="td-cell col-right">{r.garments_failed}</td>
                                                <td className="td-cell col-right">{r.orders_affected}</td>
                                                <td className="td-cell col-right">{fmtPct(r.fail_pct)}</td>
                                            </tr>
                                            {isOpen && (
                                                <tr>
                                                    <td />
                                                    <td colSpan={COLUMNS.length} style={{ padding: '8px 12px',
                                                          background: '#f6f7f9' }}>
                                                        {(r.by_issue || []).length === 0 ? (
                                                            <Text variant="bodySm" tone="subdued">No issue breakdown.</Text>
                                                        ) : (
                                                            <table className="data-table" style={{ width: '100%' }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{ width: 36 }} />
                                                                        <th>Issue</th>
                                                                        <th className="col-right">Garments Failed</th>
                                                                        <th className="col-right">Orders Affected</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(r.by_issue || []).map((bi, j) => {
                                                                        const ik = issueKey(r, bi.issue || '');
                                                                        const issueOpen = expandedIssues.has(ik);
                                                                        return (
                                                                            <React.Fragment key={`${k}|issue|${j}`}>
                                                                                <tr>
                                                                                    <td className="td-cell" style={{ width: 36 }}>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => toggleExpandedIssue(r, bi.issue || '')}
                                                                                            aria-label={issueOpen ? 'Collapse' : 'Expand'}
                                                                                            style={{ background: 'transparent', border: 'none',
                                                                                                     cursor: 'pointer', fontSize: 18,
                                                                                                     fontWeight: 700, color: '#5c5f62',
                                                                                                     padding: '2px 8px', lineHeight: 1 }}
                                                                                        >
                                                                                            {issueOpen ? '▾' : '▸'}
                                                                                        </button>
                                                                                    </td>
                                                                                    <td className="td-cell" style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                        onClick={() => toggleExpandedIssue(r, bi.issue || '')}>
                                                                                        {bi.issue || '—'}
                                                                                    </td>
                                                                                    <td className="td-cell col-right">{bi.garments_failed}</td>
                                                                                    <td className="td-cell col-right">{bi.orders_affected}</td>
                                                                                </tr>
                                                                                {issueOpen && (
                                                                                    <tr>
                                                                                        <td />
                                                                                        <td colSpan={3} style={{ padding: '8px 12px',
                                                                                              background: '#eef0f3' }}>
                                                                                            {(bi.garments || []).length === 0 ? (
                                                                                                <Text variant="bodySm" tone="subdued">No garment detail.</Text>
                                                                                            ) : (
                                                                                                <table className="data-table" style={{ width: '100%' }}>
                                                                                                    <thead>
                                                                                                        <tr>
                                                                                                            <th>Order #</th>
                                                                                                            <th>Garment (SKU / Bundle)</th>
                                                                                                            <th>Failed</th>
                                                                                                            <th className="col-right">Times</th>
                                                                                                            <th>Rework Notes</th>
                                                                                                        </tr>
                                                                                                    </thead>
                                                                                                    <tbody>
                                                                                                        {(bi.garments || []).map((g, gi) => (
                                                                                                            <tr key={`${ik}|g|${gi}`}>
                                                                                                                <td className="td-cell">
                                                                                                                    {g.order_id ? (
                                                                                                                        <Link to={`/store/${r.store_key || storeSlug || 'soccerpost'}/orders/${g.order_id}`}>
                                                                                                                            {g.order_name || g.order_id}
                                                                                                                        </Link>
                                                                                                                    ) : (g.order_name || '—')}
                                                                                                                </td>
                                                                                                                <td className="td-cell">{g.group_key || '—'}</td>
                                                                                                                <td className="td-cell">{fmtFailedAt(g.failed_at)}</td>
                                                                                                                <td className="td-cell col-right">{g.fail_count || 1}</td>
                                                                                                                <td className="td-cell" style={{ maxWidth: 360, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                                                                    {g.notes || '—'}
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        ))}
                                                                                                    </tbody>
                                                                                                </table>
                                                                                            )}
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- Packing report -------------------------------------------- */

function PackingReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const VALID_METRICS = ['items', 'qc_failed', 'ups_labels', 'packing_slips'];

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || todayIso());
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const initMetric = searchParams.get('metric');
    const [metric, setMetric] = useState(VALID_METRICS.includes(initMetric) ? initMetric : 'items');
    const [mergeTeamsLancaster, setMergeTeamsLancaster] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('items');
    const [sortDir, setSortDir] = useState('desc');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        if (metric && metric !== 'items') p.set('metric', metric);
        for (const l of locationFilter) p.append('loc', l);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, metric, locationFilter, setSearchParams]);

    const PACKING_COLUMNS = [
        { key: 'packer',         label: 'Packer',        sortType: 'string' },
        { key: 'store',          label: 'Store',         sortType: 'string' },
        { key: 'location',       label: 'Location',      sortType: 'string' },
        { key: 'items',          label: 'Items',         sortType: 'number' },
        { key: 'qc_failed',      label: 'QC Failed',     sortType: 'number' },
        { key: 'ups_labels',     label: 'UPS Labels',    sortType: 'number' },
        { key: 'packing_slips',  label: 'Packing Slips', sortType: 'number' },
    ];
    const filteredRows = useMemo(
        () => (data.rows || []).filter(r => (r.lifetime_orders || 0) >= 10),
        [data.rows]
    );
    const sortedRows = useMemo(() => {
        const col = PACKING_COLUMNS.find(c => c.key === sortKey) || PACKING_COLUMNS[0];
        const rows = filteredRows.slice();
        rows.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const cmp = col.sortType === 'string'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [filteredRows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = PACKING_COLUMNS.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            if (mergeTeamsLancaster) p.set('merge_teams_lancaster', 'true');
            const res = await fetchAuth(`/api/reports/packing?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, mergeTeamsLancaster]);
    useEffect(() => { load(); }, [load]);

    const labelFor = { items: 'Items', qc_failed: 'QC Failed',
                       ups_labels: 'UPS Labels', packing_slips: 'Packing Slips' };
    const echartOption = useMemo(() => {
        const sorted = filteredRows.slice().sort((a, b) => b[metric] - a[metric]);
        const labels = sorted.map(r => r.location ? `${r.packer} — ${r.location}` : r.packer);
        const values = sorted.map(r => r[metric] || 0);
        return {
            grid: { left: 60, right: 30, top: 30, bottom: 140, containLabel: false },
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            xAxis: {
                type: 'category', data: labels,
                axisLabel: { rotate: 45, interval: 0, fontSize: 11, align: 'right', verticalAlign: 'top' },
                axisTick: { alignWithLabel: true },
            },
            yAxis: { type: 'value', minInterval: 1 },
            series: [{ name: labelFor[metric], type: 'bar', data: values,
                       itemStyle: { color: '#5BA0E5' } }],
        };
    }, [filteredRows, metric]);

    const totals = useMemo(() => filteredRows.reduce((acc, r) => {
        acc.items         += r.items;
        acc.qc_failed     += r.qc_failed;
        acc.ups_labels    += (r.ups_labels    || 0);
        acc.packing_slips += (r.packing_slips || 0);
        return acc;
    }, { items: 0, qc_failed: 0, ups_labels: 0, packing_slips: 0 }), [filteredRows]);

    const handleExport = useCallback(() => {
        const rows = [['Packer', 'Store', 'Location', 'Items', 'QC Failed', 'UPS Labels', 'Packing Slips']];
        for (const r of sortedRows) rows.push([
            r.packer, r.store || '', r.location || '', r.items, r.qc_failed,
            r.ups_labels || 0, r.packing_slips || 0,
        ]);
        downloadCsv(`reporting_packing_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Packing</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        mergeTeamsLancaster={mergeTeamsLancaster}
                        setMergeTeamsLancaster={setMergeTeamsLancaster}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                    />
                    <ButtonGroup segmented>
                        <Button pressed={metric === 'items'}         onClick={() => setMetric('items')}>Items</Button>
                        <Button pressed={metric === 'qc_failed'}     onClick={() => setMetric('qc_failed')}>QC Failed</Button>
                        <Button pressed={metric === 'ups_labels'}    onClick={() => setMetric('ups_labels')}>UPS Labels</Button>
                        <Button pressed={metric === 'packing_slips'} onClick={() => setMetric('packing_slips')}>Packing Slips</Button>
                    </ButtonGroup>
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Totals: {totals.items} items · {totals.qc_failed} QC failed ·
                            {' '}{totals.ups_labels} UPS labels · {totals.packing_slips} packing slips
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Only includes operators with 10 or more lifetime orders.
                        </Text>
                    </InlineStack>
                    <div style={{ height: 500, position: 'relative' }}>
                        {loading ? null : (data.rows || []).length === 0 ? (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex',
                                          alignItems: 'center', justifyContent: 'center', color: '#6d7175' }}>
                                No packing activity in this window.
                            </div>
                        ) : (
                            <ReactECharts theme="polaris" option={echartOption}
                                          style={{ height: '100%', width: '100%' }}
                                          opts={{ renderer: 'svg' }} />
                        )}
                    </div>

                    {(data.rows || []).length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ marginTop: 12 }}>
                            <thead>
                                <tr>
                                    {PACKING_COLUMNS.map(c => (
                                        <th key={c.key}
                                            className={c.sortType === 'number' ? 'col-right' : undefined}
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleSort(c.key)}>
                                            {c.label}{arrow(c.key)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((r, i) => (
                                    <tr key={`${r.packer}|${r.store_key || ''}|${r.location || ''}|${i}`}>
                                        <td className="td-cell">{r.packer}</td>
                                        <td className="td-cell">{r.store || '—'}</td>
                                        <td className="td-cell">{r.location || '—'}</td>
                                        <td className="td-cell col-right">{r.items}</td>
                                        <td className="td-cell col-right">{r.qc_failed}</td>
                                        <td className="td-cell col-right">{r.ups_labels || 0}</td>
                                        <td className="td-cell col-right">{r.packing_slips || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- QC Failures report ---------------------------------------- */

function QCReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const { storeSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(6));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], totals: {}, date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('first_fail_at');
    const [sortDir, setSortDir] = useState('desc');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc', l);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, setSearchParams]);

    const QC_COLUMNS = [
        { key: 'order_name',     label: 'Order #',       sortType: 'string' },
        { key: 'first_fail_at',  label: 'First Failed',  sortType: 'string' },
        { key: 'location',       label: 'Location',      sortType: 'string' },
        { key: 'fails',          label: 'Failures',      sortType: 'number' },
        { key: 'failed_items',   label: 'Failed Items',  sortType: 'number' },
        { key: 'pickers',        label: 'Pickers',       sortType: 'string' },
        { key: 'pressers',       label: 'Pressers',      sortType: 'string' },
        { key: 'qc_by',          label: 'QC By',         sortType: 'string' },
        { key: 'reasons',        label: 'Reasons',       sortType: 'string' },
        { key: 'notes',          label: 'Notes',         sortType: 'string' },
    ];
    const _strFor = (r, key) => {
        if (key === 'pickers' || key === 'pressers' || key === 'qc_by') {
            return (r[key] || []).join(', ');
        }
        if (key === 'reasons') {
            return (r.reasons || []).map(x => x.reason).join(', ');
        }
        if (key === 'notes') {
            return (r.notes || []).join(' · ');
        }
        return r[key];
    };
    const sortedRows = useMemo(() => {
        const col = QC_COLUMNS.find(c => c.key === sortKey) || QC_COLUMNS[1];
        const rows = (data.rows || []).slice();
        rows.sort((a, b) => {
            const av = col.sortType === 'string' ? String(_strFor(a, sortKey) || '')
                                                 : (Number(a[sortKey]) || 0);
            const bv = col.sortType === 'string' ? String(_strFor(b, sortKey) || '')
                                                 : (Number(b[sortKey]) || 0);
            const cmp = col.sortType === 'string' ? av.localeCompare(bv) : av - bv;
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = QC_COLUMNS.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            const res = await fetchAuth(`/api/reports/qc_failures?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter]);
    useEffect(() => { load(); }, [load]);

    const handleExport = useCallback(() => {
        const rows = [['Order #', 'First Failed', 'Location', 'Failures',
                       'Failed Items', 'Pickers', 'Pressers', 'QC By', 'Reasons', 'Notes']];
        for (const r of sortedRows) rows.push([
            r.order_name || '',
            (r.first_fail_at || '').slice(0, 19).replace('T', ' '),
            r.location || '',
            r.fails, r.failed_items,
            (r.pickers || []).join(' / '),
            (r.pressers || []).join(' / '),
            (r.qc_by || []).join(' / '),
            (r.reasons || []).map(x => `${x.reason} (${x.n})`).join(' / '),
            (r.notes || []).join(' · '),
        ]);
        downloadCsv(`reporting_qc_failures_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows]);

    const totals = data.totals || {};

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">QC</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Totals: {totals.orders || 0} orders ·
                            {' '}{totals.fails || 0} failures ·
                            {' '}{totals.failed_items || 0} failed items/bundles
                        </Text>
                    </InlineStack>

                    {loading ? null : (data.rows || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6d7175' }}>
                            No QC failures in this window.
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="data-table" style={{ marginTop: 12, minWidth: 1100 }}>
                                <thead>
                                    <tr>
                                        {QC_COLUMNS.map(c => (
                                            <th key={c.key}
                                                className={c.sortType === 'number' ? 'col-right' : undefined}
                                                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                onClick={() => handleSort(c.key)}>
                                                {c.label}{arrow(c.key)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map(r => (
                                        <tr key={`${r.store_key}|${r.order_id}`}>
                                            <td className="td-cell">
                                                <Link to={`/store/${r.store_key || storeSlug || 'soccerpost'}/orders/${r.order_id}`}>
                                                    {r.order_name || `#${r.order_id}`}
                                                </Link>
                                            </td>
                                            <td className="td-cell" style={{ whiteSpace: 'nowrap' }}>
                                                {(r.first_fail_at || '').slice(0, 16).replace('T', ' ')}
                                            </td>
                                            <td className="td-cell">{r.location || '—'}</td>
                                            <td className="td-cell col-right">{r.fails}</td>
                                            <td className="td-cell col-right">{r.failed_items}</td>
                                            <td className="td-cell">{(r.pickers || []).join(', ') || '—'}</td>
                                            <td className="td-cell">{(r.pressers || []).join(', ') || '—'}</td>
                                            <td className="td-cell">{(r.qc_by || []).join(', ') || '—'}</td>
                                            <td className="td-cell">
                                                {(r.reasons || []).map(x => `${x.reason}${x.n > 1 ? ` ×${x.n}` : ''}`).join(', ') || '—'}
                                            </td>
                                            <td className="td-cell" style={{ maxWidth: 320, whiteSpace: 'normal' }}>
                                                {(r.notes || []).length
                                                    ? (r.notes || []).map((n, i) => (
                                                        <div key={i} style={{ fontSize: 12, lineHeight: 1.35 }}>
                                                            {n}
                                                        </div>
                                                      ))
                                                    : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- Session-Times reports (picking / pressing / packing) ------ */

function SessionTimesReport({ title, availableLocations, endpoint, roleKey, roleLabel, showEmbellishments = false, showNjMerge = false }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || todayIso());
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [mergeTeamsLancaster, setMergeTeamsLancaster] = useState(false);
    const [mergeNjWarehouses, setMergeNjWarehouses] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    // Column sort — defaults to avg-per-order ASC (fastest on top), matching
    // the chart and the prior hardcoded behavior. Click a column header to
    // re-sort; click again to flip direction.
    const [sortKey, setSortKey] = useState('avg_seconds_per_order');
    const [sortAsc, setSortAsc] = useState(true);
    const onSort = (k) => {
        if (sortKey === k) setSortAsc(v => !v);
        else { setSortKey(k); setSortAsc(true); }
    };

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc', l);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            if (mergeTeamsLancaster) p.set('merge_teams_lancaster', 'true');
            if (showNjMerge && mergeNjWarehouses) p.set('merge_nj_warehouses', 'true');
            const res = await fetchAuth(`${endpoint}?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, endpoint, mergeTeamsLancaster, mergeNjWarehouses, showNjMerge]);
    useEffect(() => { load(); }, [load]);

    const _rowLabel = (r) => r.location ? `${r[roleKey]} — ${r.location}` : r[roleKey];
    const filteredRows = useMemo(
        () => (data.rows || []).filter(r => (r.lifetime_orders || 0) >= 10),
        [data.rows]
    );
    // Table-only sort. Chart + filteredRows reference data stay in avg-asc
    // for chart readability; this is consumed by both the rendered table
    // and the CSV export so what the user sees and what they export match.
    const NUMERIC_SORT_KEYS = useMemo(() => new Set([
        'sessions', 'distinct_orders', 'embellishments', 'total_seconds', 'avg_seconds_per_order',
    ]), []);
    const sortedRows = useMemo(() => {
        const rows = filteredRows.slice();
        rows.sort((a, b) => {
            // Translate the role column key ('picker'/'presser'/'packer') into
            // the role-name lookup so the role column sorts alphabetically.
            const k = sortKey === 'role' ? roleKey : sortKey;
            const av = a[k], bv = b[k];
            if (NUMERIC_SORT_KEYS.has(k)) {
                const an = av == null ? -Infinity : av;
                const bn = bv == null ? -Infinity : bv;
                return sortAsc ? an - bn : bn - an;
            }
            const cmp = String(av || '').localeCompare(String(bv || ''));
            return sortAsc ? cmp : -cmp;
        });
        return rows;
    }, [filteredRows, sortKey, sortAsc, roleKey, NUMERIC_SORT_KEYS]);
    const arrow = (k) => (sortKey === k || (k === 'role' && sortKey === roleKey))
        ? (sortAsc ? ' ▲' : ' ▼') : '';
    const echartOption = useMemo(() => {
        const sorted = filteredRows.slice().sort((a, b) => a.avg_seconds_per_order - b.avg_seconds_per_order);
        const labels = sorted.map(_rowLabel);
        const values = sorted.map(r => r.avg_seconds_per_order || 0);
        return {
            grid: { left: 60, right: 30, top: 30, bottom: 140, containLabel: false },
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'shadow' },
                valueFormatter: v => _fmtDuration(v),
            },
            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: {
                    rotate: 45,
                    interval: 0,
                    fontSize: 11,
                    align: 'right',
                    verticalAlign: 'top',
                },
                axisTick: { alignWithLabel: true },
            },
            yAxis: { type: 'value', axisLabel: { formatter: v => _fmtDuration(v) } },
            series: [{
                name: 'Avg / order',
                type: 'bar',
                data: values,
                itemStyle: { color: '#5BA0E5' },
            }],
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredRows]);

    const handleExport = useCallback(() => {
        const header = [roleLabel, 'Store', 'Location', 'Sessions', 'Distinct orders'];
        if (showEmbellishments) header.push('Total Embellishments');
        header.push('Total seconds', 'Avg seconds / session');
        const rows = [header];
        for (const r of sortedRows) {
            const sessions = r.sessions ?? r.orders;
            const distinctOrders = r.distinct_orders ?? r.orders;
            const row = [r[roleKey], r.store || '', r.location || '', sessions, distinctOrders];
            if (showEmbellishments) row.push(r.embellishments || 0);
            row.push(Math.round(r.total_seconds || 0), Math.round(r.avg_seconds_per_order || 0));
            rows.push(row);
        }
        downloadCsv(`reporting_${roleKey}_times_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows, roleKey, roleLabel, showEmbellishments]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">{title}</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        mergeTeamsLancaster={mergeTeamsLancaster}
                        setMergeTeamsLancaster={setMergeTeamsLancaster}
                        mergeNjWarehouses={showNjMerge ? mergeNjWarehouses : undefined}
                        setMergeNjWarehouses={showNjMerge ? setMergeNjWarehouses : undefined}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            One session = one operator's claim on an order. Duration =
                            session close − session open. Only includes operators with
                            10 or more lifetime orders.
                        </Text>
                    </InlineStack>
                    <div style={{ height: 420, position: 'relative' }}>
                        {loading ? null : filteredRows.length === 0 ? (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex',
                                          alignItems: 'center', justifyContent: 'center',
                                          color: '#6d7175' }}>
                                No operators with 10+ lifetime orders matched this window.
                            </div>
                        ) : (
                            <ReactECharts
                                theme="polaris"
                                option={echartOption}
                                style={{ height: '100%', width: '100%' }}
                                opts={{ renderer: 'svg' }}
                            />
                        )}
                    </div>

                    {filteredRows.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ marginTop: 12 }}>
                            <thead>
                                <tr>
                                    <th onClick={() => onSort('role')}     style={{ cursor: 'pointer' }}>{roleLabel}{arrow('role')}</th>
                                    <th onClick={() => onSort('store')}    style={{ cursor: 'pointer' }}>Store{arrow('store')}</th>
                                    <th onClick={() => onSort('location')} style={{ cursor: 'pointer' }}>Location{arrow('location')}</th>
                                    <th onClick={() => onSort('sessions')}        style={{ cursor: 'pointer' }} className="col-right">Sessions{arrow('sessions')}</th>
                                    <th onClick={() => onSort('distinct_orders')} style={{ cursor: 'pointer' }} className="col-right">Distinct orders{arrow('distinct_orders')}</th>
                                    {showEmbellishments && (
                                        <th onClick={() => onSort('embellishments')} style={{ cursor: 'pointer' }} className="col-right">
                                            Total Embellishments{arrow('embellishments')}
                                        </th>
                                    )}
                                    <th onClick={() => onSort('total_seconds')}         style={{ cursor: 'pointer' }} className="col-right">Total time{arrow('total_seconds')}</th>
                                    <th onClick={() => onSort('avg_seconds_per_order')} style={{ cursor: 'pointer' }} className="col-right">Avg / session{arrow('avg_seconds_per_order')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((r, i) => (
                                    <tr key={`${r[roleKey]}|${r.store_key || ''}|${r.location || ''}|${i}`}>
                                        <td className="td-cell">{r[roleKey]}</td>
                                        <td className="td-cell">{r.store || '—'}</td>
                                        <td className="td-cell">{r.location || '—'}</td>
                                        <td className="td-cell col-right">{r.sessions ?? r.orders}</td>
                                        <td className="td-cell col-right">{r.distinct_orders ?? r.orders}</td>
                                        {showEmbellishments && <td className="td-cell col-right">{r.embellishments || 0}</td>}
                                        <td className="td-cell col-right">{_fmtDuration(r.total_seconds)}</td>
                                        <td className="td-cell col-right">{_fmtDuration(r.avg_seconds_per_order)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

function PickingTimesReport({ availableLocations }) {
    return <SessionTimesReport title="Picking Times" availableLocations={availableLocations}
        endpoint="/api/reports/picking_times" roleKey="picker" roleLabel="Picker"
        showNjMerge />;
}
function PressingTimesReport({ availableLocations }) {
    return <SessionTimesReport title="Pressing Times" availableLocations={availableLocations}
        endpoint="/api/reports/pressing_times" roleKey="presser" roleLabel="Presser"
        showEmbellishments />;
}
function PackingTimesReport({ availableLocations }) {
    return <SessionTimesReport title="Packing Times" availableLocations={availableLocations}
        endpoint="/api/reports/packing_times" roleKey="packer" roleLabel="Packer" />;
}

/* ---------- Club Summary report ----------------------------------------- */
// One-pager status snapshot designed to hand to a club rep. Same filter shape
// as Orders By Club; the body is a bucket table + KPI tiles + fulfillment
// duration card + weekly trend + actionable per-order lists. Print button
// triggers a clean 8.5x11 layout via @media print CSS.

// Hover explanations for the two "fulfilled" KPI tiles on Club Summary /
// Location Summary. The two labels sound alike but count different things —
// the tooltip spells out the bucket math with the live counts so the number
// is auditable at a glance. (CSS-only tooltip: .cs-kpi__tip, hidden in print.)
function _csKpiTips(buckets, fmtN) {
    const b = (k) => fmtN(buckets[k] || 0);
    const fulfilledN = (buckets.shipped || 0) + (buckets.picked_up || 0)
        + (buckets.ready_for_pickup || 0) + (buckets.in_transit || 0)
        + (buckets.partial_no_lifecycle || 0) + (buckets.cancelled || 0);
    const notifiedN = (buckets.shipped || 0) + (buckets.picked_up || 0)
        + (buckets.ready_for_pickup || 0) + (buckets.cancelled || 0);
    return {
        fulfilled: (
            <>
                <strong>Everything out of the fulfillment pipeline</strong> — 100% minus
                the Not Fulfilled tile. Includes orders still on their way to the customer.
                <br /><br />
                Shipped ({b('shipped')}) + Picked up ({b('picked_up')}) + Ready for pickup
                ({b('ready_for_pickup')}) + In transit ({b('in_transit')}) + Partial
                ({b('partial_no_lifecycle')}) + Cancelled ({b('cancelled')})
                = <strong>{fmtN(fulfilledN)}</strong> of {fmtN(buckets.total || 0)}.
            </>
        ),
        notified: (
            <>
                <strong>The customer has the order, or has been told it's ready for
                pickup.</strong> Excludes In transit and Partial — those left us, but the
                customer doesn't have them yet.
                <br /><br />
                Shipped ({b('shipped')}) + Picked up ({b('picked_up')}) + Ready for pickup
                ({b('ready_for_pickup')}) + Cancelled ({b('cancelled')})
                = <strong>{fmtN(notifiedN)}</strong> of {fmtN(buckets.total || 0)}.
            </>
        ),
        total: (
            <>
                <strong>Every order placed in this window</strong> at the selected
                location(s) — the denominator for all the percentages here. Includes
                cancelled orders.
            </>
        ),
        notFulfilled: (
            <>
                <strong>Nothing has started yet</strong> — not shipped, in transit, ready
                for pickup, or picked up. The true backlog still needing work.
                <br /><br />
                Not fulfilled = <strong>{b('not_fulfilled')}</strong> of {fmtN(buckets.total || 0)}.
            </>
        ),
    };
}

function ClubSummaryReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { storeSlug } = useParams();
    // 2026-09-02: Overdue / Ready / Not fulfilled / In transit lists start collapsed.
    const [openSections, setOpenSections] = useState({});
    const toggleSec = (k) => setOpenSections(prev => ({ ...prev, [k]: !prev[k] }));

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter,     setClubFilter]     = useState(searchParams.getAll('club'));
    // Optional single-team narrowing within the selected club. '' = all teams.
    const [teamFilter,     setTeamFilter]     = useState(searchParams.get('team') || '');
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState(null);

    // Keep URL in sync with filter state (bookmarkable).
    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        if (teamFilter) p.set('team', teamFilter);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, teamFilter, setSearchParams]);

    // Gate: don't auto-fetch. Require a Location AND a Club to be picked,
    // then let the user click "Run Report" (green button). Avoids the
    // giant "everything ever" query that fires when both filters are empty.
    const canRun = (locationFilter || []).length > 0 && (clubFilter || []).length > 0;

    const fetchData = useCallback(async () => {
        if (!canRun) { setData(null); return; }
        setLoading(true); setError(null);
        try {
            const p = new URLSearchParams();
            p.set('date_from', dateFrom); p.set('date_to', dateTo);
            (locationFilter || []).forEach(v => p.append('locations', v));
            (clubFilter     || []).forEach(v => p.append('clubs',     v));
            if (teamFilter) p.append('teams', teamFilter);
            const res = await fetchAuth(`/api/reports/club_summary?${p.toString()}`);
            if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || res.statusText);
            setData(await res.json());
        } catch (e) { setError(e.message || String(e)); }
        finally { setLoading(false); }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter, teamFilter, canRun]);

    // Deep-link auto-run (2026-08-21): a URL that already carries Location +
    // Club was shared/bookmarked — run it immediately so the recipient sees
    // the report (and the Team dropdown, pre-selected) without having to
    // click Run Report. Manual filter-picking keeps the Run gate: this only
    // fires once, on mount, and only when the URL was fully specified.
    const _deepLinkRun = useRef(null);
    if (_deepLinkRun.current === null) {
        _deepLinkRun.current = (locationFilter || []).length > 0
            && (clubFilter || []).length > 0;
    }
    useEffect(() => {
        if (_deepLinkRun.current) { _deepLinkRun.current = false; fetchData(); }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Team picker plumbing: clear the team when the club changes, and
    // auto-refresh a loaded report when the team selection changes (the
    // dropdown only exists once a report has run, so this is a cheap,
    // already-scoped query — no need to make the user press Run again).
    const _teamMount = useRef(true);
    const _clubKey = (clubFilter || []).join('|');
    useEffect(() => {
        if (_teamMount.current) return;   // don't clear the URL's team on mount
        setTeamFilter('');
    }, [_clubKey]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (_teamMount.current) { _teamMount.current = false; return; }
        if (canRun && data && data.buckets) fetchData();
    }, [teamFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch available_clubs (for the dropdown) as soon as a location is
    // picked, but WITHOUT a club selection — a lightweight recon call that
    // populates the multi-select. Same trick the Inventory-By-Club report
    // uses to avoid a heavy pre-flight.
    useEffect(() => {
        if (!(locationFilter || []).length) { setData(null); return; }
        if (canRun) return;   // full fetch will populate available_clubs
        (async () => {
            try {
                const p = new URLSearchParams();
                p.set('date_from', dateFrom); p.set('date_to', dateTo);
                (locationFilter || []).forEach(v => p.append('locations', v));
                p.set('clubs_only', 'true');
                const res = await fetchAuth(`/api/reports/club_summary?${p.toString()}`);
                if (res.ok) setData(await res.json());
            } catch (_) { /* silent — Run Report will surface any real error */ }
        })();
    }, [fetchAuth, dateFrom, dateTo, locationFilter, canRun]);

    const handlePrint = () => {
        // Stamp the isolation class so @media print shows ONLY the report
        // body (see styles.css `body.cs-printing`). afterprint removes it;
        // the timeout is a fallback for browsers that don't fire afterprint.
        document.body.classList.add('cs-printing');
        const cleanup = () => {
            document.body.classList.remove('cs-printing');
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        window.print();
        setTimeout(cleanup, 2000);
    };

    const buckets = data?.buckets || {};
    const pcts    = data?.percentages || {};
    const dur     = data?.fulfillment_duration || { n: 0, histogram: {} };
    const total   = buckets.total || 0;
    const fmtN    = (n) => (n == null ? '—' : Number(n).toLocaleString());
    const fmtPct  = (p) => (p == null ? '—' : `${Number(p).toFixed(1)}%`);
    const fmtDays = (d) => (d == null ? '—' : `${Number(d).toFixed(1)}d`);
    const fmtDate = (iso) => {
        if (!iso) return '—';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch { return iso; }
    };

    // Bucket table = individual buckets interleaved with running-subtotal
    // rows (styled distinctly). Values for subtotal rows come pre-computed
    // from data.percentages so the FE doesn't re-derive the math.
    const bucketRows = [
        { label: 'Fulfilled — shipped',                             key: 'shipped',
          tip: 'Orders shipped to the customer — a shipping label was bought and the order fulfilled. The customer has it (or it is with the carrier).' },
        { label: 'Fulfilled — picked up in store',                  key: 'picked_up',
          tip: 'Pickup orders the customer has physically collected from the store. Done.' },
        { label: 'Shipped or picked up in store',                   subtotal: 'customer_has_it',
          tip: 'Subtotal: Shipped + Picked up — every order the customer already has in hand.' },
        { label: 'Ready for pickup (in store, customer notified)',  key: 'ready_for_pickup',
          tip: 'At the store and the customer has been emailed it is ready — waiting for them to come in. Not yet in their hands.' },
        { label: 'Fulfilled from our perspective',                  subtotal: 'fulfilled_ours',
          tip: 'Subtotal: everything the customer has PLUS Ready-for-pickup. From the warehouse view these are done — the only thing left is the customer walking in.' },
        { label: 'In transit to store',                             key: 'in_transit',
          tip: 'Transferred out of the fulfillment location and on its way to the pickup store — not there yet, so not ready for the customer.' },
        { label: 'Operational processes completed',                 subtotal: 'ops_complete',
          tip: 'Subtotal: Fulfilled-from-our-perspective PLUS In-transit — every order the warehouse has finished working, whether or not it has reached the store.' },
        { label: 'Not fulfilled',                                   key: 'not_fulfilled',
          tip: 'Nothing has started — not shipped, not in transit, not ready for pickup, not picked up. This is the true backlog still needing work.' },
        { label: 'Partial (no store lifecycle)',                    key: 'partial_no_lifecycle',
          tip: 'Some of the order shipped but not all of it, and it is not a pickup order — so it never entered the in-transit / ready-for-pickup flow. Still has open items.' },
        { label: 'Cancelled',                                       key: 'cancelled',
          tip: 'Order was cancelled — excluded from the fulfillment percentages.' },
    ];

    const histogram = dur.histogram || {};
    const maxHist = Math.max(1, ...Object.values(histogram));
    // 2026-09-02: Daily order volume shows at most the LAST 30 days of the range.
    const dailyRows = (data?.daily_trend || []).slice(-30);
    const dailyTruncated = (data?.daily_trend || []).length > 30;
    const maxDay = Math.max(1, ...dailyRows.flatMap(d => [d.orders_placed || 0, d.orders_fulfilled || 0]));
    const maxWeek = Math.max(1, ...(data?.weekly_trend || [])
        .flatMap(w => [w.orders_placed || 0, w.orders_fulfilled || 0]));

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Club Summary</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo}     setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data?.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handlePrint}
                        exportLabel="Print"
                        exportDisabled={loading || !total}
                        trailing={
                            <>
                                {(clubFilter || []).length > 0
                                    && (data?.available_teams || []).length > 0 && (
                                    <div style={{ minWidth: 170 }}>
                                        <ReportPicker
                                            label="Team"
                                            options={[
                                                { label: 'All teams', value: '' },
                                                ...data.available_teams.map(t =>
                                                    ({ label: t, value: t })),
                                            ]}
                                            value={teamFilter}
                                            onChange={setTeamFilter}
                                        />
                                    </div>
                                )}
                                <Button
                                    variant="primary"
                                    tone="success"
                                    onClick={fetchData}
                                    disabled={loading || !canRun}
                                    loading={loading}
                                >
                                    Run Report
                                </Button>
                            </>
                        }
                    />
                    {!canRun && (
                        <Text variant="bodySm" tone="subdued">
                            Pick a Location and a Club, then click <strong>Run Report</strong>.
                        </Text>
                    )}
                </BlockStack>
            </Card>

            {error && <Banner tone="critical" title="Couldn't load report">{error}</Banner>}

            {/* One-pager body — everything below is what appears in the printout. */}
            {total > 0 && (
            <div className="club-summary-body">
                {/* Header */}
                <Card>
                    <BlockStack gap="200">
                        <Text variant="headingLg" as="h2">Club Summary</Text>
                        <InlineStack gap="400" wrap>
                            <Text variant="bodySm" tone="subdued">
                                Window: {data?.date_from || '—'} → {data?.date_to || '—'}
                            </Text>
                            {(clubFilter || []).length > 0 && (
                                <Text variant="bodySm" tone="subdued">
                                    Clubs: {clubFilter.join(', ')}
                                </Text>
                            )}
                            {teamFilter && (
                                <Text variant="bodySm" tone="subdued">
                                    Team: {teamFilter}
                                </Text>
                            )}
                        </InlineStack>
                    </BlockStack>
                </Card>

                {/* KPI tiles */}
                <div className="cs-kpi-row">
                    <div className="cs-kpi cs-kpi--total">
                        <div className="cs-kpi__label">Total orders</div>
                        <div className="cs-kpi__value">{fmtN(total)}</div>
                        <div className="cs-kpi__tip">{_csKpiTips(buckets, fmtN).total}</div>
                    </div>
                    <div className="cs-kpi cs-kpi--good">
                        <div className="cs-kpi__label">Fulfilled</div>
                        <div className="cs-kpi__value">{fmtPct(pcts.fulfilled_pct)}</div>
                        <div className="cs-kpi__tip">{_csKpiTips(buckets, fmtN).fulfilled}</div>
                    </div>
                    <div className="cs-kpi cs-kpi--warn">
                        <div className="cs-kpi__label">Fulfilled or customer-notified</div>
                        <div className="cs-kpi__value">{fmtPct(pcts.fulfilled_or_waiting_pct)}</div>
                        <div className="cs-kpi__tip">{_csKpiTips(buckets, fmtN).notified}</div>
                    </div>
                    <div className="cs-kpi cs-kpi--bad">
                        <div className="cs-kpi__label">Not fulfilled</div>
                        <div className="cs-kpi__value">{fmtPct(pcts.not_fulfilled_pct)}</div>
                        <div className="cs-kpi__tip">{_csKpiTips(buckets, fmtN).notFulfilled}</div>
                    </div>
                </div>

                {/* Bucket table */}
                <Card>
                    <BlockStack gap="200">
                        <Text variant="headingMd" as="h3">Order status breakdown</Text>
                        <table className="cs-bucket-table">
                            <thead>
                                <tr><th>Bucket</th><th className="num">Count</th><th className="num">% of total</th></tr>
                            </thead>
                            <tbody>
                                {bucketRows.map((b, i) => {
                                    const n = b.subtotal
                                        ? (pcts[b.subtotal] || 0)
                                        : (buckets[b.key] || 0);
                                    const pct = total ? (n * 100 / total) : 0;
                                    const cls = b.subtotal ? 'cs-bucket-table__subtotal' : '';
                                    return (
                                        <tr key={b.subtotal || b.key} className={cls}>
                                            <td>
                                                {b.tip
                                                    ? <span className="sf-tip sf-tip--left" data-tip={b.tip}>
                                                          {b.subtotal ? <em>{b.label}</em> : b.label}
                                                      </span>
                                                    : (b.subtotal ? <em>{b.label}</em> : b.label)}
                                            </td>
                                            <td className="num">{b.subtotal ? <strong>{fmtN(n)}</strong> : fmtN(n)}</td>
                                            <td className="num">{b.subtotal ? <strong>{pct.toFixed(1)}%</strong> : `${pct.toFixed(1)}%`}</td>
                                        </tr>
                                    );
                                })}
                                <tr className="cs-bucket-table__total">
                                    <td><strong>Total</strong></td>
                                    <td className="num"><strong>{fmtN(total)}</strong></td>
                                    <td className="num"><strong>100.0%</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </BlockStack>
                </Card>

                {/* Fulfillment duration */}
                <Card>
                    <BlockStack gap="300">
                        <Text variant="headingMd" as="h3">Fulfillment duration (business days)</Text>
                        <Text variant="bodySm" tone="subdued">
                            Business days (Mon–Fri, US federal holidays excluded) from order placed
                            to effective fulfillment (whichever came first: packed or shipped).
                            Same calculation as Orders By Club.
                        </Text>
                        <InlineStack gap="600" wrap>
                            <div className="cs-dur-stat">
                                <div className="cs-dur-stat__label">Average</div>
                                <div className="cs-dur-stat__value">{fmtDays(dur.avg_days)}</div>
                            </div>
                            <div className="cs-dur-stat">
                                <div className="cs-dur-stat__label">Median</div>
                                <div className="cs-dur-stat__value">{fmtDays(dur.median_days)}</div>
                            </div>
                            <div className="cs-dur-stat">
                                <div className="cs-dur-stat__label">Sample</div>
                                <div className="cs-dur-stat__value">{fmtN(dur.n)} orders</div>
                            </div>
                        </InlineStack>
                        <div className="cs-hist">
                            {['0-3','3-7','7-14','14-30','30+'].map(k => {
                                const n = histogram[k] || 0;
                                const w = maxHist ? (n * 100 / maxHist) : 0;
                                return (
                                    <div className="cs-hist__row" key={k}>
                                        <div className="cs-hist__label">{k === '30+' ? '30+ bus. days' : `${k} bus. days`}</div>
                                        <div className="cs-hist__bar-wrap">
                                            <div className="cs-hist__bar" style={{ width: `${w}%` }}>
                                                {n > 0 && <span className="cs-hist__count">{n}</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </BlockStack>
                </Card>

                {/* Weekly volume trend */}
                {(data?.weekly_trend || []).length > 0 && (
                    <Card>
                        <BlockStack gap="200">
                            <Text variant="headingMd" as="h3">Weekly order volume</Text>
                            {(() => {
                                // "Nice" tick step: 4 evenly-spaced Y ticks from 0
                                // to a rounded-up max (so bars line up with the
                                // grid). e.g. maxWeek=87 → ticks 0, 25, 50, 75, 100.
                                const roundUp = (v) => {
                                    if (v <= 5)  return 5;
                                    if (v <= 10) return 10;
                                    if (v <= 25) return 25;
                                    if (v <= 50) return 50;
                                    // Round up to next 25.
                                    return Math.ceil(v / 25) * 25;
                                };
                                const yMax = roundUp(maxWeek);
                                const yTicks = [yMax, yMax * 0.75, yMax * 0.5, yMax * 0.25, 0];
                                // Long windows (YTD) render 30+ weeks — thin the
                                // x-axis labels so they don't collide. Every
                                // column keeps its hover tooltip regardless.
                                const nWeeks = (data.weekly_trend || []).length;
                                const lblEvery = nWeeks < 30 ? 1 : (nWeeks <= 60 ? 2 : Math.ceil(nWeeks / 30));
                                return (
                                    <div className="cs-trend-wrap">
                                        <div className="cs-trend-yaxis">
                                            {yTicks.map((t, i) => (
                                                <div className="cs-trend-yaxis__tick" key={i}>{Math.round(t)}</div>
                                            ))}
                                        </div>
                                        <div className="cs-trend cs-trend--with-grid">
                                            {/* Grid lines behind bars */}
                                            <div className="cs-trend__grid">
                                                {yTicks.map((_, i) => (
                                                    <div className="cs-trend__gridline" key={i} />
                                                ))}
                                            </div>
                                            {(data.weekly_trend || []).map((w, wi) => {
                                                const hP = yMax ? ((w.orders_placed    || 0) * 100 / yMax) : 0;
                                                const hF = yMax ? ((w.orders_fulfilled || 0) * 100 / yMax) : 0;
                                                return (
                                                    <div className={`cs-trend__col${wi % 2 === 0 ? ' cs-trend__col--shaded' : ''}`} key={w.week_start} title={
                                                        `Week of ${w.week_start}\nPlaced: ${w.orders_placed}\nFulfilled: ${w.orders_fulfilled}`
                                                    }>
                                                        <div className="cs-trend__duo">
                                                            <div className="cs-trend__bar cs-trend__bar--placed"
                                                             style={{ height: `${hP}%`,
                                                                      minHeight: (w.orders_placed || 0) > 0 ? undefined : 0 }} />
                                                            <div className="cs-trend__bar cs-trend__bar--events"
                                                             style={{ height: `${hF}%`,
                                                                      minHeight: (w.orders_fulfilled || 0) > 0 ? undefined : 0 }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                            <div style={{ display: 'flex', gap: 6, marginLeft: 32, marginTop: -26 }}>
                                {(data.weekly_trend || []).map((w, wi) => {
                                    const nWeeks2 = (data.weekly_trend || []).length;
                                    const lblEvery2 = nWeeks2 < 30 ? 1 : (nWeeks2 <= 60 ? 2 : Math.ceil(nWeeks2 / 30));
                                    return (
                                        <div key={w.week_start} style={{ flex: 1, textAlign: 'center', fontSize: 10,
                                                                         color: '#6b7280', whiteSpace: 'nowrap' }}>
                                            {wi % lblEvery2 === 0 ? w.week_start.slice(5) : '\u00a0'}
                                        </div>
                                    );
                                })}
                            </div>
                            <Text variant="bodySm" tone="subdued">
                                Gray bar = orders placed that week. Green bar = orders fulfilled that
                                week (packed or shipped, any placement date — matches the Orders
                                report's Fulfilled). Weeks run Sunday through Saturday; X axis labels
                                are Sunday of each week (MM-DD).
                            </Text>
                        </BlockStack>
                    </Card>
                )}

                {dailyRows.length > 0 && (
                    <Card>
                        <BlockStack gap="200">
                            <Text variant="headingMd" as="h3">Daily order volume{dailyTruncated ? ' (last 30 days)' : ''}</Text>
                            {(() => {
                                // "Nice" tick step: 4 evenly-spaced Y ticks from 0
                                // to a rounded-up max (so bars line up with the
                                // grid). e.g. maxDay=87 → ticks 0, 25, 50, 75, 100.
                                const roundUp = (v) => {
                                    if (v <= 5)  return 5;
                                    if (v <= 10) return 10;
                                    if (v <= 25) return 25;
                                    if (v <= 50) return 50;
                                    // Round up to next 25.
                                    return Math.ceil(v / 25) * 25;
                                };
                                const yMax = roundUp(maxDay);
                                const yTicks = [yMax, yMax * 0.75, yMax * 0.5, yMax * 0.25, 0];
                                // Long windows (YTD) render 30+ weeks — thin the
                                // x-axis labels so they don't collide. Every
                                // column keeps its hover tooltip regardless.
                                const nDays = dailyRows.length;
                                const lblEvery = nDays < 30 ? 1 : (nDays <= 60 ? 2 : Math.ceil(nDays / 30));
                                return (
                                    <div className="cs-trend-wrap">
                                        <div className="cs-trend-yaxis">
                                            {yTicks.map((t, i) => (
                                                <div className="cs-trend-yaxis__tick" key={i}>{Math.round(t)}</div>
                                            ))}
                                        </div>
                                        <div className="cs-trend cs-trend--with-grid cs-trend--daily">
                                            {/* Grid lines behind bars */}
                                            <div className="cs-trend__grid">
                                                {yTicks.map((_, i) => (
                                                    <div className="cs-trend__gridline" key={i} />
                                                ))}
                                            </div>
                                            {dailyRows.map((w, wi) => {
                                                const hP = yMax ? ((w.orders_placed    || 0) * 100 / yMax) : 0;
                                                const hF = yMax ? ((w.orders_fulfilled || 0) * 100 / yMax) : 0;
                                                return (
                                                    <div className={`cs-trend__col${wi % 2 === 0 ? ' cs-trend__col--shaded' : ''}`} key={w.day} title={
                                                        `${w.day}\nPlaced: ${w.orders_placed}\nFulfilled: ${w.orders_fulfilled}`
                                                    }>
                                                        <div className="cs-trend__duo">
                                                            <div className="cs-trend__bar cs-trend__bar--placed"
                                                             style={{ height: `${hP}%`,
                                                                      minHeight: (w.orders_placed || 0) > 0 ? undefined : 0 }} />
                                                            <div className="cs-trend__bar cs-trend__bar--events"
                                                             style={{ height: `${hF}%`,
                                                                      minHeight: (w.orders_fulfilled || 0) > 0 ? undefined : 0 }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                            <div style={{ display: 'flex', gap: 1, marginLeft: 32, marginTop: -26, overflow: 'hidden' }}>
                                {dailyRows.map((w, wi) => {
                                    const nDays2 = dailyRows.length;
                                    const lblEvery2 = nDays2 < 30 ? 1 : (nDays2 <= 60 ? 2 : Math.ceil(nDays2 / 30));
                                    return (
                                        <div key={w.day} style={{ flex: 1, textAlign: 'center', fontSize: 10,
                                                                         color: '#6b7280', whiteSpace: 'nowrap' }}>
                                            {wi % lblEvery2 === 0 ? w.day.slice(5) : '\u00a0'}
                                        </div>
                                    );
                                })}
                            </div>
                            <Text variant="bodySm" tone="subdued">
                                Gray bar = orders placed that day. Green bar = orders fulfilled that day (packed or shipped, any placement date). One column per calendar day{dailyTruncated ? ' — showing the last 30 days of the selected range' : ''}; X axis labels are MM-DD.
                            </Text>
                        </BlockStack>
                    </Card>
                )}

                {/* Overdue */}
                {(data?.overdue || []).length > 0 && (
                    <Card>
                        <BlockStack gap="200">
                            <div className="cs-collapse-toggle" role="button" tabIndex={0} onClick={() => toggleSec('overdue')}><span className="cs-collapse-caret">{openSections['overdue'] ? '▾' : '▸'}</span><Text variant="headingMd" as="h3">
                                Overdue ({(data.overdue).length})
                            </Text></div>
                            {openSections['overdue'] && (<>
                            <Text variant="bodySm" tone="subdued">
                                Orders whose Fulfill By date has passed and are still not fulfilled.
                            </Text>
                            <table className="cs-list-table">
                                <thead>
                                    <tr>
                                        <th>Order</th><th>Placed</th><th>Fulfill By</th>
                                        <th className="num">Days late</th><th>Status</th><th>Customer</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.overdue.map(o => (
                                        <tr key={o.order_id}>
                                            <td><Link to={`/store/${o.store_key || storeSlug || 'soccerpost'}/orders/${o.order_id}`}>{o.order_name}</Link></td>
                                            <td>{fmtDate(o.placed_at)}</td>
                                            <td>{fmtDate(o.fulfill_by)}</td>
                                            <td className="num" style={{ color: '#c1281c', fontWeight: 600 }}>
                                                {o.days_overdue}
                                            </td>
                                            <td>{(o.fulfillment_status || '').replace(/_/g, ' ')}</td>
                                            <td>{o.customer_name || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </>)}
                        </BlockStack>
                    </Card>
                )}

                {/* Ready for pickup */}
                {(data?.ready_for_pickup || []).length > 0 && (
                    <Card>
                        <BlockStack gap="200">
                            <div className="cs-collapse-toggle" role="button" tabIndex={0} onClick={() => toggleSec('ready_for_pickup')}><span className="cs-collapse-caret">{openSections['ready_for_pickup'] ? '▾' : '▸'}</span><Text variant="headingMd" as="h3">
                                Ready for pickup ({(data.ready_for_pickup).length})
                            </Text></div>
                            {openSections['ready_for_pickup'] && (<>
                            <Text variant="bodySm" tone="subdued">
                                Orders sitting at the store — customer has been notified.
                            </Text>
                            <table className="cs-list-table">
                                <thead>
                                    <tr>
                                        <th>Order</th><th>Placed</th>
                                        <th>Customer Notified</th>
                                        <th className="num">Notified Days Ago</th>
                                        <th>Customer</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.ready_for_pickup.map(o => {
                                        // Days-ago computed client-side so it stays
                                        // accurate even if the page is left open.
                                        let daysAgo = null;
                                        if (o.ready_for_pickup_at) {
                                            const then = new Date(o.ready_for_pickup_at);
                                            const now  = new Date();
                                            daysAgo = Math.floor((now - then) / 86400000);
                                        }
                                        return (
                                            <tr key={o.order_id}>
                                                <td><Link to={`/store/${o.store_key || storeSlug || 'soccerpost'}/orders/${o.order_id}`}>{o.order_name}</Link></td>
                                                <td>{fmtDate(o.placed_at)}</td>
                                                <td>{fmtDate(o.ready_for_pickup_at)}</td>
                                                <td className="num" style={
                                                    daysAgo != null && daysAgo >= 7
                                                        ? { color: '#c1281c', fontWeight: 600 }
                                                        : undefined
                                                }>
                                                    {daysAgo == null ? '—' : daysAgo}
                                                </td>
                                                <td>{o.customer_name || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </>)}
                        </BlockStack>
                    </Card>
                )}

                {/* Unfulfilled */}
                {(data?.unfulfilled || []).length > 0 && (
                    <Card>
                        <BlockStack gap="200">
                            <div className="cs-collapse-toggle" role="button" tabIndex={0} onClick={() => toggleSec('unfulfilled')}><span className="cs-collapse-caret">{openSections['unfulfilled'] ? '▾' : '▸'}</span><Text variant="headingMd" as="h3">
                                Not fulfilled ({(data.unfulfilled).length})
                            </Text></div>
                            {openSections['unfulfilled'] && (<>
                            <Text variant="bodySm" tone="subdued">
                                Orders that haven't entered the fulfillment pipeline yet. Oldest first.
                            </Text>
                            <table className="cs-list-table">
                                <thead>
                                    <tr>
                                        <th>Order</th><th>Placed</th><th>Fulfill By</th>
                                        <th className="num">Items</th><th>Customer</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.unfulfilled.map(o => (
                                        <tr key={o.order_id}>
                                            <td><Link to={`/store/${o.store_key || storeSlug || 'soccerpost'}/orders/${o.order_id}`}>{o.order_name}</Link></td>
                                            <td>{fmtDate(o.placed_at)}</td>
                                            <td>{fmtDate(o.fulfill_by)}</td>
                                            <td className="num">{o.items_count}</td>
                                            <td>{o.customer_name || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </>)}
                        </BlockStack>
                    </Card>
                )}

                {/* In transit */}
                {(data?.in_transit || []).length > 0 && (
                    <Card>
                        <BlockStack gap="200">
                            <div className="cs-collapse-toggle" role="button" tabIndex={0} onClick={() => toggleSec('in_transit')}><span className="cs-collapse-caret">{openSections['in_transit'] ? '▾' : '▸'}</span><Text variant="headingMd" as="h3">
                                In transit ({(data.in_transit).length})
                            </Text></div>
                            {openSections['in_transit'] && (<>
                            <Text variant="bodySm" tone="subdued">
                                Orders that have left the fulfillment location and are on their way
                                to the pickup store. Oldest transfer first.
                            </Text>
                            <table className="cs-list-table">
                                <thead>
                                    <tr>
                                        <th>Order</th><th>Placed</th><th>Transferred</th>
                                        <th className="num">Days In Transit</th>
                                        <th>Destination</th><th>Customer</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.in_transit.map(o => {
                                        // Days-in-transit computed client-side so it
                                        // stays accurate even if the page is left open.
                                        let daysAgo = null;
                                        if (o.transferred_at) {
                                            const then = new Date(o.transferred_at);
                                            const now  = new Date();
                                            daysAgo = Math.floor((now - then) / 86400000);
                                        }
                                        return (
                                            <tr key={o.order_id}>
                                                <td><Link to={`/store/${o.store_key || storeSlug || 'soccerpost'}/orders/${o.order_id}`}>{o.order_name}</Link></td>
                                                <td>{fmtDate(o.placed_at)}</td>
                                                <td>{fmtDate(o.transferred_at)}</td>
                                                <td className="num" style={
                                                    daysAgo != null && daysAgo >= 7
                                                        ? { color: '#c1281c', fontWeight: 600 }
                                                        : undefined
                                                }>
                                                    {daysAgo == null ? '—' : daysAgo}
                                                </td>
                                                <td>{o.destination || '—'}</td>
                                                <td>{o.customer_name || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </>)}
                        </BlockStack>
                    </Card>
                )}
            </div>
            )}
        </BlockStack>
    );
}

/* ---------- Location Summary report ------------------------------------- */
// Location-facing version of the Club Summary one-pager: pick a location
// (no club), get the top portion only — KPI tiles, bucket table, fulfillment
// duration, weekly volume chart. No per-order lists. Same backend endpoint;
// location access is enforced server-side by _scoped_store_slocs.

// Location Summary "All Stores" rollups — the same warehouse summed across
// every store. Member loc ids MIRROR rankings.py _AGGREGATE_MEMBERS; keep in
// sync if that changes. Selecting one expands to its members (the backend then
// sums cross-store: orders_summary emits an "All Stores: X (All Locations)"
// combined panel; club_summary sums the passed locations).
const LOCATION_SUMMARY_AGGREGATES = [
    { shopify_location_id: 'aggregate:teams_lancaster_all',
      name: 'All Stores: Teams Lancaster (All Locations)',
      // 2026-09-01: TEAMS Lancaster only — the "Lancaster Warehouse" locations
      // (soccerpost 68375740594, stefans 98318975253, u90 71025131710,
      // b2b 74929766653) are DELIBERATELY excluded so this aggregate matches
      // the Orders report's lancaster_slocs_by_store (app.py) exactly. Warehouse
      // orders belong under Tinton/warehouse reporting, not the Teams rollup.
      members: ['71312965810', '77926826162', '111807562039',
                '110066008428', '77901758625'] },
    { shopify_location_id: 'aggregate:tinton_falls_all',
      name: 'All Stores: Tinton Falls Warehouse (All Locations)',
      members: ['81277419698', '110248427884', '108354502967', '80306438334',
                '86007939325', '97551581461'] },
];
function _expandAggregateLocs(ids) {
    const out = [];
    for (const id of (ids || [])) {
        const agg = LOCATION_SUMMARY_AGGREGATES.find(a => a.shopify_location_id === id);
        if (agg) out.push(...agg.members); else out.push(id);
    }
    return [...new Set(out)];
}

// Embeds one location's (or an aggregate's) Orders chart+table (reusing
// <OrdersPanel>) inside the Location Summary. Follows the summary's location +
// date range (props). Holds its OWN view + legend-hide state, seeded from
// initialView / initialHidden, so two instances (Cumulative + By Day) coexist
// and each stays interactive. `locs` is the already-expanded member id list.
function EmbeddedOrdersPanel({ locs, dateFrom, dateTo, title, initialView, initialHidden }) {
    const fetchAuth = useAuthFetch();
    // Fixed view (no toggle) — seeded from initialView.
    const [view] = useState(initialView === 'cumulative' ? 'cumulative' : 'by_day');
    const [chartHidden, setChartHidden] = useState(() => new Set(initialHidden || []));
    const [panels, setPanels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const locsKey = (locs || []).join(',');
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const list = locs || [];
            if (!list.length) { setPanels([]); return; }
            setLoading(true); setErr(null);
            try {
                const p = new URLSearchParams();
                if (dateFrom) p.set('date_from', dateFrom);
                if (dateTo)   p.set('date_to',   dateTo);
                list.forEach(l => p.append('locations', l));
                const res = await fetchAuth(`/api/reports/orders_summary?${p.toString()}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j = await res.json();
                if (!cancelled) setPanels(j.panels || []);
            } catch (e) { if (!cancelled) setErr(e.message || String(e)); }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [fetchAuth, locsKey, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

    // Single loc → that location's panel. Multiple (an aggregate) → the
    // "All Stores: X (All Locations)" combined panel (else any combined).
    const panel = useMemo(() => {
        const ps = panels || [];
        if ((locs || []).length > 1) {
            return ps.find(p => p.is_combined && /All Stores:/.test(p.name || ''))
                || ps.find(p => p.is_combined) || ps[0] || null;
        }
        return ps.find(p => !p.is_combined) || ps[0] || null;
    }, [panels, locsKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const shown = useMemo(() => {
        if (!panel) return null;
        return view === 'cumulative' ? _ordersCumulativePanels([panel])[0] : panel;
    }, [panel, view]);

    return (
        <BlockStack gap="200">
            <Text variant="headingMd" as="h3">{title}</Text>
            <DelayedSpinner loading={loading} />
            {err && <Banner tone="critical" title="Couldn't load orders">{err}</Banner>}
            {shown ? (
                <OrdersPanel panel={shown} view={view}
                             chartHidden={chartHidden} setChartHidden={setChartHidden} />
            ) : !loading ? (
                <Card><div style={{ padding: 24, textAlign: 'center', color: '#6d7175' }}>
                    No order activity for this location in the window.
                </div></Card>
            ) : null}
        </BlockStack>
    );
}

/* Weekly order volume (stacked) + Level of Effort — Location Summary only.
   Stack mirrors the Orders By-Day event buckets bottom-up: Fulfilled (green),
   Completed Partial (purple), Partial (amber) — same colors as
   ORDERS_EVENT_BUCKETS. LOE = units shipped per FULFILLMENT week:
   bundles / individual products / embellishments. */
function LocationWeeklyCharts({ weekly }) {
    const roundUp = (v) => {
        if (v <= 5)  return 5;
        if (v <= 10) return 10;
        if (v <= 25) return 25;
        if (v <= 50) return 50;
        return Math.ceil(v / 25) * 25;
    };
    const roundUpBig = (v) => {
        if (v <= 100) return roundUp(v);
        if (v <= 1000) return Math.ceil(v / 100) * 100;
        return Math.ceil(v / 1000) * 1000;
    };
    const nWeeks = weekly.length;
    // Label density (2026-08-31 user rule): under 30 weeks → every week;
    // 30–60 weeks → every 2nd; beyond that thin proportionally.
    const lblEvery = nWeeks < 30 ? 1 : (nWeeks <= 60 ? 2 : Math.ceil(nWeeks / 30));
    const stackTotal = (w) => (w.orders_fulfilled || 0) + (w.orders_completed_partial || 0) + (w.orders_partial || 0);
    const maxVol = Math.max(1, ...weekly.flatMap(w => [w.orders_placed || 0, stackTotal(w)]));
    const yMaxV = roundUp(maxVol);
    const yTicksV = [yMaxV, yMaxV * 0.75, yMaxV * 0.5, yMaxV * 0.25, 0];
    const LOE_SERIES = [
        { key: 'loe_bundles',        label: 'Bundles',        color: '#3b82f6' },
        { key: 'loe_products',       label: 'Products',       color: '#14b8a6' },
        { key: 'loe_embellishments', label: 'Embellishments', color: '#f97316' },
    ];
    const maxLoe = Math.max(1, ...weekly.flatMap(w => LOE_SERIES.map(s => w[s.key] || 0)));
    const yMaxL = roundUpBig(maxLoe);
    const yTicksL = [yMaxL, yMaxL * 0.75, yMaxL * 0.5, yMaxL * 0.25, 0];
    const fmtK = (n) => n >= 10000 ? `${Math.round(n / 1000)}k` : n.toLocaleString();
    return (
        <>
            <Card>
                <BlockStack gap="200">
                    <Text variant="headingMd" as="h3">Weekly order volume</Text>
                    <div className="cs-trend-wrap">
                        <div className="cs-trend-yaxis">
                            {yTicksV.map((t, i) => (
                                <div className="cs-trend-yaxis__tick" key={i}>{Math.round(t)}</div>
                            ))}
                        </div>
                        <div className="cs-trend cs-trend--with-grid">
                            <div className="cs-trend__grid">
                                {yTicksV.map((_, i) => <div className="cs-trend__gridline" key={i} />)}
                            </div>
                            {weekly.map((w, wi) => {
                                const tot = stackTotal(w);
                                const hP = (w.orders_placed || 0) * 100 / yMaxV;
                                const segs = [
                                    { n: w.orders_fulfilled || 0,         c: '#10b981' },
                                    { n: w.orders_completed_partial || 0, c: '#a855f7' },
                                    { n: w.orders_partial || 0,           c: '#f59e0b' },
                                ];
                                return (
                                    <div className={`cs-trend__col${wi % 2 === 0 ? ' cs-trend__col--shaded' : ''}`} key={w.week_start} title={
                                        `Week of ${w.week_start}`
                                        + `\nPlaced: ${w.orders_placed || 0}`
                                        + `\nFulfilled + Partials: ${tot}`
                                        + `\n  • Fulfilled: ${w.orders_fulfilled || 0}`
                                        + `\n  • Completed Partial: ${w.orders_completed_partial || 0}`
                                        + `\n  • Partial Fulfillment: ${w.orders_partial || 0}`
                                    }>
                                        <div className="cs-trend__duo">
                                            <div className="cs-trend__bar cs-trend__bar--placed"
                                                 style={{ height: `${hP}%` }} />
                                            <div style={{ width: '50%', display: 'flex', flexDirection: 'column-reverse', height: '100%', justifyContent: 'flex-start' }}>
                                                {segs.map((s, si) => (
                                                    <div key={si} style={{ height: `${s.n * 100 / yMaxV}%`, background: s.c, width: '100%' }} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginLeft: 32, marginTop: -26 }}>
                        {weekly.map((w, wi) => (
                            <div key={w.week_start} style={{ flex: 1, textAlign: 'center', fontSize: 10,
                                                             color: '#6b7280', whiteSpace: 'nowrap', overflow: 'visible' }}>
                                {wi % lblEvery === 0 ? w.week_start.slice(5) : '\u00a0'}
                            </div>
                        ))}
                    </div>
                    <Text variant="bodySm" tone="subdued">
                        Gray bar = orders placed that week. Stacked bar = work completed that week —
                        <span style={{ color: '#10b981', fontWeight: 600 }}> Fulfilled</span>,
                        <span style={{ color: '#a855f7', fontWeight: 600 }}> Completed Partial</span> and
                        <span style={{ color: '#f59e0b', fontWeight: 600 }}> Partial Fulfillment</span> stacked
                        (same buckets and colors as the Orders — By Day table). Weeks run Sunday
                        through Saturday; hover a column for the breakdown.
                    </Text>
                </BlockStack>
            </Card>
            <Card>
                <BlockStack gap="200">
                    <Text variant="headingMd" as="h3">Level of effort</Text>
                    <div className="cs-trend-wrap">
                        <div className="cs-trend-yaxis">
                            {yTicksL.map((t, i) => (
                                <div className="cs-trend-yaxis__tick" key={i}>{fmtK(Math.round(t))}</div>
                            ))}
                        </div>
                        <div className="cs-trend cs-trend--with-grid">
                            <div className="cs-trend__grid">
                                {yTicksL.map((_, i) => <div className="cs-trend__gridline" key={i} />)}
                            </div>
                            {weekly.map((w, wi) => {
                                const totalUnits = LOE_SERIES.reduce((a, s) => a + (w[s.key] || 0), 0);
                                return (
                                    <div className={`cs-trend__col${wi % 2 === 0 ? ' cs-trend__col--shaded' : ''}`} key={w.week_start} title={
                                        `Week of ${w.week_start}`
                                        + `\nTotal units shipped: ${totalUnits.toLocaleString()}`
                                        + LOE_SERIES.map(s => `\n  • ${s.label}: ${(w[s.key] || 0).toLocaleString()}`).join('')
                                    }>
                                        <div className="cs-trend__duo">
                                            {LOE_SERIES.map(s => (
                                                <div key={s.key} style={{
                                                    width: '33.3%', alignSelf: 'flex-end',
                                                    height: `${(w[s.key] || 0) * 100 / yMaxL}%`,
                                                    background: s.color,
                                                }} />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginLeft: 32, marginTop: -26 }}>
                        {weekly.map((w, wi) => (
                            <div key={w.week_start} style={{ flex: 1, textAlign: 'center', fontSize: 10,
                                                             color: '#6b7280', whiteSpace: 'nowrap', overflow: 'visible' }}>
                                {wi % lblEvery === 0 ? w.week_start.slice(5) : '\u00a0'}
                            </div>
                        ))}
                    </div>
                    <Text variant="bodySm" tone="subdued">
                        Units shipped out per week (by fulfillment date, matching the chart above):
                        <span style={{ color: '#3b82f6', fontWeight: 600 }}> Bundles</span>,
                        <span style={{ color: '#14b8a6', fontWeight: 600 }}> Individual products</span> (items
                        without embellishments) and
                        <span style={{ color: '#f97316', fontWeight: 600 }}> Embellishments</span> (names,
                        numbers, logos, crests). Hover a column for exact counts.
                    </Text>
                </BlockStack>
            </Card>
        </>
    );
}

function LocationSummaryReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    // Cross-store "All Stores: X" rollups are admin-level views — location-
    // scoped roles (franchise etc.) only see their own locations
    // (2026-08-31 user rule). Server still enforces via _scoped_store_slocs.
    const aggregateLocs = hasRole(user, ['admin', 'finance'])
        ? LOCATION_SUMMARY_AGGREGATES : [];

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState(null);
    // The two embedded Orders panels stay hidden until the user explicitly
    // clicks Run Report (NOT on deep-link auto-run), and re-hide on any filter
    // change — they each do a heavy Shopify fetch, so don't run them unasked.
    // 2026-08-31: deep links with a location preset auto-show the panels —
    // the main summary already auto-loads on deep link, so hiding just the
    // Orders panels made bookmarked URLs look like the tables vanished.
    // Filter CHANGES still re-hide them until the next Run Report.
    const [ordersRequested, setOrdersRequested] = useState(
        () => (searchParams.getAll('loc') || []).length > 0);
    const _lsMountedRef = useRef(false);
    useEffect(() => {
        if (!_lsMountedRef.current) { _lsMountedRef.current = true; return; }
        setOrdersRequested(false);
    }, [locationFilter, dateFrom, dateTo]);

    // Keep URL in sync with filter state (bookmarkable).
    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, setSearchParams]);

    // Gate: require at least one Location, then explicit Run Report — the
    // no-filter query would sweep every order in the window across every
    // location the user can see.
    const canRun = (locationFilter || []).length > 0;

    const fetchData = useCallback(async () => {
        if (!canRun) { setData(null); return; }
        setLoading(true); setError(null);
        try {
            const p = new URLSearchParams();
            p.set('date_from', dateFrom); p.set('date_to', dateTo);
            _expandAggregateLocs(locationFilter).forEach(v => p.append('locations', v));
            const res = await fetchAuth(`/api/reports/club_summary?${p.toString()}`);
            if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || res.statusText);
            setData(await res.json());
        } catch (e) { setError(e.message || String(e)); }
        finally { setLoading(false); }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, canRun]);

    // Deep-link auto-run — same rule as Club Summary: a URL that already
    // carries a Location came from a shared/bookmarked report, so run it on
    // mount instead of requiring a Run Report click.
    const _deepLinkRun = useRef(null);
    if (_deepLinkRun.current === null) {
        _deepLinkRun.current = (locationFilter || []).length > 0;
    }
    useEffect(() => {
        if (_deepLinkRun.current) { _deepLinkRun.current = false; fetchData(); }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePrint = () => {
        // Stamp the isolation class so @media print shows ONLY the report
        // body (see styles.css `body.cs-printing`). afterprint removes it;
        // the timeout is a fallback for browsers that don't fire afterprint.
        document.body.classList.add('cs-printing');
        const cleanup = () => {
            document.body.classList.remove('cs-printing');
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        window.print();
        setTimeout(cleanup, 2000);
    };

    const buckets = data?.buckets || {};
    const pcts    = data?.percentages || {};
    const dur     = data?.fulfillment_duration || { n: 0, histogram: {} };
    const total   = buckets.total || 0;
    const fmtN    = (n) => (n == null ? '—' : Number(n).toLocaleString());
    const fmtPct  = (p) => (p == null ? '—' : `${Number(p).toFixed(1)}%`);
    const fmtDays = (d) => (d == null ? '—' : `${Number(d).toFixed(1)}d`);

    // Selected location display names for the header line.
    const locNames = (locationFilter || []).map(id => {
        const l = [...LOCATION_SUMMARY_AGGREGATES, ...(availableLocations || [])]
            .find(x => x.shopify_location_id === id);
        return l ? l.name : id;
    });

    const bucketRows = [
        { label: 'Fulfilled — shipped',                             key: 'shipped',
          tip: 'Orders shipped to the customer — a shipping label was bought and the order fulfilled. The customer has it (or it is with the carrier).' },
        { label: 'Fulfilled — picked up in store',                  key: 'picked_up',
          tip: 'Pickup orders the customer has physically collected from the store. Done.' },
        { label: 'Shipped or picked up in store',                   subtotal: 'customer_has_it',
          tip: 'Subtotal: Shipped + Picked up — every order the customer already has in hand.' },
        { label: 'Ready for pickup (in store, customer notified)',  key: 'ready_for_pickup',
          tip: 'At the store and the customer has been emailed it is ready — waiting for them to come in. Not yet in their hands.' },
        { label: 'Fulfilled from our perspective',                  subtotal: 'fulfilled_ours',
          tip: 'Subtotal: everything the customer has PLUS Ready-for-pickup. From the warehouse view these are done — the only thing left is the customer walking in.' },
        { label: 'In transit to store',                             key: 'in_transit',
          tip: 'Transferred out of the fulfillment location and on its way to the pickup store — not there yet, so not ready for the customer.' },
        { label: 'Operational processes completed',                 subtotal: 'ops_complete',
          tip: 'Subtotal: Fulfilled-from-our-perspective PLUS In-transit — every order the warehouse has finished working, whether or not it has reached the store.' },
        { label: 'Not fulfilled',                                   key: 'not_fulfilled',
          tip: 'Nothing has started — not shipped, not in transit, not ready for pickup, not picked up. This is the true backlog still needing work.' },
        { label: 'Partial (no store lifecycle)',                    key: 'partial_no_lifecycle',
          tip: 'Some of the order shipped but not all of it, and it is not a pickup order — so it never entered the in-transit / ready-for-pickup flow. Still has open items.' },
        { label: 'Cancelled',                                       key: 'cancelled',
          tip: 'Order was cancelled — excluded from the fulfillment percentages.' },
    ];

    const histogram = dur.histogram || {};
    const maxHist = Math.max(1, ...Object.values(histogram));
    const maxWeek = Math.max(1, ...(data?.weekly_trend || [])
        .flatMap(w => [w.orders_placed || 0, w.orders_fulfilled || 0]));

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Location Summary</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo}     setDateTo={setDateTo}
                        availableLocations={[...aggregateLocs, ...(availableLocations || [])]}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={[]}
                        onExport={handlePrint}
                        exportLabel="Print"
                        exportDisabled={loading || !total}
                        trailing={
                            <Button
                                variant="primary"
                                tone="success"
                                onClick={() => { setOrdersRequested(true); fetchData(); }}
                                disabled={loading || !canRun}
                                loading={loading}
                            >
                                Run Report
                            </Button>
                        }
                    />
                    {!canRun && (
                        <Text variant="bodySm" tone="subdued">
                            Pick a Location, then click <strong>Run Report</strong>.
                        </Text>
                    )}
                </BlockStack>
            </Card>

            {error && <Banner tone="critical" title="Couldn't load report">{error}</Banner>}

            {total > 0 && (
            <div className="club-summary-body">
                {/* Header */}
                <Card>
                    <BlockStack gap="200">
                        <Text variant="headingLg" as="h2">Location Summary</Text>
                        <InlineStack gap="400" wrap>
                            <Text variant="bodySm" tone="subdued">
                                Window: {data?.date_from || '—'} → {data?.date_to || '—'}
                            </Text>
                            {locNames.length > 0 && (
                                <Text variant="bodySm" tone="subdued">
                                    Locations: {locNames.join(', ')}
                                </Text>
                            )}
                        </InlineStack>
                    </BlockStack>
                </Card>

                {/* KPI tiles */}
                <div className="cs-kpi-row">
                    <div className="cs-kpi cs-kpi--total">
                        <div className="cs-kpi__label">Total orders</div>
                        <div className="cs-kpi__value">{fmtN(total)}</div>
                        <div className="cs-kpi__tip">{_csKpiTips(buckets, fmtN).total}</div>
                    </div>
                    <div className="cs-kpi cs-kpi--good">
                        <div className="cs-kpi__label">Fulfilled</div>
                        <div className="cs-kpi__value">{fmtPct(pcts.fulfilled_pct)}</div>
                        <div className="cs-kpi__tip">{_csKpiTips(buckets, fmtN).fulfilled}</div>
                    </div>
                    <div className="cs-kpi cs-kpi--warn">
                        <div className="cs-kpi__label">Fulfilled or customer-notified</div>
                        <div className="cs-kpi__value">{fmtPct(pcts.fulfilled_or_waiting_pct)}</div>
                        <div className="cs-kpi__tip">{_csKpiTips(buckets, fmtN).notified}</div>
                    </div>
                    <div className="cs-kpi cs-kpi--bad">
                        <div className="cs-kpi__label">Not fulfilled</div>
                        <div className="cs-kpi__value">{fmtPct(pcts.not_fulfilled_pct)}</div>
                        <div className="cs-kpi__tip">{_csKpiTips(buckets, fmtN).notFulfilled}</div>
                    </div>
                </div>

                {/* Bucket table */}
                <Card>
                    <BlockStack gap="200">
                        <Text variant="headingMd" as="h3">Order status breakdown</Text>
                        <table className="cs-bucket-table">
                            <thead>
                                <tr><th>Bucket</th><th className="num">Count</th><th className="num">% of total</th></tr>
                            </thead>
                            <tbody>
                                {bucketRows.map((b) => {
                                    const n = b.subtotal
                                        ? (pcts[b.subtotal] || 0)
                                        : (buckets[b.key] || 0);
                                    const pct = total ? (n * 100 / total) : 0;
                                    const cls = b.subtotal ? 'cs-bucket-table__subtotal' : '';
                                    return (
                                        <tr key={b.subtotal || b.key} className={cls}>
                                            <td>
                                                {b.tip
                                                    ? <span className="sf-tip sf-tip--left" data-tip={b.tip}>
                                                          {b.subtotal ? <em>{b.label}</em> : b.label}
                                                      </span>
                                                    : (b.subtotal ? <em>{b.label}</em> : b.label)}
                                            </td>
                                            <td className="num">{b.subtotal ? <strong>{fmtN(n)}</strong> : fmtN(n)}</td>
                                            <td className="num">{b.subtotal ? <strong>{pct.toFixed(1)}%</strong> : `${pct.toFixed(1)}%`}</td>
                                        </tr>
                                    );
                                })}
                                <tr className="cs-bucket-table__total">
                                    <td><strong>Total</strong></td>
                                    <td className="num"><strong>{fmtN(total)}</strong></td>
                                    <td className="num"><strong>100.0%</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </BlockStack>
                </Card>

                {/* Fulfillment duration */}
                <Card>
                    <BlockStack gap="300">
                        <Text variant="headingMd" as="h3">Fulfillment duration (business days)</Text>
                        <Text variant="bodySm" tone="subdued">
                            Business days (Mon–Fri, US federal holidays excluded) from order placed
                            to effective fulfillment (whichever came first: packed or shipped).
                            Same calculation as Orders By Club.
                        </Text>
                        <InlineStack gap="600" wrap>
                            <div className="cs-dur-stat">
                                <div className="cs-dur-stat__label">Average</div>
                                <div className="cs-dur-stat__value">{fmtDays(dur.avg_days)}</div>
                            </div>
                            <div className="cs-dur-stat">
                                <div className="cs-dur-stat__label">Median</div>
                                <div className="cs-dur-stat__value">{fmtDays(dur.median_days)}</div>
                            </div>
                            <div className="cs-dur-stat">
                                <div className="cs-dur-stat__label">Sample</div>
                                <div className="cs-dur-stat__value">{fmtN(dur.n)} orders</div>
                            </div>
                        </InlineStack>
                        <div className="cs-hist">
                            {['0-3','3-7','7-14','14-30','30+'].map(k => {
                                const n = histogram[k] || 0;
                                const w = maxHist ? (n * 100 / maxHist) : 0;
                                return (
                                    <div className="cs-hist__row" key={k}>
                                        <div className="cs-hist__label">{k === '30+' ? '30+ bus. days' : `${k} bus. days`}</div>
                                        <div className="cs-hist__bar-wrap">
                                            <div className="cs-hist__bar" style={{ width: `${w}%` }}>
                                                {n > 0 && <span className="cs-hist__count">{n}</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </BlockStack>
                </Card>

                {/* Weekly volume trend + Level of Effort (2026-08-31) —
                    Location Summary only; Club Summary keeps its original
                    two-bar chart above. */}
                {(data?.weekly_trend || []).length > 0 && (
                    <LocationWeeklyCharts weekly={data.weekly_trend} />
                )}
            </div>
            )}
            {canRun && ordersRequested && (
                <>
                    <EmbeddedOrdersPanel locs={_expandAggregateLocs(locationFilter)} dateFrom={dateFrom} dateTo={dateTo}
                        title="Orders — Cumulative" initialView="cumulative"
                        initialHidden={['Orders', 'Fulfilled', 'Completed Partial', 'Partial', 'Cancelled']} />
                    <EmbeddedOrdersPanel locs={_expandAggregateLocs(locationFilter)} dateFrom={dateFrom} dateTo={dateTo}
                        title="Orders — By Day" initialView="by_day" initialHidden={[]} />
                </>
            )}
        </BlockStack>
    );
}

/* ---------- Orders By Club report --------------------------------------- */

function OrdersByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter,     setClubFilter]     = useState(searchParams.getAll('club'));
    const [sortKey,        setSortKey]        = useState('location');
    const [sortAsc,        setSortAsc]        = useState(true);
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState(null);

    // Keep URL in sync with filter state
    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, setSearchParams]);

    const fetchData = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const p = new URLSearchParams();
            p.set('date_from', dateFrom); p.set('date_to', dateTo);
            (locationFilter || []).forEach(v => p.append('locations', v));
            (clubFilter     || []).forEach(v => p.append('clubs',     v));
            const res = await fetchAuth(`/api/reports/orders_by_club?${p.toString()}`);
            if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || res.statusText);
            setData(await res.json());
        } catch (e) { setError(e.message || String(e)); }
        finally { setLoading(false); }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const sortedRows = useMemo(() => {
        if (!data?.rows) return [];
        const numeric = new Set(['total_orders','players_ordered','printed','fulfilled','cancelled','partial',
                                 'completed_partial','unfulfilled','percent_modified',
                                 'avg_fulfillment_days']);
        const rows = [...data.rows];
        rows.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            if (numeric.has(sortKey)) {
                const an = av == null ? -Infinity : av;
                const bn = bv == null ? -Infinity : bv;
                return sortAsc ? an - bn : bn - an;
            }
            const cmp = String(av || '').localeCompare(String(bv || ''));
            return sortAsc ? cmp : -cmp;
        });
        return rows;
    }, [data, sortKey, sortAsc]);

    const onSort = (k) => {
        if (sortKey === k) setSortAsc(v => !v);
        else { setSortKey(k); setSortAsc(true); }
    };

    const handleExport = useCallback(() => {
        if (!data?.rows) return;
        const rows = [
            ['Location','Club','Total Orders','Players Ordered','Printed','Fulfilled','Cancelled','Partial',
             'Completed Partial','Unfulfilled','Percent Modified','Avg Fulfillment Duration (Business Days)'],
            ...sortedRows.map(r => [
                r.location, r.club, r.total_orders, r.players_ordered, r.printed, r.fulfilled, r.cancelled, r.partial,
                r.completed_partial, r.unfulfilled,
                r.percent_modified != null ? r.percent_modified.toFixed(1) : '',
                r.avg_fulfillment_days != null ? r.avg_fulfillment_days.toFixed(1) : '',
            ]),
        ];
        downloadCsv(`orders_by_club_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows]);

    const cols = [
        { key: 'location',             label: 'Location',                 num: false },
        { key: 'club',                 label: 'Club',                     num: false },
        { key: 'total_orders',         label: 'Total Orders',             num: true  },
        { key: 'players_ordered',      label: 'Players Ordered',          num: true  },
        { key: 'printed',              label: 'Printed',                  num: true  },
        { key: 'fulfilled',            label: 'Fulfilled',                num: true  },
        { key: 'cancelled',            label: 'Cancelled',                num: true  },
        { key: 'partial',              label: 'Partial',                  num: true  },
        { key: 'completed_partial',    label: 'Completed Partial',        num: true  },
        { key: 'unfulfilled',          label: 'Unfulfilled',              num: true  },
        { key: 'percent_modified',     label: 'Percent Modified',         num: true  },
        { key: 'avg_fulfillment_days', label: 'Avg Fulfillment Duration (Business Days)', num: true  },
    ];

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">Orders By Club</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo}     setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data?.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !data?.rows?.length}
                    />
                </BlockStack>
            </Card>

            {error && <Banner tone="critical" title="Couldn't load report">{error}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data?.date_from || '—'} → {data?.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            {(data?.rows || []).length} row{(data?.rows || []).length !== 1 ? 's' : ''}
                            {' '}· {(data?.rows || []).reduce((s, r) => s + (r.total_orders || 0), 0).toLocaleString()} total orders
                        </Text>
                    </InlineStack>

                    {loading ? null : (
                        <div className="orders-summary-table-scroll">
                            <table className="orders-summary-table">
                                <thead>
                                    <tr>
                                        {cols.map(c => (
                                            <th key={c.key}
                                                className={`sortable${c.num ? ' num' : ''}`}
                                                onClick={() => onSort(c.key)}>
                                                {c.label}
                                                {sortKey === c.key ? (sortAsc ? ' ▲' : ' ▼') : ''}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.length === 0 && (
                                        <tr><td colSpan={cols.length} style={{ textAlign: 'center', padding: 16, color: '#6d7175' }}>
                                            No orders matched these filters.
                                        </td></tr>
                                    )}
                                    {sortedRows.map((r, i) => (
                                        <tr key={`${r.location}|${r.club}|${i}`}>
                                            <td>{r.location}</td>
                                            <td>{r.club}</td>
                                            <td className="num">{r.total_orders}</td>
                                            <td className="num">{r.players_ordered}</td>
                                            <td className="num">{r.printed}</td>
                                            <td className="num">{r.fulfilled}</td>
                                            <td className="num">{r.cancelled}</td>
                                            <td className="num">{r.partial}</td>
                                            <td className="num">{r.completed_partial}</td>
                                            <td className="num">{r.unfulfilled}</td>
                                            <td className="num">{r.percent_modified != null ? `${r.percent_modified.toFixed(1)}%` : '—'}</td>
                                            <td className="num">{r.avg_fulfillment_days != null ? `${r.avg_fulfillment_days.toFixed(1)} bd` : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- Revenue By Club report ----------------------------------- */

// ── Club Revenue Summary (2026-09-11) ────────────────────────────────────────
// Replaces the deprecated Grafana "Teams Las Vegas Dashboard : Revenue" with
// OpsHub-only data. One row per club; six money columns; no per-product
// breakdown (that's Product Revenue By Club). Column definitions live in the
// note at the top of the report — keep that note and the backend docstring
// (report_club_revenue_summary) in agreement.
const CRS_COLUMNS = [
    { key: 'club',           label: 'Club',                 sortType: 'string', tip: "The Team Portal club on the order, or the club tag on the customer for storefront orders. Orders with no club are left out." },
    { key: 'orders',         label: 'Orders',               sortType: 'number', tip: "Orders placed in the window (Eastern dates) at the selected locations, one per order even when it spans several locations. Cancelled orders are counted." },
    { key: 'total_revenue',  label: 'Total Revenue',        sortType: 'number', tip: "Cash collected minus Promo: net payment (merchandise after discounts, plus shipping and sales tax, less refunds) with gift-card payments taken out. Cancelled orders contribute whatever cash they kept." },
    { key: 'msrp_revenue',   label: 'Total at MSRP',        sortType: 'number', tip: "Team Portal lines only, at quantity x the full Shopify price before any discount, before tax and shipping. Promo purchases (orders the gift card mostly paid for) are left out, so this is the list-price value of what Team Portal customers paid for with their own money. Retail and add-on lines are not included. Cancelled orders excluded unless a Team Portal edit cancelled them. Where the store has a Custom MSRPs list (Surf), only the listed base SKUs count, at the custom MSRP; every other line on the order (embellishments, unlisted products) counts $0." },
    { key: 'tp_revenue',     label: 'Team Portal Revenue With Promo Purchases', sortType: 'number', tip: "What customers were charged on Team Portal lines after every discount, including package discounts, before tax and shipping, on every order in the window whether it was paid by card or by gift card. Cancelled orders excluded unless a Team Portal edit cancelled them." },
    { key: 'tp_revenue_excl_promo', label: 'Team Portal Revenue Excluding Promo Purchases', sortType: 'number', tip: "The same Team Portal revenue with promo purchases removed. A promo purchase is an order where the gift card paid for at least as much as every other payment combined; that order's Team Portal lines are left out entirely. Orders with a small gift-card top-up on a card payment still count." },
    { key: 'tp_revenue_promo', label: 'Team Portal Revenue Purchased With Promo', sortType: 'number', tip: "Team Portal Revenue With Promo Purchases minus Team Portal Revenue Excluding Promo Purchases: the Team Portal lines on promo purchases, i.e. orders the gift card paid for at least as much as every other payment combined." },
    { key: 'retail_revenue', label: 'Retail Revenue',       sortType: 'number', tip: "Price charged on retail lines, before tax and shipping. Cancelled orders excluded." },
    { key: 'addon_revenue',  label: 'Add-On Revenue',       sortType: 'number', tip: "Price charged on add-on (_soccer_collection) lines, before tax and shipping. Cancelled orders excluded." },
    { key: 'promo',          label: 'Promo (Gift Cards)',   sortType: 'number', tip: "Gift-card payments applied to the orders in the window. Already subtracted from Total Revenue; shown so the two can be reconciled. An order is a promo purchase when this covered at least as much as every other payment on it." },
];
const CRS_MONEY_KEYS = ['total_revenue', 'msrp_revenue', 'tp_revenue', 'tp_revenue_excl_promo', 'tp_revenue_promo', 'retail_revenue', 'addon_revenue', 'promo'];
// tp_revenue_promo is derived on the client: tp_revenue − tp_revenue_excl_promo (Eric, 2026-09-29).
const crsDerive = (r) => ({ ...r, tp_revenue_promo: Number(r.tp_revenue || 0) - Number(r.tp_revenue_excl_promo || 0) });

// Custom MSRPs (2026-09-18, Eric): per-store base-SKU → MSRP overrides that
// re-price Total at MSRP on this report. A base SKU ("FZ9368-463") matches
// every Shopify SKU that contains it as a whole dash-bounded piece
// ("NIKE-FZ9368-463-YM"); the longest matching base SKU wins. Seeded for Surf
// from the "Revenue Summary" sheet; anyone who can open the report for the
// store can edit the list. Only offered while the report is open under Surf.
const CUSTOM_MSRP_STORES = ['surf'];

function CustomMsrpModal({ open, onClose, store, fetchAuth }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [newSku, setNewSku] = useState('');
    const [newPrice, setNewPrice] = useState('');
    const [edits, setEdits] = useState({});       // base_sku → price text being edited
    const [busy, setBusy] = useState(null);       // base_sku being saved/deleted
    const [confirmDel, setConfirmDel] = useState(null);
    const [changed, setChanged] = useState(false);
    const [filter, setFilter] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const res = await fetchAuth(`/api/reports/msrp_overrides?store=${encodeURIComponent(store)}`);
            const j = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
            setRows(j.rows || []);
        } catch (e) { setErr(e.message || String(e)); }
        finally { setLoading(false); }
    }, [fetchAuth, store]);
    useEffect(() => { if (open) { load(); setEdits({}); setChanged(false); setNewSku(''); setNewPrice(''); setFilter(''); } }, [open, load]);

    const save = useCallback(async (base_sku, priceText) => {
        const msrp = Number(String(priceText).replace(/[$,\s]/g, ''));
        if (!base_sku.trim()) { setErr('Enter a base SKU such as FZ9368-463.'); return false; }
        if (!Number.isFinite(msrp) || msrp < 0) { setErr('MSRP must be a number.'); return false; }
        setBusy(base_sku); setErr(null);
        try {
            const res = await fetchAuth('/api/reports/msrp_overrides', {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ store, base_sku, msrp }),
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
            setChanged(true);
            await load();
            return true;
        } catch (e) { setErr(e.message || String(e)); return false; }
        finally { setBusy(null); }
    }, [fetchAuth, store, load]);

    const remove = useCallback(async (base_sku) => {
        setBusy(base_sku); setErr(null);
        try {
            const res = await fetchAuth(`/api/reports/msrp_overrides?store=${encodeURIComponent(store)}&base_sku=${encodeURIComponent(base_sku)}`, { method: 'DELETE' });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
            setChanged(true);
            await load();
        } catch (e) { setErr(e.message || String(e)); }
        finally { setBusy(null); setConfirmDel(null); }
    }, [fetchAuth, store, load]);

    const addRow = useCallback(async () => {
        const ok = await save(newSku.trim().toUpperCase(), newPrice);
        if (ok) { setNewSku(''); setNewPrice(''); }
    }, [save, newSku, newPrice]);

    const shown = useMemo(() => {
        const f = filter.trim().toUpperCase();
        return f ? rows.filter(r => r.base_sku.includes(f)) : rows;
    }, [rows, filter]);

    const fmtWhen = (iso) => {
        if (!iso) return '';
        try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; }
    };

    return (
        <Modal open={open} onClose={() => onClose(changed)} title={`Custom MSRPs — ${store === 'surf' ? 'Surf' : store}`}
               primaryAction={{ content: 'Done', onAction: () => onClose(changed) }} size="large">
            <Modal.Section>
                <BlockStack gap="300">
                    <Text as="p" tone="subdued">
                        While this list has entries, Total at MSRP on this report counts only these base SKUs, at the MSRP below; embellishments and products not listed here count $0.
                        A base SKU matches every size of the product: <code>FZ9368-463</code> covers <code>NIKE-FZ9368-463-YM</code>, <code>-YL</code>, <code>-S</code> and so on.
                        Changes apply the next time the report loads.
                    </Text>
                    {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 220px' }}>
                            <TextField label="Base SKU" value={newSku} onChange={setNewSku} autoComplete="off"
                                       placeholder="FZ9368-463" onFocus={() => setErr(null)} />
                        </div>
                        <div style={{ flex: '0 1 160px' }}>
                            <TextField label="MSRP" type="number" value={newPrice} onChange={setNewPrice} autoComplete="off"
                                       prefix="$" placeholder="24.00" step={0.01} min={0} />
                        </div>
                        <Button variant="primary" onClick={addRow} disabled={!newSku.trim() || newPrice === '' || busy !== null}>Add SKU</Button>
                        <div style={{ marginLeft: 'auto', flex: '0 1 220px' }}>
                            <TextField label="Find" labelHidden value={filter} onChange={setFilter} autoComplete="off" placeholder="Filter SKUs…" clearButton onClearButtonClick={() => setFilter('')} />
                        </div>
                    </div>
                    <div style={{ maxHeight: '55vh', overflow: 'auto', border: '1px solid var(--p-color-border, #e1e3e5)', borderRadius: 8, clipPath: 'inset(0 round 8px)' }}>
                        <table className="data-table" style={{ width: '100%' }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                <tr>
                                    <th>Base SKU</th>
                                    <th className="num" style={{ width: 160 }}>MSRP</th>
                                    <th style={{ width: 220 }}>Last edited</th>
                                    <th style={{ width: 90 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && rows.length === 0 ? (
                                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24 }}><Spinner size="small" /></td></tr>
                                ) : shown.length === 0 ? (
                                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: '#6d7175' }}>
                                        {rows.length === 0 ? 'No custom MSRPs yet — add a base SKU above.' : 'No SKUs match that filter.'}
                                    </td></tr>
                                ) : shown.map(r => {
                                    const editing = edits[r.base_sku];
                                    const isEditing = editing !== undefined;
                                    return (
                                        <tr key={r.base_sku}>
                                            <td><code>{r.base_sku}</code></td>
                                            <td className="num">
                                                {isEditing ? (
                                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                                                        <div style={{ width: 110 }}>
                                                            <TextField label="MSRP" labelHidden type="number" value={editing} autoComplete="off" prefix="$" step={0.01} min={0}
                                                                       onChange={v => setEdits(e => ({ ...e, [r.base_sku]: v }))} />
                                                        </div>
                                                        <Button size="slim" variant="primary" loading={busy === r.base_sku}
                                                                onClick={async () => { if (await save(r.base_sku, editing)) setEdits(e => { const n = { ...e }; delete n[r.base_sku]; return n; }); }}>Save</Button>
                                                        <Button size="slim" onClick={() => setEdits(e => { const n = { ...e }; delete n[r.base_sku]; return n; })}>Cancel</Button>
                                                    </div>
                                                ) : (
                                                    <button type="button" className="odv2-linkbtn" style={{ font: 'inherit', background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'inherit', textDecoration: 'underline dotted' }}
                                                            title="Edit MSRP" onClick={() => setEdits(e => ({ ...e, [r.base_sku]: String(r.msrp.toFixed(2)) }))}>
                                                        {`$${Number(r.msrp).toFixed(2)}`}
                                                    </button>
                                                )}
                                            </td>
                                            <td style={{ color: '#6d7175', fontSize: 12 }}>{fmtWhen(r.updated_at)}{r.updated_by ? ` · ${r.updated_by}` : ''}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                {confirmDel === r.base_sku ? (
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                                        <Button size="slim" tone="critical" variant="primary" loading={busy === r.base_sku} onClick={() => remove(r.base_sku)}>Remove</Button>
                                                        <Button size="slim" onClick={() => setConfirmDel(null)}>Keep</Button>
                                                    </div>
                                                ) : (
                                                    <Button size="slim" tone="critical" variant="plain" onClick={() => setConfirmDel(r.base_sku)} disabled={busy !== null}>Delete</Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Text as="p" tone="subdued" variant="bodySm">{rows.length} base SKU{rows.length === 1 ? '' : 's'} with a custom MSRP.</Text>
                </BlockStack>
            </Modal.Section>
        </Modal>
    );
}

function ClubRevenueSummaryReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const { activeStore: _crsStore } = useStore();
    const showCustomMsrp = CUSTOM_MSRP_STORES.includes(_crsStore);
    const [msrpOpen, setMsrpOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter,     setClubFilter]     = useState(searchParams.getAll('club'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], available_clubs: [], date_from: '', date_to: '', backfill_floor: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('total_revenue');
    const [sortDir, setSortDir] = useState('desc');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, setSearchParams]);

    // 2026-09-11 (Eric): keep the heading, the note, the filters, the window
    // line and the column headers on screen while the rows scroll. The table
    // box is sized to the rest of the viewport and scrolls internally, so
    // nothing above it moves; the thead is sticky inside that box.
    const tableWrapRef = useRef(null);
    const [tableMaxH, setTableMaxH] = useState(null);
    // 2026-09-25 (Eric: "this report is not full page"): the pane sat at its
    // 240px floor with the rest of the screen empty. The one measurement ran
    // from a plain useEffect at a moment the layout had not settled, and
    // nothing re-ran it — a window resize snapped it to 984px. Now a layout
    // effect (as Zero Sales and TPFR do, and they are right) plus a
    // ResizeObserver on the report column, so any later shift re-measures.
    useLayoutEffect(() => {
        const measure = () => {
            const el = tableWrapRef.current;
            if (!el) return;
            // 2026-09-21 (Eric): the page itself must not scroll — the pane
            // is the only thing that does. The app scrolls inside
            // `.page-content`, not the document, so measure against that.
            const h = paneHeightToFitScroller(el) - 6;   // 2026-09-26 (Eric): 6px in hand so the card's bottom corners never slip under the page edge
            setTableMaxH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h);
        };
        measure();
        const raf = requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        const col = tableWrapRef.current && tableWrapRef.current.closest('.reporting-content');
        const ro = col ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(col);
        return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
    }, [data.rows, loading, err, tableMaxH]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            clubFilter.forEach(c => p.append('clubs', c));
            const res = await fetchAuth(`/api/reports/club_revenue_summary?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter]);
    useEffect(() => { load(); }, [load]);

    const sortedRows = useMemo(() => {
        const col = CRS_COLUMNS.find(c => c.key === sortKey) || CRS_COLUMNS[2];
        const rows = (data.rows || []).map(crsDerive);
        rows.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const cmp = col.sortType === 'string'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = CRS_COLUMNS.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const totals = useMemo(() => (data.rows || []).map(crsDerive).reduce((acc, r) => {
        acc.orders   += Number(r.orders   || 0);
        acc.uncached += Number(r.uncached || 0);
        for (const k of CRS_MONEY_KEYS) acc[k] += Number(r[k] || 0);
        return acc;
    }, { orders: 0, uncached: 0, total_revenue: 0, msrp_revenue: 0, tp_revenue: 0, tp_revenue_excl_promo: 0, tp_revenue_promo: 0, retail_revenue: 0, addon_revenue: 0, promo: 0 }), [data.rows]);

    const handleExport = useCallback(() => {
        const rows = [['Club', 'Orders', 'Total Revenue (USD)', 'Total at MSRP, Team Portal lines excluding promo purchases (USD)',
                       'Team Portal Revenue With Promo Purchases (USD)', 'Team Portal Revenue Excluding Promo Purchases (USD)', 'Team Portal Revenue Purchased With Promo (USD)',
                       'Retail Revenue (USD)', 'Add-On Revenue (USD)', 'Promo / Gift Cards (USD)', 'Orders not yet computed']];
        for (const r of sortedRows) rows.push([
            r.club, Number(r.orders || 0),
            ...CRS_MONEY_KEYS.map(k => Number(r[k] || 0).toFixed(2)),
            Number(r.uncached || 0),
        ]);
        downloadCsv(`revenue_by_club_including_promo_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows]);

    const preFloor = data.backfill_floor && dateFrom && dateFrom < data.backfill_floor;

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            {/* 2026-09-24 (Eric): header flush with the page, like Partial Analysis
                By Club — no box around the title and filters. Note the first Card in
                a report is the pinned sticky header (styles.css), so unwrapping this
                hands that role to the results card below, which is what the other
                report does too. */}
            <BlockStack gap="400">
                    <ReportColumnHelp
                        title="Revenue By Club (Including Promo)"
                        storageKey="crs_col_help_open"
                        items={[
                            ['Total Revenue', <>cash the club's customers actually paid us: merchandise after discounts, <b>plus shipping and sales tax</b>, net of refunds (Shopify net payment — the same number as Revenue By Club), <b>minus gift-card payments</b>. Shopify counts a gift card as cash; it is money off our bottom line, so it is taken out here.</>],
                            ['Total at MSRP', <>what the Team Portal items would have sold for at list price: quantity × the Shopify variant price when the order was placed, on orders that were not promo purchases. On Surf, only the base SKUs listed under <b>Custom MSRPs</b> count, at that price; embellishments and unlisted products count $0.</>],
                            ['Team Portal Revenue', <>what the customer was charged on Team Portal lines after every discount, including package discounts (before tax and shipping). Embellishments count whatever they were charged, normally $0. Add-ons are not included here.</>],
                            ['Retail Revenue and Add-On Revenue', <>quantity × the price charged for retail lines and for add-on (<code>_soccer_collection</code>) lines — the same buckets as Add-On &amp; Retail Items By Club.</>],
                            ['Promo (Gift Cards)', <>net gift-card payments on the club's orders (sales minus gift-card refunds). Shopify store credit is not included; see Discounts By Club.</>],
                        ]}
                        footer={<>The four merchandise columns are before tax and shipping and skip cancelled orders, so they will not add up to Total Revenue. Orders are attributed to a club by the Team Portal club on the order and to a location by its fulfillment location.</>}
                    />
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                        extraActions={showCustomMsrp ? (
                            <Tooltip content="Base SKUs priced at a custom MSRP in the Total at MSRP column (Surf only)">
                                <Button onClick={() => setMsrpOpen(true)}>
                                    {`Custom MSRPs${(data.msrp_overrides || {})[_crsStore] ? ` (${(data.msrp_overrides || {})[_crsStore]})` : ''}`}
                                </Button>
                            </Tooltip>
                        ) : null}
                    />
            </BlockStack>
            {showCustomMsrp && (
                <CustomMsrpModal open={msrpOpen} store={_crsStore} fetchAuth={fetchAuth}
                                 onClose={(changed) => { setMsrpOpen(false); if (changed) load(); }} />
            )}
            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}
            {!err && preFloor && (
                <Banner tone="warning">Merchandise columns (MSRP, Team Portal, Retail, Add-On) and Promo are only computed for orders placed on or after {data.backfill_floor}. Total Revenue is complete for the whole window.</Banner>
            )}
            {!err && !loading && totals.uncached > 0 && (
                <Banner tone="warning">{totals.uncached.toLocaleString()} of {totals.orders.toLocaleString()} orders in this window have not had their merchandise columns computed yet — those show as $0 in MSRP / Team Portal / Promo until the backfill reaches them.</Banner>
            )}
            <Card>
                <BlockStack gap="300">
                    <Text variant="bodySm" tone="subdued">
                        Window: {data.date_from || '—'} → {data.date_to || '—'} ·
                        {' '}{_fmtUSD(totals.total_revenue)} total revenue · {totals.orders.toLocaleString()} orders · {(data.rows || []).length} clubs
                    </Text>
                    {loading ? null : (data.rows || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6d7175' }}>
                            No club revenue in this window.
                        </div>
                    ) : (
                        <div ref={tableWrapRef}
                             style={{ overflow: 'auto', WebkitOverflowScrolling: 'touch',
                                      maxHeight: tableMaxH || undefined,
                                      border: '1px solid var(--p-color-border, #e1e3e5)',
                                      borderRadius: 8, clipPath: 'inset(0 round 8px)', background: '#fff', marginTop: 12 }}>
                            <table className="data-table" style={{ width: '100%', minWidth: 1100 }}>
                                <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff',
                                                boxShadow: '0 1px 0 var(--p-color-border, #e1e3e5)' }}>
                                    <tr>
                                        {CRS_COLUMNS.map((c, ci) => (
                                            <th key={c.key}
                                                className={c.sortType === 'number' ? 'col-right' : undefined}
                                                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#fff',
                                                         // the sticky header paints over the box's rounded corners — round the end cells to match
                                                         borderTopLeftRadius: ci === 0 ? 8 : undefined,
                                                         borderTopRightRadius: ci === CRS_COLUMNS.length - 1 ? 8 : undefined }}
                                                onClick={() => handleSort(c.key)}>
                                                {/* 2026-09-14 (Eric): every column explains itself on hover. */}
                                                {c.tip ? (
                                                    <Tooltip content={c.tip} preferredPosition="above" width="wide">
                                                        <span style={{ borderBottom: '1px dotted currentColor' }}>{c.label}</span>
                                                    </Tooltip>
                                                ) : c.label}{arrow(c.key)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map((r, i) => (
                                        <tr key={`${r.club}|${i}`}>
                                            <td className="td-cell" style={{ whiteSpace: 'nowrap' }}>
                                                {r.club}
                                                {r.uncached > 0 && (
                                                    <span title={`${r.uncached} of ${r.orders} orders not computed yet`}
                                                          style={{ marginLeft: 6, fontSize: 11, color: '#b45309' }}>
                                                        ({r.uncached} not computed)
                                                    </span>
                                                )}
                                            </td>
                                            <td className="td-cell col-right">{(r.orders || 0).toLocaleString()}</td>
                                            {CRS_MONEY_KEYS.map(k => (
                                                <td key={k} className="td-cell col-right">{_fmtUSD(r[k])}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fff',
                                                boxShadow: '0 -2px 0 var(--p-color-border, #e1e3e5)' }}>
                                    <tr style={{ fontWeight: 600 }}>
                                        <td className="td-cell">Total</td>
                                        <td className="td-cell col-right">{totals.orders.toLocaleString()}</td>
                                        {CRS_MONEY_KEYS.map(k => (
                                            <td key={k} className="td-cell col-right">{_fmtUSD(totals[k])}</td>
                                        ))}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

function RevenueByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const { storeSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter,     setClubFilter]     = useState(searchParams.getAll('club'));
    const [crmFilter,      setCrmFilter]      = useState(searchParams.getAll('crm'));
    // 2026-10-29 (Eric): with one club selected, report by order or by team.
    const [groupBy, setGroupBy] = useState(searchParams.get('view') === 'team' ? 'team' : 'order');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], available_clubs: [], available_crms: [], orders: [],
                                       date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('revenue');
    const [sortDir, setSortDir] = useState('desc');
    const [crmOpen, setCrmOpen] = useState(false);
    const [crmSearch, setCrmSearch] = useState('');

    const isSingleClub = clubFilter.length === 1;

    // 2026-09-13 (Eric): column order. Unfulfilled was dropped from the table
    // (still in the API payload and inside Modified). `fmt` renders a cell;
    // `total` says how the totals row is built (sum / weighted / none).
    const _n = (v) => (Number(v) || 0).toLocaleString();
    const RBC_COLUMNS = [
        { key: 'club',                label: 'Club',                 sortType: 'string', tip: "The Team Portal club on the order, or the club tag on the customer for storefront orders." },
        { key: 'crm',                 label: 'CRM',                  sortType: 'string', tip: "CRM owner(s) assigned to the club on Team Portal." },
        { key: 'orders',              label: 'Orders',               sortType: 'number', fmt: _n, total: 'sum', tip: "Orders placed in the window at the selected locations. One per order, even when split across locations." },
        { key: 'players',             label: 'Players',              sortType: 'number', fmt: _n, total: 'sum', totalTitle: "Sum of each club's distinct players", tip: "Distinct Team Portal player names across the club's orders in the window." },
        { key: 'fulfilled',           label: 'Fulfilled',            sortType: 'number', fmt: _n, total: 'sum', tip: "Orders fully shipped or picked up in a single wave. Cancelled orders are not counted." },
        { key: 'unfulfilled',         label: 'Unfulfilled',          sortType: 'number', fmt: _n, total: 'sum', tip: "Orders with nothing fulfilled yet. Cancelled orders are not counted." },
        { key: 'cancelled',           label: 'Cancelled',            sortType: 'number', fmt: _n, total: 'sum', tip: "Orders cancelled in Shopify, including the original order of a Team Portal edit." },
        { key: 'partially_fulfilled', label: 'Partial',              sortType: 'number', fmt: _n, total: 'sum', tip: "Orders with some, but not all, of their fulfillments completed." },
        { key: 'completed_partial',   label: 'Completed Partial',    sortType: 'number', fmt: _n, total: 'sum', tip: "Fulfilled orders that shipped in more than one wave on different days. Not counted again under Fulfilled." },
        { key: 'pct_modified',        label: 'Modified',             sortType: 'number', fmt: v => `${(Number(v) || 0).toFixed(1)}%`, total: 'pct_modified', tip: "Every order except Unfulfilled, as a share of Orders (the Orders report's Modified count)." },
        { key: 'revenue',             label: 'Revenue',              sortType: 'number', fmt: _fmtUSD, total: 'sum', tip: "Cash collected: merchandise after discounts plus shipping and sales tax, net of refunds (Shopify net payment). Includes gift-card payments and money kept on cancelled orders." },
        // 2026-09-12 (Eric): the Grafana revenue-dashboard columns; same
        // definitions as Revenue By Club (Including Promo).
        { key: 'tp_revenue',          label: 'Team Portal Revenue',  sortType: 'number', fmt: _fmtUSD, total: 'sum', tip: "What the customer was charged on Team Portal lines after every discount, including package discounts, before tax and shipping. Cancelled orders excluded unless a Team Portal edit cancelled them." },
        { key: 'retail_revenue',      label: 'Retail',               sortType: 'number', fmt: _fmtUSD, total: 'sum', tip: "Price charged on retail lines, before tax and shipping. Cancelled orders excluded." },
        { key: 'addon_revenue',       label: 'Add-On',               sortType: 'number', fmt: _fmtUSD, total: 'sum', tip: "Price charged on add-on (_soccer_collection) lines, before tax and shipping. Cancelled orders excluded." },
        { key: 'avg_order_value',     label: 'Avg Order',            sortType: 'number', fmt: _fmtUSD, total: 'avg', tip: "Revenue divided by Orders." },
    ];
    const RBC_MONEY_KEYS = ['revenue', 'tp_revenue', 'retail_revenue', 'addon_revenue'];
    // 2026-09-23 (Eric): the filter card above the table is `position: sticky`,
    // so a table pane sized to the whole viewport slides underneath it and takes
    // its own sticky header with it. Size the pane against that card's bottom
    // edge instead: the page still scrolls the chart away, and the pane then
    // parks just below the card with its header on screen. Re-measured when the
    // card changes height (the definitions banner opening, filters wrapping).
    const rbcHeadRef = useRef(null);
    // 2026-09-26 (Eric): the chart folds away (open by default, remembered per
    // browser) and the table pane is sized to the window like Revenue By Club
    // (Including Promo): with the chart folded the Total row sits at the bottom
    // of the screen and the page never scrolls; with it open the pane keeps a
    // 240px floor and the page scrolls the chart away as before.
    const [rbcChartOpen, setRbcChartOpen] = useState(() => { try { return localStorage.getItem('rbc_chart_open') !== '0'; } catch (_e) { return true; } });   // open by default (Eric)
    const toggleRbcChart = () => setRbcChartOpen(v => { try { localStorage.setItem('rbc_chart_open', v ? '0' : '1'); } catch (_e) { /* private window */ } return !v; });
    const rbcPaneRef = useRef(null);
    const [rbcPaneH, setRbcPaneH] = useState(null);
    useLayoutEffect(() => {
        const measure = () => {
            const el = rbcPaneRef.current;
            if (!el) return;
            const h = paneHeightToFitScroller(el) - 6;
            setRbcPaneH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h);
        };
        measure();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (ro) {
            const card = rbcHeadRef.current && rbcHeadRef.current.closest('.Polaris-ShadowBevel');
            if (card) ro.observe(card);
            const pane = rbcPaneRef.current;
            if (pane && pane.parentElement && pane.parentElement.parentElement) ro.observe(pane.parentElement.parentElement);
            ro.observe(document.body);
        }
        window.addEventListener('resize', measure);
        return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
    }, [data.rows, loading, rbcChartOpen]);
    const sortedClubRows = useMemo(() => {
        const col = RBC_COLUMNS.find(c => c.key === sortKey) || RBC_COLUMNS.find(c => c.key === 'revenue');
        const rows = (data.rows || []).slice();
        rows.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const cmp = col.sortType === 'string'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = RBC_COLUMNS.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        for (const c of crmFilter)      p.append('crm',  c);
        if (clubFilter.length === 1 && groupBy === 'team') p.set('view', 'team');
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, crmFilter, groupBy, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            clubFilter.forEach(c => p.append('clubs', c));
            crmFilter.forEach(c => p.append('crms', c));
            const res = await fetchAuth(`/api/reports/revenue_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter, crmFilter]);
    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => (data.rows || []).reduce((acc, r) => {
        acc.revenue += Number(r.revenue || 0);
        acc.orders  += Number(r.orders  || 0);
        return acc;
    }, { revenue: 0, orders: 0 }), [data.rows]);

    // Per-order totals (single-club mode). Falls back to aggregate totals when
    // the backend didn't return an `orders` array (multi-club view).
    const orderTotals = useMemo(() => (data.orders || []).reduce((acc, o) => {
        acc.revenue += Number(o.revenue || 0);
        acc.items   += Number(o.items_count || 0);
        acc.tp      += Number(o.tp_revenue || 0);
        acc.retail  += Number(o.retail_revenue || 0);
        acc.addon   += Number(o.addon_revenue || 0);
        return acc;
    }, { revenue: 0, items: 0, tp: 0, retail: 0, addon: 0 }), [data.orders]);

    // 2026-10-29 (Eric): By team. Teams are known per order, not per line, so an
    // order with players on several teams is split evenly across them (Eric's
    // rule) — team rows add up to the club total. Orders counts every order with
    // a player on the team, so a shared order counts once on each of its teams.
    const teamRows = useMemo(() => {
        const NO_TEAM = '(No team)';
        const by = new Map();
        for (const o of (data.orders || [])) {
            const teams = (o.teams && o.teams.length) ? o.teams : [NO_TEAM];
            const share = 1 / teams.length;
            for (const t of teams) {
                const r = by.get(t) || { team: t, orders: 0, shared: 0, items: 0, revenue: 0, tp: 0, retail: 0, addon: 0 };
                r.orders  += 1;
                if (teams.length > 1) r.shared += 1;
                r.items   += Number(o.items_count || 0) * share;
                r.revenue += Number(o.revenue || 0) * share;
                r.tp      += Number(o.tp_revenue || 0) * share;
                r.retail  += Number(o.retail_revenue || 0) * share;
                r.addon   += Number(o.addon_revenue || 0) * share;
                by.set(t, r);
            }
        }
        return [...by.values()].sort((a, b) =>
            (a.team === NO_TEAM) - (b.team === NO_TEAM) || b.revenue - a.revenue);
    }, [data.orders]);
    const sharedOrders = useMemo(() => (data.orders || []).filter(o => (o.teams || []).length > 1).length, [data.orders]);
    const _items1 = (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

    // Show top 30 clubs in the chart; full list goes in the table below.
    const echartOption = useMemo(() => {
        const sorted = (data.rows || []).slice().sort((a, b) => b.revenue - a.revenue).slice(0, 30);
        const labels = sorted.map(r => r.club);
        const values = sorted.map(r => Number(r.revenue || 0));
        return {
            grid: { left: 60, right: 30, top: 30, bottom: 180, containLabel: false },
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
                       valueFormatter: v => _fmtUSD(v) },
            xAxis: {
                type: 'category', data: labels,
                axisLabel: { rotate: 45, interval: 0, fontSize: 11,
                             align: 'right', verticalAlign: 'top' },
                axisTick: { alignWithLabel: true },
            },
            yAxis: { type: 'value', axisLabel: { formatter: v => _fmtUSD(v) } },
            series: [{ name: 'Revenue', type: 'bar', data: values, itemStyle: { color: '#10b981' } }],
        };
    }, [data.rows]);

    const handleExport = useCallback(() => {
        // Single-club mode: per-order CSV (Order # / Date / Items / Revenue).
        if (isSingleClub && groupBy === 'team' && teamRows.length > 0) {
            const rows = [['Team', 'Orders', 'Orders shared with another team', 'Items', 'Revenue (USD)',
                           'Team Portal Revenue (USD)', 'Retail (USD)', 'Add-On (USD)']];
            for (const r of teamRows) rows.push([
                r.team, r.orders, r.shared, r.items.toFixed(1), r.revenue.toFixed(2),
                r.tp.toFixed(2), r.retail.toFixed(2), r.addon.toFixed(2),
            ]);
            const club = (clubFilter[0] || 'club').replace(/[^\w-]+/g, '_');
            downloadCsv(`revenue_by_team_${club}_${data.date_from}_to_${data.date_to}.csv`, rows);
            return;
        }
        if (isSingleClub && Array.isArray(data.orders) && data.orders.length > 0) {
            const rows = [['Order #', 'Date', 'Store', 'Items', 'Revenue (USD)',
                           'Team Portal Revenue (USD)', 'Retail (USD)', 'Add-On (USD)']];
            for (const o of data.orders) rows.push([
                o.order_name || '', o.placed_d || '', o.store_key || '',
                Number(o.items_count || 0), Number(o.revenue || 0).toFixed(2),
                Number(o.tp_revenue || 0).toFixed(2), Number(o.retail_revenue || 0).toFixed(2),
                Number(o.addon_revenue || 0).toFixed(2),
            ]);
            const club = (clubFilter[0] || 'club').replace(/[^\w-]+/g, '_');
            downloadCsv(`revenue_by_club_${club}_${data.date_from}_to_${data.date_to}.csv`, rows);
            return;
        }
        // Multi-club mode: aggregate CSV.
        const rows = [['Club', 'CRM', 'Orders', 'Distinct Players', 'Fulfilled', 'Unfulfilled', 'Cancelled', 'Partial',
                       'Completed Partial', 'Modified %', 'Revenue (USD)', 'Team Portal Revenue (USD)',
                       'Retail (USD)', 'Add-On (USD)', 'Avg order value (USD)']];
        for (const r of sortedClubRows) rows.push([
            r.club,
            r.crm || '',
            r.orders,
            Number(r.players || 0),
            Number(r.fulfilled || 0),
            Number(r.unfulfilled || 0),
            Number(r.cancelled || 0),
            Number(r.partially_fulfilled || 0),
            Number(r.completed_partial || 0),
            Number(r.pct_modified || 0).toFixed(1),
            Number(r.revenue || 0).toFixed(2),
            Number(r.tp_revenue || 0).toFixed(2),
            Number(r.retail_revenue || 0).toFixed(2),
            Number(r.addon_revenue || 0).toFixed(2),
            Number(r.avg_order_value || 0).toFixed(2),
        ]);
        downloadCsv(`reporting_revenue_by_club_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, isSingleClub, clubFilter, sortedClubRows, groupBy, teamRows]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <div ref={rbcHeadRef}>
                    {/* 2026-09-23 (Eric): the definitions live behind the link, like
                        Team Portal Franchise Reconciliation. Column wording is the same
                        text the header tooltips show, built from RBC_COLUMNS so the two
                        can never drift. */}
                    <ReportColumnHelp
                        title="Revenue By Club"
                        storageKey="rbc_col_help_open"
                        items={RBC_COLUMNS.filter(c => c.tip).map(c => [c.label, c.tip])}
                        footer={(
                            <>
                                <b>Revenue is cash</b>, so it reads higher than Team Portal + Retail + Add-On:
                                it also carries sales tax and shipping charges, and it is net of refunds, which
                                the merchandise columns are not. Items removed by later order edits stay in the
                                total when the money was kept, which is what makes this the number to reconcile
                                against payments. The three merchandise columns are computed for orders placed on
                                or after 2025-01-01. Two rules apply to every revenue column: an order tagged
                                <b> Broken Order</b> counts what Shopify actually charged for its Team Portal
                                lines rather than the Team Portal price, and an order cancelled by a
                                <b> Team Portal order edit</b> keeps its revenue, while its replacement counts
                                only what it was itself charged.
                                {/* 2026-10-29 (Eric): explain the single-club By order / By team toggle. */}
                                <br /><br />
                                <b>One club selected: By order or By team.</b> <b>By order</b> lists every order
                                the club placed in the window, one row each. <b>By team</b> groups those same orders
                                by the Team Portal team on the order. Teams are recorded per order, not per item, so
                                an order with players on more than one team (siblings, for example) has its revenue,
                                merchandise columns and items split evenly across those teams: a two-team order puts
                                half on each. The team rows therefore add up to the club total. In the Orders column
                                a shared order counts on every team it touches and is marked "shared", so that column
                                adds up to more than the club's order count. Orders with no team are grouped under
                                (No team).
                            </>
                        )}
                    />
                    </div>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || (isSingleClub
                            ? !(data.orders || []).length
                            : !(data.rows || []).length)}
                        trailing={(() => {
                            const availableCrms = data.available_crms || [];
                            const filtered = crmSearch.trim()
                                ? availableCrms.filter(c => (c || '').toLowerCase().includes(crmSearch.trim().toLowerCase()))
                                : availableCrms;
                            return (
                                <Popover
                                    active={crmOpen}
                                    activator={
                                        <Button onClick={() => setCrmOpen(v => !v)}
                                                disclosure={crmOpen ? 'up' : 'down'}
                                                pressed={crmFilter.length > 0}>
                                            CRM{crmFilter.length ? ` (${crmFilter.length})` : ''}
                                        </Button>
                                    }
                                    onClose={() => setCrmOpen(false)}
                                    preferredAlignment="left"
                                >
                                    <div style={{ minWidth: 280, maxHeight: 420, padding: 10,
                                                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <TextField labelHidden label="Search CRMs" type="search"
                                            value={crmSearch} onChange={setCrmSearch}
                                            placeholder="Search CRMs…" autoComplete="off"
                                            clearButton onClearButtonClick={() => setCrmSearch('')} />
                                        <button type="button" className="filter-pill-clear"
                                                disabled={crmFilter.length === 0}
                                                onClick={() => setCrmFilter([])}>Clear</button>
                                        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                            {filtered.length === 0 ? (
                                                <div style={{ color: 'var(--p-color-text-secondary)',
                                                              fontSize: 13, padding: '8px 4px' }}>
                                                    {availableCrms.length === 0
                                                      ? 'No CRMs in window.'
                                                      : `No matches for "${crmSearch}"`}
                                                </div>
                                            ) : (
                                                <ChoiceList titleHidden title="CRMs" allowMultiple
                                                    choices={filtered.map(c => ({
                                                        label: c === '(none)' ? '(no CRM assigned)' : c,
                                                        value: c,
                                                    }))}
                                                    selected={crmFilter}
                                                    onChange={setCrmFilter} />
                                            )}
                                        </div>
                                    </div>
                                </Popover>
                            );
                        })()}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {isSingleClub ? (
                            <Text variant="bodySm" tone="subdued">
                                Club: <strong>{clubFilter[0]}</strong>
                                {' '}· {(data.orders || []).length.toLocaleString()} orders
                                {' '}· {_fmtUSD(orderTotals.revenue)} revenue
                                {groupBy === 'team' && <>{' '}· {teamRows.length.toLocaleString()} teams</>}
                            </Text>
                        ) : (
                            <Text variant="bodySm" tone="subdued">
                                Totals: {_fmtUSD(totals.revenue)} · {totals.orders.toLocaleString()} orders ·
                                {' '}{(data.rows || []).length} clubs
                            </Text>
                        )}
                        {!isSingleClub && (data.rows || []).length > 0 && (
                            <Button variant="plain" size="slim" onClick={toggleRbcChart}>{rbcChartOpen ? 'Hide chart' : 'Show chart'}</Button>
                        )}
                        {isSingleClub && (
                            <ButtonGroup segmented>
                                <Button size="slim" pressed={groupBy === 'order'} onClick={() => setGroupBy('order')}>By order</Button>
                                <Button size="slim" pressed={groupBy === 'team'}  onClick={() => setGroupBy('team')}>By team</Button>
                            </ButtonGroup>
                        )}
                    </InlineStack>

                    {loading ? null : isSingleClub ? (
                        /* Single-club view: per-order table with totals row. */
                        (data.orders || []).length === 0 ? (
                            <div style={{ height: 200, display: 'flex',
                                          alignItems: 'center', justifyContent: 'center',
                                          color: '#6d7175' }}>
                                No orders for {clubFilter[0]} in this window.
                            </div>
                        ) : groupBy === 'team' ? (
                            /* 2026-10-29 (Eric): By team — one row per team, multi-team orders split evenly. */
                            <BlockStack gap="200">
                                {sharedOrders > 0 && (
                                    <Text variant="bodySm" tone="subdued">
                                        {sharedOrders.toLocaleString()} {sharedOrders === 1 ? 'order has' : 'orders have'} players
                                        on more than one team. Their revenue and items are split evenly across those
                                        teams, so the team rows add up to the club total; each one still counts in
                                        Orders on every team it touches.
                                    </Text>
                                )}
                                <table className="data-table" style={{ marginTop: 4 }}>
                                    <thead>
                                        <tr>
                                            {[
                                                ['Team', 'The Team Portal team on the order. Orders with no team are grouped under (No team).', false],
                                                ['Orders', 'Orders with a player on this team. An order shared with another team counts on both.', true],
                                                ['Items', 'Units at their current quantity, split evenly when the order is shared between teams', true],
                                                ['Revenue', 'Cash collected: merchandise after discounts plus shipping and sales tax, net of refunds. Split evenly when the order is shared between teams.', true],
                                                ['Team Portal', 'What the customer was charged on Team Portal lines after every discount, before tax and shipping. $0 on a cancelled order. Split evenly when shared.', true],
                                                ['Retail', 'Price charged on retail lines, before tax and shipping. $0 on a cancelled order. Split evenly when shared.', true],
                                                ['Add-On', 'Price charged on add-on (_soccer_collection) lines, before tax and shipping. $0 on a cancelled order. Split evenly when shared.', true],
                                            ].map(([label, tip, right]) => (
                                                <th key={label} className={right ? 'col-right' : undefined}>
                                                    <Tooltip content={tip} preferredPosition="above" width="wide">
                                                        <span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span>
                                                    </Tooltip>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {teamRows.map(r => (
                                            <tr key={r.team}>
                                                <td className="td-cell">{r.team}</td>
                                                <td className="td-cell col-right">
                                                    {r.orders.toLocaleString()}
                                                    {r.shared > 0 && (
                                                        <span style={{ color: 'var(--p-color-text-secondary)', fontSize: 12 }}>
                                                            {' '}({r.shared} shared)
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="td-cell col-right">{_items1(r.items)}</td>
                                                <td className="td-cell col-right">{_fmtUSD(r.revenue)}</td>
                                                <td className="td-cell col-right">{_fmtUSD(r.tp)}</td>
                                                <td className="td-cell col-right">{_fmtUSD(r.retail)}</td>
                                                <td className="td-cell col-right">{_fmtUSD(r.addon)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ fontWeight: 600, borderTop: '2px solid var(--p-color-border)' }}>
                                            <td className="td-cell">Total</td>
                                            <td className="td-cell col-right">{(data.orders || []).length.toLocaleString()}</td>
                                            <td className="td-cell col-right">{orderTotals.items.toLocaleString()}</td>
                                            <td className="td-cell col-right">{_fmtUSD(orderTotals.revenue)}</td>
                                            <td className="td-cell col-right">{_fmtUSD(orderTotals.tp)}</td>
                                            <td className="td-cell col-right">{_fmtUSD(orderTotals.retail)}</td>
                                            <td className="td-cell col-right">{_fmtUSD(orderTotals.addon)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </BlockStack>
                        ) : (
                            <table className="data-table" style={{ marginTop: 12 }}>
                                <thead>
                                    <tr>
                                        {/* 2026-09-26 (Eric): the single-club view's headers explain themselves too. */}
                                        {[
                                            ['Order #', 'The Shopify order; opens the order page', false],
                                            ['Date', 'The day the order was placed (Eastern time)', false],
                                            ['Items', 'Units on the order at its current quantity — refunded and removed lines drop out', true],
                                            ['Revenue', 'Cash collected on the order: merchandise after discounts plus shipping and sales tax, net of refunds', true],
                                            // 2026-09-27 (Eric): the merchandise split per order, same definitions as the club table.
                                            ['Team Portal', 'What the customer was charged on Team Portal lines after every discount, including package discounts, before tax and shipping. $0 on a cancelled order.', true],
                                            ['Retail', 'Price charged on retail lines, before tax and shipping. $0 on a cancelled order.', true],
                                            ['Add-On', 'Price charged on add-on (_soccer_collection) lines, before tax and shipping. $0 on a cancelled order.', true],
                                        ].map(([label, tip, right]) => (
                                            <th key={label} className={right ? 'col-right' : undefined}>
                                                <Tooltip content={tip} preferredPosition="above" width="wide">
                                                    <span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span>
                                                </Tooltip>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.orders.map(o => (
                                        <tr key={`${o.store_key}|${o.order_id}`}>
                                            <td className="td-cell">
                                                <Link to={`/store/${o.store_key || storeSlug || 'soccerpost'}/orders/${o.order_id}`}>
                                                    {o.order_name || `#${o.order_id}`}
                                                </Link>
                                            </td>
                                            <td className="td-cell">{o.placed_d || ''}</td>
                                            <td className="td-cell col-right">{(o.items_count || 0).toLocaleString()}</td>
                                            <td className="td-cell col-right">{_fmtUSD(o.revenue)}</td>
                                            <td className="td-cell col-right">{_fmtUSD(o.tp_revenue)}</td>
                                            <td className="td-cell col-right">{_fmtUSD(o.retail_revenue)}</td>
                                            <td className="td-cell col-right">{_fmtUSD(o.addon_revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ fontWeight: 600, borderTop: '2px solid var(--p-color-border)' }}>
                                        <td className="td-cell" colSpan={2}>Total</td>
                                        <td className="td-cell col-right">{orderTotals.items.toLocaleString()}</td>
                                        <td className="td-cell col-right">{_fmtUSD(orderTotals.revenue)}</td>
                                        <td className="td-cell col-right">{_fmtUSD(orderTotals.tp)}</td>
                                        <td className="td-cell col-right">{_fmtUSD(orderTotals.retail)}</td>
                                        <td className="td-cell col-right">{_fmtUSD(orderTotals.addon)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        )
                    ) : (data.rows || []).length === 0 ? (
                        <div style={{ height: 520, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No club revenue in this window.
                        </div>
                    ) : (
                        <>
                            {rbcChartOpen && (
                                <div style={{ height: 520 }}>
                                    <ReactECharts
                                        theme="polaris"
                                        option={echartOption}
                                        style={{ height: '100%', width: '100%' }}
                                        opts={{ renderer: 'svg' }}
                                    />
                                </div>
                            )}
                            {/* Wrap in an overflow-x container so the wide
                                11-column table can scroll horizontally
                                inside its grid track instead of pushing
                                the Revenue / Avg-order columns off-screen —
                                same pattern as `.orders-summary-table-scroll`.
                                Bug repro: date range with many clubs +
                                long CRM strings widens the table past the
                                viewport (1590px vs ~1104px grid track),
                                and the Revenue column falls off the right. */}
                            {/* 2026-09-23 (Eric): scroll the chart away, keep the column
                                headers and the totals row on screen. The pane is about a
                                viewport tall, so the page still scrolls (chart first) and
                                the table then fills the screen, with thead stuck to the
                                pane's top and tfoot to its bottom — the same sticky
                                mechanics as Revenue By Club (Including Promo). Sticky goes
                                on <thead>/<tfoot>, never on the cells. */}
                            {/* 2026-09-23 (Eric): the box keeps its curved corners while the
                                header and totals row are stuck to its edges. A sticky row paints
                                in its own layer, which the scroll pane's own border-radius does
                                not clip (and the table scrolls sideways, so rounding the first
                                and last cells only helps at the ends) — so the rounding and the
                                border live on this outer wrapper, which clips everything inside. */}
                            <div style={{ border: '1px solid var(--p-color-border, #e1e3e5)',
                                          borderRadius: 8, clipPath: 'inset(0 round 8px)', overflow: 'hidden',
                                          // A sticky row paints in its own compositing layer, which
                                          // Chrome does not clip with an ancestor's border-radius —
                                          // clip-path does clip it, so the corners stay curved under
                                          // the header and the totals row at any scroll position.
                                          clipPath: 'inset(0 round 8px)',
                                          background: '#fff', marginTop: 12 }}>
                            <div ref={rbcPaneRef} style={{ overflow: 'auto',
                                          WebkitOverflowScrolling: 'touch',
                                          maxHeight: rbcPaneH ? `${rbcPaneH}px` : 'calc(100vh - 284px)',
                                          background: '#fff' }}>
                            <table className="data-table rbc-table" style={{ width: '100%', minWidth: 1180 }}>
                                <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff',
                                                boxShadow: '0 1px 0 var(--p-color-border, #e1e3e5)' }}>
                                    <tr>
                                        {RBC_COLUMNS.map(c => (
                                            <th key={c.key}
                                                className={c.sortType === 'number' ? 'col-right' : undefined}
                                                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                                                         background: '#fff' }}
                                                onClick={() => handleSort(c.key)}>
                                                {/* 2026-09-13 (Eric): every column explains itself on hover. */}
                                                {c.tip ? (
                                                    <Tooltip content={c.tip} preferredPosition="above" width="wide">
                                                        <span style={{ borderBottom: '1px dotted currentColor' }}>{c.label}</span>
                                                    </Tooltip>
                                                ) : c.label}{arrow(c.key)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedClubRows.map((r, i) => (
                                        <tr key={`${r.club}|${i}`}>
                                            {RBC_COLUMNS.map(c => (
                                                <td key={c.key} className={`td-cell${c.sortType === 'number' ? ' col-right' : ''}`}>
                                                    {c.key === 'crm'
                                                        ? (r.crm || <span style={{ color: '#c4c8cc' }}>—</span>)
                                                        : c.key === 'club' && r.club
                                                        /* 2026-09-27 (Eric): the club name opens the single-club
                                                           (per-order) view, as if that club had been picked in the
                                                           Club filter. A real href, so it also opens in a new tab. */
                                                        ? <Link to={`?${(() => { const p = new URLSearchParams(searchParams); p.delete('club'); p.append('club', r.club); return p.toString(); })()}`}
                                                                onClick={(e) => { e.preventDefault(); setClubFilter([r.club]); }}
                                                                title={`Show the orders for ${r.club}`}>
                                                              {r.club}
                                                          </Link>
                                                        : c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fff',
                                                boxShadow: '0 -2px 0 var(--p-color-border, #e1e3e5)' }}>
                                    {(() => {
                                        // Totals row across the currently-displayed rows.
                                        // Avg-order = SUM(revenue) / SUM(orders) — a true
                                        // weighted average; Modified = all but Unfulfilled
                                        // over Orders, weighted the same way.
                                        const t = (sortedClubRows || []).reduce((acc, r) => {
                                            for (const k of ['unfulfilled', 'partially_fulfilled', 'fulfilled', 'completed_partial',
                                                             'cancelled', 'orders', 'players', ...RBC_MONEY_KEYS]) {
                                                acc[k] = (acc[k] || 0) + Number(r[k] || 0);
                                            }
                                            return acc;
                                        }, {});
                                        const nClubs = (sortedClubRows || []).length;
                                        const cellFor = (c) => {
                                            if (c.total === 'sum') return c.fmt(t[c.key] || 0);
                                            if (c.total === 'avg') return _fmtUSD(t.orders ? t.revenue / t.orders : 0);
                                            if (c.total === 'pct_modified') return c.fmt(t.orders ? 100 * (t.orders - (t.unfulfilled || 0)) / t.orders : 0);
                                            return '';
                                        };
                                        return (
                                            <tr style={{ fontWeight: 600,
                                                         borderTop: '2px solid var(--p-color-border, #e1e3e5)',
                                                         background: '#f6f6f7' }}>
                                                <td className="td-cell" colSpan={2}>
                                                    Total ({nClubs} club{nClubs === 1 ? '' : 's'})
                                                </td>
                                                {RBC_COLUMNS.slice(2).map(c => (
                                                    <td key={c.key} className="td-cell col-right" title={c.totalTitle}>{cellFor(c)}</td>
                                                ))}
                                            </tr>
                                        );
                                    })()}
                                </tfoot>
                            </table>
                            </div>
                            </div>
                        </>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- Add-On & Retail Items report ----------------------------- */

function AddOnRetailReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter,     setClubFilter]     = useState(searchParams.getAll('club'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], available_clubs: [],
                                       date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('total_revenue');
    const [sortDir, setSortDir] = useState('desc');

    const COLUMNS = [
        { key: 'club',           label: 'Club',           sortType: 'string' },
        { key: 'total_orders',   label: 'Total Orders',   sortType: 'number' },
        { key: 'orders_extra',   label: 'Orders + Extra', sortType: 'number' },
        // % Extra — orders_extra / total_orders, computed on the fly.
        { key: 'extra_pct',      label: '% Extra',        sortType: 'number' },
        { key: 'retail_qty',     label: 'Retail items',   sortType: 'number' },
        { key: 'retail_revenue', label: 'Retail revenue', sortType: 'number' },
        { key: 'addon_qty',      label: 'Add-on items',   sortType: 'number' },
        { key: 'addon_revenue',  label: 'Add-on revenue', sortType: 'number' },
        { key: 'total_revenue',  label: 'Total revenue',  sortType: 'number' },
    ];
    const _extraPct = (r) => {
        const t = Number(r?.total_orders || 0);
        return t > 0 ? (Number(r?.orders_extra || 0) / t) * 100 : 0;
    };
    const sortedRows = useMemo(() => {
        const col = COLUMNS.find(c => c.key === sortKey) || COLUMNS[COLUMNS.length - 1];
        const rows = (data.rows || []).slice();
        rows.sort((a, b) => {
            const av = sortKey === 'extra_pct' ? _extraPct(a) : a[sortKey];
            const bv = sortKey === 'extra_pct' ? _extraPct(b) : b[sortKey];
            const cmp = col.sortType === 'string'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = COLUMNS.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            clubFilter.forEach(c => p.append('clubs', c));
            const res = await fetchAuth(`/api/reports/addon_retail_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter]);
    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => (data.rows || []).reduce((acc, r) => {
        acc.total_orders   += Number(r.total_orders || 0);
        acc.orders_extra   += Number(r.orders_extra || 0);
        acc.retail_qty     += Number(r.retail_qty || 0);
        acc.retail_revenue += Number(r.retail_revenue || 0);
        acc.addon_qty      += Number(r.addon_qty || 0);
        acc.addon_revenue  += Number(r.addon_revenue || 0);
        acc.total_revenue  += Number(r.total_revenue || 0);
        return acc;
    }, { total_orders: 0, orders_extra: 0, retail_qty: 0, retail_revenue: 0,
         addon_qty: 0, addon_revenue: 0, total_revenue: 0 }), [data.rows]);

    const handleExport = useCallback(() => {
        const rows = [['Club', 'Total Orders', 'Orders + Extra', '% Extra',
                       'Retail items', 'Retail revenue (USD)',
                       'Add-on items', 'Add-on revenue (USD)', 'Total revenue (USD)']];
        for (const r of sortedRows) rows.push([
            r.club,
            Number(r.total_orders || 0),
            Number(r.orders_extra || 0),
            _extraPct(r).toFixed(1),
            Number(r.retail_qty || 0), Number(r.retail_revenue || 0).toFixed(2),
            Number(r.addon_qty || 0),  Number(r.addon_revenue || 0).toFixed(2),
            Number(r.total_revenue || 0).toFixed(2),
        ]);
        downloadCsv(`addon_retail_by_club_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, sortedRows]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Add-On & Retail Items By Club" storageKey="add_on_retail_items_by_club_about_open" linkLabel="About this report">
                        <Text as="p">
                            Orders + Extra = the count of orders in the club
                            that had at least one Add-On line item OR at
                            least one Retail line item on them. It's a
                            subset of the "Total Orders" column where the
                            customer bought "extra" stuff beyond the base
                            Team Portal product.
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            Totals: {totals.total_orders.toLocaleString()} orders ·
                            {' '}{totals.orders_extra.toLocaleString()} with add-on/retail ·
                            {' '}{totals.retail_qty.toLocaleString()} retail items
                            {' '}({_fmtUSD(totals.retail_revenue)}) ·
                            {' '}{totals.addon_qty.toLocaleString()} add-on items
                            {' '}({_fmtUSD(totals.addon_revenue)}) ·
                            {' '}{_fmtUSD(totals.total_revenue)} revenue
                        </Text>
                    </InlineStack>

                    {loading ? null : (data.rows || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No orders with add-on or retail items in this window.
                        </div>
                    ) : (
                        <table className="data-table" style={{ marginTop: 12 }}>
                            <thead>
                                <tr>
                                    {COLUMNS.map(c => (
                                        <th key={c.key}
                                            className={c.sortType === 'number' ? 'col-right' : undefined}
                                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                            onClick={() => handleSort(c.key)}>
                                            {c.label}{arrow(c.key)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((r, i) => (
                                    <tr key={`${r.club}|${i}`}>
                                        <td className="td-cell">{r.club}</td>
                                        <td className="td-cell col-right">{(r.total_orders || 0).toLocaleString()}</td>
                                        <td className="td-cell col-right">{(r.orders_extra || 0).toLocaleString()}</td>
                                        <td className="td-cell col-right">{_extraPct(r).toFixed(1)}%</td>
                                        <td className="td-cell col-right">{(r.retail_qty || 0).toLocaleString()}</td>
                                        <td className="td-cell col-right">{_fmtUSD(r.retail_revenue)}</td>
                                        <td className="td-cell col-right">{(r.addon_qty || 0).toLocaleString()}</td>
                                        <td className="td-cell col-right">{_fmtUSD(r.addon_revenue)}</td>
                                        <td className="td-cell col-right">{_fmtUSD(r.total_revenue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ fontWeight: 600, borderTop: '2px solid var(--p-color-border)' }}>
                                    <td className="td-cell">Total</td>
                                    <td className="td-cell col-right">{totals.total_orders.toLocaleString()}</td>
                                    <td className="td-cell col-right">{totals.orders_extra.toLocaleString()}</td>
                                    <td className="td-cell col-right">
                                        {totals.total_orders > 0
                                          ? ((totals.orders_extra / totals.total_orders) * 100).toFixed(1) + '%'
                                          : '—'}
                                    </td>
                                    <td className="td-cell col-right">{totals.retail_qty.toLocaleString()}</td>
                                    <td className="td-cell col-right">{_fmtUSD(totals.retail_revenue)}</td>
                                    <td className="td-cell col-right">{totals.addon_qty.toLocaleString()}</td>
                                    <td className="td-cell col-right">{_fmtUSD(totals.addon_revenue)}</td>
                                    <td className="td-cell col-right">{_fmtUSD(totals.total_revenue)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- PPP Overview report -------------------------------------- */

// Pastel column-group backgrounds. Picker = yellow, Presser = blue, Packer = green.
const PPP_COLORS = {
    pick:  '#FFF7CC',   // pastel yellow
    press: '#D6EAF8',   // pastel blue
    pack:  '#D4EFDF',   // pastel green
};

const PPP_COLUMNS = [
    { key: 'name',             label: 'Name',     group: null,    align: 'left',  sortType: 'string' },
    { key: 'primary_location', label: 'Location', group: null,    align: 'left',  sortType: 'string' },
    { key: 'pick_orders',    label: 'Orders',        group: 'pick',  align: 'right', sortType: 'number' },
    { key: 'pick_items',     label: 'Items',         group: 'pick',  align: 'right', sortType: 'number' },
    { key: 'pick_manual',    label: 'Manual',        group: 'pick',  align: 'right', sortType: 'number' },
    { key: 'pick_scan',      label: 'Scan',          group: 'pick',  align: 'right', sortType: 'number' },
    { key: 'pick_lineitems', label: 'Line items',    group: 'pick',  align: 'right', sortType: 'number' },
    { key: 'press_orders',   label: 'Orders',        group: 'press', align: 'right', sortType: 'number' },
    { key: 'press_bundles',  label: 'Bundles',       group: 'press', align: 'right', sortType: 'number' },
    { key: 'pack_orders',    label: 'Orders',        group: 'pack',  align: 'right', sortType: 'number' },
    { key: 'pack_items',     label: 'Items',         group: 'pack',  align: 'right', sortType: 'number' },
    { key: 'pack_failed',    label: 'QC Failed',     group: 'pack',  align: 'right', sortType: 'number' },
];

/* ---------- Products By Club report ------------------------------------ */

// One card per product. Image + name on the left, a small per-product
// table on the right with one column per variant: variant title (size /
// color combo from Shopify) on top, SKU as a small mono subhead, and the
// quantity sold below. Variant columns let the operator scan one product's
// size breakdown at a glance — and the explicit SKU row preserves the
// information needed for a copy-paste / CSV-style export.
function ProductsByClubCard({ product }) {
    const p = product || {};
    const variants = p.variants || [];
    return (
        <div className="pbc-card" style={{
            display: 'grid',
            gridTemplateColumns: '80px 1fr',
            gap: 16,
            padding: 14,
            border: '1px solid var(--p-color-border, #e1e3e5)',
            borderRadius: 8,
            background: '#fff',
        }}>
            <div>
                {p.image_url
                    ? <SmartProductImage src={p.image_url} alt="" size={144}
                           style={{ width: 72, height: 72, objectFit: 'contain',
                                    border: '1px solid #eee',
                                    borderRadius: 4, display: 'block' }} />
                    : <div style={{ width: 72, height: 72, background: '#fafafa',
                                    border: '1px solid #eee', borderRadius: 4,
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', color: '#c4c8cc',
                                    fontSize: 10 }}>no img</div>}
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ marginBottom: 8 }}>
                    <Text variant="headingSm" as="h3">{p.title || '—'}</Text>
                </div>
                {variants.length === 0 ? (
                    <Text tone="subdued">No variants in window.</Text>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="data-table pbc-variant-table"
                               style={{ width: 'auto', minWidth: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left',
                                                 background: '#f6f6f7',
                                                 borderBottom: '1px solid #e1e3e5',
                                                 padding: '6px 10px',
                                                 fontSize: 11, color: '#6d7175',
                                                 letterSpacing: 0.04,
                                                 textTransform: 'uppercase',
                                                 fontWeight: 700 }}>Variant</th>
                                    {variants.map(v => (
                                        <th key={v.sku}
                                            style={{ textAlign: 'center',
                                                     background: '#f6f6f7',
                                                     borderBottom: '1px solid #e1e3e5',
                                                     padding: '6px 10px',
                                                     fontWeight: 600,
                                                     fontSize: 13,
                                                     whiteSpace: 'nowrap' }}>
                                            {v.variant_title || '—'}
                                        </th>
                                    ))}
                                    <th style={{ textAlign: 'right',
                                                 background: '#f6f6f7',
                                                 borderBottom: '1px solid #e1e3e5',
                                                 padding: '6px 10px',
                                                 fontWeight: 700, fontSize: 13,
                                                 whiteSpace: 'nowrap' }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '4px 10px',
                                                 fontSize: 11, color: '#6d7175',
                                                 letterSpacing: 0.04,
                                                 textTransform: 'uppercase',
                                                 fontWeight: 600,
                                                 borderBottom: '1px solid #f1f2f3' }}>SKU</td>
                                    {variants.map(v => (
                                        <td key={v.sku}
                                            style={{ textAlign: 'center',
                                                     padding: '4px 10px',
                                                     fontFamily: 'ui-monospace, Menlo, monospace',
                                                     fontSize: 11,
                                                     color: '#6d7175',
                                                     whiteSpace: 'nowrap',
                                                     borderBottom: '1px solid #f1f2f3' }}>
                                            {v.sku}
                                        </td>
                                    ))}
                                    <td style={{ borderBottom: '1px solid #f1f2f3' }}></td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 10px',
                                                 fontSize: 11, color: '#6d7175',
                                                 letterSpacing: 0.04,
                                                 textTransform: 'uppercase',
                                                 fontWeight: 700 }}>Qty</td>
                                    {variants.map(v => (
                                        <td key={v.sku}
                                            style={{ textAlign: 'center',
                                                     padding: '8px 10px',
                                                     fontSize: 16,
                                                     fontWeight: 700,
                                                     color: (v.qty > 0) ? '#111418' : '#c4c8cc',
                                                     whiteSpace: 'nowrap' }}>
                                            {(v.qty || 0).toLocaleString()}
                                        </td>
                                    ))}
                                    <td style={{ textAlign: 'right',
                                                 padding: '8px 10px',
                                                 fontSize: 16,
                                                 fontWeight: 700,
                                                 color: '#111418',
                                                 whiteSpace: 'nowrap' }}>
                                        {(p.total || 0).toLocaleString()}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------- Revenue By Club & Vendor report -------------------------- */
// TP-only per-club revenue split by product vendor. Every order whose
// source is any TP-flavored bucket contributes its TP-portion revenue
// (order gross minus cached addon_revenue + retail_revenue). Per-SKU
// revenue is bucketed by vendor via a live Shopify get_products_by_skus
// lookup at report time — same trick Products By Vendor uses.
function RevenueByClubVendorReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter,     setClubFilter]     = useState(searchParams.getAll('club'));
    const [data, setData] = useState({ rows: [], available_clubs: [], date_from: '', date_to: '' });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('revenue');
    const [sortDir, setSortDir] = useState('desc');
    const [expanded, setExpanded] = useState(() => new Set());

    // Cheap available_clubs pre-fetch when locations picked — same trick
    // Club Summary uses. Keeps club dropdown populated even before the
    // user hits Run Report.
    const canRun = locationFilter.length > 0;

    // Keep URL in sync with filter state (bookmarkable).
    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, setSearchParams]);

    const load = useCallback(async () => {
        if (!canRun) { setData({ rows: [], available_clubs: [] }); return; }
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            clubFilter.forEach(c => p.append('clubs', c));
            const res = await fetchAuth(`/api/reports/revenue_by_club_vendor?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter, canRun]);

    // NO auto-fetch — Run Report gate (Shopify vendor lookup is the slow
    // step; don't spam it on every filter tweak).

    const RBCV_COLUMNS = [
        { key: 'club',                   label: 'Club',                    sortType: 'string' },
        { key: 'primary_location',       label: 'Location',                sortType: 'string' },
        { key: 'orders',                 label: 'Orders',                  sortType: 'number' },
        { key: 'primary_vendor',         label: 'Primary Vendor',          sortType: 'string' },
        { key: 'primary_vendor_revenue', label: 'Primary Vendor Revenue',  sortType: 'number' },
        { key: 'revenue',                label: 'Total Revenue',           sortType: 'number' },
    ];
    const sortedRows = useMemo(() => {
        const col = RBCV_COLUMNS.find(c => c.key === sortKey) || RBCV_COLUMNS[RBCV_COLUMNS.length - 1];
        const rows = (data.rows || []).slice();
        rows.sort((a, b) => {
            // primary_vendor_revenue is a derived value (first entry of
            // vendors[] which is pre-sorted by revenue DESC). Pull it
            // inline so both display and sort share one source of truth.
            const derive = (r) => r.primary_vendor_revenue != null
                ? r.primary_vendor_revenue
                : (((r.vendors || [])[0] || {}).revenue || 0);
            const av = sortKey === 'primary_vendor_revenue' ? derive(a) : a[sortKey];
            const bv = sortKey === 'primary_vendor_revenue' ? derive(b) : b[sortKey];
            const cmp = col.sortType === 'string'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.rows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            const col = RBCV_COLUMNS.find(c => c.key === key);
            setSortDir(col && col.sortType === 'string' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    const money = (v) => (v == null ? '—'
        : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
            .format(Number(v) || 0));
    const fmtN = (v) => (v == null ? '—' : Number(v).toLocaleString());

    const toggle = (club) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(club)) next.delete(club); else next.add(club);
            return next;
        });
    };

    const handleExport = useCallback(() => {
        const rows = [['Club', 'Location', 'Orders', 'Primary Vendor', 'Primary Vendor Revenue', 'Total Revenue',
                       'Vendor', 'Vendor Orders', 'Vendor Qty', 'Vendor Revenue']];
        for (const r of sortedRows) {
            const primaryRev = ((r.vendors || [])[0] || {}).revenue || 0;
            if (!(r.vendors || []).length) {
                rows.push([r.club, r.primary_location || '', r.orders, r.primary_vendor || '', primaryRev, r.revenue, '', '', '', '']);
                continue;
            }
            for (const v of r.vendors) {
                rows.push([r.club, r.primary_location || '', r.orders, r.primary_vendor || '', primaryRev, r.revenue,
                           v.vendor, v.orders_count, v.qty, v.revenue]);
            }
        }
        downloadCsv(`revenue_by_club_vendor_${data.date_from || ''}_to_${data.date_to || ''}.csv`, rows);
    }, [sortedRows, data.date_from, data.date_to]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <Text variant="headingMd" as="h2">Revenue By Club & Vendor</Text>
                    <Banner tone="info">
                        <p>
                            <strong>Team Portal line items only.</strong> Revenue is limited to the
                            Team Portal portion of each order — pure retail orders are excluded,
                            and add-on / retail line items on Team Portal-mixed orders are
                            subtracted so each club's total reflects only its Team Portal spend.
                        </p>
                    </Banner>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo}     setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                        trailing={
                            <Button
                                variant="primary"
                                tone="success"
                                onClick={load}
                                disabled={loading || !canRun}
                                loading={loading}
                            >
                                Run Report
                            </Button>
                        }
                    />
                    {!canRun && (
                        <Text variant="bodySm" tone="subdued">
                            Pick a Location, then click <strong>Run Report</strong>.
                        </Text>
                    )}
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            {(data.rows || []).length > 0 && (
                <Card>
                    <BlockStack gap="300">
                        <InlineStack gap="400" wrap>
                            <Text variant="bodySm" tone="subdued">
                                Window: {data.date_from || '—'} → {data.date_to || '—'}
                            </Text>
                            <Text variant="bodySm" tone="subdued">
                                {(data.rows || []).length} club{(data.rows || []).length === 1 ? '' : 's'}
                                {' '}· TP total: {money((data.rows || []).reduce((s, r) => s + (r.revenue || 0), 0))}
                            </Text>
                        </InlineStack>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="orders-summary-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 30 }}></th>
                                        {RBCV_COLUMNS.map(c => (
                                            <th key={c.key}
                                                className={`sortable${c.sortType === 'number' ? ' num' : ''}`}
                                                onClick={() => handleSort(c.key)}>
                                                {c.label}{arrow(c.key)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map(r => (
                                        <React.Fragment key={r.club}>
                                            <tr onClick={() => toggle(r.club)}
                                                style={{ cursor: 'pointer' }}>
                                                <td style={{ textAlign: 'center' }}>
                                                    {expanded.has(r.club) ? '▾' : '▸'}
                                                </td>
                                                <td>{r.club}</td>
                                                <td>{r.primary_location || '—'}</td>
                                                <td className="num">{fmtN(r.orders)}</td>
                                                <td>{r.primary_vendor || '—'}</td>
                                                <td className="num">
                                                    {money(((r.vendors || [])[0] || {}).revenue || 0)}
                                                </td>
                                                <td className="num">{money(r.revenue)}</td>
                                            </tr>
                                            {expanded.has(r.club) && (
                                                <tr>
                                                    <td colSpan={RBCV_COLUMNS.length + 1}
                                                        style={{ background: '#f9fafb', padding: '8px 16px' }}>
                                                        <VendorBreakdown vendors={r.vendors || []}
                                                                          clubTotal={r.revenue} />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                    {(() => {
                                        // Totals row — sums across every rendered club.
                                        // Primary Vendor Revenue sums each club's own
                                        // top-vendor slice (not one global vendor).
                                        const tOrders  = sortedRows.reduce((s, r) => s + (r.orders || 0), 0);
                                        const tPrimary = sortedRows.reduce((s, r) =>
                                            s + ((((r.vendors || [])[0] || {}).revenue) || 0), 0);
                                        const tRevenue = sortedRows.reduce((s, r) => s + (r.revenue || 0), 0);
                                        return (
                                            <tr style={{ background: '#f9fafb',
                                                         borderTop: '2px solid #d1d5db',
                                                         fontWeight: 700 }}>
                                                <td></td>
                                                <td>Total ({sortedRows.length} club{sortedRows.length === 1 ? '' : 's'})</td>
                                                <td>—</td>
                                                <td className="num">{fmtN(tOrders)}</td>
                                                <td>—</td>
                                                <td className="num">{money(tPrimary)}</td>
                                                <td className="num">{money(tRevenue)}</td>
                                            </tr>
                                        );
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </BlockStack>
                </Card>
            )}
        </BlockStack>
    );
}

// Small sortable table rendered inside each expanded Club row.
function VendorBreakdown({ vendors, clubTotal }) {
    const [sortKey, setSortKey] = useState('revenue');
    const [sortDir, setSortDir] = useState('desc');
    const sorted = useMemo(() => {
        const list = (vendors || []).slice();
        list.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const cmp = sortKey === 'vendor'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [vendors, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            setSortDir(key === 'vendor' ? 'asc' : 'desc');
        }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
        .format(Number(v) || 0);
    if (!sorted.length) return <Text tone="subdued">No vendor breakdown.</Text>;
    return (
        <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
                <tr>
                    {[
                        { k: 'vendor',       l: 'Vendor',      num: false },
                        { k: 'orders_count', l: 'Orders',      num: true  },
                        { k: 'qty',          l: 'Qty',         num: true  },
                        { k: 'revenue',      l: 'Revenue',     num: true  },
                        { k: '__pct',        l: '% of Club',   num: true, noSort: true },
                    ].map(c => (
                        <th key={c.k}
                            onClick={c.noSort ? undefined : () => handleSort(c.k)}
                            style={{
                                textAlign: c.num ? 'right' : 'left',
                                padding: '4px 8px', cursor: c.noSort ? 'default' : 'pointer',
                                userSelect: 'none', color: '#374151',
                                fontWeight: 600, borderBottom: '1px solid #e5e7eb',
                            }}>
                            {c.l}{!c.noSort && arrow(c.k)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {sorted.map((v, i) => (
                    <tr key={v.vendor + '_' + i}>
                        <td style={{ padding: '4px 8px' }}>{v.vendor}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                            {Number(v.orders_count || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                            {Number(v.qty || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                            {money(v.revenue)}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: '#6b7280' }}>
                            {clubTotal > 0
                                ? `${(100 * v.revenue / clubTotal).toFixed(1)}%`
                                : '—'}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/* ---------- Discounts By Club report --------------------------------- */
// Per-club discount + Shopify-credit rollup with a summary card row on top
// and a per-order breakdown table below. Requires a club selection to
// populate order data (the enumerate-clubs step still runs across the
// caller's whole scope so the picker is populated).
function DiscountsByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    // Optional single-team narrowing within the selected club. '' = all teams.
    const [teamFilter, setTeamFilter] = useState(searchParams.get('team') || '');
    const [crmFilter, setCrmFilter] = useState(searchParams.getAll('crm'));
    const [crmOpen, setCrmOpen]     = useState(false);
    const [crmSearch, setCrmSearch] = useState('');
    const [loading, setLoading] = useState(false);
    // Dropdown options (clubs + CRMs) — refreshed cheaply on filter change
    // (run=0). The heavy report (orders/rows) only loads on Run Report.
    const [opts, setOpts] = useState({ available_clubs: [], available_crms: [] });
    const [hasRun, setHasRun] = useState(false);
    const [pendingClubRun, setPendingClubRun] = useState(null);   // club picked from the summary → run for it
    const [data, setData] = useState({ available_teams: [], summary: null, rows: [],
                                       orders: [], date_from: '', date_to: '',
                                       selected_club: null, mode: 'location' });
    const [err, setErr] = useState(null);
    // 2026-10-17 (Eric): the table fills the rest of the window and scrolls inside its own box, like Club Revenue
    // Summary: nothing above it moves, the header row stays put, and the page itself never scrolls.
    const tableWrapRef = useRef(null);
    const [tableMaxH, setTableMaxH] = useState(null);
    useLayoutEffect(() => {
        const measure = () => {
            const el = tableWrapRef.current;
            if (!el) return;
            const h = paneHeightToFitScroller(el) - 6;     // 6px in hand so the card's bottom corners never slip under the page edge
            setTableMaxH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h);
        };
        measure();
        const raf = requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        const col = tableWrapRef.current && tableWrapRef.current.closest('.reporting-content');
        const ro = col ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(col);
        return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
    }, [data.rows, data.orders, loading, err, tableMaxH]);
    const [sortKey, setSortKey] = useState('placed_d');
    const [sortDir, setSortDir] = useState('desc');

    // 2026-09-25 (Eric): every picked club goes to the backend, not just the first.
    const selectedClubs = clubFilter.filter(Boolean);
    const selectedClub = selectedClubs.join(', ');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        for (const c of crmFilter)      p.append('crm',  c);
        if (teamFilter) p.set('team', teamFilter);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, crmFilter, teamFilter, setSearchParams]);

    // Clear the team when the club changes (but not on mount, so a
    // deep-linked &team= survives the first render).
    const _teamMount = useRef(true);
    useEffect(() => {
        if (_teamMount.current) { _teamMount.current = false; return; }
        setTeamFilter('');
    }, [selectedClub]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cheap options fetch (run=0): populate the club + CRM dropdowns without
    // the heavy per-order Shopify pull. Fires on filter change.
    const loadOptions = useCallback(async () => {
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            crmFilter.forEach(c => p.append('crms', c));
            p.set('run', '0');
            const res = await fetchAuth(`/api/reports/discounts_by_club?${p.toString()}`);
            if (res.ok) {
                const j = await res.json();
                setOpts({ available_clubs: j.available_clubs || [],
                          available_crms:  j.available_crms  || [] });
            }
        } catch { /* non-fatal — dropdowns just stay empty */ }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, crmFilter]);

    useEffect(() => { loadOptions(); }, [loadOptions]);

    // The heavy report (run=1) — fires only on the Run Report button. Runs
    // club-level (a club selected) or location-level (per-club rows).
    const runReport = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            selectedClubs.forEach(c => p.append('clubs', c));
            if (teamFilter)   p.append('teams', teamFilter);
            crmFilter.forEach(c => p.append('crms', c));
            p.set('run', '1');
            const res = await fetchAuth(`/api/reports/discounts_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
            setHasRun(true);
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, selectedClub, teamFilter, crmFilter]);
    // 2026-10-16 (Eric): a club in the summary used to be a <Link to="?club=…"> — the URL changed, but the club
    // filter is read from the URL only on mount and the URL-sync effect wrote the old state straight back, so
    // nothing happened. The click now selects the club and runs the report for it as soon as the filter lands.
    useEffect(() => {
        if (pendingClubRun && selectedClub === pendingClubRun) { setPendingClubRun(null); runReport(); }
    }, [pendingClubRun, selectedClub, runReport]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            // Amount columns sort desc-first (biggest first); string cols asc.
            setSortDir(['placed_d', 'order_name', 'customer_name', 'club', 'crm'].includes(key) ? 'asc' : 'desc');
        }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const _numCols = ['packages', 'tap_mango', 'shopify_credit', 'refund_credit', 'other', 'total', 'orders'];
    const sortedOrders = useMemo(() => {
        const rows = (data.orders || []).slice();
        rows.sort((a, b) => {
            let cmp;
            if (_numCols.includes(sortKey)) {
                cmp = (Number(a[sortKey]) || 0) - (Number(b[sortKey]) || 0);
            } else {
                cmp = String(a[sortKey] || '').localeCompare(String(b[sortKey] || ''));
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.orders, sortKey, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

    // Location-level per-club rows.
    const sortedRows = useMemo(() => {
        const rows = (data.rows || []).slice();
        const key = _numCols.includes(sortKey) ? sortKey : (['club', 'crm'].includes(sortKey) ? sortKey : 'total');
        rows.sort((a, b) => {
            let cmp;
            if (_numCols.includes(key)) cmp = (Number(a[key]) || 0) - (Number(b[key]) || 0);
            else cmp = String(a[key] || '').localeCompare(String(b[key] || ''));
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

    const rowsTotals = useMemo(() => {
        const t = { packages: 0, tap_mango: 0, shopify_credit: 0, refund_credit: 0, other: 0, total: 0, orders: 0 };
        for (const r of (data.rows || [])) {
            t.packages += Number(r.packages) || 0; t.tap_mango += Number(r.tap_mango) || 0;
            t.shopify_credit += Number(r.shopify_credit) || 0; t.refund_credit += Number(r.refund_credit) || 0; t.other += Number(r.other) || 0;
            t.total += Number(r.total) || 0; t.orders += Number(r.orders) || 0;
        }
        return t;
    }, [data.rows]);

    // A package shows only when Shopify really gave a PACKAGES DISCOUNT (Eric, 2026-10-05);
    // `prorated` = the Team Portal's package price differed from what was charged.
    const _pkgText = (o) => (o.package_details || [])
        .map(p => `${p.title}${p.discount != null ? ' — $' + Number(p.discount).toFixed(2) : ''}${p.prorated ? ' (package $' + Number(p.package_discount).toFixed(2) + ')' : ''}`)
        .join(' | ');
    const handleExport = useCallback(() => {
        let rows;
        if (data.mode === 'location') {
            rows = [['Club', 'CRM', 'Packages', 'Tap Mango', 'Shopify Credit', 'Refund Credit', 'Other', 'Total', 'Orders']];
            for (const r of (data.rows || [])) {
                rows.push([r.club || '', r.crm || '',
                    Number(r.packages || 0).toFixed(2), Number(r.tap_mango || 0).toFixed(2),
                    Number(r.shopify_credit || 0).toFixed(2), Number(r.refund_credit || 0).toFixed(2), Number(r.other || 0).toFixed(2),
                    Number(r.total || 0).toFixed(2), String(r.orders || 0)]);
            }
        } else {
            // Club + CRM on every order row (Eric, 2026-10-20) so a club's export
            // stands on its own: the selected club(s) and the club's CRM as the page
            // shows them above the table.
            rows = [['Club', 'CRM', 'Order', 'Date', 'Customer', 'Packages', 'Package details',
                     'Tap Mango', 'Shopify Credit', 'Refund Credit', 'Other', 'Other codes', 'Total']];
            for (const o of (data.orders || [])) {
                rows.push([data.selected_club || selectedClub || '', data.club_crm || '',
                    o.order_name || '', o.placed_d || '', o.customer_name || '',
                    Number(o.packages || 0).toFixed(2), _pkgText(o),
                    Number(o.tap_mango || 0).toFixed(2), Number(o.shopify_credit || 0).toFixed(2), Number(o.refund_credit || 0).toFixed(2),
                    Number(o.other || 0).toFixed(2), (o.other_labels || []).join('; '),
                    Number(o.total || 0).toFixed(2)]);
            }
        }
        const scope = (data.mode === 'location'
            ? (locationFilter[0] || 'location') : (selectedClub || 'club')).replace(/[^\w-]+/g, '_');
        downloadCsv(`discounts_by_club_${scope}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, selectedClub, locationFilter]);

    const summary = data.summary;

    const SummaryCard = ({ label, value, tone }) => (
        <div style={{
            flex: '1 1 200px', minWidth: 180,
            border: '1px solid #dfe3e8', borderRadius: 8,
            padding: '12px 16px', background: tone === 'accent' ? '#f6f8fa' : '#fff',
        }}>
            <div style={{ fontSize: 12, color: '#6d7175', textTransform: 'uppercase',
                          letterSpacing: 0.5 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
                {_fmtUSDc(value)}
            </div>
        </div>
    );

    const th = (key, label, align = 'right') => (
        <th onClick={() => handleSort(key)}
            style={{ cursor: 'pointer', userSelect: 'none',
                     textAlign: align, padding: '6px 10px',
                     borderBottom: '1px solid #dfe3e8',
                     whiteSpace: 'nowrap', background: '#fafbfb' }}>
            {label}{sortArrow(key)}
        </th>
    );

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Discounts By Club" storageKey="discounts_by_club_about_open" linkLabel="About this report">
                        <Text as="p">
                            Pick a club and date range; see every discount code
                            applied and Shopify store credit used against that
                            club's orders in the window. Buckets: Packages
                            ("PACKAGES DISCOUNT"), Tap Mango (TM-/TM= codes),
                            Shopify Credit (net SUCCESS store-credit txns:
                            SALE − REFUND), Other (everything else).
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={opts.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !hasRun ||
                            (data.mode === 'location' ? !(data.rows || []).length
                                                      : !(data.orders || []).length)}
                        trailing={
                            <InlineStack gap="200" blockAlign="center">
                                {!selectedClub && (() => {
                                    const availableCrms = opts.available_crms || [];
                                    const filtered = crmSearch.trim()
                                        ? availableCrms.filter(c => (c || '').toLowerCase().includes(crmSearch.trim().toLowerCase()))
                                        : availableCrms;
                                    return (
                                        <Popover active={crmOpen}
                                            activator={
                                                <Button onClick={() => setCrmOpen(v => !v)}
                                                        disclosure={crmOpen ? 'up' : 'down'}
                                                        pressed={crmFilter.length > 0}>
                                                    CRM{crmFilter.length ? ` (${crmFilter.length})` : ''}
                                                </Button>}
                                            onClose={() => setCrmOpen(false)} preferredAlignment="left">
                                            <div style={{ minWidth: 280, maxHeight: 420, padding: 10,
                                                          display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <TextField labelHidden label="Search CRMs" type="search"
                                                    value={crmSearch} onChange={setCrmSearch}
                                                    placeholder="Search CRMs…" autoComplete="off"
                                                    clearButton onClearButtonClick={() => setCrmSearch('')} />
                                                <button type="button" className="filter-pill-clear"
                                                        disabled={crmFilter.length === 0}
                                                        onClick={() => setCrmFilter([])}>Clear</button>
                                                <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                                    {filtered.length === 0 ? (
                                                        <div style={{ color: 'var(--p-color-text-secondary)',
                                                                      fontSize: 13, padding: '8px 4px' }}>
                                                            {availableCrms.length === 0 ? 'No CRMs in window.' : `No matches for "${crmSearch}"`}
                                                        </div>
                                                    ) : (
                                                        <ChoiceList titleHidden title="CRMs" allowMultiple
                                                            choices={filtered.map(c => ({
                                                                label: c === '(none)' ? '(no CRM assigned)' : c, value: c }))}
                                                            selected={crmFilter} onChange={setCrmFilter} />
                                                    )}
                                                </div>
                                            </div>
                                        </Popover>
                                    );
                                })()}
                                {selectedClub && (data.available_teams || []).length > 0 && (
                                    <div style={{ minWidth: 170 }}>
                                        <ReportPicker label="Team"
                                            options={[{ label: 'All teams', value: '' },
                                                ...data.available_teams.map(t => ({ label: t, value: t }))]}
                                            value={teamFilter} onChange={setTeamFilter} />
                                    </div>
                                )}
                                <Button variant="primary" loading={loading} onClick={runReport}>
                                    Run Report
                                </Button>
                            </InlineStack>
                        }
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}
            {(data.refund_credit_pending || 0) > 0 && (
                <Banner tone="info" title="Refund Credit is still being worked out">
                    <p>{data.refund_credit_pending.toLocaleString()} order{data.refund_credit_pending === 1 ? '' : 's'} that used store credit show $0.00 Refund Credit for now. It is looked up once per order and remembered, so click Run Report again in a minute for the rest.</p>
                </Banner>
            )}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {selectedClub ? (
                            <Text variant="bodySm" tone="subdued">
                                Club: <strong>{selectedClub}</strong>
                                {teamFilter ? <> · Team: <strong>{teamFilter}</strong></> : null}
                                {summary ? <> · {summary.orders.toLocaleString()} orders</> : null}
                            </Text>
                        ) : (
                            <Text variant="bodySm" tone="subdued">
                                Location level · one row per club
                                {crmFilter.length ? <> · CRM: <strong>{crmFilter.join(', ')}</strong></> : null}
                                {hasRun ? <> · {(data.rows || []).length} clubs</> : null}
                            </Text>
                        )}
                    </InlineStack>

                    {loading ? null : !hasRun ? (
                        <div style={{ height: 200, display: 'flex', alignItems: 'center',
                                      justifyContent: 'center', color: '#6d7175', textAlign: 'center', padding: 16 }}>
                            {selectedClub
                              ? <>Ready — click <strong>&nbsp;Run Report&nbsp;</strong> to load discounts for {selectedClub}.</>
                              : <>Pick a location (and optional CRM), then click <strong>&nbsp;Run Report&nbsp;</strong> for a per-club summary. Or select a specific club for order-level detail.</>}
                        </div>
                    ) : data.mode === 'location' ? (
                        !(data.rows || []).length ? (
                            <div style={{ height: 200, display: 'flex', alignItems: 'center',
                                          justifyContent: 'center', color: '#6d7175' }}>
                                No discounts or store-credit charges at this location in this window.
                            </div>
                        ) : (
                            <div ref={tableWrapRef} style={{ overflow: 'auto', WebkitOverflowScrolling: 'touch', maxHeight: tableMaxH || undefined,
                                                            border: '1px solid var(--p-color-border, #e1e3e5)', borderRadius: 8, clipPath: 'inset(0 round 8px)', background: '#fff' }}>
                                <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', boxShadow: '0 1px 0 var(--p-color-border, #e1e3e5)' }}>
                                        <tr>
                                            {th('club',           'Club',           'left')}
                                            {th('crm',            'CRM',            'left')}
                                            {th('packages',       'Packages')}
                                            {th('tap_mango',      'Tap Mango')}
                                            {th('shopify_credit', 'Shopify Credit')}
                                            {th('refund_credit',  'Refund Credit')}
                                            {th('other',          'Other')}
                                            {th('total',          'Total')}
                                            {th('orders',         'Orders')}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedRows.map(r => (
                                            <tr key={r.club} style={{ borderBottom: '1px solid #f1f2f3' }}>
                                                <td style={{ padding: '6px 10px' }}>
                                                    <Button variant="plain" onClick={() => { setClubFilter([r.club]); setPendingClubRun(r.club); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} }}>
                                                        {r.club}
                                                    </Button>
                                                </td>
                                                <td style={{ padding: '6px 10px' }}>{r.crm || ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.packages > 0 ? _fmtUSDc(r.packages) : ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.tap_mango > 0 ? _fmtUSDc(r.tap_mango) : ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.shopify_credit > 0 ? _fmtUSDc(r.shopify_credit) : ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.refund_credit > 0 ? _fmtUSDc(r.refund_credit) : ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.other > 0 ? _fmtUSDc(r.other) : ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{_fmtUSDc(r.total)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{(r.orders || 0).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                                                            </tbody>
                                    <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fafbfb', boxShadow: '0 -1px 0 #dfe3e8' }}>
<tr style={{ borderTop: '2px solid #dfe3e8', background: '#fafbfb', fontWeight: 600 }}>
                                            <td style={{ padding: '8px 10px' }} colSpan={2}>Total ({(data.rows || []).length} clubs)</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(rowsTotals.packages)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(rowsTotals.tap_mango)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(rowsTotals.shopify_credit)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(rowsTotals.refund_credit)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(rowsTotals.other)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(rowsTotals.total)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{rowsTotals.orders.toLocaleString()}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )
                    ) : !summary || summary.orders === 0 ? (
                        <div style={{ height: 200, display: 'flex', alignItems: 'center',
                                      justifyContent: 'center', color: '#6d7175' }}>
                            No discounts or store-credit charges against {selectedClub} in this window.
                        </div>
                    ) : (
                        <BlockStack gap="400">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                <SummaryCard label="Packages"       value={summary.packages}       />
                                <SummaryCard label="Tap Mango"      value={summary.tap_mango}      />
                                <SummaryCard label="Shopify Credit" value={summary.shopify_credit} />
                                <SummaryCard label="Refund Credit"  value={summary.refund_credit}  />
                                <SummaryCard label="Other"          value={summary.other}          />
                                <SummaryCard label="Total"          value={summary.total} tone="accent" />
                            </div>

                            <div ref={tableWrapRef} style={{ overflow: 'auto', WebkitOverflowScrolling: 'touch', maxHeight: tableMaxH || undefined,
                                                            border: '1px solid var(--p-color-border, #e1e3e5)', borderRadius: 8, clipPath: 'inset(0 round 8px)', background: '#fff' }}>
                                <table style={{ width: '100%', minWidth: 1040, borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', boxShadow: '0 1px 0 var(--p-color-border, #e1e3e5)' }}>
                                        <tr>
                                            {th('order_name',     'Order',    'left')}
                                            {th('placed_d',       'Date',     'left')}
                                            {th('customer_name',  'Customer', 'left')}
                                            {th('packages',       'Packages')}
                                            <th style={{ textAlign: 'left', padding: '6px 10px',
                                                         borderBottom: '1px solid #dfe3e8', background: '#fafbfb' }}>Package details</th>
                                            {th('tap_mango',      'Tap Mango')}
                                            {th('shopify_credit', 'Shopify Credit')}
                                            {th('refund_credit',  'Refund Credit')}
                                            {th('other',          'Other')}
                                            {th('total',          'Total')}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedOrders.map(o => (
                                            <tr key={`${o.store_key}:${o.order_id}`} style={{ borderBottom: '1px solid #f1f2f3' }}>
                                                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                                    <Link to={`/store/${o.store_key || 'soccerpost'}/orders/${o.order_id}`}>{o.order_name || o.order_id}</Link>
                                                </td>
                                                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{o.placed_d || ''}</td>
                                                <td style={{ padding: '6px 10px' }}>{o.customer_name || ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{o.packages > 0 ? _fmtUSDc(o.packages) : ''}</td>
                                                <td style={{ padding: '6px 10px', fontSize: 12, minWidth: 240 }}>
                                                    {(o.package_details || []).map((p, i) => (
                                                        <div key={i}>
                                                            {p.title}{p.discount != null ? <> — <strong>{_fmtUSDc(p.discount)}</strong></> : null}
                                                            {p.prorated ? <span style={{ color: '#6d7175' }}> (package {_fmtUSDc(p.package_discount)})</span> : null}
                                                        </div>
                                                    ))}
                                                </td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{o.tap_mango > 0 ? _fmtUSDc(o.tap_mango) : ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{o.shopify_credit > 0 ? _fmtUSDc(o.shopify_credit) : ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{o.refund_credit > 0 ? _fmtUSDc(o.refund_credit) : ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}
                                                    title={(o.other_labels || []).join(', ')}>{o.other > 0 ? _fmtUSDc(o.other) : ''}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{_fmtUSDc(o.total)}</td>
                                            </tr>
                                        ))}
                                                                            </tbody>
                                    <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fafbfb', boxShadow: '0 -1px 0 #dfe3e8' }}>
<tr style={{ borderTop: '2px solid #dfe3e8', background: '#fafbfb', fontWeight: 600 }}>
                                            <td style={{ padding: '8px 10px' }} colSpan={3}>Total ({summary.orders.toLocaleString()} orders)</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(summary.packages)}</td>
                                            <td style={{ padding: '8px 10px' }}></td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(summary.tap_mango)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(summary.shopify_credit)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(summary.refund_credit)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(summary.other)}</td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{_fmtUSDc(summary.total)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}


function ProductsByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    // 2026-09-09 (Eric): multi-club. Every selected club is sent; the
    // backend sums quantities across them.
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ products: [], available_clubs: [],
                                       date_from: '', date_to: '', selected_club: null });
    const [err, setErr] = useState(null);
    // Sort key + direction. 'product' = alpha by title; 'total' = sum qty.
    // Default product/asc matches the backend's pre-sort.
    const [sortKey, setSortKey] = useState('product');
    // 2026-09-25 (Eric): 'Hide embellishments' toggle (off by default) and a
    // 'Filter by SKU' box on the sort row. Both client-side over the loaded
    // products: the list, the totals line and the CSV follow them.
    const [hideEmb, setHideEmb] = useState(searchParams.get('hide_emb') === '1');
    const [skuFilter, setSkuFilter] = useState(searchParams.get('sku') || '');
    const visibleProducts = useMemo(() => {
        const needle = skuFilter.trim().toUpperCase();
        let list = data.products || [];
        if (hideEmb) list = list.filter(p => !INVENTORY_EMBELLISHMENT_TYPES.has(String(p.product_type || '').trim().toLowerCase()));
        if (needle) {
            list = list.map(p => {
                const variants = (p.variants || []).filter(v => String(v.sku || '').toUpperCase().includes(needle));
                return variants.length ? { ...p, variants, total: variants.reduce((s, v) => s + (Number(v.qty) || 0), 0) } : null;
            }).filter(Boolean);
        }
        return list;
    }, [data.products, hideEmb, skuFilter]);
    const hiddenEmbCount = hideEmb ? (data.products || []).filter(p => INVENTORY_EMBELLISHMENT_TYPES.has(String(p.product_type || '').trim().toLowerCase())).length : 0;
    const [sortDir, setSortDir] = useState('asc');

    const selectedClub = clubFilter[0] || '';
    const clubsKey = clubFilter.join('|');

    const sortedProducts = useMemo(() => {
        const list = visibleProducts.slice();
        list.sort((a, b) => {
            let cmp;
            if (sortKey === 'total') {
                cmp = (Number(a.total) || 0) - (Number(b.total) || 0);
            } else {
                cmp = (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [visibleProducts, sortKey, sortDir]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            // Sensible default direction per key.
            setSortDir(key === 'total' ? 'desc' : 'asc');
        }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        if (hideEmb) p.set('hide_emb', '1');
        if (skuFilter.trim()) p.set('sku', skuFilter.trim());
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, hideEmb, skuFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            // Every selected club goes up; the endpoint sums across them and
            // still returns `available_clubs` so the picker stays populated.
            clubFilter.forEach(c => p.append('clubs', c));
            const res = await fetchAuth(`/api/reports/products_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubsKey]);
    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => {
        const products = visibleProducts;
        return {
            products: products.length,
            units:    products.reduce((s, p) => s + (p.total || 0), 0),
        };
    }, [visibleProducts]);

    const handleExport = useCallback(() => {
        const rows = [['Product', 'SKU', 'Variant', 'Quantity']];
        for (const p of visibleProducts) {
            for (const v of (p.variants || [])) {
                rows.push([p.title || '', v.sku || '',
                           v.variant_title || '',
                           Number(v.qty || 0)]);
            }
        }
        const club = (clubFilter.length > 1 ? `${clubFilter.length}_clubs` : (selectedClub || 'club')).replace(/[^\w-]+/g, '_');
        downloadCsv(`products_by_club_${club}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, clubFilter, selectedClub, visibleProducts]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Products Sold By Club" storageKey="products_sold_by_club_about_open" linkLabel="About this report">
                        <Text as="p">
                            Pick one or more clubs and a date range; see every product
                            (and its variants) sold to those clubs in the window, summed.
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        multiClub
                        onExport={handleExport}
                        exportDisabled={loading || clubFilter.length === 0 || !(data.products || []).length}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {selectedClub ? (
                            <Text variant="bodySm" tone="subdued">
                                {clubFilter.length > 1 ? 'Clubs' : 'Club'}: <strong>{clubFilter.join(', ')}</strong>
                                {' '}· {totals.products.toLocaleString()} products
                                {' '}· {totals.units.toLocaleString()} units
                            </Text>
                        ) : (
                            <Text variant="bodySm" tone="subdued">
                                Select a club to see products.
                            </Text>
                        )}
                    </InlineStack>

                    {loading ? null : !selectedClub ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            Pick one or more clubs from the filter above to load the report.
                        </div>
                    ) : (data.products || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No products sold to {clubFilter.join(', ')} in this window.
                        </div>
                    ) : (
                        <BlockStack gap="300">
                            <InlineStack gap="200" align="end" blockAlign="center" wrap>
                                <div style={{ width: 220 }}>
                                    <TextField label="Filter by SKU" labelHidden placeholder="Filter by SKU" value={skuFilter}
                                               onChange={setSkuFilter} autoComplete="off" clearButton onClearButtonClick={() => setSkuFilter('')} size="slim" />
                                </div>
                                <Button size="slim" pressed={hideEmb} onClick={() => setHideEmb(v => !v)}>
                                    {hideEmb ? `Embellishments hidden (${hiddenEmbCount})` : 'Hide embellishments'}
                                </Button>
                                <Text variant="bodySm" tone="subdued">Sort by</Text>
                                <ButtonGroup variant="segmented">
                                    <Button
                                        size="slim"
                                        pressed={sortKey === 'product'}
                                        onClick={() => handleSort('product')}
                                    >
                                        Product{sortArrow('product')}
                                    </Button>
                                    <Button
                                        size="slim"
                                        pressed={sortKey === 'total'}
                                        onClick={() => handleSort('total')}
                                    >
                                        Total qty{sortArrow('total')}
                                    </Button>
                                </ButtonGroup>
                            </InlineStack>
                            {sortedProducts.map((p) => (
                                <ProductsByClubCard key={p.product_id} product={p} />
                            ))}
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}


/* ---------- Embellishment Report ------------------------------------ */
// 2026-10-12 (Eric, for Andy): per product, units ordered with each
// embellishment SKU (logo / number / name) for the picked clubs + window.
function EmbellishmentReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ products: [], embellishments: [], available_clubs: [],
                                       none_total: 0, grand_total: 0, approx_count: 0,
                                       date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const clubsKey = clubFilter.join('|');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            clubFilter.forEach(c => p.append('clubs', c));
            const res = await fetchAuth(`/api/reports/embellishments_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubsKey]);
    useEffect(() => { load(); }, [load]);

    // 2026-10-12 (Eric): same fill-to-window-bottom mechanism as Revenue By Club
    // (paneHeightToFitScroller + ResizeObserver on .reporting-content).
    const tableWrapRef = useRef(null);
    const [tableMaxH, setTableMaxH] = useState(null);
    useLayoutEffect(() => {
        const measure = () => {
            const el = tableWrapRef.current;
            if (!el) return;
            const h = paneHeightToFitScroller(el) - 6;
            setTableMaxH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h);
        };
        measure();
        const raf = requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        const col = tableWrapRef.current && tableWrapRef.current.closest('.reporting-content');
        const ro = col ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(col);
        return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
    }, [data.products, loading, err, clubFilter, tableMaxH]);

    const embs = data.embellishments || [];
    const products = data.products || [];
    const [showVariants, setShowVariants] = useState(true);

    const handleExport = useCallback(() => {
        const rows = [['Product', 'Variant', 'Product type', ...embs.map(e => e.sku), 'No Embellishment', 'Total']];
        for (const p of products) {
            rows.push([p.title || '', '', p.product_type || '',
                       ...embs.map(e => Number((p.cells || {})[e.sku] || 0)),
                       Number(p.none || 0), Number(p.total || 0)]);
            for (const v of (p.variants || [])) {
                rows.push([p.title || '', v.variant_title || v.sku || '', p.product_type || '',
                           ...embs.map(e => Number((v.cells || {})[e.sku] || 0)),
                           Number(v.none || 0), Number(v.total || 0)]);
            }
        }
        const club = (clubFilter.length > 1 ? `${clubFilter.length}_clubs` : (clubFilter[0] || 'club')).replace(/[^\w-]+/g, '_');
        downloadCsv(`embellishment_report_${club}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, embs, products, clubFilter]);

    const cellStyle = { padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e1e3e5', whiteSpace: 'nowrap' };
    const headStyle = { ...cellStyle, background: '#f6f6f7', verticalAlign: 'bottom', whiteSpace: 'normal', minWidth: 80, maxWidth: 140 };
    const numCells = (row) => (
        <>
            {embs.map((e, ei) => {
                const v = Number((row.cells || {})[e.sku] || 0);
                const ap = (row.approx || []).includes(e.sku);
                return <td key={e.sku} className={`emb-td-num${ei % 2 === 1 ? ' emb-col-alt' : ''}`} style={cellStyle}>{v ? `${ap ? '≈' : ''}${v}` : ''}</td>;
            })}
            <td className={`emb-td-num${embs.length % 2 === 1 ? ' emb-col-alt' : ''}`} style={cellStyle}>{row.none || ''}</td>
            <td className={`emb-td-num${(embs.length + 1) % 2 === 1 ? ' emb-col-alt' : ''}`} style={{ ...cellStyle, fontWeight: 600 }}>{row.total}</td>
        </>
    );

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp
                        title="Embellishment Report"
                        storageKey="embellishment_report_about_open"
                        linkLabel="About this report"
                        items={[
                            ['What it counts', <>for every garment ordered by the selected club(s) in the date range, the units of each embellishment (logo, number, name, crest, decal) that were ordered on that garment. Rows are products; columns are embellishment SKUs. Cancelled orders and Fee lines are excluded, the same as Products by Club.</>],
                            ['How a garment is matched to its embellishments', <>the report reads each order's line items from Shopify and pairs them two ways. <b>Bundled (required) embellishments:</b> Shopify groups the garment and its embellishment lines into one bundle, and every embellishment line in that bundle counts against that garment. <b>Optional embellishments (Team Portal):</b> an optional logo is its own line item that points at the garment line it was added to; each such line counts against that garment, even when the order also holds bundles. A garment with two embellishments (for example a logo and a number) counts once in Total and once in each of those columns, so the cells plus No Embellishment can exceed Total.</>],
                            ['Total and No Embellishment', <>Total is the units of the garment ordered. No Embellishment is the units ordered with no embellishment attached.</>],
                            ['≈ (approximate)', <>the garment quantity and its embellishment quantity differ on the same lines (for example one hoodie with a number line of two). The cell shows the lower number and is flagged so the order can be checked before production.</>],
                            ['What is not counted', <>embellishment lines that do not point at any garment in the order (none on a healthy order), and anything on a cancelled order.</>],
                        ]}
                    />
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        multiClub
                        onExport={handleExport}
                        exportDisabled={loading || clubFilter.length === 0 || !products.length}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {clubFilter.length > 0 ? (
                            <Text variant="bodySm" tone="subdued">
                                {clubFilter.length > 1 ? 'Clubs' : 'Club'}: <strong>{clubFilter.join(', ')}</strong>
                                {' '}· {products.length.toLocaleString()} products
                                {' '}· {(data.grand_total || 0).toLocaleString()} garment units
                                {data.approx_count ? ` · ${data.approx_count} approximate count(s)` : ''}
                            </Text>
                        ) : (
                            <Text variant="bodySm" tone="subdued">Select a club to see products.</Text>
                        )}
                        {products.length > 0 && (
                            <Button size="slim" onClick={() => setShowVariants(v => !v)}>
                                {showVariants ? 'Collapse variants' : 'Show variants'}
                            </Button>
                        )}
                    </InlineStack>

                    {loading ? null : clubFilter.length === 0 ? (
                        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6d7175' }}>
                            Pick one or more clubs from the filter above to load the report.
                        </div>
                    ) : products.length === 0 ? (
                        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6d7175' }}>
                            No products sold to {clubFilter.join(', ')} in this window.
                        </div>
                    ) : (
                        <div ref={tableWrapRef} style={{ overflow: 'auto', WebkitOverflowScrolling: 'touch', maxHeight: tableMaxH || undefined }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                                <thead style={{ position: 'sticky', top: 0 }}>
                                    <tr>
                                        <th style={{ ...headStyle, textAlign: 'left', minWidth: 240 }}>Product</th>
                                        {embs.map((e, ei) => (
                                            <th key={e.sku} className={`emb-th-vert${ei % 2 === 1 ? ' emb-col-alt' : ''}`} style={headStyle} title={`${e.sku} ${e.title}`}>
                                                <div className="emb-th-vert__inner"><span className="emb-th-vert__title">{e.title}</span><span className="emb-th-vert__sku">{e.sku}</span></div>
                                            </th>
                                        ))}
                                        <th className={`emb-th-vert${embs.length % 2 === 1 ? ' emb-col-alt' : ''}`} style={headStyle}><div className="emb-th-vert__inner"><span className="emb-th-vert__sku">No Embellishment</span></div></th>
                                        <th className={`emb-th-vert${(embs.length + 1) % 2 === 1 ? ' emb-col-alt' : ''}`} style={headStyle}><div className="emb-th-vert__inner"><span className="emb-th-vert__sku">Total</span></div></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map(p => (
                                        <React.Fragment key={p.product_id}>
                                            <tr style={showVariants ? { fontWeight: 600 } : undefined}>
                                                <td style={{ ...cellStyle, textAlign: 'left', whiteSpace: 'normal' }}>
                                                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                                                        {p.image_url ? (
                                                            <img src={p.image_url} alt="" width={36} height={36}
                                                                 style={{ objectFit: 'contain', borderRadius: 4, flex: '0 0 auto' }} />
                                                        ) : <div style={{ width: 36, height: 36, flex: '0 0 auto' }} />}
                                                        <div>
                                                            <div>{p.title}</div>
                                                            <div style={{ fontSize: 11, color: '#6d7175', fontWeight: 400 }}>{p.product_type}</div>
                                                        </div>
                                                    </InlineStack>
                                                </td>
                                                {numCells(p)}
                                            </tr>
                                            {showVariants && (p.variants || []).map(v => (
                                                <tr key={`${p.product_id}|${v.sku}`}>
                                                    <td style={{ ...cellStyle, textAlign: 'left', whiteSpace: 'normal', paddingLeft: 52 }}>
                                                        <span>{v.variant_title}</span>
                                                        {v.variant_title !== v.sku && <span style={{ marginLeft: 8, fontSize: 11, color: '#6d7175' }}>{v.sku}</span>}
                                                    </td>
                                                    {numCells(v)}
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                                {/* 2026-10-20 (Eric): the Total row stays pinned to the bottom of the pane, like the other reports */}
                                <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 2, boxShadow: '0 -2px 0 #e1e3e5' }}>
                                    <tr style={{ fontWeight: 600, background: '#f6f6f7' }}>
                                        <td style={{ ...cellStyle, textAlign: 'left', background: '#f6f6f7' }}>Total</td>
                                        {embs.map((e, ei) => <td key={e.sku} className={`emb-td-num${ei % 2 === 1 ? ' emb-col-alt' : ''}`} style={{ ...cellStyle, background: '#f6f6f7' }}>{e.total}</td>)}
                                        <td className={`emb-td-num${embs.length % 2 === 1 ? ' emb-col-alt' : ''}`} style={{ ...cellStyle, background: '#f6f6f7' }}>{data.none_total || 0}</td>
                                        <td className={`emb-td-num${(embs.length + 1) % 2 === 1 ? ' emb-col-alt' : ''}`} style={{ ...cellStyle, background: '#f6f6f7' }}>{data.grand_total || 0}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}


/* ---------- Products Sold By Vendor --------------------------------- */
// Sibling of Products Sold By Club — same filter shell + card layout but
// pivots on vendor instead of club. Vendor + SKU filters are optional; the
// report runs unscoped by default so you can see everything across the
// window, then narrow.
function ProductsByVendorReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    // 2026-07-27: vendor filter is now multi-select (was single string). URL
    // may still carry a single `?vendor=X` from before the change — fold
    // it into the array on first load.
    const [vendorFilter, setVendorFilter] = useState(() => {
        const many = searchParams.getAll('vendor');
        return many.length ? many : (searchParams.get('vendor') ? [searchParams.get('vendor')] : []);
    });
    // 2026-07-27: SKU filter is now multi-select too. Fold a legacy
    // `?sku=X` URL param into the array on first load.
    const [skuFilter, setSkuFilter] = useState(() => {
        const many = searchParams.getAll('sku');
        return many.length ? many : (searchParams.get('sku') ? [searchParams.get('sku')] : []);
    });
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ products: [], available_vendors: [],
                                       available_skus: [],
                                       date_from: '', date_to: '',
                                       selected_vendor: null, selected_sku: null });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('product');
    const [sortDir, setSortDir] = useState('asc');
    const [vendorOpen, setVendorOpen] = useState(false);
    const [vendorSearch, setVendorSearch] = useState('');
    const [skuOpen, setSkuOpen] = useState(false);
    const [skuSearch, setSkuSearch] = useState('');

    const sortedProducts = useMemo(() => {
        const list = (data.products || []).slice();
        list.sort((a, b) => {
            let cmp;
            if (sortKey === 'total') {
                cmp = (Number(a.total) || 0) - (Number(b.total) || 0);
            } else if (sortKey === 'vendor') {
                cmp = (a.vendor || '').toLowerCase().localeCompare((b.vendor || '').toLowerCase());
                if (cmp === 0) cmp = (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
            } else {
                cmp = (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [data.products, sortKey, sortDir]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'total' ? 'desc' : 'asc');
        }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const v of vendorFilter)   p.append('vendor', v);
        for (const s of skuFilter)      p.append('sku',    s);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, vendorFilter, skuFilter, setSearchParams]);

    const load = useCallback(async () => {
        // Gate the fetch on a location being picked — the report scans
        // every SKU sold in the window and does a Shopify catalog lookup
        // per SKU, which is minutes-long across all locations.
        if (locationFilter.length === 0) {
            setData({ products: [], available_vendors: [], available_skus: [],
                      date_from: dateFrom, date_to: dateTo,
                      selected_vendor: null, selected_sku: null,
                      requires_location: true });
            return;
        }
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            vendorFilter.forEach(v => p.append('vendors', v));
            skuFilter.forEach(s => p.append('skus', s));
            const res = await fetchAuth(`/api/reports/products_by_vendor?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, vendorFilter, skuFilter]);
    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => {
        const products = data.products || [];
        return {
            products: products.length,
            units:    products.reduce((s, p) => s + (p.total || 0), 0),
            vendors:  new Set(products.map(p => p.vendor).filter(Boolean)).size,
        };
    }, [data.products]);

    const handleExport = useCallback(() => {
        const rows = [['Vendor', 'Product', 'SKU', 'Variant', 'Quantity']];
        for (const p of (data.products || [])) {
            for (const v of (p.variants || [])) {
                rows.push([p.vendor || '', p.title || '', v.sku || '',
                           v.variant_title || '',
                           Number(v.qty || 0)]);
            }
        }
        // Filename encodes selected vendors — one name if one is picked, a
        // count if several are picked, "all_vendors" when nothing narrows.
        const vlabel = vendorFilter.length === 0
            ? 'all_vendors'
            : vendorFilter.length === 1
                ? vendorFilter[0].replace(/[^\w-]+/g, '_')
                : `${vendorFilter.length}_vendors`;
        downloadCsv(`products_by_vendor_${vlabel}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, vendorFilter]);

    const availableVendors = data.available_vendors || [];
    const availableSkuOptions = data.available_skus || [];
    const filteredVendors = useMemo(() => {
        const q = vendorSearch.trim().toLowerCase();
        if (!q) return availableVendors;
        return availableVendors.filter(v => searchMatches(v, q));
    }, [availableVendors, vendorSearch]);
    const filteredSkuOptions = useMemo(() => {
        const q = skuSearch.trim().toLowerCase();
        if (!q) return availableSkuOptions;
        return availableSkuOptions.filter(o =>
            searchMatches(o.sku, q) ||
            searchMatches(o.title, q));
    }, [availableSkuOptions, skuSearch]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Products Sold By Vendor" storageKey="products_sold_by_vendor_about_open" linkLabel="About this report">
                        <Text as="p">
                            Every product sold in the window, grouped by Shopify
                            vendor. Optional filters narrow to a single vendor
                            and/or SKU — leave both empty to see everything.
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.products || []).length}
                        trailing={(
                            <>
                                <Popover
                                    active={vendorOpen}
                                    activator={
                                        <Button onClick={() => setVendorOpen(v => !v)}
                                                disclosure={vendorOpen ? 'up' : 'down'}
                                                pressed={vendorFilter.length > 0}>
                                            {vendorFilter.length === 0
                                                ? 'Vendor'
                                                : vendorFilter.length === 1
                                                    ? `Vendor: ${vendorFilter[0]}`
                                                    : `Vendors (${vendorFilter.length})`}
                                        </Button>
                                    }
                                    onClose={() => setVendorOpen(false)}
                                    preferredAlignment="left"
                                >
                                    <div style={{ minWidth: 280, maxHeight: 420, padding: 10,
                                                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <TextField labelHidden label="Search vendors" type="search"
                                            value={vendorSearch} onChange={setVendorSearch}
                                            placeholder="Search vendors…" autoComplete="off"
                                            clearButton onClearButtonClick={() => setVendorSearch('')} />
                                        <button type="button" className="filter-pill-clear"
                                                disabled={vendorFilter.length === 0}
                                                onClick={() => { setVendorFilter([]); setSkuFilter([]); }}>
                                            Clear
                                        </button>
                                        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                            {filteredVendors.length === 0 ? (
                                                <div style={{ color: 'var(--p-color-text-secondary)',
                                                              fontSize: 13, padding: '8px 4px' }}>
                                                    {availableVendors.length === 0
                                                      ? 'No vendors in window.'
                                                      : `No matches for "${vendorSearch}"`}
                                                </div>
                                            ) : (
                                                <ChoiceList titleHidden title="Vendors"
                                                    allowMultiple
                                                    choices={filteredVendors.map(v => ({
                                                        label: v, value: v,
                                                    }))}
                                                    selected={vendorFilter}
                                                    onChange={sel => {
                                                        // Multi-select — the whole new selection wins.
                                                        // Clear SKU when the vendor set changes (the SKU
                                                        // dropdown is scoped to selected vendors, so a
                                                        // sticky SKU might no longer match).
                                                        const next = sel || [];
                                                        const changed = next.length !== vendorFilter.length
                                                            || next.some(v => !vendorFilter.includes(v));
                                                        setVendorFilter(next);
                                                        if (changed) setSkuFilter([]);
                                                        // Keep popover open so multi-select is
                                                        // ergonomic (was closed on every click
                                                        // when single-select).
                                                    }} />
                                            )}
                                        </div>
                                    </div>
                                </Popover>
                                <Popover
                                    active={skuOpen}
                                    activator={
                                        <Button onClick={() => setSkuOpen(v => !v)}
                                                disclosure={skuOpen ? 'up' : 'down'}
                                                pressed={skuFilter.length > 0}>
                                            {skuFilter.length === 0
                                                ? 'SKU'
                                                : skuFilter.length === 1
                                                    ? `SKU: ${skuFilter[0]}`
                                                    : `SKUs (${skuFilter.length})`}
                                        </Button>
                                    }
                                    onClose={() => setSkuOpen(false)}
                                    // Flow the panel LEFT of the button — the
                                    // SKU chip sits far right in the filter
                                    // bar, so a right-flowing popover clips
                                    // at the viewport edge on ~1440px screens
                                    // (measured x=1032-1432 vs vw=1440).
                                    preferredAlignment="right"
                                    // fluidContent lets the popover grow past
                                    // Polaris' default 400px cap so our 672px
                                    // inner width is honored.
                                    fluidContent
                                >
                                    <div style={{ width: 672, maxHeight: 460, padding: 10,
                                                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <TextField labelHidden label="Search SKUs" type="search"
                                            value={skuSearch} onChange={setSkuSearch}
                                            placeholder="Search SKU or product name…" autoComplete="off"
                                            clearButton onClearButtonClick={() => setSkuSearch('')} />
                                        <InlineStack gap="200" wrap={false}>
                                            <button type="button" className="filter-pill-clear"
                                                    disabled={skuFilter.length === 0}
                                                    onClick={() => setSkuFilter([])}>
                                                Clear
                                            </button>
                                            {/* Select-all-filtered: adds every SKU currently
                                                rendered by the search into the selection
                                                (union, doesn't wipe prior picks). Rendered
                                                only when there's an active search AND at
                                                least one result. Cap of 300 matches the
                                                render slice below so the button never
                                                promises rows the list doesn't actually show. */}
                                            {skuSearch.trim() && filteredSkuOptions.length > 0 && (() => {
                                                const shown = filteredSkuOptions.slice(0, 300).map(o => o.sku);
                                                const notYetSelected = shown.filter(s => !skuFilter.includes(s));
                                                return (
                                                    <button type="button" className="filter-pill-clear"
                                                            disabled={notYetSelected.length === 0}
                                                            onClick={() => setSkuFilter(prev => {
                                                                const set = new Set(prev);
                                                                for (const s of shown) set.add(s);
                                                                return Array.from(set);
                                                            })}>
                                                        {notYetSelected.length === shown.length
                                                            ? `Select all ${shown.length} filtered`
                                                            : `Add remaining ${notYetSelected.length}`}
                                                    </button>
                                                );
                                            })()}
                                        </InlineStack>
                                        {/* Force each Polaris choice row onto a
                                            single truncated line — Polaris'
                                            default label flex-child grows to
                                            fit which lets long SKU+title
                                            strings wrap onto 3+ lines. */}
                                        <style>{`
                                            .pbv-sku-picker .Polaris-Choice { align-items: center; }
                                            .pbv-sku-picker .Polaris-Choice__Label {
                                                min-width: 0;
                                                flex: 1 1 auto;
                                                white-space: nowrap;
                                                overflow: hidden;
                                                text-overflow: ellipsis;
                                            }
                                        `}</style>
                                        <div className="pbv-sku-picker"
                                             style={{ overflowY: 'auto', flex: '1 1 auto',
                                                      minHeight: 0 }}>
                                            {filteredSkuOptions.length === 0 ? (
                                                <div style={{ color: 'var(--p-color-text-secondary)',
                                                              fontSize: 13, padding: '8px 4px' }}>
                                                    {availableSkuOptions.length === 0
                                                      ? 'No SKUs in window.'
                                                      : `No matches for "${skuSearch}"`}
                                                </div>
                                            ) : (
                                                <ChoiceList titleHidden title="SKUs"
                                                    allowMultiple
                                                    choices={filteredSkuOptions.slice(0, 300).map(o => {
                                                        const label = `${o.sku} — ${o.title}${o.vendor ? ` · ${o.vendor}` : ''}`;
                                                        return {
                                                            value: o.sku,
                                                            label: (
                                                                // Truncate on one line with an ellipsis
                                                                // and expose the full text on hover
                                                                // so long SKU+title combos don't wrap
                                                                // and blow up the row height.
                                                                <span title={label}
                                                                      style={{ display: 'inline-block',
                                                                               maxWidth: '100%',
                                                                               whiteSpace: 'nowrap',
                                                                               overflow: 'hidden',
                                                                               textOverflow: 'ellipsis',
                                                                               verticalAlign: 'middle' }}>
                                                                    {label}
                                                                </span>
                                                            ),
                                                        };
                                                    })}
                                                    selected={skuFilter}
                                                    onChange={sel => {
                                                        // Multi-select: ChoiceList only reports the
                                                        // CURRENTLY VISIBLE (filtered) slice, so a
                                                        // sticky pick outside the current search would
                                                        // vanish on every click. Merge: keep previously-
                                                        // selected SKUs that aren't in the visible
                                                        // options, then apply the visible-slice choice.
                                                        const visible = new Set(
                                                            filteredSkuOptions.slice(0, 300).map(o => o.sku));
                                                        const nextVisible = new Set(sel || []);
                                                        const merged = [
                                                            ...skuFilter.filter(s => !visible.has(s)),
                                                            ...Array.from(nextVisible),
                                                        ];
                                                        setSkuFilter(merged);
                                                        // Keep popover open for multi-pick.
                                                    }} />
                                            )}
                                        </div>
                                    </div>
                                </Popover>
                            </>
                        )}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {locationFilter.length > 0 && (
                            <Text variant="bodySm" tone="subdued">
                                {vendorFilter.length === 0
                                  ? <><strong>{totals.vendors.toLocaleString()}</strong> vendors</>
                                  : vendorFilter.length === 1
                                      ? <>Vendor: <strong>{vendorFilter[0]}</strong></>
                                      : <><strong>{vendorFilter.length}</strong> vendors: <strong>{vendorFilter.join(', ')}</strong></>}
                                {' '}· {totals.products.toLocaleString()} products
                                {' '}· {totals.units.toLocaleString()} units
                                {skuFilter.length === 1
                                    ? <> · SKU: <strong>{skuFilter[0]}</strong></>
                                    : skuFilter.length > 1
                                        ? <> · <strong>{skuFilter.length}</strong> SKUs</>
                                        : null}
                            </Text>
                        )}
                    </InlineStack>

                    {loading ? null : locationFilter.length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            Pick a location from the filter above to load the report.
                        </div>
                    ) : (data.products || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No products sold in this window
                            {vendorFilter.length === 1
                                ? ` for ${vendorFilter[0]}`
                                : vendorFilter.length > 1
                                    ? ` for the selected ${vendorFilter.length} vendors`
                                    : ''}
                            {skuFilter.length === 1
                                ? ` matching ${skuFilter[0]}`
                                : skuFilter.length > 1
                                    ? ` matching any of the ${skuFilter.length} selected SKUs`
                                    : ''}.
                        </div>
                    ) : (
                        <BlockStack gap="300">
                            <InlineStack gap="200" align="end" blockAlign="center">
                                <Text variant="bodySm" tone="subdued">Sort by</Text>
                                <ButtonGroup variant="segmented">
                                    <Button
                                        size="slim"
                                        pressed={sortKey === 'vendor'}
                                        onClick={() => handleSort('vendor')}
                                    >
                                        Vendor{sortArrow('vendor')}
                                    </Button>
                                    <Button
                                        size="slim"
                                        pressed={sortKey === 'product'}
                                        onClick={() => handleSort('product')}
                                    >
                                        Product{sortArrow('product')}
                                    </Button>
                                    <Button
                                        size="slim"
                                        pressed={sortKey === 'total'}
                                        onClick={() => handleSort('total')}
                                    >
                                        Total qty{sortArrow('total')}
                                    </Button>
                                </ButtonGroup>
                            </InlineStack>
                            {sortedProducts.map((p) => (
                                <ProductsByVendorCard key={p.product_id} product={p} />
                            ))}
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

// Same layout as ProductsByClubCard but surfaces the vendor as a subtle
// chip under the title.
function ProductsByVendorCard({ product }) {
    const p = product || {};
    const variants = p.variants || [];
    return (
        <div className="pbv-card" style={{
            display: 'grid', gridTemplateColumns: '80px 1fr', gap: 16,
            padding: 14,
            border: '1px solid var(--p-color-border, #e1e3e5)',
            borderRadius: 8, background: '#fff',
        }}>
            <div>
                {p.image_url
                    ? <SmartProductImage src={p.image_url} alt="" size={144}
                           style={{ width: 72, height: 72, objectFit: 'contain',
                                    border: '1px solid #eee', borderRadius: 4,
                                    display: 'block' }} />
                    : <div style={{ width: 72, height: 72, background: '#fafafa',
                                    border: '1px solid #eee', borderRadius: 4,
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', color: '#c4c8cc',
                                    fontSize: 10 }}>no img</div>}
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ marginBottom: 8 }}>
                    <Text variant="headingSm" as="h3">{p.title || '—'}</Text>
                    <InlineStack gap="200" blockAlign="center">
                        {p.vendor ? (
                            <span style={{ fontSize: 11, padding: '2px 8px',
                                           background: '#eef2ff', color: '#3730a3',
                                           borderRadius: 4, fontWeight: 600 }}>
                                {p.vendor}
                            </span>
                        ) : null}
                        <Text variant="bodySm" tone="subdued">
                            <strong>{(p.total || 0).toLocaleString()}</strong> units
                        </Text>
                    </InlineStack>
                </div>
                {variants.length === 0 ? (
                    <Text tone="subdued">No variants in window.</Text>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="data-table pbv-variant-table"
                               style={{ width: 'auto', minWidth: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', background: '#f6f6f7',
                                                 borderBottom: '1px solid #e1e3e5',
                                                 padding: '6px 10px', fontSize: 11,
                                                 color: '#6d7175', letterSpacing: 0.04,
                                                 textTransform: 'uppercase',
                                                 fontWeight: 700 }}>Variant</th>
                                    {variants.map(v => (
                                        <th key={v.sku}
                                            style={{ textAlign: 'center', background: '#f6f6f7',
                                                     borderBottom: '1px solid #e1e3e5',
                                                     padding: '6px 10px', fontWeight: 600,
                                                     fontSize: 13, whiteSpace: 'nowrap' }}>
                                            {v.variant_title || '—'}
                                        </th>
                                    ))}
                                    <th style={{ textAlign: 'right', background: '#f6f6f7',
                                                 borderBottom: '1px solid #e1e3e5',
                                                 padding: '6px 10px', fontWeight: 700,
                                                 fontSize: 13, whiteSpace: 'nowrap' }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '4px 10px', fontSize: 11,
                                                 color: '#6d7175', letterSpacing: 0.04,
                                                 textTransform: 'uppercase', fontWeight: 600,
                                                 borderBottom: '1px solid #f1f2f3' }}>SKU</td>
                                    {variants.map(v => (
                                        <td key={v.sku}
                                            style={{ textAlign: 'center', padding: '4px 10px',
                                                     fontFamily: 'ui-monospace, Menlo, monospace',
                                                     fontSize: 11, color: '#6d7175',
                                                     whiteSpace: 'nowrap',
                                                     borderBottom: '1px solid #f1f2f3' }}>
                                            {v.sku}
                                        </td>
                                    ))}
                                    <td style={{ borderBottom: '1px solid #f1f2f3' }}></td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 10px', fontSize: 11,
                                                 color: '#6d7175', letterSpacing: 0.04,
                                                 textTransform: 'uppercase', fontWeight: 700 }}>Qty</td>
                                    {variants.map(v => (
                                        <td key={v.sku}
                                            style={{ textAlign: 'center', padding: '8px 10px',
                                                     fontSize: 16, fontWeight: 700,
                                                     color: (v.qty > 0) ? '#111418' : '#c4c8cc',
                                                     whiteSpace: 'nowrap' }}>
                                            {(v.qty || 0).toLocaleString()}
                                        </td>
                                    ))}
                                    <td style={{ textAlign: 'right', padding: '8px 10px',
                                                 fontSize: 16, fontWeight: 700,
                                                 color: '#111418', whiteSpace: 'nowrap' }}>
                                        {(p.total || 0).toLocaleString()}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}


/* ---------- Custom CRM Products report ------------------------------- */
// 2026-09-28: reads the nightly inventory snapshot (products whose
// custom.custom_crm metafield names a rep) plus the order cache's per-SKU
// line quantities for units sold in the window — the Team Portal script and
// tables it used to read are retired. Product list is the CURRENT catalog
// snapshot; date range only scopes the sold column and the Club/CRM
// dropdown populations. Location filter is authoritative: users only see
// clubs/products at their authorized shopify_location_ids. v1 = Soccer
// Post store only (the metafield hasn't been rolled out to other stores).
function CustomCRMProductsReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const { user } = useAuth();
    // Franchise users don't get the Shopify admin link on product titles —
    // they shouldn't be jumping into the merchant admin console.
    const canOpenShopifyAdmin = user && !allRolesIn(user, ['franchise']);
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    const [crmFilter,  setCrmFilter]  = useState(searchParams.getAll('crm'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ products: [], available_clubs: [], available_crms: [],
                                       date_from: '', date_to: '',
                                       selected_clubs: [], selected_crms: [] });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('vendor');
    const [sortDir, setSortDir] = useState('asc');
    const [clubOpen, setClubOpen] = useState(false);
    const [clubSearch, setClubSearch] = useState('');
    const [crmOpen, setCrmOpen] = useState(false);
    const [crmSearch, setCrmSearch] = useState('');

    // Persist filters into the URL so they survive refresh / share links.
    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        for (const c of crmFilter)      p.append('crm',  c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, crmFilter, setSearchParams]);

    const load = useCallback(async () => {
        // Gate on a location being picked — mirrors Products Sold By Vendor.
        // The report joins orders + inventory per SKU across every user
        // location; without a scope it would be minutes-long.
        if (locationFilter.length === 0) {
            setData({ products: [], available_clubs: [], available_crms: [],
                      date_from: dateFrom, date_to: dateTo,
                      selected_clubs: clubFilter, selected_crms: crmFilter,
                      requires_location: true });
            return;
        }
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            clubFilter.forEach(c => p.append('clubs', c));
            crmFilter.forEach(c  => p.append('crms',  c));
            const res = await fetchAuth(`/api/reports/custom_crm_products?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter, crmFilter]);
    useEffect(() => { load(); }, [load]);

    const sortedProducts = useMemo(() => {
        const list = (data.products || []).slice();
        list.sort((a, b) => {
            let cmp;
            if (sortKey === 'sold') {
                cmp = (Number(a.total_sold) || 0) - (Number(b.total_sold) || 0);
            } else if (sortKey === 'on_hand') {
                cmp = (Number(a.total_on_hand) || 0) - (Number(b.total_on_hand) || 0);
            } else if (sortKey === 'vendor') {
                cmp = (a.vendor || '').toLowerCase().localeCompare((b.vendor || '').toLowerCase());
                if (cmp === 0) cmp = (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
            } else {
                cmp = (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [data.products, sortKey, sortDir]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir((key === 'sold' || key === 'on_hand') ? 'desc' : 'asc');
        }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const totals = useMemo(() => {
        const products = data.products || [];
        return {
            products: products.length,
            units:    products.reduce((s, p) => s + (Number(p.total_sold) || 0), 0),
            on_hand:  products.reduce((s, p) => s + (Number(p.total_on_hand) || 0), 0),
            crms:     new Set(products.flatMap(p => p.crms || []).filter(Boolean)).size,
        };
    }, [data.products]);

    const availableClubs = data.available_clubs || [];
    const availableCrms  = data.available_crms  || [];
    const filteredClubs = useMemo(() => {
        const q = clubSearch.trim().toLowerCase();
        if (!q) return availableClubs;
        return availableClubs.filter(c => searchMatches(c, q));
    }, [availableClubs, clubSearch]);
    const filteredCrms = useMemo(() => {
        const q = crmSearch.trim().toLowerCase();
        if (!q) return availableCrms;
        return availableCrms.filter(c => searchMatches(c, q));
    }, [availableCrms, crmSearch]);

    const handleExport = useCallback(() => {
        const rows = [['Vendor','Product','CRM(s)','SKU','Variant','Location',
                       'On Hand','Committed','Available','Incoming','Units Sold']];
        for (const p of (data.products || [])) {
            const crmStr = (p.crms || []).join(' + ');
            for (const v of (p.variants || [])) {
                if (!(v.locations || []).length) {
                    rows.push([p.vendor || '', p.title || '', crmStr,
                               v.sku || '', v.variant_title || '',
                               '', 0, 0, 0, 0, Number(v.units_sold || 0)]);
                }
                for (const loc of (v.locations || [])) {
                    rows.push([p.vendor || '', p.title || '', crmStr,
                               v.sku || '', v.variant_title || '',
                               loc.location_name || '',
                               Number(loc.on_hand || 0), Number(loc.committed || 0),
                               Number(loc.available || 0), Number(loc.incoming || 0),
                               Number(v.units_sold || 0)]);
                }
            }
        }
        const label = crmFilter.length === 1
            ? crmFilter[0].replace(/[^\w-]+/g, '_')
            : crmFilter.length > 1 ? `${crmFilter.length}_crms` : 'all_crms';
        downloadCsv(`custom_crm_products_${label}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, crmFilter]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Custom CRM Products" storageKey="custom_crm_products_about_open" linkLabel="About this report">
                        <Text as="p">
                            Products carrying the <code>custom.custom_crm</code> metafield
                            in Shopify, scoped to your locations. Stock (on hand, committed,
                            available, incoming) comes from the nightly inventory snapshot
                            taken at 4:30 AM ET, the same one the Inventory Dashboard reads;
                            its Refresh now button refreshes this report too. Units sold is
                            live from the order cache: every line sold in the date range on
                            orders at your locations, Team Portal and retail alike, cancelled
                            orders excluded. The date range also narrows which clubs appear
                            in the picker. CRM filter matches products where a rep is listed
                            (co-owned products surface for either rep).
                        </Text>
                    </ReportColumnHelp>
                    {data.stock_as_of && (
                        <Text as="p" variant="bodySm" tone="subdued">
                            Stock as of {new Date(data.stock_as_of).toLocaleString()} · units sold are live
                        </Text>
                    )}
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.products || []).length}
                        trailing={(
                            <>
                                <Popover
                                    active={clubOpen}
                                    activator={
                                        <Button onClick={() => setClubOpen(v => !v)}
                                                disclosure={clubOpen ? 'up' : 'down'}
                                                pressed={clubFilter.length > 0}>
                                            {clubFilter.length === 0
                                                ? 'Club'
                                                : clubFilter.length === 1
                                                    ? `Club: ${clubFilter[0]}`
                                                    : `Clubs (${clubFilter.length})`}
                                        </Button>
                                    }
                                    onClose={() => setClubOpen(false)}
                                    preferredAlignment="left"
                                >
                                    <div style={{ minWidth: 320, maxHeight: 460, padding: 10,
                                                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <TextField labelHidden label="Search clubs" type="search"
                                            value={clubSearch} onChange={setClubSearch}
                                            placeholder="Search clubs…" autoComplete="off"
                                            clearButton onClearButtonClick={() => setClubSearch('')} />
                                        <button type="button" className="filter-pill-clear"
                                                disabled={clubFilter.length === 0}
                                                onClick={() => setClubFilter([])}>
                                            Clear
                                        </button>
                                        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                            {filteredClubs.length === 0 ? (
                                                <div style={{ color: 'var(--p-color-text-secondary)',
                                                              fontSize: 13, padding: '8px 4px' }}>
                                                    {availableClubs.length === 0
                                                      ? 'No clubs at your locations in this window.'
                                                      : `No matches for "${clubSearch}"`}
                                                </div>
                                            ) : (
                                                <ChoiceList titleHidden title="Clubs"
                                                    allowMultiple
                                                    choices={filteredClubs.map(c => ({ label: c, value: c }))}
                                                    selected={clubFilter}
                                                    onChange={sel => setClubFilter(sel || [])} />
                                            )}
                                        </div>
                                    </div>
                                </Popover>
                                <Popover
                                    active={crmOpen}
                                    activator={
                                        <Button onClick={() => setCrmOpen(v => !v)}
                                                disclosure={crmOpen ? 'up' : 'down'}
                                                pressed={crmFilter.length > 0}>
                                            {crmFilter.length === 0
                                                ? 'CRM'
                                                : crmFilter.length === 1
                                                    ? `CRM: ${crmFilter[0]}`
                                                    : `CRMs (${crmFilter.length})`}
                                        </Button>
                                    }
                                    onClose={() => setCrmOpen(false)}
                                    preferredAlignment="right"
                                >
                                    <div style={{ minWidth: 280, maxHeight: 460, padding: 10,
                                                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <TextField labelHidden label="Search CRMs" type="search"
                                            value={crmSearch} onChange={setCrmSearch}
                                            placeholder="Search reps…" autoComplete="off"
                                            clearButton onClearButtonClick={() => setCrmSearch('')} />
                                        <button type="button" className="filter-pill-clear"
                                                disabled={crmFilter.length === 0}
                                                onClick={() => setCrmFilter([])}>
                                            Clear
                                        </button>
                                        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                            {filteredCrms.length === 0 ? (
                                                <div style={{ color: 'var(--p-color-text-secondary)',
                                                              fontSize: 13, padding: '8px 4px' }}>
                                                    {availableCrms.length === 0
                                                      ? 'No CRM products at your locations.'
                                                      : `No matches for "${crmSearch}"`}
                                                </div>
                                            ) : (
                                                <ChoiceList titleHidden title="CRMs"
                                                    allowMultiple
                                                    choices={filteredCrms.map(c => ({ label: c, value: c }))}
                                                    selected={crmFilter}
                                                    onChange={sel => setCrmFilter(sel || [])} />
                                            )}
                                        </div>
                                    </div>
                                </Popover>
                            </>
                        )}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {locationFilter.length > 0 && (
                            <Text variant="bodySm" tone="subdued">
                                {totals.products.toLocaleString()} products
                                {' · '}{totals.units.toLocaleString()} sold in window
                                {' · '}{totals.on_hand.toLocaleString()} on-hand
                                {' · '}{totals.crms.toLocaleString()} CRMs
                            </Text>
                        )}
                    </InlineStack>

                    {loading ? null : locationFilter.length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            Pick a location from the filter above to load the report.
                        </div>
                    ) : (data.products || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            {(availableClubs.length === 0)
                                ? 'No clubs shopped at your locations in this window — nothing to show.'
                                : (clubFilter.length > 0)
                                    ? 'No CRM products with sales to the selected clubs in this window.'
                                    : (crmFilter.length > 0)
                                        ? 'No CRM products at your locations for the selected reps.'
                                        : 'No CRM products at your locations.'}
                        </div>
                    ) : (
                        <BlockStack gap="300">
                            <InlineStack gap="200" align="end" blockAlign="center">
                                <Text variant="bodySm" tone="subdued">Sort by</Text>
                                <ButtonGroup variant="segmented">
                                    <Button size="slim" pressed={sortKey === 'vendor'}
                                            onClick={() => handleSort('vendor')}>
                                        Vendor{sortArrow('vendor')}
                                    </Button>
                                    <Button size="slim" pressed={sortKey === 'product'}
                                            onClick={() => handleSort('product')}>
                                        Product{sortArrow('product')}
                                    </Button>
                                    <Button size="slim" pressed={sortKey === 'sold'}
                                            onClick={() => handleSort('sold')}>
                                        Sold{sortArrow('sold')}
                                    </Button>
                                    <Button size="slim" pressed={sortKey === 'on_hand'}
                                            onClick={() => handleSort('on_hand')}>
                                        On hand{sortArrow('on_hand')}
                                    </Button>
                                </ButtonGroup>
                            </InlineStack>
                            {sortedProducts.map((p) => (
                                <CustomCRMProductCard key={p.product_id} product={p}
                                                       canOpenShopifyAdmin={canOpenShopifyAdmin} />
                            ))}
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

// One card per product. Variants nested; per-location inventory nested
// under each variant. CRM chips + vendor above the variant table.
function CustomCRMProductCard({ product, canOpenShopifyAdmin }) {
    const p = product || {};
    const variants = p.variants || [];
    // v1 is soccerpost store only, so the admin subdomain is fixed.
    const adminUrl = p.product_id && !String(p.product_id).startsWith('orphan-title:')
        ? `https://admin.shopify.com/store/soccerpostshop/products/${p.product_id}`
        : null;
    return (
        <div className="pbv-card" style={{
            display: 'grid', gridTemplateColumns: '80px 1fr', gap: 16,
            padding: 14,
            border: '1px solid var(--p-color-border, #e1e3e5)',
            borderRadius: 8, background: '#fff',
        }}>
            <div>
                {p.image_url
                    ? <SmartProductImage src={p.image_url} alt="" size={144}
                           style={{ width: 72, height: 72, objectFit: 'contain',
                                    border: '1px solid #eee', borderRadius: 4,
                                    display: 'block' }} />
                    : <div style={{ width: 72, height: 72, background: '#fafafa',
                                    border: '1px solid #eee', borderRadius: 4,
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', color: '#c4c8cc',
                                    fontSize: 10 }}>no img</div>}
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ marginBottom: 8 }}>
                    <Text variant="headingSm" as="h3">
                        {(canOpenShopifyAdmin && adminUrl) ? (
                            <a href={adminUrl} target="_blank" rel="noopener noreferrer"
                               style={{ color: 'inherit', textDecoration: 'none',
                                        borderBottom: '1px dotted #6d7175' }}
                               title="Open in Shopify admin">
                                {p.title || '—'}
                            </a>
                        ) : (p.title || '—')}
                    </Text>
                    <InlineStack gap="200" blockAlign="center" wrap>
                        {p.vendor ? (
                            <span style={{ fontSize: 11, padding: '2px 8px',
                                           background: '#eef2ff', color: '#3730a3',
                                           borderRadius: 4, fontWeight: 600 }}>
                                {p.vendor}
                            </span>
                        ) : null}
                        {(p.crms || []).map(c => (
                            <span key={c} style={{ fontSize: 11, padding: '2px 8px',
                                           background: '#ecfdf5', color: '#065f46',
                                           borderRadius: 4, fontWeight: 600 }}>
                                {c}
                            </span>
                        ))}
                        <Text variant="bodySm" tone="subdued">
                            <strong>{(p.total_sold || 0).toLocaleString()}</strong> sold in window
                            {' · '}
                            <strong>{(p.total_on_hand || 0).toLocaleString()}</strong> on hand
                        </Text>
                    </InlineStack>
                </div>
                {variants.length === 0 ? (
                    <Text tone="subdued">No variants.</Text>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="data-table"
                               style={{ width: 'auto', minWidth: '100%' }}>
                            <thead>
                                <tr>
                                    {['Variant','SKU','Location','On hand','Committed','Available','Incoming','Sold'].map((h, i) => (
                                        <th key={h}
                                            style={{ textAlign: i >= 3 ? 'right' : 'left',
                                                     background: '#f6f6f7',
                                                     borderBottom: '1px solid #e1e3e5',
                                                     padding: '6px 10px', fontSize: 11,
                                                     color: '#6d7175', letterSpacing: 0.04,
                                                     textTransform: 'uppercase',
                                                     fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {variants.flatMap(v => {
                                    const rows = [];
                                    const locs = (v.locations && v.locations.length)
                                        ? v.locations
                                        : [{ location_name: '—', on_hand: 0, committed: 0,
                                             available: 0, incoming: 0 }];
                                    locs.forEach((loc, idx) => {
                                        rows.push(
                                            <tr key={`${v.sku}-${loc.location_id || idx}`}>
                                                <td style={{ padding: '6px 10px',
                                                             borderBottom: '1px solid #f1f2f3',
                                                             fontWeight: 600 }}>
                                                    {idx === 0 ? (v.variant_title || '—') : ''}
                                                </td>
                                                <td style={{ padding: '6px 10px',
                                                             fontFamily: 'ui-monospace, Menlo, monospace',
                                                             fontSize: 12, color: '#6d7175',
                                                             borderBottom: '1px solid #f1f2f3',
                                                             whiteSpace: 'nowrap' }}>
                                                    {idx === 0 ? (v.sku || '') : ''}
                                                </td>
                                                <td style={{ padding: '6px 10px',
                                                             borderBottom: '1px solid #f1f2f3' }}>
                                                    {loc.location_name || '—'}
                                                </td>
                                                {['on_hand','committed','available','incoming'].map(k => (
                                                    <td key={k}
                                                        style={{ textAlign: 'right',
                                                                 padding: '6px 10px',
                                                                 borderBottom: '1px solid #f1f2f3',
                                                                 color: (loc[k] || 0) > 0 ? '#111418' : '#c4c8cc' }}>
                                                        {(loc[k] || 0).toLocaleString()}
                                                    </td>
                                                ))}
                                                <td style={{ textAlign: 'right',
                                                             padding: '6px 10px',
                                                             borderBottom: '1px solid #f1f2f3',
                                                             fontWeight: 700,
                                                             color: (v.units_sold || 0) > 0 ? '#111418' : '#c4c8cc' }}>
                                                    {idx === 0 ? (v.units_sold || 0).toLocaleString() : ''}
                                                </td>
                                            </tr>
                                        );
                                    });
                                    return rows;
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}


/* ---------- Jumbled Bundles report ----------------------------------- */
// Bundles (lineItemGroup) whose child items are split across 2+ FOs on
// orders that are still fully unfulfilled. One row per (order, group,
// wrong-location) — a bundle split across 3 locs → 2 rows, one per
// outlier. Data is pre-computed at cache time in
// operations_{sk}_order_cache.jumbled_bundles_json, so the endpoint is
// pure SQL; no live Shopify calls at read time.
function JumbledBundlesReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || '2025-01-01');
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter,     setClubFilter]     = useState(searchParams.getAll('club'));
    const [expLocFilter,   setExpLocFilter]   = useState(searchParams.getAll('exploc'));
    const [expLocOpen,     setExpLocOpen]     = useState(false);
    const [expLocSearch,   setExpLocSearch]   = useState('');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], available_clubs: [],
                                       available_expected_locations: [],
                                       orders: 0, date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('placed_d');
    const [sortDir, setSortDir] = useState('desc');
    // Auto Fix selection + workflow state
    const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());
    const [fixModalOpen, setFixModalOpen] = useState(false);
    const [fixSubmitting, setFixSubmitting] = useState(false);
    const [fixResults, setFixResults] = useState(null);  // {results:[...]} after submit

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        for (const e of expLocFilter)   p.append('exploc', e);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, expLocFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            clubFilter.forEach(c => p.append('clubs', c));
            expLocFilter.forEach(e => p.append('expected_locations', e));
            const res = await fetchAuth(`/api/reports/jumbled_bundles?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter, expLocFilter]);

    useEffect(() => { load(); }, [load]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'wrong_item_count' ? 'desc' : 'asc');
        }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const sortedRows = useMemo(() => {
        const rows = (data.rows || []).slice();
        rows.sort((a, b) => {
            let cmp;
            if (sortKey === 'wrong_item_count') {
                cmp = (Number(a.wrong_item_count) || 0) - (Number(b.wrong_item_count) || 0);
            } else if (sortKey === 'wrong_locations') {
                cmp = (a.wrong_locations || []).join(', ')
                        .localeCompare((b.wrong_locations || []).join(', '));
            } else if (sortKey === 'bundle_label') {
                // Kept for URL back-compat; sort by first bundle's label now.
                cmp = ((a.bundles || [])[0]?.label || '')
                        .localeCompare(((b.bundles || [])[0]?.label || ''));
            } else {
                cmp = String(a[sortKey] || '').localeCompare(String(b[sortKey] || ''));
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]);

    const rowKey = (r) => `${r.store_key}:${r.order_id}`;

    // Drop stale selections when the underlying rows change (filter/date).
    useEffect(() => {
        if (selectedRowKeys.size === 0) return;
        const valid = new Set(sortedRows.map(rowKey));
        let changed = false;
        const next = new Set();
        for (const k of selectedRowKeys) {
            if (valid.has(k)) next.add(k);
            else changed = true;
        }
        if (changed) setSelectedRowKeys(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedRows]);

    const allSelected = sortedRows.length > 0 && selectedRowKeys.size === sortedRows.length;
    const someSelected = selectedRowKeys.size > 0 && !allSelected;
    const toggleAll = () => {
        if (allSelected) setSelectedRowKeys(new Set());
        else setSelectedRowKeys(new Set(sortedRows.map(rowKey)));
    };
    // Anchor index for shift-click range selection. Not state — a ref so
    // it doesn't trigger re-renders on every click.
    const shiftAnchorRef = useRef(null);
    const toggleRow = (k, idx = null, shiftKey = false) => {
        setSelectedRowKeys(prev => {
            const next = new Set(prev);
            if (shiftKey && shiftAnchorRef.current != null && idx != null) {
                // Range selection: apply the target state of THIS row (its
                // toggle result) to every row from anchor..idx inclusive.
                const targetSelected = !next.has(k);
                const [lo, hi] = shiftAnchorRef.current <= idx
                    ? [shiftAnchorRef.current, idx]
                    : [idx, shiftAnchorRef.current];
                for (let i = lo; i <= hi; i++) {
                    const rk = rowKey(sortedRows[i]);
                    if (targetSelected) next.add(rk);
                    else next.delete(rk);
                }
            } else {
                if (next.has(k)) next.delete(k); else next.add(k);
            }
            return next;
        });
        // Update the anchor to the current row on every click (shift or not)
        // so the next shift-click always ranges from the most recent click.
        if (idx != null) shiftAnchorRef.current = idx;
    };
    const selectedOrderRows = useMemo(
        () => sortedRows.filter(r => selectedRowKeys.has(rowKey(r))),
        [sortedRows, selectedRowKeys],
    );

    const handleExport = useCallback(() => {
        const csvRows = [['Store', 'Order', 'Placed', 'Customer', 'Club',
                          'Expected location', 'Bundles',
                          'Wrong locations',
                          'Wrong items (qty × sku — title @ wrong location, bundle)',
                          'Wrong item count']];
        for (const r of sortedRows) {
            const bundlesStr = (r.bundles || [])
                .map(b => b.player_suffix ? `${b.label} (${b.player_suffix})` : b.label)
                .join('; ');
            const itemsStr = (r.wrong_items || [])
                .map(i => `${i.quantity}× ${i.sku || '(no sku)'} — ${i.title || ''} @ ${i.wrong_location || ''}${i.bundle_label ? ` [bundle: ${i.bundle_label}]` : ''}`)
                .join('; ');
            csvRows.push([
                r.store_key, r.order_name, r.placed_d,
                r.customer_name, r.club,
                r.expected_location,
                bundlesStr,
                (r.wrong_locations || []).join(', '),
                itemsStr,
                r.wrong_item_count,
            ]);
        }
        downloadCsv(`jumbled_bundles_${dateFrom}_to_${dateTo}.csv`, csvRows);
    }, [sortedRows, dateFrom, dateTo]);

    const submitFix = useCallback(async () => {
        if (selectedOrderRows.length === 0) return;
        setFixSubmitting(true); setFixResults(null);
        try {
            const res = await fetchAuth('/api/reports/jumbled_bundles/fix', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orders: selectedOrderRows.map(r => ({
                        store_key: r.store_key,
                        order_id:  r.order_id,
                    })),
                }),
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErr(j.detail || `HTTP ${res.status}`);
                setFixSubmitting(false);
                return;
            }
            setFixResults(j);
            // Clear selection for the orders that fully succeeded.
            const fullyOk = new Set(
                (j.results || [])
                    .filter(r => (r.moves || []).length > 0
                                 && (r.moves || []).every(m => m.ok))
                    .map(r => `${r.store_key}:${r.order_id}`)
            );
            setSelectedRowKeys(prev => {
                const next = new Set(prev);
                for (const k of fullyOk) next.delete(k);
                return next;
            });
            // Refresh the report so fixed orders drop out.
            load();
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setFixSubmitting(false);
        }
    }, [selectedOrderRows, fetchAuth, load]);

    // "Stock in destination location, and re-fix" — retries just the
    // moves that failed with a stocking error, after forcibly activating
    // + bumping on_hand at the destination. Detects the failure by
    // string-match on the Shopify error text. Reuses submitFix's flow.
    const STOCK_ERROR_RE = /stocked at the new location/i;
    const stuckMoves = useMemo(() => {
        const rs = (fixResults?.results) || [];
        const out = [];
        for (const r of rs) {
            for (const m of (r.moves || [])) {
                if (!m.ok && m.error && STOCK_ERROR_RE.test(m.error)) {
                    out.push({ store_key: r.store_key, order_id: r.order_id, fo_id: m.fo_id });
                }
            }
        }
        return out;
    }, [fixResults]);

    const submitForceStockAndFix = useCallback(async () => {
        if (stuckMoves.length === 0) return;
        setFixSubmitting(true);
        try {
            const res = await fetchAuth('/api/reports/jumbled_bundles/force_stock_and_fix', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ moves: stuckMoves }),
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErr(j.detail || `HTTP ${res.status}`);
                setFixSubmitting(false);
                return;
            }
            // Merge the retry results into the existing fixResults so the
            // modal shows the second attempt inline. Match by (order_id,
            // fo_id) — override the original move.ok/error with the new
            // ones so retried moves render as ✓ or with the fresh error.
            const retryByKey = new Map();
            for (const r of (j.results || [])) {
                for (const m of (r.moves || [])) {
                    retryByKey.set(`${r.order_id}::${m.fo_id}`, m);
                }
            }
            setFixResults(prev => {
                if (!prev) return j;
                return {
                    ...prev,
                    results: (prev.results || []).map(r => ({
                        ...r,
                        moves: (r.moves || []).map(m => {
                            const retry = retryByKey.get(`${r.order_id}::${m.fo_id}`);
                            if (!retry) return m;
                            return {
                                ...m,
                                ok: retry.ok,
                                error: retry.error,
                                to_location: retry.to_location || m.to_location,
                                force_stock: retry.force_stock,
                            };
                        }),
                    })),
                };
            });
            // Also drop any orders whose moves are now all-ok from the
            // selection + refresh the report so cleared orders drop out.
            const okOrders = new Set(
                (j.results || [])
                    .filter(r => (r.moves || []).length > 0
                                 && (r.moves || []).every(m => m.ok))
                    .map(r => `${r.store_key}:${r.order_id}`)
            );
            setSelectedRowKeys(prev => {
                const next = new Set(prev);
                for (const k of okOrders) next.delete(k);
                return next;
            });
            load();
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setFixSubmitting(false);
        }
    }, [stuckMoves, fetchAuth, load]);

    const th = (key, label, align = 'left', extraStyle) => (
        <th onClick={() => handleSort(key)}
            style={{ cursor: 'pointer', userSelect: 'none',
                     textAlign: align, padding: '6px 10px',
                     borderBottom: '1px solid #dfe3e8',
                     whiteSpace: 'nowrap', background: '#fafbfb',
                     ...(extraStyle || {}) }}>
            {label}{sortArrow(key)}
        </th>
    );

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Jumbled Bundles" storageKey="jumbled_bundles_about_open" linkLabel="About this report">
                        <Text as="p">
                            Orders with a bundle whose child items are split
                            across 2 or more fulfillment orders. Two split
                            kinds appear: <em>cross-location</em> (items
                            ended up at a different Shopify location than
                            the order's expected fulfillment location —
                            Auto Fix routes them back) and{' '}
                            <em>same-location</em> (bundle split across
                            multiple FOs at the correct location — no
                            routing to fix; flagged so the operator knows
                            to piece it together). Partially-fulfilled
                            orders are included when the jumbled bundle
                            remains in the still-open FOs (marked{' '}
                            <code>(partial)</code>). Cancelled + fully-
                            done orders are excluded.
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                        trailing={(() => {
                            const avail = data.available_expected_locations || [];
                            const q = (expLocSearch || '').trim().toLowerCase();
                            const filtered = q ? avail.filter(x => searchMatches(x, q)) : avail;
                            return (
                                <Popover
                                    active={expLocOpen}
                                    activator={
                                        <Button disclosure onClick={() => setExpLocOpen(o => !o)}
                                                pressed={expLocFilter.length > 0}>
                                            Expected location{expLocFilter.length ? ` (${expLocFilter.length})` : ''}
                                        </Button>
                                    }
                                    onClose={() => setExpLocOpen(false)}
                                    preferredAlignment="left"
                                >
                                    <div style={{ minWidth: 280, maxHeight: 420, padding: 10,
                                                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <TextField labelHidden label="Search expected locations" type="search"
                                            value={expLocSearch} onChange={setExpLocSearch}
                                            placeholder="Search expected locations…" autoComplete="off"
                                            clearButton onClearButtonClick={() => setExpLocSearch('')} />
                                        <button type="button" className="filter-pill-clear"
                                                disabled={expLocFilter.length === 0}
                                                onClick={() => setExpLocFilter([])}>Clear</button>
                                        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                            {filtered.length === 0 ? (
                                                <div style={{ color: 'var(--p-color-text-secondary)',
                                                              fontSize: 13, padding: '8px 4px' }}>
                                                    {avail.length === 0
                                                      ? 'No expected locations in window.'
                                                      : `No matches for "${expLocSearch}"`}
                                                </div>
                                            ) : (
                                                <ChoiceList titleHidden title="Expected locations" allowMultiple
                                                    choices={filtered.map(x => ({ label: x, value: x }))}
                                                    selected={expLocFilter}
                                                    onChange={setExpLocFilter} />
                                            )}
                                        </div>
                                    </div>
                                </Popover>
                            );
                        })()}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap blockAlign="center">
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            {(data.orders || 0).toLocaleString()} affected orders
                            {' '}· {(data.rows || []).length.toLocaleString()} outlier rows
                        </Text>
                        {selectedRowKeys.size > 0 && (
                            <>
                                <Text variant="bodySm">
                                    <strong>{selectedRowKeys.size}</strong> selected
                                </Text>
                                <Button variant="primary" tone="critical"
                                        onClick={() => { setFixResults(null); setFixModalOpen(true); }}>
                                    Auto Fix ({selectedRowKeys.size})
                                </Button>
                                <Button onClick={() => setSelectedRowKeys(new Set())}>Clear selection</Button>
                            </>
                        )}
                    </InlineStack>

                    {loading ? null : sortedRows.length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No jumbled bundles found in this window.
                        </div>
                    ) : (
                        // Wrapper owns BOTH scroll axes (maxHeight caps
                        // vertical, min-content on the table drives
                        // horizontal). This anchors position:sticky
                        // inside the wrapper so both the header row and
                        // the Order column stay pinned as the user
                        // scrolls. Per CLAUDE.md rule: overflow:hidden
                        // on any ancestor would break sticky — Polaris
                        // Card is safe. `overscrollBehavior: contain`
                        // stops the page scrolling once the wrapper
                        // hits its end.
                        <div style={{ overflow: 'auto', maxHeight: '70vh',
                                      overscrollBehavior: 'contain' }}>
                            <table style={{ width: '100%', minWidth: 1100,
                                            borderCollapse: 'separate',
                                            borderSpacing: 0,
                                            fontSize: 13 }}>
                                <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                                    <tr>
                                        <th style={{ padding: '6px 10px', textAlign: 'center',
                                                     borderBottom: '1px solid #dfe3e8',
                                                     background: '#fafbfb', whiteSpace: 'nowrap',
                                                     position: 'sticky', left: 0, zIndex: 4,
                                                     width: 36 }}>
                                            <input type="checkbox" aria-label="Select all rows"
                                                   checked={allSelected}
                                                   ref={el => { if (el) el.indeterminate = someSelected; }}
                                                   onChange={toggleAll} />
                                        </th>
                                        {th('order_name',        'Order', 'left', { position: 'sticky', left: 36, zIndex: 4 })}
                                        {th('placed_d',          'Placed')}
                                        {th('customer_name',     'Customer')}
                                        {th('club',              'Club')}
                                        {th('expected_location', 'Expected location')}
                                        <th style={{ padding: '6px 10px', textAlign: 'left',
                                                     borderBottom: '1px solid #dfe3e8',
                                                     background: '#fafbfb', whiteSpace: 'nowrap' }}>
                                            Bundle(s)
                                        </th>
                                        {th('wrong_locations',   'Wrong location(s)')}
                                        <th style={{ padding: '6px 10px', textAlign: 'left',
                                                     borderBottom: '1px solid #dfe3e8',
                                                     background: '#fafbfb', whiteSpace: 'nowrap' }}>
                                            Wrong items
                                        </th>
                                        {th('wrong_item_count',  'Qty', 'right')}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map((r, idx) => {
                                        const k = rowKey(r);
                                        const isSel = selectedRowKeys.has(k);
                                        return (
                                        <tr key={`${r.store_key}:${r.order_id}:${idx}`}
                                            style={{ borderBottom: '1px solid #f1f2f3',
                                                     background: isSel ? '#f6f8fa' : undefined }}>
                                            <td style={{ padding: '6px 10px', textAlign: 'center',
                                                         position: 'sticky', left: 0, zIndex: 2,
                                                         background: isSel ? '#f6f8fa' : '#fff',
                                                         borderRight: '1px solid #f1f2f3', width: 36 }}>
                                                {/* Same-location splits have no cross-loc FOs
                                                    to reassign, so Auto Fix would be a no-op —
                                                    disable the checkbox on those rows. */}
                                                <input type="checkbox"
                                                       aria-label={`Select ${r.order_name || r.order_id}`}
                                                       checked={isSel}
                                                       disabled={r.split_kind === 'same_location'}
                                                       title={r.split_kind === 'same_location'
                                                              ? "Same-location split — Auto Fix doesn't apply."
                                                              : undefined}
                                                       // Shift-click supports range selection —
                                                       // click one row, shift-click another to
                                                       // select every row between them.
                                                       onClick={(e) => toggleRow(k, idx, e.shiftKey)}
                                                       onChange={() => {}} />
                                            </td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap',
                                                         position: 'sticky', left: 36, zIndex: 2,
                                                         background: isSel ? '#f6f8fa' : '#fff',
                                                         borderRight: '1px solid #f1f2f3' }}>
                                                <Link to={`/store/${r.store_key || 'soccerpost'}/orders/${r.order_id}`}>
                                                    {r.order_name || r.order_id}
                                                </Link>
                                                {r.is_partial && (
                                                    <span style={{ color: '#8a4b00', fontSize: 11,
                                                                   marginLeft: 6 }}
                                                          title="This order has already been partially fulfilled — the jumbled bundle is in the remaining open FOs.">
                                                        (partial)
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                                {r.placed_d || ''}
                                            </td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{r.customer_name || ''}</td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{r.club || ''}</td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                                {r.expected_location || ''}
                                            </td>
                                            <td style={{ padding: '6px 10px' }}>
                                                {(r.bundles || []).map((b, i) => (
                                                    <div key={i} style={{ whiteSpace: 'nowrap' }}>
                                                        {b.label}
                                                        {b.player_suffix && (
                                                            <span style={{ color: '#6d7175', fontSize: 12 }}>
                                                                {' — '}{b.player_suffix}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </td>
                                            <td style={{ padding: '6px 10px',
                                                         color: '#bf0711', fontWeight: 500 }}>
                                                {r.split_kind === 'same_location' ? (
                                                    // Same-location split: no "wrong loc" to show;
                                                    // report the split-count at the expected loc.
                                                    (r.same_location_bundles || []).map((b, i) => (
                                                        <div key={i} style={{ whiteSpace: 'nowrap',
                                                                              color: '#8a4b00' }}>
                                                            Split across {b.fo_count} FOs at {r.expected_location}
                                                        </div>
                                                    ))
                                                ) : (
                                                    (r.wrong_locations || []).map((w, i) => (
                                                        <div key={i} style={{ whiteSpace: 'nowrap' }}>{w}</div>
                                                    ))
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 10px' }}>
                                                {r.split_kind === 'same_location' ? (
                                                    <span style={{ color: '#6d7175', fontSize: 12 }}>
                                                        Same-location split — no cross-location items to reroute.
                                                    </span>
                                                ) : (
                                                    (r.wrong_items || []).map((it, i) => (
                                                        <div key={i} style={{ whiteSpace: 'nowrap' }}>
                                                            <strong>{it.quantity}×</strong>{' '}
                                                            <code style={{ fontSize: 11 }}>{it.sku || '(no sku)'}</code>
                                                            {' — '}{it.title || ''}
                                                            {it.wrong_location && (
                                                                <span style={{ color: '#6d7175', fontSize: 12 }}>
                                                                    {' → '}{it.wrong_location}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right',
                                                         whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                {r.split_kind === 'same_location' ? '—' : r.wrong_item_count}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>

            {/* Auto Fix confirmation + results modal */}
            {fixModalOpen && (() => {
                const totalMoves = selectedOrderRows.reduce(
                    (s, r) => s + (r.wrong_locations || []).length, 0);
                const close = () => {
                    if (fixSubmitting) return;
                    setFixModalOpen(false);
                    setFixResults(null);
                };
                const showingResults = !!fixResults;
                return (
                    <Modal open
                        title={showingResults ? 'Auto Fix results' :
                               `Auto Fix ${selectedOrderRows.length} order${selectedOrderRows.length === 1 ? '' : 's'}?`}
                        onClose={close}
                        primaryAction={showingResults
                            ? { content: 'Done', onAction: close }
                            : { content: `Fix ${selectedOrderRows.length} order${selectedOrderRows.length === 1 ? '' : 's'}`,
                                onAction: submitFix, loading: fixSubmitting,
                                destructive: true }}
                        secondaryActions={showingResults ? undefined : [
                            { content: 'Cancel', onAction: close, disabled: fixSubmitting },
                        ]}
                    >
                        <Modal.Section>
                            {!showingResults ? (
                                <BlockStack gap="300">
                                    <Text>
                                        For each selected order, every fulfillment order
                                        currently at a wrong location will be reassigned to
                                        the order's expected fulfillment location (from the
                                        <code> _soccer_fulfillment_location</code> attribute).
                                    </Text>
                                    <Text tone="subdued">
                                        This is a Shopify <strong>Change Location</strong> —
                                        FO-move only. It shifts committed inventory but does
                                        NOT move physical stock counts between locations.
                                        Assumes the goods are already at (or being sent to)
                                        the expected location.
                                    </Text>
                                    <Text>
                                        Planned: <strong>{totalMoves}</strong> FO move{totalMoves === 1 ? '' : 's'}
                                        {' '}across <strong>{selectedOrderRows.length}</strong> order{selectedOrderRows.length === 1 ? '' : 's'}.
                                    </Text>
                                    <div style={{ maxHeight: 260, overflowY: 'auto',
                                                  border: '1px solid #dfe3e8', borderRadius: 6,
                                                  padding: 8, background: '#fafbfb', fontSize: 12 }}>
                                        {selectedOrderRows.map(r => (
                                            <div key={rowKey(r)} style={{ marginBottom: 8 }}>
                                                <div><strong>{r.order_name}</strong> → expected <strong>{r.expected_location}</strong></div>
                                                {(r.wrong_locations || []).map((w, i) => (
                                                    <div key={i} style={{ color: '#6d7175', marginLeft: 12 }}>
                                                        move FO at <em>{w}</em> → {r.expected_location}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </BlockStack>
                            ) : (
                                <BlockStack gap="300">
                                    {(() => {
                                        const rs = fixResults?.results || [];
                                        const okCount = rs.filter(r =>
                                            !r.error && (r.moves || []).length > 0 &&
                                            (r.moves || []).every(m => m.ok)).length;
                                        const partialCount = rs.filter(r =>
                                            (r.moves || []).some(m => m.ok) &&
                                            (r.moves || []).some(m => !m.ok)).length;
                                        const failCount = rs.filter(r =>
                                            r.error || ((r.moves || []).length > 0 &&
                                                        (r.moves || []).every(m => !m.ok))).length;
                                        const noopCount = rs.filter(r =>
                                            !r.error && (r.moves || []).length === 0).length;
                                        return (
                                            <>
                                                <Text>
                                                    <strong>{okCount}</strong> fully fixed
                                                    {partialCount > 0 && <>, <strong>{partialCount}</strong> partial</>}
                                                    {failCount > 0 && <>, <strong>{failCount}</strong> failed</>}
                                                    {noopCount > 0 && <>, <strong>{noopCount}</strong> already OK</>}
                                                    .
                                                </Text>
                                                {stuckMoves.length > 0 && (
                                                    <div style={{ padding: 10, border: '1px solid #fbe0d0',
                                                                  background: '#fff5f0', borderRadius: 6 }}>
                                                        <BlockStack gap="200">
                                                            <Text>
                                                                <strong>{stuckMoves.length}</strong> move{stuckMoves.length === 1 ? '' : 's'}
                                                                {' '}failed because the item wasn't stocked at the destination location.
                                                            </Text>
                                                            <Text tone="subdued">
                                                                This will activate the item at the destination, bump on-hand
                                                                by the FO's remaining quantity, then re-attempt the move.
                                                            </Text>
                                                            <div>
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={submitForceStockAndFix}
                                                                    loading={fixSubmitting}
                                                                    disabled={fixSubmitting}
                                                                >
                                                                    Stock in destination location, and re-fix ({stuckMoves.length})
                                                                </Button>
                                                            </div>
                                                        </BlockStack>
                                                    </div>
                                                )}
                                                <div style={{ maxHeight: 320, overflowY: 'auto',
                                                              border: '1px solid #dfe3e8', borderRadius: 6,
                                                              padding: 8, background: '#fafbfb', fontSize: 12 }}>
                                                    {rs.map((r, i) => (
                                                        <div key={i} style={{ marginBottom: 8 }}>
                                                            <div>
                                                                <strong>{r.order_name || r.order_id}</strong>
                                                                {r.expected_location && <> → {r.expected_location}</>}
                                                                {r.error && <span style={{ color: '#bf0711' }}> — {r.error}</span>}
                                                                {r.note  && <span style={{ color: '#6d7175' }}> — {r.note}</span>}
                                                            </div>
                                                            {(r.moves || []).map((m, j) => (
                                                                <div key={j} style={{ marginLeft: 12,
                                                                                        color: m.ok ? '#008060' : '#bf0711' }}>
                                                                    {m.ok ? '✓' : '✗'} {m.from_location} → {m.to_location}
                                                                    {m.error && <span> — {m.error}</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </BlockStack>
                            )}
                        </Modal.Section>
                    </Modal>
                );
            })()}
        </BlockStack>
    );
}


/* ---------- Split Team Fulfillment report ---------------------------- */
// Sibling of Jumbled Bundles (2026-09-06): orders whose TEAM goods were
// fulfilled / are pending from two or more origin locations (the location
// that packed a transferred FO, else the location it sits at now). One row
// per order; the "Origins" column lists every source with its item count
// and status. Data is pre-computed at cache time in
// operations_{sk}_order_cache.split_team_json — pure SQL at read time.
function SplitTeamFulfillmentReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || '2025-01-01');
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter,     setClubFilter]     = useState(searchParams.getAll('club'));
    const [expLocFilter,   setExpLocFilter]   = useState(searchParams.getAll('exploc'));
    const [expLocOpen,     setExpLocOpen]     = useState(false);
    const [expLocSearch,   setExpLocSearch]   = useState('');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], available_clubs: [],
                                       available_expected_locations: [],
                                       orders: 0, date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('placed_d');
    const [sortDir, setSortDir] = useState('desc');
    // Auto Fix selection + workflow state
    const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());
    const [fixModalOpen, setFixModalOpen] = useState(false);
    const [fixSubmitting, setFixSubmitting] = useState(false);
    const [fixResults, setFixResults] = useState(null);  // {results:[...]} after submit

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        for (const e of expLocFilter)   p.append('exploc', e);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, expLocFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            clubFilter.forEach(c => p.append('clubs', c));
            expLocFilter.forEach(e => p.append('expected_locations', e));
            const res = await fetchAuth(`/api/reports/split_team_fulfillment?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter, expLocFilter]);

    useEffect(() => { load(); }, [load]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'wrong_item_count' ? 'desc' : 'asc');
        }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const sortedRows = useMemo(() => {
        const rows = (data.rows || []).slice();
        rows.sort((a, b) => {
            let cmp;
            if (sortKey === 'wrong_item_count') {
                cmp = (Number(a.wrong_item_count) || 0) - (Number(b.wrong_item_count) || 0);
            } else if (sortKey === 'wrong_locations') {
                cmp = (a.wrong_locations || []).join(', ')
                        .localeCompare((b.wrong_locations || []).join(', '));
            } else if (sortKey === 'bundle_label') {
                // Kept for URL back-compat; sort by first bundle's label now.
                cmp = ((a.bundles || [])[0]?.label || '')
                        .localeCompare(((b.bundles || [])[0]?.label || ''));
            } else {
                cmp = String(a[sortKey] || '').localeCompare(String(b[sortKey] || ''));
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]);

    const rowKey = (r) => `${r.store_key}:${r.order_id}`;

    // Drop stale selections when the underlying rows change (filter/date).
    useEffect(() => {
        if (selectedRowKeys.size === 0) return;
        const valid = new Set(sortedRows.map(rowKey));
        let changed = false;
        const next = new Set();
        for (const k of selectedRowKeys) {
            if (valid.has(k)) next.add(k);
            else changed = true;
        }
        if (changed) setSelectedRowKeys(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedRows]);

    const allSelected = sortedRows.length > 0 && selectedRowKeys.size === sortedRows.length;
    const someSelected = selectedRowKeys.size > 0 && !allSelected;
    const toggleAll = () => {
        if (allSelected) setSelectedRowKeys(new Set());
        else setSelectedRowKeys(new Set(sortedRows.map(rowKey)));
    };
    // Anchor index for shift-click range selection. Not state — a ref so
    // it doesn't trigger re-renders on every click.
    const shiftAnchorRef = useRef(null);
    const toggleRow = (k, idx = null, shiftKey = false) => {
        setSelectedRowKeys(prev => {
            const next = new Set(prev);
            if (shiftKey && shiftAnchorRef.current != null && idx != null) {
                // Range selection: apply the target state of THIS row (its
                // toggle result) to every row from anchor..idx inclusive.
                const targetSelected = !next.has(k);
                const [lo, hi] = shiftAnchorRef.current <= idx
                    ? [shiftAnchorRef.current, idx]
                    : [idx, shiftAnchorRef.current];
                for (let i = lo; i <= hi; i++) {
                    const rk = rowKey(sortedRows[i]);
                    if (targetSelected) next.add(rk);
                    else next.delete(rk);
                }
            } else {
                if (next.has(k)) next.delete(k); else next.add(k);
            }
            return next;
        });
        // Update the anchor to the current row on every click (shift or not)
        // so the next shift-click always ranges from the most recent click.
        if (idx != null) shiftAnchorRef.current = idx;
    };
    const selectedOrderRows = useMemo(
        () => sortedRows.filter(r => selectedRowKeys.has(rowKey(r))),
        [sortedRows, selectedRowKeys],
    );

    const handleExport = useCallback(() => {
        const csvRows = [['Store', 'Order', 'Placed', 'Customer', 'Club',
                          'Expected location', 'Origins',
                          'Wrong locations',
                          'Wrong items (qty × sku — title @ wrong origin, status)',
                          'Wrong item count']];
        for (const r of sortedRows) {
            const bundlesStr = (r.bundles || [])
                .map(b => b.player_suffix ? `${b.label} (${b.player_suffix})` : b.label)
                .join('; ');
            const itemsStr = (r.wrong_items || [])
                .map(i => `${i.quantity}× ${i.sku || '(no sku)'} — ${i.title || ''} @ ${i.wrong_location || ''}${i.bundle_label ? ` [${i.bundle_label}]` : ''}`)
                .join('; ');
            csvRows.push([
                r.store_key, r.order_name, r.placed_d,
                r.customer_name, r.club,
                r.expected_location,
                bundlesStr,
                (r.wrong_locations || []).join(', '),
                itemsStr,
                r.wrong_item_count,
            ]);
        }
        downloadCsv(`split_team_fulfillment_${dateFrom}_to_${dateTo}.csv`, csvRows);
    }, [sortedRows, dateFrom, dateTo]);

    const submitFix = useCallback(async () => {
        if (selectedOrderRows.length === 0) return;
        setFixSubmitting(true); setFixResults(null);
        try {
            const res = await fetchAuth('/api/reports/split_team_fulfillment/fix', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orders: selectedOrderRows.map(r => ({
                        store_key: r.store_key,
                        order_id:  r.order_id,
                    })),
                }),
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErr(j.detail || `HTTP ${res.status}`);
                setFixSubmitting(false);
                return;
            }
            setFixResults(j);
            // Clear selection for the orders that fully succeeded.
            const fullyOk = new Set(
                (j.results || [])
                    .filter(r => (r.moves || []).length > 0
                                 && (r.moves || []).every(m => m.ok))
                    .map(r => `${r.store_key}:${r.order_id}`)
            );
            setSelectedRowKeys(prev => {
                const next = new Set(prev);
                for (const k of fullyOk) next.delete(k);
                return next;
            });
            // Refresh the report so fixed orders drop out.
            load();
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setFixSubmitting(false);
        }
    }, [selectedOrderRows, fetchAuth, load]);

    // "Stock in destination location, and re-fix" — retries just the
    // moves that failed with a stocking error, after forcibly activating
    // + bumping on_hand at the destination. Detects the failure by
    // string-match on the Shopify error text. Reuses submitFix's flow.
    const STOCK_ERROR_RE = /stocked at the new location/i;
    const stuckMoves = useMemo(() => {
        const rs = (fixResults?.results) || [];
        const out = [];
        for (const r of rs) {
            for (const m of (r.moves || [])) {
                if (!m.ok && m.error && STOCK_ERROR_RE.test(m.error)) {
                    out.push({ store_key: r.store_key, order_id: r.order_id, fo_id: m.fo_id });
                }
            }
        }
        return out;
    }, [fixResults]);

    const submitForceStockAndFix = useCallback(async () => {
        if (stuckMoves.length === 0) return;
        setFixSubmitting(true);
        try {
            const res = await fetchAuth('/api/reports/split_team_fulfillment/force_stock_and_fix', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ moves: stuckMoves }),
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErr(j.detail || `HTTP ${res.status}`);
                setFixSubmitting(false);
                return;
            }
            // Merge the retry results into the existing fixResults so the
            // modal shows the second attempt inline. Match by (order_id,
            // fo_id) — override the original move.ok/error with the new
            // ones so retried moves render as ✓ or with the fresh error.
            const retryByKey = new Map();
            for (const r of (j.results || [])) {
                for (const m of (r.moves || [])) {
                    retryByKey.set(`${r.order_id}::${m.fo_id}`, m);
                }
            }
            setFixResults(prev => {
                if (!prev) return j;
                return {
                    ...prev,
                    results: (prev.results || []).map(r => ({
                        ...r,
                        moves: (r.moves || []).map(m => {
                            const retry = retryByKey.get(`${r.order_id}::${m.fo_id}`);
                            if (!retry) return m;
                            return {
                                ...m,
                                ok: retry.ok,
                                error: retry.error,
                                to_location: retry.to_location || m.to_location,
                                force_stock: retry.force_stock,
                            };
                        }),
                    })),
                };
            });
            // Also drop any orders whose moves are now all-ok from the
            // selection + refresh the report so cleared orders drop out.
            const okOrders = new Set(
                (j.results || [])
                    .filter(r => (r.moves || []).length > 0
                                 && (r.moves || []).every(m => m.ok))
                    .map(r => `${r.store_key}:${r.order_id}`)
            );
            setSelectedRowKeys(prev => {
                const next = new Set(prev);
                for (const k of okOrders) next.delete(k);
                return next;
            });
            load();
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setFixSubmitting(false);
        }
    }, [stuckMoves, fetchAuth, load]);

    const th = (key, label, align = 'left', extraStyle) => (
        <th onClick={() => handleSort(key)}
            style={{ cursor: 'pointer', userSelect: 'none',
                     textAlign: align, padding: '6px 10px',
                     borderBottom: '1px solid #dfe3e8',
                     whiteSpace: 'nowrap', background: '#fafbfb',
                     ...(extraStyle || {}) }}>
            {label}{sortArrow(key)}
        </th>
    );

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Split Team Fulfillment" storageKey="split_team_fulfillment_about_open" linkLabel="About this report">
                        <Text as="p">
                            Orders whose team-portal goods come from two or
                            more fulfillment locations when they should all
                            come from the order's expected location. The
                            origin of a fulfillment is the location that
                            packed it (when OpsHub recorded the transfer),
                            otherwise the location it is assigned to now — so
                            a store fulfilling items itself counts as a second
                            origin. Retail add-ons, fees and bundle-internal
                            splits (see Jumbled Bundles) are not counted. Auto
                            Fix moves the still-open wrong-origin fulfillments
                            to the expected location; parts already picked up
                            or shipped cannot be moved and are listed for
                            reference. Cancelled + fully-done orders are
                            excluded.
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                        trailing={(() => {
                            const avail = data.available_expected_locations || [];
                            const q = (expLocSearch || '').trim().toLowerCase();
                            const filtered = q ? avail.filter(x => searchMatches(x, q)) : avail;
                            return (
                                <Popover
                                    active={expLocOpen}
                                    activator={
                                        <Button disclosure onClick={() => setExpLocOpen(o => !o)}
                                                pressed={expLocFilter.length > 0}>
                                            Expected location{expLocFilter.length ? ` (${expLocFilter.length})` : ''}
                                        </Button>
                                    }
                                    onClose={() => setExpLocOpen(false)}
                                    preferredAlignment="left"
                                >
                                    <div style={{ minWidth: 280, maxHeight: 420, padding: 10,
                                                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <TextField labelHidden label="Search expected locations" type="search"
                                            value={expLocSearch} onChange={setExpLocSearch}
                                            placeholder="Search expected locations…" autoComplete="off"
                                            clearButton onClearButtonClick={() => setExpLocSearch('')} />
                                        <button type="button" className="filter-pill-clear"
                                                disabled={expLocFilter.length === 0}
                                                onClick={() => setExpLocFilter([])}>Clear</button>
                                        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                            {filtered.length === 0 ? (
                                                <div style={{ color: 'var(--p-color-text-secondary)',
                                                              fontSize: 13, padding: '8px 4px' }}>
                                                    {avail.length === 0
                                                      ? 'No expected locations in window.'
                                                      : `No matches for "${expLocSearch}"`}
                                                </div>
                                            ) : (
                                                <ChoiceList titleHidden title="Expected locations" allowMultiple
                                                    choices={filtered.map(x => ({ label: x, value: x }))}
                                                    selected={expLocFilter}
                                                    onChange={setExpLocFilter} />
                                            )}
                                        </div>
                                    </div>
                                </Popover>
                            );
                        })()}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap blockAlign="center">
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            {(data.orders || 0).toLocaleString()} affected orders
                            {' '}· {(data.rows || []).length.toLocaleString()} outlier rows
                        </Text>
                        {selectedRowKeys.size > 0 && (
                            <>
                                <Text variant="bodySm">
                                    <strong>{selectedRowKeys.size}</strong> selected
                                </Text>
                                <Button variant="primary" tone="critical"
                                        onClick={() => { setFixResults(null); setFixModalOpen(true); }}>
                                    Auto Fix ({selectedRowKeys.size})
                                </Button>
                                <Button onClick={() => setSelectedRowKeys(new Set())}>Clear selection</Button>
                            </>
                        )}
                    </InlineStack>

                    {loading ? null : sortedRows.length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No split team fulfillments found in this window.
                        </div>
                    ) : (
                        // Wrapper owns BOTH scroll axes (maxHeight caps
                        // vertical, min-content on the table drives
                        // horizontal). This anchors position:sticky
                        // inside the wrapper so both the header row and
                        // the Order column stay pinned as the user
                        // scrolls. Per CLAUDE.md rule: overflow:hidden
                        // on any ancestor would break sticky — Polaris
                        // Card is safe. `overscrollBehavior: contain`
                        // stops the page scrolling once the wrapper
                        // hits its end.
                        <div style={{ overflow: 'auto', maxHeight: '70vh',
                                      overscrollBehavior: 'contain' }}>
                            <table style={{ width: '100%', minWidth: 1100,
                                            borderCollapse: 'separate',
                                            borderSpacing: 0,
                                            fontSize: 13 }}>
                                <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                                    <tr>
                                        <th style={{ padding: '6px 10px', textAlign: 'center',
                                                     borderBottom: '1px solid #dfe3e8',
                                                     background: '#fafbfb', whiteSpace: 'nowrap',
                                                     position: 'sticky', left: 0, zIndex: 4,
                                                     width: 36 }}>
                                            <input type="checkbox" aria-label="Select all rows"
                                                   checked={allSelected}
                                                   ref={el => { if (el) el.indeterminate = someSelected; }}
                                                   onChange={toggleAll} />
                                        </th>
                                        {th('order_name',        'Order', 'left', { position: 'sticky', left: 36, zIndex: 4 })}
                                        {th('placed_d',          'Placed')}
                                        {th('customer_name',     'Customer')}
                                        {th('club',              'Club')}
                                        {th('expected_location', 'Expected location')}
                                        <th style={{ padding: '6px 10px', textAlign: 'left',
                                                     borderBottom: '1px solid #dfe3e8',
                                                     background: '#fafbfb', whiteSpace: 'nowrap' }}>
                                            Origins
                                        </th>
                                        {th('wrong_locations',   'Other origin(s)')}
                                        <th style={{ padding: '6px 10px', textAlign: 'left',
                                                     borderBottom: '1px solid #dfe3e8',
                                                     background: '#fafbfb', whiteSpace: 'nowrap' }}>
                                            Items from other origins
                                        </th>
                                        {th('wrong_item_count',  'Qty', 'right')}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map((r, idx) => {
                                        const k = rowKey(r);
                                        const isSel = selectedRowKeys.has(k);
                                        return (
                                        <tr key={`${r.store_key}:${r.order_id}:${idx}`}
                                            style={{ borderBottom: '1px solid #f1f2f3',
                                                     background: isSel ? '#f6f8fa' : undefined }}>
                                            <td style={{ padding: '6px 10px', textAlign: 'center',
                                                         position: 'sticky', left: 0, zIndex: 2,
                                                         background: isSel ? '#f6f8fa' : '#fff',
                                                         borderRight: '1px solid #f1f2f3', width: 36 }}>
                                                {/* Same-location splits have no cross-loc FOs
                                                    to reassign, so Auto Fix would be a no-op —
                                                    disable the checkbox on those rows. */}
                                                <input type="checkbox"
                                                       aria-label={`Select ${r.order_name || r.order_id}`}
                                                       checked={isSel}
                                                       disabled={r.split_kind === 'same_location'}
                                                       title={r.split_kind === 'same_location'
                                                              ? "Same-location split — Auto Fix doesn't apply."
                                                              : undefined}
                                                       // Shift-click supports range selection —
                                                       // click one row, shift-click another to
                                                       // select every row between them.
                                                       onClick={(e) => toggleRow(k, idx, e.shiftKey)}
                                                       onChange={() => {}} />
                                            </td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap',
                                                         position: 'sticky', left: 36, zIndex: 2,
                                                         background: isSel ? '#f6f8fa' : '#fff',
                                                         borderRight: '1px solid #f1f2f3' }}>
                                                <Link to={`/store/${r.store_key || 'soccerpost'}/orders/${r.order_id}`}>
                                                    {r.order_name || r.order_id}
                                                </Link>
                                                {r.is_partial && (
                                                    <span style={{ color: '#8a4b00', fontSize: 11,
                                                                   marginLeft: 6 }}
                                                          title="This order has already been partially fulfilled — some team goods are still in open FOs.">
                                                        (partial)
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                                {r.placed_d || ''}
                                            </td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{r.customer_name || ''}</td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{r.club || ''}</td>
                                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                                {r.expected_location || ''}
                                            </td>
                                            <td style={{ padding: '6px 10px' }}>
                                                {(r.bundles || []).map((b, i) => (
                                                    <div key={i} style={{ whiteSpace: 'nowrap' }}>
                                                        {b.label}
                                                        {b.player_suffix && (
                                                            <span style={{ color: '#6d7175', fontSize: 12 }}>
                                                                {' — '}{b.player_suffix}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </td>
                                            <td style={{ padding: '6px 10px',
                                                         color: '#bf0711', fontWeight: 500 }}>
                                                {r.split_kind === 'same_location' ? (
                                                    // Same-location split: no "wrong loc" to show;
                                                    // report the split-count at the expected loc.
                                                    (r.same_location_bundles || []).map((b, i) => (
                                                        <div key={i} style={{ whiteSpace: 'nowrap',
                                                                              color: '#8a4b00' }}>
                                                            Split across {b.fo_count} FOs at {r.expected_location}
                                                        </div>
                                                    ))
                                                ) : (
                                                    (r.wrong_locations || []).map((w, i) => (
                                                        <div key={i} style={{ whiteSpace: 'nowrap' }}>{w}</div>
                                                    ))
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 10px' }}>
                                                {r.split_kind === 'same_location' ? (
                                                    <span style={{ color: '#6d7175', fontSize: 12 }}>
                                                        Same-location split — no cross-location items to reroute.
                                                    </span>
                                                ) : (
                                                    (r.wrong_items || []).map((it, i) => (
                                                        <div key={i} style={{ whiteSpace: 'nowrap' }}>
                                                            <strong>{it.quantity}×</strong>{' '}
                                                            <code style={{ fontSize: 11 }}>{it.sku || '(no sku)'}</code>
                                                            {' — '}{it.title || ''}
                                                            {it.wrong_location && (
                                                                <span style={{ color: '#6d7175', fontSize: 12 }}>
                                                                    {' → '}{it.wrong_location}{it.bundle_label ? ` · ${it.bundle_label}` : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right',
                                                         whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                {r.split_kind === 'same_location' ? '—' : r.wrong_item_count}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>

            {/* Auto Fix confirmation + results modal */}
            {fixModalOpen && (() => {
                const totalMoves = selectedOrderRows.reduce(
                    (s, r) => s + (r.wrong_locations || []).length, 0);
                const close = () => {
                    if (fixSubmitting) return;
                    setFixModalOpen(false);
                    setFixResults(null);
                };
                const showingResults = !!fixResults;
                return (
                    <Modal open
                        title={showingResults ? 'Auto Fix results' :
                               `Auto Fix ${selectedOrderRows.length} order${selectedOrderRows.length === 1 ? '' : 's'}?`}
                        onClose={close}
                        primaryAction={showingResults
                            ? { content: 'Done', onAction: close }
                            : { content: `Fix ${selectedOrderRows.length} order${selectedOrderRows.length === 1 ? '' : 's'}`,
                                onAction: submitFix, loading: fixSubmitting,
                                destructive: true }}
                        secondaryActions={showingResults ? undefined : [
                            { content: 'Cancel', onAction: close, disabled: fixSubmitting },
                        ]}
                    >
                        <Modal.Section>
                            {!showingResults ? (
                                <BlockStack gap="300">
                                    <Text>
                                        For each selected order, every fulfillment order
                                        currently at a wrong location will be reassigned to
                                        the order's expected fulfillment location (from the
                                        <code> _soccer_fulfillment_location</code> attribute).
                                    </Text>
                                    <Text tone="subdued">
                                        This is a Shopify <strong>Change Location</strong> —
                                        FO-move only. It shifts committed inventory but does
                                        NOT move physical stock counts between locations.
                                        Assumes the goods are already at (or being sent to)
                                        the expected location.
                                    </Text>
                                    <Text>
                                        Planned: <strong>{totalMoves}</strong> FO move{totalMoves === 1 ? '' : 's'}
                                        {' '}across <strong>{selectedOrderRows.length}</strong> order{selectedOrderRows.length === 1 ? '' : 's'}.
                                    </Text>
                                    <div style={{ maxHeight: 260, overflowY: 'auto',
                                                  border: '1px solid #dfe3e8', borderRadius: 6,
                                                  padding: 8, background: '#fafbfb', fontSize: 12 }}>
                                        {selectedOrderRows.map(r => (
                                            <div key={rowKey(r)} style={{ marginBottom: 8 }}>
                                                <div><strong>{r.order_name}</strong> → expected <strong>{r.expected_location}</strong></div>
                                                {(r.wrong_locations || []).map((w, i) => (
                                                    <div key={i} style={{ color: '#6d7175', marginLeft: 12 }}>
                                                        move FO at <em>{w}</em> → {r.expected_location}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </BlockStack>
                            ) : (
                                <BlockStack gap="300">
                                    {(() => {
                                        const rs = fixResults?.results || [];
                                        const okCount = rs.filter(r =>
                                            !r.error && (r.moves || []).length > 0 &&
                                            (r.moves || []).every(m => m.ok)).length;
                                        const partialCount = rs.filter(r =>
                                            (r.moves || []).some(m => m.ok) &&
                                            (r.moves || []).some(m => !m.ok)).length;
                                        const failCount = rs.filter(r =>
                                            r.error || ((r.moves || []).length > 0 &&
                                                        (r.moves || []).every(m => !m.ok))).length;
                                        const noopCount = rs.filter(r =>
                                            !r.error && (r.moves || []).length === 0).length;
                                        return (
                                            <>
                                                <Text>
                                                    <strong>{okCount}</strong> fully fixed
                                                    {partialCount > 0 && <>, <strong>{partialCount}</strong> partial</>}
                                                    {failCount > 0 && <>, <strong>{failCount}</strong> failed</>}
                                                    {noopCount > 0 && <>, <strong>{noopCount}</strong> already OK</>}
                                                    .
                                                </Text>
                                                {stuckMoves.length > 0 && (
                                                    <div style={{ padding: 10, border: '1px solid #fbe0d0',
                                                                  background: '#fff5f0', borderRadius: 6 }}>
                                                        <BlockStack gap="200">
                                                            <Text>
                                                                <strong>{stuckMoves.length}</strong> move{stuckMoves.length === 1 ? '' : 's'}
                                                                {' '}failed because the item wasn't stocked at the destination location.
                                                            </Text>
                                                            <Text tone="subdued">
                                                                This will activate the item at the destination, bump on-hand
                                                                by the FO's remaining quantity, then re-attempt the move.
                                                            </Text>
                                                            <div>
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={submitForceStockAndFix}
                                                                    loading={fixSubmitting}
                                                                    disabled={fixSubmitting}
                                                                >
                                                                    Stock in destination location, and re-fix ({stuckMoves.length})
                                                                </Button>
                                                            </div>
                                                        </BlockStack>
                                                    </div>
                                                )}
                                                <div style={{ maxHeight: 320, overflowY: 'auto',
                                                              border: '1px solid #dfe3e8', borderRadius: 6,
                                                              padding: 8, background: '#fafbfb', fontSize: 12 }}>
                                                    {rs.map((r, i) => (
                                                        <div key={i} style={{ marginBottom: 8 }}>
                                                            <div>
                                                                <strong>{r.order_name || r.order_id}</strong>
                                                                {r.expected_location && <> → {r.expected_location}</>}
                                                                {r.error && <span style={{ color: '#bf0711' }}> — {r.error}</span>}
                                                                {r.note  && <span style={{ color: '#6d7175' }}> — {r.note}</span>}
                                                            </div>
                                                            {(r.moves || []).map((m, j) => (
                                                                <div key={j} style={{ marginLeft: 12,
                                                                                        color: m.ok ? '#008060' : '#bf0711' }}>
                                                                    {m.ok ? '✓' : '✗'} {m.from_location} → {m.to_location}
                                                                    {m.error && <span> — {m.error}</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </BlockStack>
                            )}
                        </Modal.Section>
                    </Modal>
                );
            })()}
        </BlockStack>
    );
}

/* ---------- Team Portal Franchise Reconciliation report --------------
 * Data source: /api/reports/tp_franchise_reconciliation (fetches Shopify
 * live for $ figures, so wide date ranges can be slow — the backend caps
 * at 250 orders/request and surfaces a `truncated` warning when hit.
 * See app.py :: report_tp_franchise_reconciliation.
 * ----------------------------------------------------------------------
 */
// 2026-09-22 (Eric): every column carries a definition tooltip. Wording
// mirrors _tpfr_build_row in app.py — keep the two in step.
const _TPFR_COLS = [
    ['club',                'Club',                 'Team Portal club on the order.'],
    ['order_date',          'Order Date',           'Date the order was placed (ET). Orders placed before the window but with a label, refund or cancellation inside it are listed with $0 in the sale columns and only those in-window costs shown.'],
    ['order_name',          'Order #',              'Shopify order number; opens the order page.'],
    ['source',              'Source',               'Where the order came from, same badge as the Orders list: Team Portal, Retail, Add-On or a mix. Retail orders the franchise fulfilled are paid out too — their own goods, so no COGS comes off.'],
    ['order_status',        'Status',               'Shopify fulfillment status right now.'],
    ['subtotal',            'Subtotal',             'Merchandise before discounts (Shopify subtotal with discounts added back). Broken Orders show the post-discount subtotal because their line discounts are placeholders.'],
    ['shipping',            'Shipping',             'Shipping charged to the customer.'],
    ['discounts',           'Discounts',            'Discount codes and manual discounts on the order. $0 on Broken Orders.'],
    ['tax',                 'Tax',                  'Sales tax collected. Soccer Post remits it, so it is not part of Net Total; when a refund returns tax, that tax is added back in Net Total too.'],
    ['total_sales',         'Total Sales',          'Shopify order total: discounted merchandise + shipping + tax.'],
    ['corp_cogs',           'Retail Product B2B',   'What the retail and add-on items WE shipped on this order cost us: B2B Price × quantity fulfilled from our warehouses, Teams and corporate stores (an item with no B2B Price falls back to Shopify’s cost per item). Team Portal lines and embellishments are excluded. Subtracted from Net Total. Amber = an item has neither a B2B Price nor a cost (counted $0).'],
    ['franchise_retail',    'Franchise Retail',     'Retail and add-on items the franchise fulfilled from its own stock, at the price the customer paid (discounted unit price × quantity). Their sale — shown for reference and NOT subtracted from Net Total.'],
    ['shipping_label_cost', 'Label Cost',           'UPS labels bought in OpsHub for this order inside the window.'],
    ['returns',             'Returns',              'Refunds issued inside the window that are not tied to a cancellation, including any sales tax refunded.'],
    ['cancellations',       'Cancellations',        'Refunds issued within 24 hours of the order being cancelled, when the cancellation falls inside the window, including any sales tax refunded.'],
    ['fees_amount',         'Shopify CC Fees',      'Card processing fees Shopify actually charged on the order\'s successful payments.'],
    ['sp_fees',             'SP Fees',              '1% Soccer Post fee on the merchandise kept: subtotal minus discounts, returns and cancellations.'],
    ['sp_discount',         'SP Discount',          'The same 1% forgiven this year, shown as a credit so the two net to $0.'],
    ['net_order_total',     'Net Total',            'Subtotal + Shipping − Discounts − Returns − Cancellations (less the sales tax inside those refunds) − Shopify CC Fees − Label Cost − Retail Product B2B. Tax is excluded (Soccer Post remits it). On a franchise\u2019s own retail order there is no COGS deduction, so the payout is what we collected minus the Shopify fees (and the label, when we bought one).'],
];
const _TPFR_NUM = new Set(['subtotal','shipping','discounts','tax','total_sales','corp_cogs','franchise_retail','shipping_label_cost',
                           'returns','cancellations','fees_amount','sp_fees','sp_discount','net_order_total']);

function TPFranchiseReconciliationReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || todayIso());
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter,     setClubFilter]     = useState(searchParams.getAll('club'));
    // 2026-09-22 (Eric): "Run Report" — filters edit locally; the query runs
    // only for the APPLIED set (the URL on first load, then each click).
    // Picking franchises one at a time used to fire a fetch per pick, so
    // Sherif saw mixed intermediate results.
    const [applied, setApplied] = useState(() => ({
        dateFrom: searchParams.get('from') || todayIso(), dateTo: searchParams.get('to') || todayIso(),
        locations: searchParams.getAll('loc'), clubs: searchParams.getAll('club'),
    }));
    // 2026-09-22 (Eric): "How each column is calculated" banner, same style as
    // Club Revenue Summary. The top of this report is sticky and the banner is
    // ~500px tall, so it starts collapsed behind a link and the open/closed
    // choice is remembered per browser.
    const [showColHelp, setShowColHelp] = useState(() => { try { return localStorage.getItem('tpfr_col_help_open') === '1'; } catch (_e) { return false; } });
    const setColHelp = (open) => { setShowColHelp(open); try { localStorage.setItem('tpfr_col_help_open', open ? '1' : '0'); } catch (_e) { /* ignore */ } };
    const dirty = applied.dateFrom !== dateFrom || applied.dateTo !== dateTo
        || applied.locations.join('|') !== locationFilter.join('|') || applied.clubs.join('|') !== clubFilter.join('|');
    const [runSeq, setRunSeq] = useState(0);
    const runReport = () => {
        setApplied({ dateFrom, dateTo, locations: locationFilter.slice(), clubs: clubFilter.slice() });
        setRunSeq(n => n + 1);
    };
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        rows: [], available_clubs: [], available_locations: [],
        orders: 0, date_from: '', date_to: '',
        truncated: false, cap: 250, note: '',
    });
    // Sticky top (Eric, 2026-09-22): the table is the only thing that
    // scrolls — pane sized to the rest of the app scroller, like Zero Sales.
    const tpfrPaneRef = useRef(null);
    const [tpfrPaneH, setTpfrPaneH] = useState(null);
    useEffect(() => {
        document.body.classList.add('report-flush-bottom');
        return () => document.body.classList.remove('report-flush-bottom');
    }, []);
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('order_date');
    const [sortDir, setSortDir] = useState('desc');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (applied.dateFrom) p.set('date_from', applied.dateFrom);
            if (applied.dateTo)   p.set('date_to',   applied.dateTo);
            applied.locations.forEach(l => p.append('locations', l));
            applied.clubs.forEach(c => p.append('clubs', c));
            p.set('run', '1');
            const res = await fetchAuth(`/api/reports/tp_franchise_reconciliation?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, applied]);

    // 2026-09-22 (Eric): "Don't run the report UNTIL I press the button."
    // Nothing heavy happens on load or on a deep link — the page only fetches
    // `run=0`, which fills the Location and Club pickers. `runSeq` is bumped
    // by the button and is the ONLY thing that triggers the real query.
    const loadRef = useRef(load);
    useEffect(() => { loadRef.current = load; }, [load]);
    useEffect(() => { if (runSeq > 0) loadRef.current(); }, [runSeq]);

    const optKey = `${dateFrom}|${dateTo}|${locationFilter.join(',')}`;
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const p = new URLSearchParams();
                if (dateFrom) p.set('date_from', dateFrom);
                if (dateTo)   p.set('date_to',   dateTo);
                locationFilter.forEach(l => p.append('locations', l));
                p.set('run', '0');
                const res = await fetchAuth(`/api/reports/tp_franchise_reconciliation?${p.toString()}`);
                if (!res.ok || cancelled) return;
                const j = await res.json();
                setData(d => ({ ...d,
                    available_locations: j.available_locations || d.available_locations,
                    available_clubs:     j.available_clubs     || [] }));
            } catch (_e) { /* the pickers just stay as they are */ }
        })();
        return () => { cancelled = true; };
    }, [fetchAuth, optKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        const measure = () => {
            const el = tpfrPaneRef.current; if (!el) return;
            const h = paneHeightToFitScroller(el) - 6;   // 2026-09-26 (Eric): 6px in hand so the card's bottom corners never slip under the page edge
            setTpfrPaneH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [data.rows, loading, err, data.note, data.truncated, tpfrPaneH, showColHelp]);

    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            // Sensible defaults: money columns descending, everything else ascending
            const moneyCols = new Set(['subtotal','shipping','discounts','tax',
                'total_sales','corp_cogs','franchise_retail','shipping_label_cost','returns','cancellations','net_order_total']);
            setSortDir(moneyCols.has(key) || key === 'order_date' ? 'desc' : 'asc');
        }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const sortedRows = useMemo(() => {
        const rows = (data.rows || []).slice();
        const money = new Set(['subtotal','shipping','discounts','tax',
            'total_sales','corp_cogs','franchise_retail','shipping_label_cost','returns','cancellations',
            'net_order_total','fees_amount','fees_rate','sp_fees','sp_discount']);
        rows.sort((a, b) => {
            let cmp;
            if (money.has(sortKey)) {
                cmp = (Number(a[sortKey]) || 0) - (Number(b[sortKey]) || 0);
            } else {
                cmp = String(a[sortKey] || '').localeCompare(String(b[sortKey] || ''));
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]);

    // Totals footer row — sum every money column across the visible set.
    const totals = useMemo(() => {
        const acc = { subtotal: 0, shipping: 0, discounts: 0, tax: 0,
                      total_sales: 0, corp_cogs: 0, franchise_retail: 0, shipping_label_cost: 0, returns: 0,
                      cancellations: 0, fees_amount: 0,
                      sp_fees: 0, sp_discount: 0,
                      net_order_total: 0 };
        for (const r of (data.rows || [])) {
            for (const k of Object.keys(acc)) {
                acc[k] += Number(r[k]) || 0;
            }
        }
        return acc;
    }, [data.rows]);

    const handleExport = useCallback(() => {
        const csvRows = [['Club','Order Date','Order Number','Source','Order Status',
            'Order Subtotal','Shipping Charges','Discounts','Tax','Total Sales',
            'Retail Product B2B','Franchise Retail','Shipping Label Cost','Returns','Cancellations','Shopify CC Fees',
            'SP Fees','SP Discount','Net Order Total']];
        for (const r of sortedRows) {
            csvRows.push([
                r.club || '',
                r.order_date || '',
                r.order_name || '',
                sourceLabel(r.source),
                r.order_status || '',
                Number(r.subtotal || 0).toFixed(2),
                Number(r.shipping || 0).toFixed(2),
                Number(r.discounts || 0).toFixed(2),
                Number(r.tax || 0).toFixed(2),
                Number(r.total_sales || 0).toFixed(2),
                Number(r.corp_cogs || 0).toFixed(2),
                Number(r.franchise_retail || 0).toFixed(2),
                Number(r.shipping_label_cost || 0).toFixed(2),
                Number(r.returns || 0).toFixed(2),
                Number(r.cancellations || 0).toFixed(2),
                Number(r.fees_amount || 0).toFixed(2),
                Number(r.sp_fees || 0).toFixed(2),
                Number(r.sp_discount || 0).toFixed(2),
                Number(r.net_order_total || 0).toFixed(2),
            ]);
        }
        downloadCsv(`tp_franchise_reconciliation_${dateFrom}_to_${dateTo}.csv`, csvRows);
    }, [sortedRows, dateFrom, dateTo]);

    // Build location-filter options from backend response (scoped to what
    // the caller may see; empty for franchise since scoping is automatic).
    // ReportFilters expects {name, shopify_location_id, is_franchise} — the
    // {label, value} shape used elsewhere in this file breaks its internal
    // sort (a.name.localeCompare) when name is missing.
    const locOptions = (data.available_locations || []).map(l => ({
        name: l.name || l.shopify_location_id,
        shopify_location_id: l.shopify_location_id,
        is_franchise: false,
    }));

    // ReportFilters' club dropdown consumes a plain array of strings.
    const clubOptions = (data.available_clubs || []);

    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center" wrap>
                        <Text variant="headingMd" as="h2">Team Portal Franchise Reconciliation</Text>
                        {!showColHelp && <Button variant="plain" onClick={() => setColHelp(true)}>How each column is calculated</Button>}
                    </InlineStack>
                    {showColHelp && (
                        <Banner tone="info" title="How each column is calculated" onDismiss={() => setColHelp(false)}>
                            <BlockStack gap="150">
                                {_TPFR_COLS.filter(([k]) => !['club', 'order_name', 'order_status'].includes(k)).map(([key, label, tip]) => (
                                    <Text as="p" key={key}><b>{label}</b> — {tip}</Text>
                                ))}
                                <Text as="p" tone="subdued">One row per order fulfilled by a franchise location (the Team Portal's fulfillment location on the order). Orders placed inside the window show every column; orders placed earlier but with a label, refund or cancellation inside the window show only those costs.</Text>
                            </BlockStack>
                        </Banner>
                    )}
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo}     setDateTo={setDateTo}
                        availableLocations={locOptions}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={clubOptions}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        multiClub
                        // 2026-09-22 (Eric): Run Report sits next to the last
                        // dropdown, like Revenue By Club & Vendor — `trailing`
                        // renders inside the filter row, not out by Export.
                        trailing={(
                            <InlineStack gap="200" blockAlign="center">
                                {dirty && !loading && <Text as="span" variant="bodySm" tone="subdued">Filters changed</Text>}
                                <Button variant="primary" tone="success" onClick={runReport} loading={loading} disabled={loading}>Run Report</Button>
                            </InlineStack>
                        )}
                        onExport={handleExport}
                        exportDisabled={sortedRows.length === 0}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}
            {data.note && <Banner tone="info" title="Heads up">{data.note}</Banner>}
            {data.truncated && (
                <Banner tone="warning" title={`Results capped at ${data.cap} orders`}>
                    Narrow the date range or club filter to see the rest.
                </Banner>
            )}

            <Card padding="0">
                <BlockStack gap="0">
                    <InlineStack gap="400" wrap blockAlign="center">
                      <div style={{ padding: '12px 16px 8px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Text variant="bodySm" tone="subdued">
                            Window: {runSeq === 0 ? `${dateFrom} → ${dateTo} (not run yet)` : `${data.date_from || '—'} → ${data.date_to || '—'}`}
                        </Text>
                        {runSeq > 0 && (
                            <Text variant="bodySm" tone="subdued">
                                {(data.orders || 0).toLocaleString()} orders
                            </Text>
                        )}
                        {loading && <Spinner size="small" accessibilityLabel="Loading" />}
                      </div>
                    </InlineStack>

                    {loading ? null : runSeq === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            Pick your window, location and club, then click <strong>&nbsp;Run Report</strong>.
                        </div>
                    ) : sortedRows.length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No orders match this window.
                        </div>
                    ) : (
                        <div className="orders-summary-table-scroll" ref={tpfrPaneRef}
                             style={{ maxHeight: tpfrPaneH || undefined, border: 0, borderTop: '1px solid #e1e3e5', borderRadius: '0 0 8px 8px' }}>
                            <table className="orders-summary-table">
                                <thead>
                                    <tr>
                                        {_TPFR_COLS.map(([key, label, tip]) => (
                                            <th key={key} className={`sortable${_TPFR_NUM.has(key) ? ' num' : ''}`} onClick={() => handleSort(key)}>
                                                <Tooltip content={tip} preferredPosition="above" width="wide">
                                                    <span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span>
                                                </Tooltip>{sortArrow(key)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map((r, i) => (
                                        <tr key={r.order_id || i}
                                            style={r.placed_in_range ? undefined : { opacity: 0.85 }}>
                                            <td>{r.club || '—'}</td>
                                            <td>{r.order_date || '—'}</td>
                                            <td>{r.order_id
                                                ? <Link to={`/store/${r.store_key || 'soccerpost'}/orders/${r.order_id}`}>{r.order_name || r.order_id}</Link>
                                                : (r.order_name || '—')}</td>
                                            <td><SourceBadge source={r.source} /></td>
                                            <td>{r.order_status || '—'}</td>
                                            <td className="num">{_tpfrMoney(r.subtotal)}</td>
                                            <td className="num">{_tpfrMoney(r.shipping)}</td>
                                            <td className="num">{_tpfrMoney(r.discounts)}</td>
                                            <td className="num">{_tpfrMoney(r.tax)}</td>
                                            <td className="num">{_tpfrMoney(r.total_sales)}</td>
                                            <td className="num" title={`${r.corp_lines || 0} retail / add-on line${r.corp_lines === 1 ? '' : 's'} we fulfilled${r.retail_addon_cogs_missing ? ` — ${r.retail_addon_cogs_missing} with no B2B price or cost (counted $0)` : ''}`}
                                                style={r.retail_addon_cogs_missing ? { color: '#b98900' } : undefined}>{_tpfrMoney(r.corp_cogs)}</td>
                                            <td className="num" title={`${r.franchise_lines || 0} retail / add-on line${r.franchise_lines === 1 ? '' : 's'} the franchise fulfilled itself, at sale price — not deducted`}>{_tpfrMoney(r.franchise_retail)}</td>
                                            <td className="num">{_tpfrMoney(r.shipping_label_cost)}</td>
                                            <td className="num">{_tpfrMoney(r.returns)}</td>
                                            <td className="num">{_tpfrMoney(r.cancellations)}</td>
                                            <td className="num">{_tpfrMoney(r.fees_amount)}</td>
                                            <td className="num">{_tpfrMoney(r.sp_fees)}</td>
                                            <td className="num">{_tpfrMoney(r.sp_discount)}</td>
                                            <td className="num" style={{ fontWeight: 600 }}>{_tpfrMoney(r.net_order_total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot style={{ position: 'sticky', bottom: 0,
                                                background: '#f6f6f7',
                                                borderTop: '2px solid #8c9196' }}>
                                    <tr style={{ fontWeight: 700 }}>
                                        <td colSpan={5}>Totals</td>
                                        <td className="num">{_tpfrMoney(totals.subtotal)}</td>
                                        <td className="num">{_tpfrMoney(totals.shipping)}</td>
                                        <td className="num">{_tpfrMoney(totals.discounts)}</td>
                                        <td className="num">{_tpfrMoney(totals.tax)}</td>
                                        <td className="num">{_tpfrMoney(totals.total_sales)}</td>
                                        <td className="num">{_tpfrMoney(totals.corp_cogs)}</td>
                                        <td className="num">{_tpfrMoney(totals.franchise_retail)}</td>
                                        <td className="num">{_tpfrMoney(totals.shipping_label_cost)}</td>
                                        <td className="num">{_tpfrMoney(totals.returns)}</td>
                                        <td className="num">{_tpfrMoney(totals.cancellations)}</td>
                                        <td className="num">{_tpfrMoney(totals.fees_amount)}</td>
                                        <td className="num">{_tpfrMoney(totals.sp_fees)}</td>
                                        <td className="num">{_tpfrMoney(totals.sp_discount)}</td>
                                        <td className="num">{_tpfrMoney(totals.net_order_total)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

// Cell styling shared by the TPFR table above.
const _tpfrTh = { textAlign: 'left', padding: '8px 10px',
                  borderBottom: '2px solid #8c9196', cursor: 'pointer',
                  whiteSpace: 'nowrap', userSelect: 'none' };
const _tpfrThNum = { ..._tpfrTh, textAlign: 'right' };
const _tpfrTd = { padding: '6px 10px', whiteSpace: 'nowrap' };
const _tpfrTdNum = { ..._tpfrTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
function _tpfrMoney(n) {
    const v = Number(n);
    if (!isFinite(v)) return '—';
    return `$${v.toFixed(2)}`;
}


/* ---------- Product Revenue By Club (MSRP) report --------------------- */

function _fmtMoney(n) {
    if (n === null || n === undefined) return '—';
    const v = Number(n);
    if (!isFinite(v)) return '—';
    return `$${v.toFixed(2)}`;
}

function ProductRevenueByClubCard({ product }) {
    const p = product || {};
    const variants = p.variants || [];
    const delta = (p.priced_known && p.msrp_known)
        ? (p.priced_revenue - p.msrp_revenue) : null;
    const deltaTone = delta == null
        ? '#6d7175'
        : delta > 0 ? '#0a6a1f' : delta < 0 ? '#991b1b' : '#6d7175';
    return (
        <div className="prbc-card" style={{
            display: 'grid', gridTemplateColumns: '80px 1fr', gap: 16,
            padding: 14,
            border: '1px solid var(--p-color-border, #e1e3e5)',
            borderRadius: 8, background: '#fff',
        }}>
            <div>
                {p.image_url
                    ? <SmartProductImage src={p.image_url} alt="" size={144}
                           style={{ width: 72, height: 72, objectFit: 'contain',
                                    border: '1px solid #eee', borderRadius: 4,
                                    display: 'block' }} />
                    : <div style={{ width: 72, height: 72, background: '#fafafa',
                                    border: '1px solid #eee', borderRadius: 4,
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', color: '#c4c8cc',
                                    fontSize: 10 }}>no img</div>}
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ marginBottom: 6 }}>
                    <Text variant="headingSm" as="h3">{p.title || '—'}</Text>
                    <InlineStack gap="200" blockAlign="center">
                        {p.product_type ? (
                            <span style={{ fontSize: 11, padding: '2px 8px',
                                           background: '#eef2ff', color: '#3730a3',
                                           borderRadius: 4, fontWeight: 600 }}>
                                {p.product_type}
                            </span>
                        ) : null}
                        <Text variant="bodySm" tone="subdued">
                            <strong>{(p.total_qty || 0).toLocaleString()}</strong> units
                            {' '}· Priced: <strong>{_fmtMoney(p.priced_known ? p.priced_revenue : null)}</strong>
                            {' '}· MSRP: <strong>{_fmtMoney(p.msrp_known ? p.msrp_revenue : null)}</strong>
                            {delta != null && (
                                <span style={{ color: deltaTone, marginLeft: 8 }}>
                                    ({delta >= 0 ? '+' : ''}{_fmtMoney(delta)} vs MSRP)
                                </span>
                            )}
                        </Text>
                    </InlineStack>
                </div>
                {variants.length === 0 ? (
                    <Text tone="subdued">No variants in window.</Text>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="data-table prbc-variant-table"
                               style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {['Variant', 'SKU', 'Qty', 'Priced / unit', 'Priced Revenue', 'MSRP / unit', 'MSRP Revenue', 'Δ vs MSRP']
                                      .map((h, i) => (
                                        <th key={h} style={{
                                            textAlign: i < 2 ? 'left' : 'right',
                                            background: '#f6f6f7',
                                            borderBottom: '1px solid #e1e3e5',
                                            padding: '6px 10px', fontSize: 11,
                                            color: '#6d7175', letterSpacing: 0.04,
                                            textTransform: 'uppercase',
                                            fontWeight: 700, whiteSpace: 'nowrap',
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {variants.map(v => {
                                    const vDelta = (v.priced_revenue != null && v.msrp_revenue != null)
                                        ? (v.priced_revenue - v.msrp_revenue) : null;
                                    const vTone = vDelta == null
                                        ? '#6d7175'
                                        : vDelta > 0 ? '#0a6a1f'
                                        : vDelta < 0 ? '#991b1b' : '#6d7175';
                                    // Priced / unit = priced_revenue / qty (average sold price
                                    // across this club's orders for this SKU in the window).
                                    // Falls through to "—" when no orders are cached yet.
                                    const pricedPerUnit = (v.priced_revenue_seen && v.qty > 0)
                                        ? (v.priced_revenue / v.qty) : null;
                                    const cellSty = { padding: '6px 10px', fontSize: 13,
                                                      borderBottom: '1px solid #f1f2f3' };
                                    return (
                                        <tr key={v.sku}>
                                            <td style={cellSty}>{v.variant_title || '—'}</td>
                                            <td style={{ ...cellSty,
                                                         fontFamily: 'ui-monospace, Menlo, monospace',
                                                         fontSize: 11, color: '#6d7175' }}>
                                                {v.sku}
                                            </td>
                                            <td style={{ ...cellSty, textAlign: 'right',
                                                         fontWeight: 600 }}>
                                                {(v.qty || 0).toLocaleString()}
                                            </td>
                                            <td style={{ ...cellSty, textAlign: 'right',
                                                         color: '#6d7175' }}>
                                                {pricedPerUnit != null
                                                    ? _fmtMoney(pricedPerUnit)
                                                    : <span style={{ color: '#c4c8cc' }}>—</span>}
                                            </td>
                                            <td style={{ ...cellSty, textAlign: 'right' }}>
                                                {v.priced_revenue_seen
                                                    ? _fmtMoney(v.priced_revenue)
                                                    : <span style={{ color: '#c4c8cc' }}>—</span>}
                                            </td>
                                            <td style={{ ...cellSty, textAlign: 'right',
                                                         color: '#6d7175' }}>
                                                {_fmtMoney(v.msrp_price)}
                                            </td>
                                            <td style={{ ...cellSty, textAlign: 'right' }}>
                                                {_fmtMoney(v.msrp_revenue)}
                                            </td>
                                            <td style={{ ...cellSty, textAlign: 'right',
                                                         color: vTone }}>
                                                {vDelta == null ? '—'
                                                    : `${vDelta >= 0 ? '+' : ''}${_fmtMoney(vDelta)}`}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------- Inventory Status By Club report ---------------------------- */
// Sister to Products Sold By Club. Same filter shell, different table:
// product universe is tag-driven (Shopify products tagged "club: <name>"),
// columns show current inventory (on hand / available / committed / incoming)
// at the selected location plus the bin location values configured in the
// picking-portal settings.

function InventoryByClubCard({ product }) {
    const [open, setOpen] = useState(false);
    const variants = product.variants || [];
    const hasVariants = variants.length > 1
        || (variants.length === 1 && (variants[0].title || '').toLowerCase() !== 'default title');
    return (
        <div style={{ border: '1px solid #e1e3e5', borderRadius: 8, clipPath: 'inset(0 round 8px)', overflow: 'hidden' }}>
            <div
                onClick={() => hasVariants && setOpen(o => !o)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', background: '#f6f6f7',
                    cursor: hasVariants ? 'pointer' : 'default',
                    borderBottom: open ? '1px solid #e1e3e5' : 'none',
                }}
            >
                {product.image_url ? (
                    <SmartProductImage src={product.image_url} alt="" size={144}
                         style={{ width: 44, height: 44, objectFit: 'cover',
                                  borderRadius: 4, border: '1px solid #e1e3e5' }} />
                ) : (
                    <div style={{ width: 44, height: 44, borderRadius: 4,
                                  background: '#fff', border: '1px solid #e1e3e5' }} />
                )}
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {product.title || '(untitled product)'}
                        {hasVariants && (
                            <span style={{ color: '#6d7175', fontWeight: 400, marginLeft: 8 }}>
                                {open ? '▼' : '▶'} {variants.length} variants
                            </span>
                        )}
                    </div>
                    {product.product_type && (
                        <div style={{ fontSize: 11, color: '#6d7175' }}>{product.product_type}</div>
                    )}
                </div>
                <InventoryCells row={product} />
            </div>
            {open && hasVariants && (
                <div>
                    {variants.map((v) => (
                        <div key={v.variant_id}
                             style={{ display: 'flex', alignItems: 'center', gap: 12,
                                      padding: '8px 14px 8px 70px',
                                      borderBottom: '1px solid #f1f1f2',
                                      fontSize: 12 }}>
                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                <div style={{ fontWeight: 500 }}>{v.title || '(untitled variant)'}</div>
                                {v.sku && (
                                    <div style={{ color: '#6d7175', fontSize: 11 }}>{v.sku}</div>
                                )}
                            </div>
                            <InventoryCells row={v} variant />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function InventoryCells({ row, variant = false }) {
    const cell = (n, label) => (
        <div style={{ width: 78, textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: '#6d7175', textTransform: 'uppercase',
                          letterSpacing: '.04em' }}>{label}</div>
            <div style={{ fontSize: variant ? 12 : 13, fontWeight: 600,
                          color: n < 0 ? '#bf0711' : '#202223' }}>
                {Number(n || 0).toLocaleString()}
            </div>
        </div>
    );
    const bins = row.bin_locations || [];
    return (
        <>
            {cell(row.sold || row.sold_total, 'Sold')}
            {cell(row.on_hand,   'On Hand')}
            {cell(row.available, 'Available')}
            {cell(row.committed, 'Committed')}
            {cell(row.incoming,  'Incoming')}
            <div style={{ width: 160, textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: '#6d7175', textTransform: 'uppercase',
                              letterSpacing: '.04em' }}>Bin</div>
                {bins.length ? bins.map((b, i) => (
                    <div key={i} style={{ fontSize: variant ? 12 : 13, fontWeight: 500 }}>{b}</div>
                )) : (
                    <div style={{ fontSize: 12, color: '#6d7175' }}>—</div>
                )}
            </div>
        </>
    );
}

const INVENTORY_EMBELLISHMENT_TYPES = new Set(['logo', 'number', 'name', 'crest', 'decals', 'sponsor', 'surname', 'fee', 'embroidered logo', 'embroidered number']);

function InventoryByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { storeSlug } = useParams();
    // Cross-store id collision: shopify_location_id values are NOT unique
    // across stores. If the picker offered every allowed location globally
    // and the user picked "Surf: Teams Las Vegas" (84227621175) while on
    // /store/soccerpost/reporting/inventory-by-club, the backend resolved
    // 84227621175 in soccerpost's locations table and silently returned
    // Eatontown Warehouse data. Scoping the picker to the current store's
    // rows only closes that leak, plus we send `store=` on the request so
    // the backend rejects cross-store ids as a belt-and-suspenders defense.
    const scopedLocations = useMemo(
        () => (availableLocations || []).filter(l => !storeSlug || l.store_key === storeSlug),
        [availableLocations, storeSlug]);

    // 2026-08-04: default window shortened to 7 days (was 29). Report now
    // works with a location alone (no club required) — see backend.
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(6));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ products: [], available_clubs: [],
                                       date_from: '', date_to: '', selected_club: null,
                                       target_location: null });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('sold');
    // 2026-09-25 (Eric): 'Hide embellishments' toggle — off by default. Client-side:
    // rows whose product type is a logo / number / name / crest / decal / sponsor /
    // surname / fee line drop out of the list, the totals and the CSV.
    const [hideEmb, setHideEmb] = useState(searchParams.get('hide_emb') === '1');
    const isEmbellishment = (p) => INVENTORY_EMBELLISHMENT_TYPES.has(String(p.product_type || '').trim().toLowerCase());
    const visibleProducts = useMemo(
        () => hideEmb ? (data.products || []).filter(p => !isEmbellishment(p)) : (data.products || []),
        [data.products, hideEmb]); // eslint-disable-line react-hooks/exhaustive-deps
    const hiddenEmbCount = (data.products || []).length - visibleProducts.length;
    const [sortDir, setSortDir] = useState('desc');

    // 2026-09-25 (Eric): every picked club goes to the backend, not just the first.
    const selectedClubs = clubFilter.filter(Boolean);
    const selectedClub = selectedClubs.join(', ');

    const sortedProducts = useMemo(() => {
        const list = visibleProducts.slice();
        const cmpStr = (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase());
        list.sort((a, b) => {
            let cmp;
            switch (sortKey) {
                case 'product':   cmp = cmpStr(a.title, b.title); break;
                case 'sold':      cmp = (a.sold_total || 0) - (b.sold_total || 0); break;
                case 'on_hand':   cmp = (a.on_hand    || 0) - (b.on_hand    || 0); break;
                case 'available': cmp = (a.available  || 0) - (b.available  || 0); break;
                case 'committed': cmp = (a.committed  || 0) - (b.committed  || 0); break;
                case 'incoming':  cmp = (a.incoming   || 0) - (b.incoming   || 0); break;
                default:          cmp = 0;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [visibleProducts, sortKey, sortDir]);

    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else {
            setSortKey(key);
            setSortDir(key === 'product' ? 'asc' : 'desc');
        }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        if (hideEmb) p.set('hide_emb', '1');
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, hideEmb, setSearchParams]);

    const load = useCallback(async () => {
        // 2026-08-17: hard-gate on a location selection. Previously the report
        // fetched with `locations=[]`, which the backend interpreted as
        // "all of the caller's allowed locations". For an admin that meant
        // every store — so a soccerpost admin loading the report cold got
        // inventory reads against surf/rush/u90 too, which is nonsense here
        // and slow. Users also had no signal that they were seeing an
        // implicit "everything" — matched what they'd think was one loc's
        // data. Short-circuit here so the empty-state placeholder renders
        // instead and no request goes out at all.
        if (!locationFilter || locationFilter.length === 0) {
            setLoading(false);
            setData({ products: [], available_clubs: [],
                      date_from: dateFrom, date_to: dateTo,
                      selected_club: null, target_location: null });
            return;
        }
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            selectedClubs.forEach(c => p.append('clubs', c));
            // Pin the request to the current store so the backend won't
            // silently resolve a cross-store shopify_location_id against a
            // different store's locations table.
            if (storeSlug) p.set('store', storeSlug);
            const res = await fetchAuth(`/api/reports/inventory_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, selectedClub, storeSlug]);

    // 2026-08-19: explicit "Run Report" gate — NO auto-fetch on any filter
    // change (previously fired on every location/club/date pick). Rationale:
    // the report is Shopify-heavy (~30s at big locs) and intermediate
    // renders during filter picking led to Erin seeing no-club results while
    // still choosing a club. Now the ONLY trigger for the full report is a
    // click on the green Run Report button.
    //
    // Side effect: the club dropdown needs available_clubs to populate. We
    // fetch that separately via `clubs_only=true` on location change — a
    // lightweight DB-only query that skips products + Shopify entirely.
    const [lastRunKey, setLastRunKey] = useState('');
    const currentFilterKey = useMemo(
        () => JSON.stringify([dateFrom, dateTo, [...locationFilter].sort(), selectedClub]),
        [dateFrom, dateTo, locationFilter, selectedClub],
    );
    const filtersChanged = lastRunKey !== '' && lastRunKey !== currentFilterKey;
    const runReport = useCallback(async () => {
        setLastRunKey(currentFilterKey);
        await load();
    }, [load, currentFilterKey]);

    // Populate available_clubs on location / date change WITHOUT running
    // the heavy report. Skips when no location is picked (nothing to scope).
    useEffect(() => {
        if (!locationFilter || locationFilter.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const p = new URLSearchParams();
                if (dateFrom) p.set('date_from', dateFrom);
                if (dateTo)   p.set('date_to',   dateTo);
                locationFilter.forEach(l => p.append('locations', l));
                if (storeSlug) p.set('store', storeSlug);
                p.set('clubs_only', 'true');
                const res = await fetchAuth(`/api/reports/inventory_by_club?${p.toString()}`);
                if (!res.ok || cancelled) return;
                const j = await res.json();
                if (cancelled) return;
                // Merge available_clubs into existing data so the dropdown
                // populates without wiping the last-run product results.
                setData(d => ({ ...d, available_clubs: j.available_clubs || [] }));
            } catch { /* silent — dropdown just stays empty until Run Report */ }
        })();
        return () => { cancelled = true; };
    }, [fetchAuth, dateFrom, dateTo, locationFilter, storeSlug]);

    const totals = useMemo(() => {
        const products = visibleProducts;
        return {
            products:  products.length,
            sold:      products.reduce((s, p) => s + (p.sold_total || 0), 0),
            on_hand:   products.reduce((s, p) => s + (p.on_hand    || 0), 0),
            available: products.reduce((s, p) => s + (p.available  || 0), 0),
            committed: products.reduce((s, p) => s + (p.committed  || 0), 0),
            incoming:  products.reduce((s, p) => s + (p.incoming   || 0), 0),
        };
    }, [visibleProducts]);

    const handleExport = useCallback(() => {
        const rows = [['Product', 'Variant', 'SKU', 'Sold', 'On Hand', 'Available', 'Committed', 'Incoming', 'Bin Locations']];
        for (const p of visibleProducts) {
            const variants = p.variants && p.variants.length
                ? p.variants
                : [{ title: '', sku: '', sold: p.sold_total,
                     on_hand: p.on_hand, available: p.available,
                     committed: p.committed, incoming: p.incoming,
                     bin_locations: p.bin_locations }];
            for (const v of variants) {
                // When the bin metafield is configured at product level
                // (not per-variant), v.bin_locations is empty but
                // p.bin_locations carries the value. Fall back to the
                // parent product's bins so every CSV row has a bin.
                const vBins = (v.bin_locations || []).length
                    ? v.bin_locations
                    : (p.bin_locations || []);
                rows.push([p.title || '', v.title || '', v.sku || '',
                           Number(v.sold || 0),
                           Number(v.on_hand || 0),
                           Number(v.available || 0),
                           Number(v.committed || 0),
                           Number(v.incoming || 0),
                           vBins.join(' | ')]);
            }
        }
        const club = (selectedClub || 'club').replace(/[^\w-]+/g, '_');
        downloadCsv(`inventory_by_club_${club}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, selectedClub, visibleProducts]);

    const targetLoc = data.target_location || {};

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Inventory Status By Location / Club" storageKey="inventory_status_by_location_club_about_open" linkLabel="About this report">
                        <Text as="p">
                            Current On Hand / Available / Committed / Incoming at the
                            selected location, plus bin location(s) from picking
                            settings, alongside how many sold in the window. Pick a
                            club to scope to products tagged
                            <code style={{ margin: '0 4px' }}>club: &lt;name&gt;</code>;
                            leave the club filter empty to see every product that
                            sold at this location in the window.
                        </Text>
                    </ReportColumnHelp>
                    <Banner tone="info">
                        <strong>If a club is selected, sold counts are scoped to the selected club only.</strong>{' '}
                        Products that are tagged for multiple clubs (e.g. shared
                        embellishments / generic apparel) show only the units that
                        shipped on this club's orders in the window — not the
                        product's total sales across all clubs.
                    </Banner>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={scopedLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.products || []).length}
                        trailing={
                            <Button
                                variant="primary"
                                tone="success"
                                onClick={runReport}
                                disabled={loading || !(locationFilter && locationFilter.length > 0)}
                                loading={loading}
                            >
                                Run report
                            </Button>
                        }
                    />
                    {(filtersChanged || !locationFilter?.length) && (
                        <Text variant="bodySm" tone={filtersChanged ? 'caution' : 'subdued'}>
                            {filtersChanged
                                ? 'Filters changed — click Run Report to refresh.'
                                : 'Pick a location first.'}
                        </Text>
                    )}
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {targetLoc.name && (
                            <Text variant="bodySm" tone="subdued">
                                Inventory at <strong>{targetLoc.name}</strong>
                                {!targetLoc.has_bin_config &&
                                    ' · no bin config for this loc'}
                            </Text>
                        )}
                        {selectedClub ? (
                            <Text variant="bodySm" tone="subdued">
                                Club: <strong>{selectedClub}</strong>
                                {' '}· {totals.products.toLocaleString()} products
                                {' '}· {totals.sold.toLocaleString()} sold
                                {' '}· {totals.on_hand.toLocaleString()} on hand
                            </Text>
                        ) : (data.products || []).length > 0 ? (
                            <Text variant="bodySm" tone="subdued">
                                All clubs at this location
                                {' '}· {totals.products.toLocaleString()} products
                                {' '}· {totals.sold.toLocaleString()} sold
                                {' '}· {totals.on_hand.toLocaleString()} on hand
                            </Text>
                        ) : (
                            <Text variant="bodySm" tone="subdued">
                                Select a location to run the report.
                            </Text>
                        )}
                    </InlineStack>

                    {loading ? null : !locationFilter.length ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            Select a location to run the report.
                        </div>
                    ) : (data.products || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            {selectedClub
                                ? <>No products sold for <code>{selectedClub}</code> at this location in the window.</>
                                : <>No products sold at this location in the window.</>}
                        </div>
                    ) : (
                        <BlockStack gap="300">
                            <InlineStack gap="200" align="end" blockAlign="center" wrap>
                                <Button size="slim" pressed={hideEmb} onClick={() => setHideEmb(v => !v)}>
                                    {hideEmb ? `Embellishments hidden (${hiddenEmbCount})` : 'Hide embellishments'}
                                </Button>
                                <Text variant="bodySm" tone="subdued">Sort by</Text>
                                <ButtonGroup variant="segmented">
                                    <Button size="slim" pressed={sortKey === 'product'}   onClick={() => handleSort('product')}>Product{sortArrow('product')}</Button>
                                    <Button size="slim" pressed={sortKey === 'sold'}      onClick={() => handleSort('sold')}>Sold{sortArrow('sold')}</Button>
                                    <Button size="slim" pressed={sortKey === 'on_hand'}   onClick={() => handleSort('on_hand')}>On Hand{sortArrow('on_hand')}</Button>
                                    <Button size="slim" pressed={sortKey === 'available'} onClick={() => handleSort('available')}>Available{sortArrow('available')}</Button>
                                    <Button size="slim" pressed={sortKey === 'committed'} onClick={() => handleSort('committed')}>Committed{sortArrow('committed')}</Button>
                                    <Button size="slim" pressed={sortKey === 'incoming'}  onClick={() => handleSort('incoming')}>Incoming{sortArrow('incoming')}</Button>
                                </ButtonGroup>
                            </InlineStack>
                            <BlockStack gap="200">
                                {sortedProducts.map((p) => (
                                    <InventoryByClubCard key={p.product_id} product={p} />
                                ))}
                            </BlockStack>
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

// ── Product Analysis By Club (Fulfilled vs Missing) ──────────────────
// Sister to InventoryByClub. Adds two-state toggle: Fulfilled vs Not
// Fulfilled OR Partial Items Missing. Backend returns all three qty
// columns on every row; the toggle just picks which render.
function ProductFulfillmentByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { storeSlug } = useParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    const [view, setView] = useState(['missing', 'partial', 'sku'].includes(searchParams.get('view')) ? searchParams.get('view') : 'fulfilled');
    // 2026-09-15 (Eric): SKU filter — case-insensitive "contains" on the SKU,
    // applied client-side to every view (View C keeps an order only for its
    // matching items, so the totals column still shows the whole order).
    const [skuFilter, setSkuFilter] = useState(searchParams.get('sku') || '');
    const skuNeedle = skuFilter.trim().toLowerCase();
    const skuMatch = (sku) => !skuNeedle || String(sku || '').toLowerCase().includes(skuNeedle);
    // View D (2026-09-22, Eric): SKU Analysis — View C's orders rolled up per SKU.
    // `q` filters on product / vendor / variant text (client-side, this view only).
    const [skuA, setSkuA] = useState({ rows: [], loaded: false });
    const [prodFilter, setProdFilter] = useState(searchParams.get('q') || '');
    const prodNeedle = prodFilter.trim().toLowerCase();
    const [dSort, setDSort] = useState({ key: 'missing', asc: false });
    const dSortVal = (r, k) => {
        if (k === 'product') return String(r.product_title || r.title || '');   // sort by what the cell shows
        if (k === 'vendor')  return String(r.vendor || '');
        if (k === 'variant') return String(r.variant_title || '');
        if (k === 'sku')     return String(r.sku || '');
        if (k === 'missing') return r.missing_qty || 0;
        if (k === 'orders')  return r.orders_affected || 0;
        if (k === 'clubs')   return r.clubs_affected || 0;
        if (k === 'on_hand' || k === 'available' || k === 'incoming') return r[k] == null ? -1 : r[k];
        return '';
    };
    const dSorted = useMemo(() => {
        const rows = skuA.rows.filter(r => skuMatch(r.sku) && (!prodNeedle ||
            `${r.product_title || ''} ${r.vendor || ''} ${r.variant_title || ''}`.toLowerCase().includes(prodNeedle)));
        const { key, asc } = dSort;
        const coll = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        rows.sort((x, y) => {
            const a = dSortVal(x, key), b = dSortVal(y, key);
            let c = (typeof a === 'number' && typeof b === 'number') ? a - b : coll.compare(String(a), String(b));
            if (c === 0) c = (y.missing_qty || 0) - (x.missing_qty || 0);
            if (c === 0) c = coll.compare(String(x.sku || ''), String(y.sku || ''));
            return asc ? c : -c;
        });
        return rows;
    }, [skuA.rows, dSort, skuNeedle, prodNeedle]); // eslint-disable-line react-hooks/exhaustive-deps
    const dToggle = (k) => setDSort(s => s.key === k ? { key: k, asc: !s.asc } : { key: k, asc: ['product', 'vendor', 'variant', 'sku'].includes(k) });
    const dArrow = (k) => dSort.key === k ? (dSort.asc ? ' ▲' : ' ▼') : '';
    // View C (2026-09-13, Eric): orders in progress + the items still to pick.
    // 2026-09-24 (Eric): "if all clubs are selected, it should operate like no
    // clubs are selected". The backend already reads an empty club list as every
    // club, so sending all of them is pure weight: Max selected 262 and the URL
    // passed nginx's 8k request-line limit, which answers HTTP 414 before the
    // request ever reaches us. Ticking every club now sends nothing at all.
    // Held in a ref, not read from `data`: the fetch callback must not take
    // available_clubs as a dependency, because the fetch itself sets `data` and
    // the pair would loop.
    const availableClubsRef = useRef([]);
    const clubsForQuery = useCallback((selected) => {
        const sel = [...new Set((selected || []).filter(Boolean))];
        const all = [...new Set((availableClubsRef.current || []).filter(Boolean))];
        if (!sel.length) return [];
        if (all.length && sel.length >= all.length) return [];   // every club = no filter
        return sel;
    }, []);
    const [partial, setPartial] = useState({ orders: [], loaded: false });
    const [pSort, setPSort] = useState({ key: 'placed_d', asc: true });   // 2026-09-15 (Eric): oldest first by default
    const pRemaining = (o) => o.items.reduce((t, it) => t + it.remaining, 0);
    const pSortVal = (o, k) => {
        if (k === 'order') return String(o.order_name || '');
        if (k === 'club') return String(o.club || '');
        if (k === 'placed_d') return String(o.placed_d || '');
        if (k === 'status') return String(o.status || '');
        if (k === 'picked') return o.units_total ? o.units_picked / o.units_total : 0;
        if (k === 'missing') return pRemaining(o);
        if (k === 'on_hand' || k === 'available' || k === 'incoming') {
            const vals = o.items.map(it => it[k]).filter(v => v != null);
            return vals.length ? Math.min(...vals) : -1;
        }
        return '';
    };
    const pSorted = useMemo(() => {
        const rows = skuNeedle
            ? partial.orders.map(o => ({ ...o, items: o.items.filter(it => skuMatch(it.sku)) })).filter(o => o.items.length)
            : [...partial.orders];
        const { key, asc } = pSort;
        const coll = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        rows.sort((x, y) => {
            const a = pSortVal(x, key), b = pSortVal(y, key);
            let c = (typeof a === 'number' && typeof b === 'number') ? a - b : coll.compare(String(a), String(b));
            if (c === 0 && key === 'picked') c = x.units_picked - y.units_picked;
            if (c === 0) c = coll.compare(String(x.order_name || ''), String(y.order_name || ''));
            return asc ? c : -c;
        });
        return rows;
    }, [partial.orders, pSort, skuNeedle]); // eslint-disable-line react-hooks/exhaustive-deps
    const pToggle = (k) => setPSort(s => s.key === k ? { key: k, asc: !s.asc } : { key: k, asc: k === 'order' || k === 'club' || k === 'status' });
    const pArrow = (k) => pSort.key === k ? (pSort.asc ? ' ▲' : ' ▼') : '';
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], available_clubs: [],
                                       date_from: '', date_to: '', selected_club: null,
                                       target_location: null });
    const [err, setErr] = useState(null);
    // SKU-cell popover: single-open-at-a-time, keyed by variant_id (unique per row).
    const [openSkuKey, setOpenSkuKey] = useState(null);


    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        if (view === 'missing' || view === 'partial' || view === 'sku') p.set('view', view);
        if (skuFilter.trim()) p.set('sku', skuFilter.trim());
        if (view === 'sku' && prodFilter.trim()) p.set('q', prodFilter.trim());
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, view, skuFilter, prodFilter, setSearchParams]);

    // 2026-09-13 (Eric): Run Report gate, like Inventory By Club — the all-clubs
    // run can take a couple of minutes, so nothing fires until the button is
    // clicked (deep links with a location auto-run once on mount).
    const canRun = (locationFilter || []).length > 0;
    const load = useCallback(async () => {
        if (!canRun) { setData(d => ({ ...d, rows: [] })); return; }
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (view === 'partial') {
                if (dateFrom) p.set('date_from', dateFrom);
                if (dateTo)   p.set('date_to',   dateTo);
                locationFilter.forEach(l => p.append('locations', l));
                clubsForQuery(clubFilter).forEach(c => p.append('clubs', c));
                const res = await fetchAuth(`/api/reports/product_fulfillment_by_club/partial_orders?${p.toString()}`);
                if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(j.detail || `HTTP ${res.status}`);
                }
                const j = await res.json();
                setPartial({ orders: j.orders || [], loaded: true });
                return;
            }
            if (view === 'sku') {
                if (dateFrom) p.set('date_from', dateFrom);
                if (dateTo)   p.set('date_to',   dateTo);
                locationFilter.forEach(l => p.append('locations', l));
                clubsForQuery(clubFilter).forEach(c => p.append('clubs', c));
                const res = await fetchAuth(`/api/reports/product_fulfillment_by_club/sku_analysis?${p.toString()}`);
                if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(j.detail || `HTTP ${res.status}`);
                }
                const j = await res.json();
                setSkuA({ rows: j.rows || [], loaded: true });
                return;
            }
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            // 2026-09-13 (Eric): none selected = every club at the location; several = those.
            clubsForQuery(clubFilter).forEach(c => p.append('clubs', c));
            const res = await fetchAuth(`/api/reports/product_fulfillment_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter, canRun, view]);
    // 2026-09-15 (Eric): NO auto-run, not even from a deep link with a location —
    // the report only runs when Run Report is clicked.
    // Club picker options without running the heavy report: Revenue By Club's
    // available_clubs for the same window + locations (cached, fast).
    useEffect(() => {
        if (!canRun) return;
        const p = new URLSearchParams();
        if (dateFrom) p.set('date_from', dateFrom);
        if (dateTo)   p.set('date_to',   dateTo);
        locationFilter.forEach(l => p.append('locations', l));
        fetchAuth(`/api/reports/revenue_by_club?${p.toString()}`).then(r => r.ok ? r.json() : null)
            .then(j => { if (j && Array.isArray(j.available_clubs)) {
                availableClubsRef.current = j.available_clubs;
                setData(d => ({ ...d, available_clubs: j.available_clubs }));
            } })
            .catch(() => {});
    }, [fetchAuth, dateFrom, dateTo, locationFilter, canRun]);

    const [sortKey, setSortKey] = useState('missing');
    const [sortDir, setSortDir] = useState('desc');
    const sortedRows = useMemo(() => {
        const list = (data.rows || []).filter(r => skuMatch(r.sku));
        const cmpStr = (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase());
        list.sort((a, b) => {
            let cmp = 0;
            switch (sortKey) {
                case 'product':       cmp = cmpStr(a.product_title, b.product_title); break;
                case 'variant':       cmp = cmpStr(a.variant_title, b.variant_title); break;
                case 'sku':           cmp = cmpStr(a.sku, b.sku); break;
                case 'fulfilled':     cmp = (a.fulfilled_qty     || 0) - (b.fulfilled_qty     || 0); break;
                case 'not_fulfilled': cmp = (a.not_fulfilled_qty || 0) - (b.not_fulfilled_qty || 0); break;
                case 'missing':       cmp = (a.missing_qty       || 0) - (b.missing_qty       || 0); break;
                case 'on_hand':       cmp = (a.on_hand           || 0) - (b.on_hand           || 0); break;
                case 'available':     cmp = (a.available         || 0) - (b.available         || 0); break;
                case 'committed':     cmp = (a.committed         || 0) - (b.committed         || 0); break;
                case 'incoming':      cmp = (a.incoming          || 0) - (b.incoming          || 0); break;
                default: cmp = 0;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [data.rows, sortKey, sortDir, skuNeedle]); // eslint-disable-line react-hooks/exhaustive-deps
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir(['product','variant','sku'].includes(key) ? 'asc' : 'desc'); }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const handleExport = useCallback(() => {
        if (view === 'sku') {
            const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
            const lines = [['Product', 'Vendor', 'Variant', 'SKU', 'Missing Qty', 'Orders Affected', 'Clubs Affected', 'On Hand', 'Available', 'Incoming', 'Bin', 'Orders'].map(esc).join(',')];
            for (const r of dSorted) {
                lines.push([r.product_title, r.vendor, r.variant_title, r.sku, r.missing_qty, r.orders_affected, r.clubs_affected, r.on_hand ?? '', r.available ?? '', r.incoming ?? '',
                            (r.bin_locations || []).join(' / '), (r.orders || []).map(o => `${o.order_name} (${o.remaining})`).join('; ')].map(esc).join(','));
            }
            const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob); const a = document.createElement('a');
            a.href = url; a.download = `sku_analysis_${dateFrom}_to_${dateTo}.csv`; a.click(); URL.revokeObjectURL(url);
            return;
        }
        if (view === 'partial') {
            const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
            const lines = [['Order', 'Club', 'Date', 'Status', 'Units picked', 'Units total', 'SKU', 'Item', 'Remaining', 'Bin', 'On Hand', 'Available', 'Incoming'].map(esc).join(',')];
            for (const o of pSorted) for (const it of o.items) {
                lines.push([o.order_name, o.club, o.placed_d, o.status, o.units_picked, o.units_total, it.sku, it.title, it.remaining, (it.bin_locations || []).join(' / '), it.on_hand ?? '', it.available ?? '', it.incoming ?? ''].map(esc).join(','));
            }
            const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob); const a = document.createElement('a');
            a.href = url; a.download = `partial_orders_${dateFrom}_to_${dateTo}.csv`; a.click(); URL.revokeObjectURL(url);
            return;
        }
        const headers = ['Product', 'Variant', 'SKU'];
        if (view === 'fulfilled') headers.push('Fulfilled', 'Not Fulfilled');
        else                       headers.push('Missing');
        headers.push('On Hand', 'Available', 'Committed', 'Incoming', 'Bin');
        const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const lines = [headers.map(escape).join(',')];
        for (const r of sortedRows) {
            const cells = [r.product_title || '', r.variant_title || '', r.sku || ''];
            if (view === 'fulfilled') {
                cells.push(r.fulfilled_qty || 0, r.not_fulfilled_qty || 0);
            } else {
                cells.push(r.missing_qty || 0);
            }
            cells.push(r.on_hand || 0, r.available || 0, r.committed || 0,
                       r.incoming || 0, (r.bin_locations || []).join(' / '));
            lines.push(cells.map(escape).join(','));
        }
        const csv = lines.join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeClub = (clubFilter.length === 1 ? clubFilter[0] : clubFilter.length ? `${clubFilter.length}-clubs` : 'all-clubs').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
        a.download = `product-analysis-by-club-${safeClub}-${dateFrom}-${dateTo}-${view}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [sortedRows, view, clubFilter, dateFrom, dateTo, partial, pSorted, dSorted]);

    return (
        <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Partial Analysis By Club</Text>

            <ReportFilters
                dateFrom={dateFrom} setDateFrom={setDateFrom}
                dateTo={dateTo} setDateTo={setDateTo}
                locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                clubFilter={clubFilter} setClubFilter={setClubFilter}
                availableLocations={availableLocations}
                availableClubs={data.available_clubs || []}
                multiClub
                trailing={
                    // 2026-09-15 (Eric): SKU filter sits right under the date selector, then
                    // Run Report — whose label stays put while the report runs (Polaris'
                    // `loading` swapped it for a bare spinner for the whole long run).
                    <InlineStack gap="200" blockAlign="center">
                        <div style={{ minWidth: 240 }}
                             onKeyDown={e => { if (e.key === 'Enter' && canRun && !loading) load(); }}>
                            {/* Enter in the SKU box runs the report (2026-09-15: a user typed a SKU,
                                pressed Enter, saw nothing — the filter only applies to loaded results). */}
                            <TextField label="SKU" labelHidden placeholder="SKU contains… (Enter runs the report)" value={skuFilter} onChange={setSkuFilter}
                                       autoComplete="off" clearButton onClearButtonClick={() => setSkuFilter('')} />
                        </div>
                        {view === 'sku' && (
                            <div style={{ minWidth: 240 }}>
                                <TextField label="Product" labelHidden placeholder="Product / vendor / variant contains…" value={prodFilter} onChange={setProdFilter}
                                           autoComplete="off" clearButton onClearButtonClick={() => setProdFilter('')} />
                            </div>
                        )}
                        <Button variant="primary" tone="success" onClick={load} disabled={loading || !canRun}>
                            {loading ? 'Running…' : 'Run Report'}
                        </Button>
                    </InlineStack>
                }
            />
            {!canRun && (
                <Text variant="bodySm" tone="subdued">
                    Pick a Location (and optionally one or more Clubs — none means every club at that location), then click <strong>Run Report</strong>.
                </Text>
            )}

            <InlineStack gap="200" align="space-between">
                <ButtonGroup segmented>
                    <Button pressed={view === 'fulfilled'} onClick={() => setView('fulfilled')}>
                        Fulfilled vs Not Fulfilled
                    </Button>
                    <Button pressed={view === 'missing'} onClick={() => setView('missing')}>
                        Open Items
                    </Button>
                    <Button pressed={view === 'partial'} onClick={() => setView('partial')}>
                        Partials: Missing SKUs
                    </Button>
                    <Button pressed={view === 'sku'} onClick={() => setView('sku')}>
                        SKU Analysis
                    </Button>
                </ButtonGroup>
                <Button onClick={() => handleExport()} disabled={view === 'partial' ? !pSorted.length : view === 'sku' ? !dSorted.length : !sortedRows.length}>Export CSV</Button>
            </InlineStack>

            {/* 2026-09-24 (Eric): the view definitions collapse away once read, but
                open expanded — four view names that do not explain themselves. */}
            <ReportColumnHelp storageKey="pfbc_view_help" defaultOpen
                              linkLabel="What each view shows">
                    <Text as="p"><strong>View A — Fulfilled vs Not Fulfilled</strong></Text>
                    <Text as="p" tone="subdued">
                        Row shows: Fulfilled Qty + Not Fulfilled Qty + inventory columns.
                        Counts every unit ordered by the club in the window.
                        <br />Fulfilled = shipped OR transferred-to-pickup.
                        <br />Not Fulfilled = everything else, regardless of pick state.
                    </Text>
                    <Text as="p"><strong>View B — Open Items</strong></Text>
                    <Text as="p" tone="subdued">
                        Row shows: Missing Qty + inventory columns.
                        The "what does the warehouse still need to pick" workload.
                        <br />Partial orders (some items already fulfilled): count all remaining unfulfilled items that are not picked.
                        <br />In-progress orders (at least one item picked, nothing fulfilled): count only unpicked items.
                        <br />Unstarted orders (nothing picked, nothing fulfilled): count all items (all are unpicked).
                    </Text>
                    <Text as="p"><strong>View C — Partials: Missing SKUs</strong></Text>
                    <Text as="p" tone="subdued">
                        One row per SKU still to pick on an order in progress at the location (at least one unit picked, not yet fully fulfilled here),
                        with its bin and the location's On Hand, Available and Incoming stock. Available turns red when it is below the units still to pick.
                        <br />Untouched orders (nothing picked) and fully picked orders are not shown.
                    </Text>
                    <Text as="p"><strong>View D — SKU Analysis</strong></Text>
                    <Text as="p" tone="subdued">
                        The same orders as View C, rolled up per product: one row per Product / Vendor / Variant / SKU with the units still to pick,
                        how many orders and clubs are waiting on it, and the location's bin and stock. Click a SKU to see the orders it is holding up.
                        Sort any column; filter by SKU or by product / vendor / variant text.
                    </Text>
            </ReportColumnHelp>

            {err && <Banner tone="critical">{err}</Banner>}
            {loading && <Text as="p">Loading…</Text>}

            {view === 'partial' && !loading && !err && (
                <Card>
                    <BlockStack gap="200">
                        {!partial.loaded ? (
                            <Text as="p" tone="subdued">
                                {skuNeedle
                                    ? <>Click <strong>Run Report</strong> (or press Enter in the SKU box) to find partial orders missing a SKU containing "{skuFilter.trim()}".</>
                                    : <>Click <strong>Run Report</strong> to load the partial orders.</>}
                            </Text>
                        ) : pSorted.length === 0 ? (
                            <Text as="p" tone="subdued">{skuNeedle ? `No partial orders are missing a SKU containing "${skuFilter.trim()}".` : 'No partial orders for the current filters.'}</Text>
                        ) : (
                            <div className="orders-summary-table-scroll">
                                <table className="orders-summary-table">
                                    <thead>
                                        <tr>
                                            <th className="sortable" onClick={() => pToggle('order')}>Order{pArrow('order')}</th>
                                            <th className="sortable" onClick={() => pToggle('club')}>Club{pArrow('club')}</th>
                                            <th className="sortable" onClick={() => pToggle('placed_d')}>Date{pArrow('placed_d')}</th>
                                            <th className="sortable" onClick={() => pToggle('status')}>Status{pArrow('status')}</th>
                                            <th className="sortable num" onClick={() => pToggle('picked')} title="Sorts by the picked share of the order">Picked / Total{pArrow('picked')}</th>
                                            <th className="sortable" onClick={() => pToggle('missing')} title="Sorts by units still to pick">Missing SKU{pArrow('missing')}</th>
                                            <th className="sortable num" onClick={() => pToggle('on_hand')} title="Stock at this location; sorts by the order's lowest SKU">On Hand{pArrow('on_hand')}</th>
                                            <th className="sortable num" onClick={() => pToggle('available')} title="On hand minus committed; red when below the units still to pick">Available{pArrow('available')}</th>
                                            <th className="sortable num" onClick={() => pToggle('incoming')} title="Inbound on open transfers / purchase orders">Incoming{pArrow('incoming')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* 2026-09-14 (Eric): one row per missing SKU with On Hand / Available /
                                            Incoming at the location; the order cells span the order's SKU rows. */}
                                        {pSorted.map(o => o.items.map((it, ix) => {
                                            const n = o.items.length;
                                            const short = it.available != null && it.available < it.remaining;
                                            const cell = (v) => (v == null ? <span style={{ color: '#9ea1a3' }}>—</span> : v);
                                            return (
                                                <tr key={`${o.order_id}|${it.sku}`} className={ix === 0 ? 'pfbc-order-first' : undefined}>
                                                    {ix === 0 && (<>
                                                        <td rowSpan={n} style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}><Link to={`/store/${storeSlug || 'soccerpost'}/orders/${o.order_id}`}>{o.order_name}</Link></td>
                                                        <td rowSpan={n} style={{ verticalAlign: 'top' }}>{o.club}</td>
                                                        <td rowSpan={n} style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{o.placed_d}</td>
                                                        <td rowSpan={n} style={{ verticalAlign: 'top' }}><Badge tone={o.status === 'PARTIALLY_FULFILLED' ? 'attention' : undefined}>{(o.status || '').replace(/_/g, ' ').toLowerCase()}</Badge></td>
                                                        <td rowSpan={n} className="num" style={{ verticalAlign: 'top' }}>{o.units_picked} / {o.units_total}</td>
                                                    </>)}
                                                    <td><b>{it.remaining}×</b> <code>{it.sku}</code> {it.title}
                                                        {it.bin_locations?.length ? <span style={{ color: '#6d7175' }}> · bin {it.bin_locations.join(' / ')}</span> : null}
                                                    </td>
                                                    <td className="num">{cell(it.on_hand)}</td>
                                                    <td className="num" style={short ? { color: '#d72c0d', fontWeight: 600 } : undefined}>{cell(it.available)}</td>
                                                    <td className="num">{cell(it.incoming)}</td>
                                                </tr>
                                            );
                                        }))}
                                    </tbody>
                                    <tfoot>
                                        <tr><td colSpan={4}><b>{pSorted.length} orders</b></td><td className="num"><b>{pSorted.reduce((s, o) => s + o.units_picked, 0)} / {pSorted.reduce((s, o) => s + o.units_total, 0)}</b></td><td><b>{pSorted.reduce((s, o) => s + o.items.reduce((t, it) => t + it.remaining, 0), 0)} units to pick</b></td><td className="num" colSpan={3}><b>{pSorted.reduce((s, o) => s + o.items.filter(it => it.available != null && it.available < it.remaining).length, 0)} SKUs short</b></td></tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </BlockStack>
                </Card>
            )}

            {view === 'sku' && !loading && !err && (
                <Card>
                    <BlockStack gap="200">
                        {!skuA.loaded ? (
                            <Text as="p" tone="subdued">Click <strong>Run Report</strong> to roll the partial orders up by SKU.</Text>
                        ) : dSorted.length === 0 ? (
                            <Text as="p" tone="subdued">{(skuNeedle || prodNeedle) ? 'No missing SKUs match the current filters.' : 'No partial orders for the current filters.'}</Text>
                        ) : (
                            <div className="orders-summary-table-scroll">
                                <table className="orders-summary-table">
                                    <thead>
                                        <tr>
                                            <th className="sortable" onClick={() => dToggle('product')}>Product{dArrow('product')}</th>
                                            <th className="sortable" onClick={() => dToggle('vendor')}>Vendor{dArrow('vendor')}</th>
                                            <th className="sortable" onClick={() => dToggle('variant')}>Variant{dArrow('variant')}</th>
                                            <th className="sortable" onClick={() => dToggle('sku')}>SKU{dArrow('sku')}</th>
                                            <th className="sortable num" onClick={() => dToggle('missing')} title="Units still to pick across the affected orders">Missing Qty{dArrow('missing')}</th>
                                            <th className="sortable num" onClick={() => dToggle('orders')} title="Orders in progress waiting on this SKU">Orders{dArrow('orders')}</th>
                                            <th className="sortable num" onClick={() => dToggle('clubs')} title="Distinct clubs among those orders">Clubs{dArrow('clubs')}</th>
                                            <th className="sortable num" onClick={() => dToggle('on_hand')} title="Stock at this location">On Hand{dArrow('on_hand')}</th>
                                            <th className="sortable num" onClick={() => dToggle('available')} title="On hand minus committed; red when below Missing Qty">Available{dArrow('available')}</th>
                                            <th className="sortable num" onClick={() => dToggle('incoming')} title="Inbound on open transfers / purchase orders">Incoming{dArrow('incoming')}</th>
                                            <th>Bin</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dSorted.map(r => {
                                            const skuKey = `d|${r.key || r.sku}`;   // r.key is unique per row (no-SKU rows share sku '')
                                            const orders = Array.isArray(r.orders) ? r.orders : [];
                                            const short = r.available != null && r.available < r.missing_qty;
                                            const cell = (v) => (v == null ? <span style={{ color: '#9ea1a3' }}>—</span> : v);
                                            return (
                                                <tr key={skuKey}>
                                                    <td>{r.product_title || r.title || <span style={{ color: '#9ea1a3' }}>—</span>}</td>
                                                    <td>{r.vendor || <span style={{ color: '#9ea1a3' }}>—</span>}</td>
                                                    <td>{r.variant_title}</td>
                                                    <td>
                                                        <Popover active={openSkuKey === skuKey} onClose={() => setOpenSkuKey(null)} preferredAlignment="left"
                                                                 activator={<Button variant="plain" onClick={() => setOpenSkuKey(k => k === skuKey ? null : skuKey)} disabled={orders.length === 0}>{r.sku || '—'}</Button>}>
                                                            <div style={{ padding: 12, minWidth: 260, maxHeight: 320, overflowY: 'auto' }}>
                                                                <Text as="p" tone="subdued">{orders.length} order{orders.length === 1 ? '' : 's'} waiting on {r.sku}</Text>
                                                                <BlockStack gap="100">
                                                                    {orders.map(o => (
                                                                        <InlineStack key={o.order_id} gap="200" blockAlign="baseline">
                                                                            <Link to={`/store/${storeSlug || 'soccerpost'}/orders/${o.order_id}?loc=all`} onClick={() => setOpenSkuKey(null)}>{o.order_name || o.order_id}</Link>
                                                                            <span style={{ color: '#6d7175' }}>{o.remaining}× · {o.club} · {o.placed_d}</span>
                                                                        </InlineStack>
                                                                    ))}
                                                                </BlockStack>
                                                            </div>
                                                        </Popover>
                                                    </td>
                                                    <td className="num"><b>{r.missing_qty}</b></td>
                                                    <td className="num">{r.orders_affected}</td>
                                                    <td className="num">{r.clubs_affected}</td>
                                                    <td className="num">{cell(r.on_hand)}</td>
                                                    <td className="num" style={short ? { color: '#d72c0d', fontWeight: 600 } : undefined}>{cell(r.available)}</td>
                                                    <td className="num">{cell(r.incoming)}</td>
                                                    <td>{(r.bin_locations || []).join(' / ')}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={4}><b>{dSorted.length} SKUs</b></td>
                                            <td className="num"><b>{dSorted.reduce((s, r) => s + (r.missing_qty || 0), 0)}</b></td>
                                            <td className="num"><b>{new Set(dSorted.flatMap(r => (r.orders || []).map(o => o.order_id))).size}</b></td>
                                            <td className="num"><b>{new Set(dSorted.flatMap(r => (r.orders || []).map(o => o.club))).size}</b></td>
                                            <td colSpan={4}></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </BlockStack>
                </Card>
            )}

            {view !== 'partial' && view !== 'sku' && !loading && !err && (
                <Card>
                    <div className="orders-summary-table-scroll">
                        <table className="orders-summary-table">
                            <thead>
                                <tr>
                                    <th className="sortable" onClick={() => handleSort('product')}>
                                        Product{sortArrow('product')}
                                    </th>
                                    <th className="sortable" onClick={() => handleSort('variant')}>
                                        Variant{sortArrow('variant')}
                                    </th>
                                    <th className="sortable" onClick={() => handleSort('sku')}>
                                        SKU{sortArrow('sku')}
                                    </th>
                                    {view === 'fulfilled' && (<>
                                        <th className="sortable num" onClick={() => handleSort('fulfilled')}>
                                            Fulfilled{sortArrow('fulfilled')}
                                        </th>
                                        <th className="sortable num" onClick={() => handleSort('not_fulfilled')}>
                                            Not Fulfilled{sortArrow('not_fulfilled')}
                                        </th>
                                    </>)}
                                    {view === 'missing' && (
                                        <th className="sortable num" onClick={() => handleSort('missing')}>
                                            Missing{sortArrow('missing')}
                                        </th>
                                    )}
                                    <th className="sortable num" onClick={() => handleSort('on_hand')}>
                                        On Hand{sortArrow('on_hand')}
                                    </th>
                                    <th className="sortable num" onClick={() => handleSort('available')}>
                                        Available{sortArrow('available')}
                                    </th>
                                    <th className="sortable num" onClick={() => handleSort('committed')}>
                                        Committed{sortArrow('committed')}
                                    </th>
                                    <th className="sortable num" onClick={() => handleSort('incoming')}>
                                        Incoming{sortArrow('incoming')}
                                    </th>
                                    <th>Bin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.length === 0 && (
                                    <tr><td colSpan={view === 'fulfilled' ? 10 : 9} style={{ textAlign: 'center', padding: 16, color: '#6d7175' }}>
                                        No rows for the current filters.
                                    </td></tr>
                                )}
                                {sortedRows.map(r => {
                                    const skuKey = r.variant_id || r.sku;
                                    const orders = Array.isArray(r.orders) ? r.orders : [];
                                    return (
                                    <tr key={`${r.variant_id || r.sku}`}>
                                        <td>{r.product_title}</td>
                                        <td>{r.variant_title}</td>
                                        <td>
                                            <Popover
                                                active={openSkuKey === skuKey}
                                                onClose={() => setOpenSkuKey(null)}
                                                preferredAlignment="left"
                                                activator={
                                                    <Button
                                                        variant="plain"
                                                        onClick={() => setOpenSkuKey(k => k === skuKey ? null : skuKey)}
                                                        disabled={orders.length === 0}
                                                    >
                                                        {r.sku || '—'}
                                                    </Button>
                                                }
                                            >
                                                <div style={{ padding: 12, minWidth: 200, maxHeight: 320, overflowY: 'auto' }}>
                                                    <Text as="p" tone="subdued">
                                                        {orders.length} order{orders.length === 1 ? '' : 's'} contain {r.sku}
                                                    </Text>
                                                    <BlockStack gap="100">
                                                        {orders.map(o => (
                                                            <Link
                                                                key={o.order_id}
                                                                to={`/store/${storeSlug || 'soccerpost'}/orders/${o.order_id}?loc=all`}
                                                                onClick={() => setOpenSkuKey(null)}
                                                            >
                                                                {o.order_name || o.order_id}
                                                            </Link>
                                                        ))}
                                                    </BlockStack>
                                                </div>
                                            </Popover>
                                        </td>
                                        {view === 'fulfilled' && (<>
                                            <td className="num">{r.fulfilled_qty}</td>
                                            <td className="num">{r.not_fulfilled_qty}</td>
                                        </>)}
                                        {view === 'missing' && (
                                            <td className="num">{r.missing_qty}</td>
                                        )}
                                        <td className="num">{r.on_hand}</td>
                                        <td className="num">{r.available}</td>
                                        <td className="num">{r.committed}</td>
                                        <td className="num">{r.incoming}</td>
                                        <td>{(r.bin_locations || []).join(', ')}</td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr style={{fontWeight:600}}>
                                    <td colSpan={3}>Totals ({(data.rows || []).length} rows)</td>
                                    {view === 'fulfilled' && (<>
                                        <td className="num">{(data.rows||[]).reduce((s,r)=>s+(r.fulfilled_qty||0),0)}</td>
                                        <td className="num">{(data.rows||[]).reduce((s,r)=>s+(r.not_fulfilled_qty||0),0)}</td>
                                    </>)}
                                    {view === 'missing' && (
                                        <td className="num">{(data.rows||[]).reduce((s,r)=>s+(r.missing_qty||0),0)}</td>
                                    )}
                                    <td className="num">{(data.rows||[]).reduce((s,r)=>s+(r.on_hand||0),0)}</td>
                                    <td className="num">{(data.rows||[]).reduce((s,r)=>s+(r.available||0),0)}</td>
                                    <td className="num">{(data.rows||[]).reduce((s,r)=>s+(r.committed||0),0)}</td>
                                    <td className="num">{(data.rows||[]).reduce((s,r)=>s+(r.incoming||0),0)}</td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </Card>
            )}
        </BlockStack>
    );
}

// Pickup Location By Club — non-cancelled PICK_UP orders for a single club,
// grouped by pickup Soccer Post. Color-coded so operators can spot customers
// who chose a wrong-city pickup by mistake.
//   green  = pickup share >= 15% (primary)
//   yellow = pickup share 2 - 15% (secondary / reasonable)
//   red    = pickup share < 2%   (likely wrong-location mistakes)
const _pickupSubHeadStyle = {
    textAlign: 'left', padding: '6px 24px',
    background: '#f0f2f5', borderBottom: '1px solid #e1e3e5',
    fontSize: 10, color: '#6d7175',
    letterSpacing: 0.04, textTransform: 'uppercase',
    fontWeight: 700, whiteSpace: 'nowrap',
};
const _pickupSubCellStyle = {
    padding: '6px 24px', borderBottom: '1px solid #f1f2f3', fontSize: 13,
};

/* ---------- Store Fulfillments report ----------------------------------- */
// Pickup-cycle status per retail store (Being Prepared → In Transit → Ready
// For Pickup → Picked Up), modeled on the Grafana "Store Fulfillments
// (External)" dashboard but realtime. Tiles + per-store summary; every count
// is clickable and filters the drill-down order table below.

const SF_BUCKETS = [
    { k: 'prepared', label: 'Being Prepared',   color: '#6d7175', staleKey: null },
    { k: 'transit',  label: 'In Transit',       color: '#1a4fa0', staleKey: 'transit_stale', staleLabel: 'in transit' },
    { k: 'rfp',      label: 'Ready For Pickup', color: '#916a00', staleKey: 'rfp_stale',     staleLabel: 'waiting' },
    { k: 'picked',   label: 'Picked Up',        color: '#108043', staleKey: null },
];

const SF_BUCKET_LABELS = {
    prepared: 'Being Prepared', transit: 'In Transit', transit_stale: 'In Transit 7d+',
    rfp: 'Ready For Pickup', rfp_stale: 'Ready 7d+', picked: 'Picked Up',
    picked_no_rfp: 'Picked Up (never marked ready)', picked_partial: 'Partially Picked Up',
};

// Horizontal scrollbar at the TOP of a wide table as well as the bottom
// (2026-09-03, user request for the Store Fulfillments drill-down). A thin
// dummy scroller above the real one, kept the same scrollWidth via a
// ResizeObserver, with scroll positions mirrored both ways.
export function DoubleScroll({ children, style }) {
    // The top bar is a custom track + thumb (plain DOM), not a native
    // scrollbar: macOS overlay scrollbars vanish when idle and Playwright's
    // headless Chromium hides them entirely, so "always visible" has to be
    // drawn by us. Bottom keeps the native scrollbar.
    const bodyRef = useRef(null);
    const trackRef = useRef(null);
    const [geom, setGeom] = useState({ sw: 0, cw: 0, left: 0 });
    const drag = useRef(null);
    useEffect(() => {
        const body = bodyRef.current;
        if (!body) return undefined;
        const measure = () => setGeom({ sw: body.scrollWidth, cw: body.clientWidth, left: body.scrollLeft });
        measure();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (ro) { ro.observe(body); if (body.firstElementChild) ro.observe(body.firstElementChild); }
        return () => { if (ro) ro.disconnect(); };
    }, [children]);
    const onBodyScroll = () => { const b = bodyRef.current; if (b) setGeom(g => (g.left === b.scrollLeft ? g : { ...g, left: b.scrollLeft })); };
    const overflow = geom.sw > geom.cw + 1;
    const trackW = trackRef.current ? trackRef.current.clientWidth : geom.cw;
    const thumbW = overflow ? Math.max(40, trackW * geom.cw / geom.sw) : trackW;
    const maxThumbLeft = Math.max(0, trackW - thumbW);
    const maxScroll = Math.max(1, geom.sw - geom.cw);
    const thumbLeft = overflow ? (geom.left / maxScroll) * maxThumbLeft : 0;
    const scrollTo = (px) => { const b = bodyRef.current; if (b) b.scrollLeft = Math.max(0, Math.min(maxScroll, px)); };
    const onThumbDown = (e) => {
        e.preventDefault();
        drag.current = { x: e.clientX, left: geom.left };
        const move = (ev) => { if (!drag.current) return; scrollTo(drag.current.left + (ev.clientX - drag.current.x) * (maxScroll / Math.max(1, maxThumbLeft))); };
        const up = () => { drag.current = null; window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
        window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
    };
    const onTrackDown = (e) => {
        if (e.target !== trackRef.current) return;
        const rect = trackRef.current.getBoundingClientRect();
        const frac = (e.clientX - rect.left - thumbW / 2) / Math.max(1, maxThumbLeft);
        scrollTo(frac * maxScroll);
    };
    return (
        <div style={style}>
            <div ref={trackRef} className="double-scroll-track" onMouseDown={onTrackDown}
                 role="scrollbar" aria-orientation="horizontal" aria-valuemin={0} aria-valuemax={100}
                 aria-valuenow={overflow ? Math.round((geom.left / maxScroll) * 100) : 0}
                 title={overflow ? 'Drag to scroll the table sideways' : undefined}>
                <div className={`double-scroll-thumb${overflow ? '' : ' double-scroll-thumb--full'}`}
                     style={{ width: thumbW, transform: `translateX(${thumbLeft}px)` }}
                     onMouseDown={overflow ? onThumbDown : undefined} />
            </div>
            <div ref={bodyRef} onScroll={onBodyScroll} style={{ overflowX: 'auto' }}>
                {children}
            </div>
        </div>
    );
}

function StoreFulfillmentsReport() {
    const fetchAuth = useAuthFetch();
    const { storeSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    // Derived from the URL (not its own useState) so the tab strip round-trips

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(122));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ stores: [], stale_days: 7 });
    const [err, setErr] = useState(null);
    // Drill-down selection: { dest: store name | null (all), bucket: key | null (all) }
    // Drill-down selection initializes from the URL so a filtered view
    // (?dest=Soccer Post Dublin&bucket=transit_stale) is shareable.
    const [sel, setSel] = useState(() => {
        const d = searchParams.get('dest');
        const b = searchParams.get('bucket');
        return (d || b) ? { dest: d || null, bucket: b || null } : null;
    });
    const [detail, setDetail] = useState({ rows: [], total: 0, loading: false, err: null });
    // "Filter Corporate Stores" — hides franchise stores. Shareable via ?corporate=1.
    const [corpOnly, setCorpOnly] = useState(searchParams.get('corporate') === '1');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        if (sel?.dest)   p.set('dest',   sel.dest);
        if (sel?.bucket) p.set('bucket', sel.bucket);
        if (corpOnly)    p.set('corporate', '1');
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, sel, corpOnly, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            const res = await fetchAuth(`/api/reports/store-fulfillments/summary?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) { setErr(e.message || String(e)); }
        finally { setLoading(false); }
    }, [fetchAuth, dateFrom, dateTo]);
    useEffect(() => { load(); }, [load]);

    // Date change invalidates the current drill-down — but not on mount,
    // where sel may have just been restored from a shared URL.
    const _sfMountedRef = useRef(false);
    useEffect(() => {
        if (!_sfMountedRef.current) { _sfMountedRef.current = true; return; }
        setSel(null);
        setDetail({ rows: [], total: 0, loading: false, err: null });
    }, [dateFrom, dateTo]);

    // Load the drill-down for a URL-restored selection once on mount.
    useEffect(() => {
        if (sel) loadDetail(sel.dest, sel.bucket, 0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadDetail = useCallback(async (dest, bucket, offset = 0, append = false) => {
        setDetail(prev => ({ ...prev, loading: true, err: null, rows: append ? prev.rows : [] }));
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            if (dest)     p.set('dest', dest);
            if (bucket)   p.set('bucket', bucket);
            p.set('limit', '500'); p.set('offset', String(offset));
            const res = await fetchAuth(`/api/reports/store-fulfillments/orders?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            const j = await res.json();
            setDetail(prev => ({
                rows: append ? [...prev.rows, ...(j.rows || [])] : (j.rows || []),
                total: j.total || 0, loading: false, err: null,
            }));
        } catch (e) {
            setDetail(prev => ({ ...prev, loading: false, err: e.message || String(e) }));
        }
    }, [fetchAuth, dateFrom, dateTo]);

    // "Slide closed" animation for the by-store table: capture the wrapper's
    // height before a collapse/expand re-render, then transition height from
    // old → new (FLIP). Rows can't animate height natively in a <table>;
    // animating the container gives the slide.
    const sumWrapRef = useRef(null);
    const animFromRef = useRef(null);
    const captureSummaryHeight = () => {
        if (sumWrapRef.current) animFromRef.current = sumWrapRef.current.offsetHeight;
    };
    useLayoutEffect(() => {
        const el = sumWrapRef.current;
        const from = animFromRef.current;
        animFromRef.current = null;
        if (!el || from == null) return;
        const to = el.offsetHeight;
        if (from === to) return;
        el.style.height = `${from}px`;
        el.style.overflow = 'hidden';
        el.style.transition = 'height 300ms ease';
        requestAnimationFrame(() => { el.style.height = `${to}px`; });
        const t = setTimeout(() => {
            el.style.height = ''; el.style.overflow = ''; el.style.transition = '';
        }, 340);
        return () => clearTimeout(t);
    }, [sel?.dest]);

    const pick = useCallback((dest, bucket) => {
        captureSummaryHeight();
        setSel({ dest, bucket });
        loadDetail(dest, bucket, 0, false);
    }, [loadDetail]);

    // The button only makes sense to someone who can see both kinds of store —
    // for a franchise owner or a single-store manager there is nothing to filter.
    // Judged on what the API actually returned for this user, so it follows
    // their scope without a second endpoint.
    const allStores = data.stores || [];
    const showCorpFilter = useMemo(() => (
        allStores.some(s => s.is_franchise) && allStores.some(s => !s.is_franchise)
    ), [allStores]);

    // Column sort (2026-09-11). Default matches the server's own ORDER BY:
    // busiest store first. Store sorts alphabetically, every other column
    // numerically; a null median sorts last in both directions so stores with
    // no completed pickups never crowd out real numbers.
    const [sortKey, setSortKey] = useState('total');
    const [sortDir, setSortDir] = useState('desc');
    const toggleSort = useCallback((key) => {
        captureSummaryHeight();
        // Re-clicking a column flips it. Landing on a NEW column opens it the
        // way that column is actually read: names A→Z, counts biggest-first.
        setSortDir(d => (sortKey === key
            ? (d === 'desc' ? 'asc' : 'desc')
            : (key === 'store' ? 'asc' : 'desc')));
        setSortKey(key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortKey]);

    // Everything below renders from this list, not from data.stores.
    const stores = useMemo(() => {
        const base = corpOnly && showCorpFilter ? allStores.filter(s => !s.is_franchise) : allStores;
        const sign = sortDir === 'asc' ? 1 : -1;
        return [...base].sort((a, b) => {
            if (sortKey === 'store') {
                // Sort on what the cell SHOWS. The table strips the "Soccer Post "
                // prefix for width, so comparing the stored name put
                // "Retail - no shipping required" above every "Soccer Post …"
                // and the column read as unsorted.
                const an = String(a.store || '').replace(/^Soccer Post /i, '');
                const bn = String(b.store || '').replace(/^Soccer Post /i, '');
                return sign * an.localeCompare(bn, undefined, { sensitivity: 'base', numeric: true });
            }
            const x = a[sortKey], y = b[sortKey];
            const xe = x === null || x === undefined;
            const ye = y === null || y === undefined;
            if (xe && ye) return 0;
            if (xe) return 1;          // nulls last, whichever direction
            if (ye) return -1;
            return sign * (Number(x) - Number(y));
        });
    }, [allStores, corpOnly, showCorpFilter, sortKey, sortDir]);

    const totals = useMemo(() => {
        const t = { total: 0, prepared: 0, transit: 0, transit_stale: 0, rfp: 0,
                    rfp_stale: 0, picked: 0, picked_no_rfp: 0, picked_partial: 0 };
        for (const s of stores) {
            for (const k of Object.keys(t)) t[k] += (s[k] || 0);
        }
        t.pct_picked = t.total ? Math.round(1000.0 * t.picked / t.total) / 10 : 0;
        return t;
    }, [stores]);

    const staleDays = data.stale_days || 7;

    const fmtD = (iso) => iso ? String(iso).slice(0, 10) : '—';
    const cellBtn = (val, dest, bucket, { bold = false, tone = null } = {}) => (
        <button type="button"
            onClick={() => val > 0 && pick(dest, bucket)}
            disabled={!val}
            title={val ? `Show these orders` : undefined}
            style={{
                border: 0, background: 'none', padding: 0, font: 'inherit',
                cursor: val ? 'pointer' : 'default',
                color: tone || (val ? '#2c6ecb' : '#8c9196'),
                fontWeight: bold ? 650 : 500,
                textDecoration: val ? 'underline' : 'none',
                textDecorationColor: 'rgba(44,110,203,0.35)',
            }}>
            {Number(val || 0).toLocaleString()}
        </button>
    );

    const handleExportSummary = () => {
        const rows = [['Store', 'Total', 'Being Prepared', 'In Transit', `In Transit ${staleDays}d+`,
                       'Ready For Pickup', `Ready ${staleDays}d+`, 'Picked Up', 'Picked Up w/o RFP',
                       '% Picked Up', 'Median hrs RFP→Pickup']];
        for (const s of stores) {
            rows.push([s.store, s.total, s.prepared, s.transit, s.transit_stale, s.rfp, s.rfp_stale,
                       s.picked, s.picked_no_rfp, s.pct_picked, s.median_rfp_to_pickup_hours ?? '']);
        }
        downloadCsv(`store_fulfillments_${dateFrom}_to_${dateTo}.csv`, rows);
    };

    const handleExportDetail = () => {
        const rows = [['Order', 'Customer', 'Phone', 'Store', 'Fulfilled From', 'Club', 'Teams', 'Players', 'Status', 'Created',
                       'Transferred', 'Ready For Pickup', 'Picked Up', 'Days In Transit',
                       'Days Since RFP', 'Hours RFP→Pickup']];
        for (const r of detail.rows) {
            rows.push([r.order_name, r.customer_name || '', r.customer_phone || '', r.store, r.fulfillment_location || '', r.club, (r.teams || []).join('; '), (r.players || []).join('; '),
                       SF_BUCKET_LABELS[r.bucket] || r.bucket, fmtD(r.created_at), fmtD(r.transferred_at),
                       fmtD(r.rfp_at), fmtD(r.picked_up_at), r.days_in_transit ?? '',
                       r.days_since_rfp ?? '', r.hours_rfp_to_pickup ?? '']);
        }
        downloadCsv(`store_fulfillments_orders_${dateFrom}_to_${dateTo}.csv`, rows);
    };

    const selLabel = sel
        ? `${sel.dest || 'All stores'} — ${sel.bucket ? (SF_BUCKET_LABELS[sel.bucket] || sel.bucket) : 'All statuses'}`
        : null;

    // Sortable header cell. Keeps the existing tooltip + tinted backgrounds and
    // adds a click target with an arrow on the active column. `aria-sort` so
    // the state is announced, not just drawn.
    const sortableTh = (key, label, { align = 'right', style = {}, tip = '' } = {}) => {
        const active = sortKey === key;
        return (
            <th className={`sf-tip${align === 'left' ? ' sf-tip--left' : ''}`}
                data-tip={tip}
                aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                style={{ textAlign: align, cursor: 'pointer', userSelect: 'none', ...style }}
                onClick={() => toggleSort(key)}>
                {label}
                <span style={{ opacity: active ? 0.75 : 0.22, marginLeft: 4, fontSize: 10 }}>
                    {active ? (sortDir === 'asc' ? '\u25b2' : '\u25bc') : '\u25bc'}
                </span>
            </th>
        );
    };

    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Store Fulfillments" storageKey="store_fulfillments_about_open" linkLabel="About this report">
                        <Text as="p">
                            Live pickup-cycle status of every pickup order destined for a retail store
                            (orders created in the window; statuses are as of right now). Click any
                            number to see its orders.
                        </Text>
                    </ReportColumnHelp>
                    {showCorpFilter && (
                        <InlineStack gap="200" blockAlign="center">
                            <Button
                                pressed={corpOnly}
                                onClick={() => { captureSummaryHeight(); setCorpOnly(v => !v); }}
                            >
                                Filter Corporate Stores
                            </Button>
                            <Text tone="subdued" as="span">
                                {corpOnly
                                    ? `Showing ${stores.length} corporate store${stores.length === 1 ? '' : 's'}; ${allStores.length - stores.length} franchise hidden`
                                    : `${allStores.length} stores, including ${allStores.filter(s => s.is_franchise).length} franchise`}
                            </Text>
                        </InlineStack>
                    )}
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={[]}
                        locationFilter={[]} setLocationFilter={() => {}}
                        onExport={handleExportSummary}
                        exportDisabled={loading || !stores.length}
                        exportLabel="Export summary CSV"
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            {/* ── Status tiles ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                {SF_BUCKETS.map(b => {
                    const n = totals[b.k] || 0;
                    const stale = b.staleKey ? (totals[b.staleKey] || 0) : 0;
                    return (
                        <div key={b.k}
                            onClick={() => n > 0 && pick(null, b.k)}
                            style={{
                                background: '#fff', border: '1px solid #e3e3e3', borderRadius: 12,
                                borderTop: `4px solid ${b.color}`,
                                padding: '14px 16px', cursor: n ? 'pointer' : 'default',
                                boxShadow: sel && !sel.dest && sel.bucket === b.k ? `0 0 0 2px ${b.color}` : 'none',
                            }}>
                            <div style={{ fontSize: 13, color: '#6d7175', fontWeight: 600 }}>{b.label}</div>
                            <div style={{ fontSize: 30, fontWeight: 700, color: b.color, fontVariantNumeric: 'tabular-nums' }}>
                                {loading ? '…' : n.toLocaleString()}
                            </div>
                            {b.staleKey ? (
                                <div
                                    onClick={(e) => { e.stopPropagation(); stale > 0 && pick(null, b.staleKey); }}
                                    style={{ fontSize: 12.5, marginTop: 2, fontWeight: 600,
                                             color: stale ? '#b42318' : '#8c9196',
                                             cursor: stale ? 'pointer' : 'default',
                                             textDecoration: stale ? 'underline' : 'none' }}>
                                    {stale.toLocaleString()} {b.staleLabel} {staleDays}+ days
                                </div>
                            ) : b.k === 'picked' ? (
                                <div style={{ fontSize: 12.5, marginTop: 2, color: '#6d7175', fontWeight: 600 }}>
                                    {totals.pct_picked}% of orders
                                </div>
                            ) : (
                                <div style={{ fontSize: 12.5, marginTop: 2, color: '#8c9196' }}>not yet transferred</div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* ── Per-store summary ── */}
            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="headingSm" as="h3">By store</Text>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || dateFrom} → {data.date_to || dateTo} ·
                            Teams &amp; warehouse destinations excluded
                        </Text>
                    </InlineStack>
                    <div ref={sumWrapRef} style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                {/* 2026-09-01 user spec: In Transit + its stale column share a
                                    pale-yellow tint; Ready + its stale column share a very pale
                                    blue — the pairs read as one group at a glance. Every header
                                    carries a tooltip explaining the bucket. */}
                                <tr>
                                    {sortableTh('store', 'Store', { align: 'left', tip: 'Destination pickup store the orders are headed to' })}
                                    {sortableTh('total', 'Total', { tip: 'All pickup orders for this store in the window (every bucket combined)' })}
                                    {sortableTh('prepared', 'Being Prepared', { tip: "Still being picked / pressed / packed at the fulfillment location — hasn't left for the store yet" })}
                                    {sortableTh('transit', 'In Transit', { style: { background: '#fef9e7' }, tip: 'Transferred out of the fulfillment location — on its way to the store' })}
                                    {sortableTh('transit_stale', `${staleDays}d+`, { style: { color: '#b42318', background: '#fef9e7' }, tip: `In transit for more than ${staleDays} days — likely lost, delayed, or never scanned in at the store` })}
                                    {sortableTh('rfp', 'Ready', { style: { background: '#eff6ff' }, tip: "At the store and the customer has been notified it's Ready For Pickup" })}
                                    {sortableTh('rfp_stale', `${staleDays}d+`, { style: { color: '#b42318', background: '#eff6ff' }, tip: `Ready For Pickup for more than ${staleDays} days — the customer hasn't come in yet` })}
                                    {sortableTh('picked', 'Picked Up', { tip: 'Customer has picked the order up — cycle complete' })}
                                    {sortableTh('picked_no_rfp', 'No-RFP', { tip: 'Picked up but never marked Ready For Pickup' })}
                                    {sortableTh('pct_picked', '% Picked', { tip: "Share of this store's pickup orders already picked up" })}
                                    {sortableTh('median_rfp_to_pickup_hours', 'Med hrs RFP→PU', { tip: 'Median hours from the ready-for-pickup email to actual pickup' })}
                                </tr>
                            </thead>
                            <tbody>
                                {stores
                                    // A store-specific drill-down collapses the summary
                                    // to just that store; the ✕ on the sub-table restores.
                                    .filter(s => !sel?.dest || s.store === sel.dest)
                                    .map(s => (
                                    <tr key={s.store} style={{ borderTop: '1px solid #f0f0f0' }}>
                                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                                            {s.store.replace(/^Soccer Post /, '')}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{cellBtn(s.total, s.store, null, { bold: true, tone: '#1a1a1a' })}</td>
                                        <td style={{ textAlign: 'right' }}>{cellBtn(s.prepared, s.store, 'prepared')}</td>
                                        <td style={{ textAlign: 'right', background: '#fef9e7' }}>{cellBtn(s.transit, s.store, 'transit')}</td>
                                        <td style={{ textAlign: 'right', background: '#fef9e7' }}>{cellBtn(s.transit_stale, s.store, 'transit_stale', { bold: true, tone: s.transit_stale ? '#b42318' : null })}</td>
                                        <td style={{ textAlign: 'right', background: '#eff6ff' }}>{cellBtn(s.rfp, s.store, 'rfp')}</td>
                                        <td style={{ textAlign: 'right', background: '#eff6ff' }}>{cellBtn(s.rfp_stale, s.store, 'rfp_stale', { bold: true, tone: s.rfp_stale ? '#b42318' : null })}</td>
                                        <td style={{ textAlign: 'right' }}>{cellBtn(s.picked, s.store, 'picked')}</td>
                                        <td style={{ textAlign: 'right' }}>{cellBtn(s.picked_no_rfp, s.store, 'picked_no_rfp', { tone: s.picked_no_rfp ? '#916a00' : null })}</td>
                                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.pct_picked}%</td>
                                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                            {s.median_rfp_to_pickup_hours != null ? s.median_rfp_to_pickup_hours : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </BlockStack>
            </Card>

            {/* ── Drill-down ── */}
            {sel && (
                <Card>
                    <BlockStack gap="300">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <Text variant="headingSm" as="h3">{selLabel}</Text>
                            <Text variant="bodySm" tone="subdued">
                                {detail.total.toLocaleString()} order{detail.total !== 1 ? 's' : ''}
                                {detail.rows.length < detail.total ? ` (showing ${detail.rows.length.toLocaleString()})` : ''}
                            </Text>
                            <Button size="slim" onClick={handleExportDetail} disabled={!detail.rows.length}>Export CSV</Button>
                            <button type="button"
                                onClick={() => { captureSummaryHeight(); setSel(null); setDetail({ rows: [], total: 0, loading: false, err: null }); }}
                                title="Close — show all stores again"
                                style={{ marginLeft: 'auto', border: 0, background: 'none', cursor: 'pointer',
                                         fontSize: 20, lineHeight: 1, color: '#6d7175', padding: '2px 8px' }}>
                                ✕
                            </button>
                        </div>
                        {detail.err && <Banner tone="critical">{detail.err}</Banner>}
                        <DoubleScroll>
                            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Order</th>
                                        <th style={{ textAlign: 'left' }}>Customer</th>
                                        <th style={{ textAlign: 'left' }}>Phone</th>
                                        <th style={{ textAlign: 'left' }}>Store</th>
                                        <th style={{ textAlign: 'left' }} title="Location fulfilling / that fulfilled this order">Fulfilled From</th>
                                        <th style={{ textAlign: 'left' }}>Club</th>
                                        <th style={{ textAlign: 'left' }}>Team(s)</th>
                                        <th style={{ textAlign: 'left' }}>Player(s)</th>
                                        <th style={{ textAlign: 'left' }}>Status</th>
                                        <th style={{ textAlign: 'left' }}>Created</th>
                                        <th style={{ textAlign: 'left' }}>Transferred</th>
                                        <th style={{ textAlign: 'left' }}>Ready</th>
                                        <th style={{ textAlign: 'left' }}>Picked Up</th>
                                        <th style={{ textAlign: 'right' }}>Days In Transit</th>
                                        <th style={{ textAlign: 'right' }}>Days Since RFP</th>
                                        <th style={{ textAlign: 'right' }}>Hrs RFP→PU</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detail.rows.map(r => {
                                        const staleRow = (r.bucket === 'transit' && (r.days_in_transit ?? 0) >= staleDays)
                                            || (r.bucket === 'rfp' && (r.days_since_rfp ?? 0) >= staleDays);
                                        return (
                                            <tr key={r.order_id + r.bucket}
                                                style={{ borderTop: '1px solid #f0f0f0',
                                                         background: staleRow ? '#fff4f4' : 'transparent' }}>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <Link to={`/store/${r.store_key || storeSlug || 'soccerpost'}/orders/${r.order_id}`}>{r.order_name}</Link>
                                                    {r.bucket === 'picked' && r.store_status_pct > 0 && r.store_status_pct < 100 && (
                                                        <span style={{ marginLeft: 6, fontSize: 11.5, color: '#916a00', fontWeight: 600 }}>
                                                            {r.store_status_pct}%
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{r.customer_name || '—'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {r.customer_phone
                                                        ? <a href={`tel:${r.customer_phone}`}>{r.customer_phone}</a>
                                                        : '—'}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{(r.store || '').replace(/^Soccer Post /, '')}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{r.fulfillment_location || '—'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{r.club}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{(r.teams || []).join(', ')}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{(r.players || []).join(', ')}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{SF_BUCKET_LABELS[r.bucket] || r.bucket}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{fmtD(r.created_at)}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{fmtD(r.transferred_at)}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{fmtD(r.rfp_at)}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{fmtD(r.picked_up_at)}</td>
                                                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                                             color: staleRow && r.bucket === 'transit' ? '#b42318' : undefined,
                                                             fontWeight: staleRow && r.bucket === 'transit' ? 650 : 400 }}>
                                                    {r.days_in_transit ?? ''}
                                                </td>
                                                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                                             color: staleRow && r.bucket === 'rfp' ? '#b42318' : undefined,
                                                             fontWeight: staleRow && r.bucket === 'rfp' ? 650 : 400 }}>
                                                    {r.days_since_rfp ?? ''}
                                                </td>
                                                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.hours_rfp_to_pickup ?? ''}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </DoubleScroll>
                        {detail.loading && <InlineStack align="center"><Spinner size="small" /></InlineStack>}
                        {!detail.loading && detail.rows.length < detail.total && (
                            <InlineStack align="center">
                                <Button onClick={() => loadDetail(sel.dest, sel.bucket, detail.rows.length, true)}>
                                    Load {Math.min(500, detail.total - detail.rows.length)} more
                                </Button>
                            </InlineStack>
                        )}
                    </BlockStack>
                </Card>
            )}
        </BlockStack>
    );
}

function PickupLocationByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const { storeSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(89));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], available_clubs: [],
                                       date_from: '', date_to: '',
                                       selected_club: null, total_orders: 0 });
    const [err, setErr] = useState(null);

    // 2026-09-25 (Eric): every picked club goes to the backend, not just the first.
    const selectedClubs = clubFilter.filter(Boolean);
    const selectedClub = selectedClubs.join(', ');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            selectedClubs.forEach(c => p.append('clubs', c));
            const res = await fetchAuth(`/api/reports/pickup_location_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, selectedClub]);
    useEffect(() => { load(); }, [load]);

    const [sortKey, setSortKey] = useState('orders');
    const [sortDir, setSortDir] = useState('desc');
    // Row expand: pickup_store -> { loading, rows, err }
    const [expanded, setExpanded] = useState({});   // pickup_store -> bool
    const [drillCache, setDrillCache] = useState({}); // pickup_store -> {loading,rows,err}

    // Clear the drill-down cache + expanded state whenever the filter
    // scope changes — cache is keyed by pickup_store only, so stale
    // orders from a prior club/date would surface under a same-named
    // pickup row otherwise.
    useEffect(() => {
        setExpanded({});
        setDrillCache({});
    }, [selectedClub, dateFrom, dateTo, locationFilter]);

    const fetchDrilldown = useCallback(async (pickup) => {
        setDrillCache(prev => ({ ...prev, [pickup]: { loading: true, rows: [], err: null } }));
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            selectedClubs.forEach(c => p.append('clubs', c));
            p.set('pickup_store', pickup);
            const res = await fetchAuth(`/api/reports/pickup_location_by_club/orders?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            const j = await res.json();
            setDrillCache(prev => ({ ...prev, [pickup]: { loading: false, rows: j.rows || [], err: null } }));
        } catch (e) {
            setDrillCache(prev => ({ ...prev, [pickup]: { loading: false, rows: [], err: e.message || String(e) } }));
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, selectedClub]);

    const toggleRow = useCallback((pickup) => {
        setExpanded(prev => {
            const willOpen = !prev[pickup];
            if (willOpen && !drillCache[pickup]) fetchDrilldown(pickup);
            return { ...prev, [pickup]: willOpen };
        });
    }, [drillCache, fetchDrilldown]);

    const sortedRows = useMemo(() => {
        const list = (data.rows || []).slice();
        list.sort((a, b) => {
            let cmp = 0;
            if (sortKey === 'pickup')      cmp = (a.pickup_store || '').localeCompare(b.pickup_store || '');
            else if (sortKey === 'orders') cmp = (a.orders || 0) - (b.orders || 0);
            else if (sortKey === 'pct')    cmp = (a.pct || 0) - (b.pct || 0);
            else if (sortKey === 'badge')  cmp = (a.badge || '').localeCompare(b.badge || '');
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [data.rows, sortKey, sortDir]);
    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir(key === 'pickup' ? 'asc' : 'desc'); }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const badgeStyle = (badge) => {
        // Pastel backgrounds per user spec 2026-08-01.
        if (badge === 'green')  return { background: '#d7f5df', color: '#1a6d3a' };
        if (badge === 'yellow') return { background: '#fdf5c8', color: '#7a5a10' };
        if (badge === 'red')    return { background: '#fbd7d7', color: '#a02323' };
        return {};
    };
    const rowBg = (badge) => {
        if (badge === 'green')  return '#eaf9ef';
        if (badge === 'yellow') return '#fcf7d9';
        if (badge === 'red')    return '#fdeaea';
        return 'transparent';
    };

    const handleExport = useCallback(() => {
        const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const lines = [['Pickup Store', 'Orders', 'Percent', 'Category'].map(escape).join(',')];
        for (const r of sortedRows) {
            lines.push([r.pickup_store, r.orders,
                        (r.pct != null ? r.pct.toFixed(2) + '%' : ''),
                        r.badge].map(escape).join(','));
        }
        const csv = lines.join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeClub = (selectedClub || 'all-clubs').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
        a.download = `pickup-location-by-club-${safeClub}-${dateFrom}-${dateTo}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [sortedRows, selectedClub, dateFrom, dateTo]);

    const total = data.total_orders || 0;
    const counts = useMemo(() => {
        const c = { green: 0, yellow: 0, red: 0 };
        for (const r of (data.rows || [])) c[r.badge] = (c[r.badge] || 0) + 1;
        return c;
    }, [data.rows]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Pickup Location By Club" storageKey="pickup_location_by_club_about_open" linkLabel="About this report">
                        <Text as="p">
                            Pick a fulfillment location and a club. Rows are ranked by pickup order count.
                            Green = primary destination (≥15% of the club's pickup orders).
                            Yellow = reasonable secondary (2–15%). Red = likely a wrong-store mistake (&lt;2%).
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        singleClub
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            {!selectedClub ? (
                <Card>
                    <div style={{ padding: 24, textAlign: 'center', color: '#6d7175' }}>
                        Pick a club above to see its pickup-location distribution.
                    </div>
                </Card>
            ) : (data.rows || []).length === 0 ? (
                <Card>
                    <div style={{ padding: 24, textAlign: 'center', color: '#6d7175' }}>
                        No pickup orders for <b>{selectedClub}</b> in this window.
                    </div>
                </Card>
            ) : (
                <Card>
                    <BlockStack gap="300">
                        <InlineStack gap="400" wrap>
                            <Text variant="bodySm" tone="subdued">
                                Window: {data.date_from || '—'} → {data.date_to || '—'}
                            </Text>
                            <Text variant="bodySm" tone="subdued">
                                {(data.rows || []).length} pickup location{(data.rows || []).length !== 1 ? 's' : ''} · {total.toLocaleString()} orders
                            </Text>
                            <Text variant="bodySm">
                                <span style={{ ...badgeStyle('green'),  padding: '2px 8px', borderRadius: 4, marginRight: 6 }}>Primary {counts.green}</span>
                                <span style={{ ...badgeStyle('yellow'), padding: '2px 8px', borderRadius: 4, marginRight: 6 }}>Secondary {counts.yellow}</span>
                                <span style={{ ...badgeStyle('red'),    padding: '2px 8px', borderRadius: 4 }}>Outlier {counts.red}</span>
                            </Text>
                        </InlineStack>
                        <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {[
                                        {k:'pickup', l:'Pickup Location'},
                                        {k:'orders', l:'Orders', right:true},
                                        {k:'pct',    l:'% of Club', right:true},
                                        {k:'badge',  l:'Category'},
                                    ].map(c => (
                                        <th key={c.k}
                                            onClick={() => handleSort(c.k)}
                                            style={{
                                                textAlign: c.right ? 'right' : 'left',
                                                padding: '8px 10px',
                                                background: sortKey === c.k ? '#eef2ff' : '#f6f6f7',
                                                borderBottom: '1px solid #e1e3e5',
                                                fontSize: 11,
                                                color: sortKey === c.k ? '#3730a3' : '#6d7175',
                                                letterSpacing: 0.04,
                                                textTransform: 'uppercase',
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                            }}>
                                            {c.l}{sortArrow(c.k)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((r, i) => {
                                    const isOpen = !!expanded[r.pickup_store];
                                    const drill  = drillCache[r.pickup_store];
                                    return (
                                    <React.Fragment key={i}>
                                    <tr style={{ background: rowBg(r.badge), cursor: 'pointer' }}
                                        onClick={() => toggleRow(r.pickup_store)}
                                        title="Click to see the orders in this row">
                                        <td style={{ padding: '8px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     whiteSpace: 'nowrap' }}>
                                            <span style={{ display:'inline-block', width:14, color:'#6d7175' }}>
                                                {isOpen ? '▾' : '▸'}
                                            </span>
                                            {r.pickup_store}
                                        </td>
                                        <td style={{ padding: '8px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     textAlign: 'right',
                                                     fontWeight: 600,
                                                     whiteSpace: 'nowrap' }}>
                                            {(r.orders || 0).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '8px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     textAlign: 'right',
                                                     whiteSpace: 'nowrap' }}>
                                            {r.pct != null ? `${r.pct.toFixed(1)}%` : '—'}
                                        </td>
                                        <td style={{ padding: '8px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     whiteSpace: 'nowrap' }}>
                                            <span style={{ ...badgeStyle(r.badge),
                                                           padding: '3px 10px',
                                                           borderRadius: 4,
                                                           textTransform: 'capitalize',
                                                           fontSize: 12,
                                                           fontWeight: 600 }}>
                                                {r.badge === 'green' ? 'Primary'
                                                 : r.badge === 'yellow' ? 'Secondary'
                                                 : 'Outlier'}
                                            </span>
                                        </td>
                                    </tr>
                                    {isOpen && drill?.loading && (
                                        <tr><td colSpan={4} style={{ padding:'12px 40px', background:'#fafbfb', color:'#6d7175', borderBottom:'1px solid #e1e3e5' }}>Loading orders…</td></tr>
                                    )}
                                    {isOpen && drill?.err && (
                                        <tr><td colSpan={4} style={{ padding:'12px 40px', background:'#fafbfb', color:'#a02323', borderBottom:'1px solid #e1e3e5' }}>Couldn't load orders: {drill.err}</td></tr>
                                    )}
                                    {isOpen && drill && !drill.loading && !drill.err && (
                                        <>
                                            <tr>
                                                <th style={_pickupSubHeadStyle}>Order</th>
                                                <th style={{..._pickupSubHeadStyle, textAlign:'right'}}>Placed</th>
                                                <th style={{..._pickupSubHeadStyle, textAlign:'right'}}>Customer</th>
                                                <th style={_pickupSubHeadStyle}>Billing City / State</th>
                                            </tr>
                                            {(drill.rows || []).length === 0 && (
                                                <tr><td colSpan={4} style={{ padding:'12px 40px', background:'#fafbfb', color:'#6d7175', borderBottom:'1px solid #f1f2f3' }}>No orders.</td></tr>
                                            )}
                                            {(drill.rows || []).map((o, j) => (
                                                <tr key={j} style={{ background:'#fafbfb' }}>
                                                    <td style={{..._pickupSubCellStyle, whiteSpace:'nowrap'}}>
                                                        <a href={`/store/${storeSlug}/orders/${o.order_id}?loc=all`}
                                                           onClick={(e) => e.stopPropagation()}
                                                           style={{ color:'#2c6ecb', textDecoration:'none' }}>
                                                            {o.order_name || o.order_id}
                                                        </a>
                                                    </td>
                                                    <td style={{..._pickupSubCellStyle, whiteSpace:'nowrap', textAlign:'right'}}>{o.placed_d || '—'}</td>
                                                    <td style={{..._pickupSubCellStyle, textAlign:'right'}}>{o.customer_name || '—'}</td>
                                                    <td style={{..._pickupSubCellStyle, whiteSpace:'nowrap'}}>{o.billing_city_state || '—'}</td>
                                                </tr>
                                            ))}
                                        </>
                                    )}
                                    </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    </BlockStack>
                </Card>
            )}
        </BlockStack>
    );
}

/* ---------- Soccer Post Annapolis Pickup Analysis ---------------------- */
// Sibling of PickupLocationByClubReport but hard-scoped to Annapolis.
// Rows = clubs with any Annapolis pickup order in the window; columns =
// Annapolis + top-10 OTHER pickup destinations across all rows + Other.
// Expand row → the Annapolis pickup orders for that club (billing City/ST).

// Click-to-copy email cell for the Annapolis drill-down. Shows a small
// transient "Copied!" confirmation next to the address; falls back to a
// selection-based copy for browsers that don't expose navigator.clipboard.
function CopyableEmail({ email }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(email);
            } else {
                const ta = document.createElement('textarea');
                ta.value = email;
                ta.setAttribute('readonly', '');
                ta.style.position = 'fixed';
                ta.style.left = '-1000px';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch { /* silent — email still selectable in the DOM */ }
    }, [email]);
    return (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
            <a href={`mailto:${email}`}
               onClick={(e) => e.stopPropagation()}
               style={{ color:'#2c6ecb', textDecoration: 'none' }}>
                {email}
            </a>
            <button type="button"
                    onClick={handleCopy}
                    title="Copy email"
                    style={{
                        border: '1px solid #dadde0',
                        background: copied ? '#d7f5df' : '#fff',
                        color: copied ? '#1a6d3a' : '#6d7175',
                        borderRadius: 4,
                        padding: '1px 6px',
                        fontSize: 11,
                        cursor: 'pointer',
                        lineHeight: 1.2,
                    }}>
                {copied ? '✓' : 'Copy'}
            </button>
        </span>
    );
}

function AnnapolisPickupAnalysisReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const { storeSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(89));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ columns: [], rows: [], grand_total: null, date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('annapolis');
    const [sortDir, setSortDir] = useState('desc');
    // "Hide Picked Up" toggle — when on, the drill-down filters out orders
    // whose Annapolis pickup already completed (drill-row picked_up=true).
    // URL-persisted so the view is shareable.
    const [hidePickedUp, setHidePickedUp] = useState(searchParams.get('hidepu') === '1');
    const [expanded, setExpanded] = useState({});      // club -> bool
    const [drillCache, setDrillCache] = useState({});  // club -> {loading,rows,err}

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        if (sortKey && sortKey !== 'annapolis') p.set('sort', sortKey);
        if (sortDir && sortDir !== 'desc') p.set('dir', sortDir);
        if (hidePickedUp) p.set('hidepu', '1');
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, sortKey, sortDir, hidePickedUp, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            const res = await fetchAuth(`/api/reports/annapolis_pickup_analysis?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo]);
    useEffect(() => { load(); }, [load]);

    // Clear drill cache when the window changes so stale rows can't leak in.
    useEffect(() => { setExpanded({}); setDrillCache({}); }, [dateFrom, dateTo]);

    const fetchDrilldown = useCallback(async (club) => {
        setDrillCache(prev => ({ ...prev, [club]: { loading: true, rows: [], err: null } }));
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            p.set('club', club);
            const res = await fetchAuth(`/api/reports/annapolis_pickup_analysis/orders?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            const j = await res.json();
            setDrillCache(prev => ({ ...prev, [club]: { loading: false, rows: j.rows || [], err: null } }));
        } catch (e) {
            setDrillCache(prev => ({ ...prev, [club]: { loading: false, rows: [], err: e.message || String(e) } }));
        }
    }, [fetchAuth, dateFrom, dateTo]);

    const toggleRow = useCallback((club) => {
        setExpanded(prev => {
            const willOpen = !prev[club];
            if (willOpen && !drillCache[club]) fetchDrilldown(club);
            return { ...prev, [club]: willOpen };
        });
    }, [drillCache, fetchDrilldown]);

    const cellVal = (r, key) => {
        if (key === 'club')         return r.club || '';
        if (key === 'annapolis')    return r.annapolis || 0;
        if (key === 'total_pickup') return r.total_pickup || 0;
        if (key.startsWith('type:')) return (r.per_pickup || {})[key.slice(5)] || 0;
        return 0;
    };

    const sortedRows = useMemo(() => {
        const list = (data.rows || []).slice();
        list.sort((a, b) => {
            const av = cellVal(a, sortKey);
            const bv = cellVal(b, sortKey);
            if (typeof av === 'string' || typeof bv === 'string') {
                const cmp = String(av).localeCompare(String(bv));
                return sortDir === 'asc' ? cmp : -cmp;
            }
            return sortDir === 'asc' ? av - bv : bv - av;
        });
        return list;
    }, [data.rows, sortKey, sortDir]);

    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir(key === 'club' ? 'asc' : 'desc'); }
    };
    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    // CSV export = full drill-down rows across every club (Club, Order,
    // Placed, Picked Up, Pickup Override, Customer, Email, Billing City/
    // State). Hits the bulk endpoint so Shopify billing lookups run in
    // parallel server-side — much faster than issuing one drill request
    // per club from the browser.
    const [exporting, setExporting] = useState(false);
    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            const res = await fetchAuth(`/api/reports/annapolis_pickup_analysis/orders_all?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            const j = await res.json();
            const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
            const header = ['Club', 'Order', 'Placed', 'Picked Up',
                            'Pickup Override', 'Customer', 'Email',
                            'Billing City', 'Billing State'];
            const lines = [header.map(escape).join(',')];
            for (const r of (j.rows || [])) {
                if (hidePickedUp && r.picked_up) continue;
                lines.push([
                    r.club,
                    r.order_name || r.order_id,
                    r.placed_d || '',
                    r.picked_up ? 'Yes' : '',
                    r.pickup_override || '',
                    r.customer_name || '',
                    r.email || '',
                    r.billing_city || '',
                    r.billing_state || '',
                ].map(escape).join(','));
            }
            const csv = lines.join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `annapolis-pickup-analysis-${dateFrom}-${dateTo}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setExporting(false);
        }
    }, [fetchAuth, dateFrom, dateTo, hidePickedUp]);

    const headCellStyle = (key, right) => ({
        textAlign: right ? 'right' : 'left',
        padding: '8px 10px',
        background: sortKey === key ? '#eef2ff' : '#f6f6f7',
        borderBottom: '1px solid #e1e3e5',
        fontSize: 11,
        color: sortKey === key ? '#3730a3' : '#6d7175',
        letterSpacing: 0.04,
        textTransform: 'uppercase',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        userSelect: 'none',
    });
    const cellStyle = (right) => ({
        padding: '8px 10px',
        borderBottom: '1px solid #f1f2f3',
        textAlign: right ? 'right' : 'left',
        whiteSpace: 'nowrap',
    });

    const totalCols = 3 + (data.columns || []).length;  // club + annapolis + all-stores + total

    // Vertical (rotated 90° top→bottom) header cell — used for the store
    // columns so a wide "all stores" table doesn't blow past the viewport.
    // writing-mode gives us stacked-character orientation; the fixed height
    // reserves room for the longest store name. Alternating light-blue /
    // white column shading makes it easier to scan across many columns —
    // pass `alt` to shade the store slot (odd-indexed columns).
    const vertHeadStyle = (active, alt) => ({
        writingMode: 'vertical-rl',
        transform: 'rotate(180deg)',
        padding: '12px 2px',
        background: active ? '#eef2ff' : (alt ? '#e6f0fa' : '#f6f6f7'),
        borderBottom: '1px solid #e1e3e5',
        fontSize: 11,
        color: active ? '#3730a3' : '#6d7175',
        letterSpacing: 0.04,
        textTransform: 'uppercase',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        userSelect: 'none',
        // Fit the longest store name ("Soccer Post Huntingdon Valley" ≈ 28
        // chars × 8px at 11px font ≈ 224px) with headroom.
        height: 240,
        verticalAlign: 'bottom',
        textAlign: 'left',
        minWidth: 28,
        width: 28,
    });
    // Zebra background for store body cells + grand-total cells.
    const stripedBg = (i) => (i % 2 === 1 ? '#f0f6fc' : '#ffffff');

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Soccer Post Annapolis Pickup Analysis" storageKey="soccer_post_annapolis_pickup_analysis_about_open" linkLabel="About this report">
                        <Text as="p">
                            Every club with a pickup order at Soccer Post Annapolis in the window.
                            Columns show the club's Annapolis count and how their other pickup orders
                            distribute across every pickup destination (greatest → least). Click a
                            row to see the Annapolis orders, email, and billing city/state.
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo}     setDateTo={setDateTo}
                        availableLocations={[]}
                        locationFilter={[]} setLocationFilter={() => {}}
                        onExport={handleExport}
                        exportDisabled={loading || exporting || !(data.rows || []).length}
                        trailing={
                            <Button pressed={hidePickedUp} onClick={() => setHidePickedUp(v => !v)}>
                                {hidePickedUp ? '✓ Hiding Picked Up' : 'Filter Out Picked Up'}
                            </Button>
                        }
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                            {(data.rows || []).length} club{(data.rows || []).length !== 1 ? 's' : ''} ·{' '}
                            {(data.grand_total?.annapolis ?? 0).toLocaleString()} Annapolis pickup orders
                        </Text>
                    </InlineStack>

                    {(sortedRows.length === 0) ? (
                        <div style={{ padding: 32, color: '#6d7175', textAlign: 'center' }}>
                            {loading ? '' : 'No Annapolis pickup orders in this window.'}
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort('club')}      style={headCellStyle('club')}>Club{arrow('club')}</th>
                                        <th onClick={() => handleSort('annapolis')} style={{ ...headCellStyle('annapolis', true), background: sortKey === 'annapolis' ? '#eef2ff' : '#eef8f0' }}>
                                            Annapolis{arrow('annapolis')}
                                        </th>
                                        {(data.columns || []).map((s, ci) => (
                                            <th key={s}
                                                onClick={() => handleSort(`type:${s}`)}
                                                title={s}
                                                style={vertHeadStyle(sortKey === `type:${s}`, ci % 2 === 1)}>
                                                {s}{arrow(`type:${s}`)}
                                            </th>
                                        ))}
                                        <th onClick={() => handleSort('total_pickup')}
                                            title="Total Pickup"
                                            style={vertHeadStyle(sortKey === 'total_pickup', false)}>
                                            Total Pickup{arrow('total_pickup')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.map((r, i) => {
                                        const isOpen = !!expanded[r.club];
                                        const drill  = drillCache[r.club];
                                        return (
                                        <React.Fragment key={i}>
                                        <tr style={{ cursor: 'pointer' }}
                                            onClick={() => toggleRow(r.club)}
                                            title="Click to see this club's Annapolis orders">
                                            <td style={cellStyle()}>
                                                <span style={{ display:'inline-block', width:14, color:'#6d7175' }}>{isOpen ? '▾' : '▸'}</span>
                                                {r.club}
                                            </td>
                                            <td style={{ ...cellStyle(true), fontWeight: 700, background: '#f4faf6' }}>
                                                {(r.annapolis || 0).toLocaleString()}
                                            </td>
                                            {(data.columns || []).map((s, ci) => {
                                                const v = (r.per_pickup || {})[s] || 0;
                                                return (
                                                    <td key={s} style={{ ...cellStyle(true), color: v ? undefined : '#c7c9cc', minWidth: 28, background: stripedBg(ci) }}>
                                                        {v ? v.toLocaleString() : '·'}
                                                    </td>
                                                );
                                            })}
                                            <td style={{ ...cellStyle(true), fontWeight: 600 }}>{(r.total_pickup || 0).toLocaleString()}</td>
                                        </tr>
                                        {isOpen && drill?.loading && (
                                            <tr><td colSpan={totalCols} style={{ padding:'12px 40px', background:'#fafbfb', color:'#6d7175', borderBottom:'1px solid #e1e3e5' }}>Loading orders…</td></tr>
                                        )}
                                        {isOpen && drill?.err && (
                                            <tr><td colSpan={totalCols} style={{ padding:'12px 40px', background:'#fafbfb', color:'#a02323', borderBottom:'1px solid #e1e3e5' }}>Couldn't load orders: {drill.err}</td></tr>
                                        )}
                                        {isOpen && drill && !drill.loading && !drill.err && (
                                            <tr style={{ background:'#fafbfb' }}>
                                                <td colSpan={totalCols} style={{ padding: '4px 8px 12px 32px', borderBottom:'1px solid #e1e3e5' }}>
                                                    <table style={{ borderCollapse: 'collapse' }}>
                                                        <thead>
                                                            <tr>
                                                                <th style={{ ..._pickupSubHeadStyle, textAlign:'left' }}>Order</th>
                                                                <th style={{ ..._pickupSubHeadStyle, textAlign:'left' }}>Placed</th>
                                                                <th style={{ ..._pickupSubHeadStyle, textAlign:'left' }}>Picked Up</th>
                                                                <th style={{ ..._pickupSubHeadStyle, textAlign:'left' }}>Pickup Override</th>
                                                                <th style={{ ..._pickupSubHeadStyle, textAlign:'left' }}>Customer</th>
                                                                <th style={{ ..._pickupSubHeadStyle, textAlign:'left' }}>Email</th>
                                                                <th style={{ ..._pickupSubHeadStyle, textAlign:'left' }}>Billing City / State</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(() => {
                                                                const visible = (drill.rows || []).filter(o => !hidePickedUp || !o.picked_up);
                                                                if (visible.length === 0) {
                                                                    return (
                                                                        <tr><td colSpan={7} style={{ padding:'12px 8px', color:'#6d7175' }}>
                                                                            {hidePickedUp && (drill.rows || []).length > 0
                                                                                ? `All ${(drill.rows || []).length} order${(drill.rows || []).length === 1 ? '' : 's'} for this club at Annapolis have been picked up.`
                                                                                : 'No orders.'}
                                                                        </td></tr>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                            {(drill.rows || []).filter(o => !hidePickedUp || !o.picked_up).map((o, j) => (
                                                                <tr key={j}>
                                                                    <td style={{..._pickupSubCellStyle, textAlign:'left', whiteSpace:'nowrap', paddingRight: 24 }}>
                                                                        <a href={`/store/${storeSlug}/orders/${o.order_id}?loc=all`}
                                                                           onClick={(e) => e.stopPropagation()}
                                                                           style={{ color:'#2c6ecb', textDecoration:'none' }}>
                                                                            {o.order_name || o.order_id}
                                                                        </a>
                                                                    </td>
                                                                    <td style={{..._pickupSubCellStyle, textAlign:'left', whiteSpace:'nowrap', paddingRight: 24 }}>{o.placed_d || '—'}</td>
                                                                    <td style={{..._pickupSubCellStyle, textAlign:'left', whiteSpace:'nowrap', paddingRight: 24 }}>
                                                                        {o.picked_up
                                                                            ? <span style={{ background:'#d7f5df', color:'#1a6d3a', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600 }}>Picked Up</span>
                                                                            : <span style={{ color:'#6d7175' }}>—</span>}
                                                                    </td>
                                                                    <td style={{..._pickupSubCellStyle, textAlign:'left', whiteSpace:'nowrap', paddingRight: 24 }}>
                                                                        {o.pickup_override
                                                                            ? <span style={{ background:'#fdf5c8', color:'#7a5a10', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600 }} title="Rerouted from Annapolis">→ {o.pickup_override}</span>
                                                                            : <span style={{ color:'#c7c9cc' }}>—</span>}
                                                                    </td>
                                                                    <td style={{..._pickupSubCellStyle, textAlign:'left', paddingRight: 24 }}>{o.customer_name || '—'}</td>
                                                                    <td style={{..._pickupSubCellStyle, textAlign:'left', whiteSpace:'nowrap', paddingRight: 24 }}>
                                                                        {o.email ? <CopyableEmail email={o.email} /> : '—'}
                                                                    </td>
                                                                    <td style={{..._pickupSubCellStyle, textAlign:'left', whiteSpace:'nowrap' }}>{o.billing_city_state || '—'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                        );
                                    })}
                                    {data.grand_total && (
                                        <tr style={{ fontWeight:600 }}>
                                            <td style={{ ...cellStyle(), background: '#f6f6f7' }}>Grand Total</td>
                                            <td style={{ ...cellStyle(true), background:'#eef8f0' }}>{(data.grand_total.annapolis || 0).toLocaleString()}</td>
                                            {(data.columns || []).map((s, ci) => (
                                                <td key={s} style={{ ...cellStyle(true), background: stripedBg(ci) }}>{((data.grand_total.per_pickup || {})[s] || 0).toLocaleString()}</td>
                                            ))}
                                            <td style={{ ...cellStyle(true), background: '#f6f6f7' }}>{(data.grand_total.total_pickup || 0).toLocaleString()}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

function ProductRevenueByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ products: [], available_clubs: [],
                                       date_from: '', date_to: '', selected_club: null,
                                       cached_orders: 0, total_orders: 0 });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('priced');
    const [sortDir, setSortDir] = useState('desc');

    // 2026-09-25 (Eric): every picked club goes to the backend, not just the first.
    const selectedClubs = clubFilter.filter(Boolean);
    const selectedClub = selectedClubs.join(', ');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            selectedClubs.forEach(c => p.append('clubs', c));
            const res = await fetchAuth(`/api/reports/product_revenue_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, selectedClub]);
    useEffect(() => { load(); }, [load]);

    const sortedProducts = useMemo(() => {
        const list = (data.products || []).slice();
        const keyFns = {
            product: (p) => (p.title || '').toLowerCase(),
            qty:     (p) => Number(p.total_qty) || 0,
            priced:  (p) => Number(p.priced_revenue) || 0,
            msrp:    (p) => Number(p.msrp_revenue) || 0,
            delta:   (p) => ((p.priced_revenue || 0) - (p.msrp_revenue || 0)),
            type:    (p) => (p.product_type || '').toLowerCase(),
        };
        const fn = keyFns[sortKey] || keyFns.priced;
        list.sort((a, b) => {
            const av = fn(a), bv = fn(b);
            const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [data.products, sortKey, sortDir]);

    const handleSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir(['product', 'type'].includes(key) ? 'asc' : 'desc'); }
    };
    const arrow = (k) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const totals = useMemo(() => {
        const ps = data.products || [];
        return {
            products: ps.length,
            units:    ps.reduce((s, p) => s + (p.total_qty || 0), 0),
            priced:   ps.reduce((s, p) => s + (p.priced_revenue || 0), 0),
            msrp:     ps.reduce((s, p) => s + (p.msrp_revenue || 0), 0),
            allPricedKnown: ps.every(p => p.priced_known),
            allMsrpKnown:   ps.every(p => p.msrp_known),
        };
    }, [data.products]);

    const handleExport = useCallback(() => {
        const rows = [['Product', 'Product Type', 'SKU', 'Variant',
                       'Qty', 'Priced / unit', 'Priced Revenue',
                       'MSRP / unit', 'MSRP Revenue', 'Δ vs MSRP']];
        for (const p of (data.products || [])) {
            for (const v of (p.variants || [])) {
                const d = (v.priced_revenue != null && v.msrp_revenue != null)
                    ? (v.priced_revenue - v.msrp_revenue) : '';
                const pricedPerUnit = (v.priced_revenue_seen && v.qty > 0)
                    ? (v.priced_revenue / v.qty) : null;
                rows.push([
                    p.title || '', p.product_type || '', v.sku || '',
                    v.variant_title || '',
                    Number(v.qty || 0),
                    pricedPerUnit != null ? Number(pricedPerUnit).toFixed(2) : '',
                    v.priced_revenue_seen ? Number(v.priced_revenue || 0).toFixed(2) : '',
                    v.msrp_price != null ? Number(v.msrp_price).toFixed(2) : '',
                    v.msrp_revenue != null ? Number(v.msrp_revenue).toFixed(2) : '',
                    d === '' ? '' : Number(d).toFixed(2),
                ]);
            }
        }
        const club = (selectedClub || 'club').replace(/[^\w-]+/g, '_');
        downloadCsv(`product_revenue_by_club_${club}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, selectedClub]);

    const cachedPct = (data.total_orders > 0)
        ? Math.round(100 * data.cached_orders / data.total_orders) : 100;

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Product Revenue By Club (MSRP)" storageKey="product_revenue_by_club_msrp_about_open" linkLabel="About this report">
                        <Text as="p">
                            <b>Merchandise value only — not cash collected.</b> For each product:
                            Priced Revenue = the club's selling price at order time × quantity
                            currently on the order (pre-discount); MSRP Revenue re-prices the same
                            units at today's catalog price. The delta shows whether the club pays
                            above or below catalog. This report <b>excludes shipping charges, sales
                            tax, and any items later removed by order edits</b> — so it will read
                            LOWER than Revenue By Club, which is the amount actually charged.
                            Use Revenue By Club for money in the door; use this report for what
                            merchandise moved and at what price.
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !selectedClub || !(data.products || []).length}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            {selectedClub && data.total_orders > 0 && cachedPct < 100 && (
                <Banner tone="info" title="Backfill in progress">
                    Only {data.cached_orders.toLocaleString()} of {data.total_orders.toLocaleString()} orders
                    {' '}({cachedPct}%) have priced revenue cached yet. Numbers will firm up
                    as the backfill completes.
                </Banner>
            )}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {selectedClub ? (
                            <Text variant="bodySm" tone="subdued">
                                Club: <strong>{selectedClub}</strong>
                                {' '}· {totals.products.toLocaleString()} products
                                {' '}· <strong>{Number(data.total_orders || 0).toLocaleString()}</strong> orders
                                {' '}· {totals.units.toLocaleString()} units
                                {' '}· Priced: <strong>{_fmtMoney(totals.allPricedKnown ? totals.priced : null)}</strong>
                                {' '}· MSRP: <strong>{_fmtMoney(totals.allMsrpKnown ? totals.msrp : null)}</strong>
                            </Text>
                        ) : (
                            <Text variant="bodySm" tone="subdued">
                                Select a club to see products.
                            </Text>
                        )}
                    </InlineStack>

                    {loading ? null : !selectedClub ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            Pick a club from the filter above to load the report.
                        </div>
                    ) : (data.products || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No products sold to {selectedClub} in this window.
                        </div>
                    ) : (
                        <BlockStack gap="300">
                            <InlineStack gap="200" align="end" blockAlign="center">
                                <Text variant="bodySm" tone="subdued">Sort by</Text>
                                <ButtonGroup variant="segmented">
                                    <Button size="slim" pressed={sortKey === 'product'} onClick={() => handleSort('product')}>Product{arrow('product')}</Button>
                                    <Button size="slim" pressed={sortKey === 'type'}    onClick={() => handleSort('type')}>Type{arrow('type')}</Button>
                                    <Button size="slim" pressed={sortKey === 'qty'}     onClick={() => handleSort('qty')}>Qty{arrow('qty')}</Button>
                                    <Button size="slim" pressed={sortKey === 'priced'}  onClick={() => handleSort('priced')}>Priced{arrow('priced')}</Button>
                                    <Button size="slim" pressed={sortKey === 'msrp'}    onClick={() => handleSort('msrp')}>MSRP{arrow('msrp')}</Button>
                                    <Button size="slim" pressed={sortKey === 'delta'}   onClick={() => handleSort('delta')}>Δ{arrow('delta')}</Button>
                                </ButtonGroup>
                            </InlineStack>
                            {sortedProducts.map((p) => (
                                <ProductRevenueByClubCard key={p.product_id} product={p} />
                            ))}
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}


/* ---------- Numbers & Names By Club report ---------------------------- */

function NumbersNamesCard({ product, sortMode, viewMode = 'letter', onViewModeChange }) {
    const p = product || {};
    const isNameProduct = p.product_type === 'Name' || p.product_type === 'Embroidered Name';
    const isNumberProduct = p.product_type === 'Number' || p.product_type === 'Embroidered Number';

    // Per-card view mode is controlled by parent (so CSV export can read it).
    // Meaningful for BOTH Name and Number product types:
    //   Name   → Letter (per-char) ↔ Name (whole value)
    //   Number → Digit  (per-char) ↔ Number (whole value)
    const hasValueToggle = isNameProduct || isNumberProduct;
    const effectiveView = hasValueToggle ? viewMode : 'letter';
    const setViewMode = onViewModeChange || (() => {});

    const rows = useMemo(() => {
        // 'name' view: list whole personalization values ("ACK", "EMA", "13").
        // 'letter' view: list individual chars (current behavior). For Number
        // products in 'letter' view we always fill the full 0-9 digit set so
        // the column shows zeros for any digit that didn't occur.
        let src;
        if (effectiveView === 'name') {
            src = (p.values || []).map(v => ({ key: v.value, count: v.count }));
        } else if (isNumberProduct) {
            const byChar = {};
            for (const c of (p.characters || [])) byChar[c.char] = c.count;
            src = [];
            for (let d = 0; d <= 9; d++) {
                const ch = String(d);
                src.push({ key: ch, count: byChar[ch] || 0 });
            }
        } else {
            src = (p.characters || []).map(c => ({ key: c.char, count: c.count }));
        }
        if (sortMode === 'alpha') {
            // For Number products in 'name' view (whole numbers like 12/119),
            // sort numerically so 12 < 119, not lexicographically.
            if (isNumberProduct && effectiveView === 'name') {
                src.sort((a, b) => {
                    const an = Number(a.key), bn = Number(b.key);
                    const aOk = Number.isFinite(an), bOk = Number.isFinite(bn);
                    if (aOk && bOk && an !== bn) return an - bn;
                    if (aOk && !bOk) return -1;
                    if (!aOk && bOk) return 1;
                    return (a.key || '').localeCompare(b.key || '');
                });
            } else {
                // ASCII codepoint sort: digits (48-57), then uppercase A-Z (65-90),
                // then lowercase a-z (97-122). For multi-char values ('ACK' vs
                // 'BAIRD') the codepoint compare on the whole string achieves the
                // same Aa…Zz ordering character-by-character.
                src.sort((a, b) => {
                    const ak = a.key || '', bk = b.key || '';
                    const len = Math.min(ak.length, bk.length);
                    for (let i = 0; i < len; i++) {
                        const d = ak.charCodeAt(i) - bk.charCodeAt(i);
                        if (d) return d;
                    }
                    return ak.length - bk.length;
                });
            }
        } else {
            // 'count' — desc, ties broken by key asc.
            src.sort((a, b) => (b.count || 0) - (a.count || 0)
                            || (a.key || '').localeCompare(b.key || ''));
        }
        return src;
    }, [p.characters, p.values, sortMode, effectiveView, isNumberProduct]);

    const firstColumnHeader = isNumberProduct
        ? (effectiveView === 'name' ? 'Number' : 'Digit')
        : (effectiveView === 'name' ? 'Name'   : 'Letter');

    return (
        <div className="nn-card" style={{
            display: 'grid', gridTemplateColumns: '80px 1fr', gap: 16,
            padding: 14, border: '1px solid var(--p-color-border, #e1e3e5)',
            borderRadius: 8, background: '#fff',
        }}>
            <div>
                {p.image_url
                    ? <SmartProductImage src={p.image_url} alt="" size={144}
                          style={{ width: 72, height: 72, objectFit: 'contain',
                                   border: '1px solid #eee',
                                   borderRadius: 4, display: 'block' }} />
                    : <div style={{ width: 72, height: 72, background: '#fafafa',
                                    border: '1px solid #eee', borderRadius: 4,
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', color: '#c4c8cc',
                                    fontSize: 10 }}>no img</div>}
            </div>
            <div style={{ minWidth: 0 }}>
                <InlineStack gap="200" align="space-between" blockAlign="start">
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <Text variant="headingSm" as="h3">{p.title || '—'}</Text>
                        <div style={{ display: 'inline-flex', gap: 12,
                                      alignItems: 'center', marginTop: 2 }}>
                            <Text tone="subdued" variant="bodySm">
                                {(p.sku_qty_total || 0).toLocaleString()} SKUs sold
                                {' · '}
                                {(p.total_chars || 0).toLocaleString()} characters
                            </Text>
                            {hasValueToggle && (
                                <ButtonGroup variant="segmented">
                                    <Button size="slim"
                                            pressed={viewMode === 'letter'}
                                            onClick={() => setViewMode('letter')}>
                                        {isNumberProduct ? 'Digit' : 'Letter'}
                                    </Button>
                                    <Button size="slim"
                                            pressed={viewMode === 'name'}
                                            onClick={() => setViewMode('name')}>
                                        {isNumberProduct ? 'Number' : 'Name'}
                                    </Button>
                                </ButtonGroup>
                            )}
                        </div>
                    </div>
                    <Badge>{p.product_type || ''}</Badge>
                </InlineStack>
                {(() => {
                    const thStyle = {
                        background: '#f6f6f7',
                        borderBottom: '1px solid #e1e3e5',
                        padding: '6px 10px',
                        fontSize: 11, color: '#6d7175',
                        letterSpacing: 0.04,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                    };
                    return (
                        <table className="data-table nn-occurrence-table"
                               style={{ width: '100%', marginTop: 10,
                                        borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>
                                        {firstColumnHeader}
                                    </th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>
                                        Occurrences
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.key}>
                                        <td style={{ padding: '6px 10px',
                                                     fontFamily: 'ui-monospace, Menlo, monospace',
                                                     fontWeight: 700,
                                                     borderBottom: '1px solid #f1f2f3' }}>
                                            {r.key}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     textAlign: 'right',
                                                     fontWeight: 600,
                                                     borderBottom: '1px solid #f1f2f3' }}>
                                            {(r.count || 0).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    );
                })()}
            </div>
        </div>
    );
}

function NumbersNamesByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ products: [], available_clubs: [],
                                       date_from: '', date_to: '', selected_clubs: [] });
    const [err, setErr] = useState(null);
    const [sortMode, setSortMode] = useState('count'); // 'count' or 'alpha'
    // Per-card view mode for Name products. Keyed by product_id. Absent →
    // 'letter' (default). Lifted out of NumbersNamesCard so handleExport
    // can read each card's current view when building the CSV.
    const [cardViews, setCardViews] = useState({});
    const setCardView = useCallback((pid, mode) => {
        setCardViews(prev => ({ ...prev, [pid]: mode }));
    }, []);

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            clubFilter.forEach(c => p.append('clubs', c));
            const res = await fetchAuth(`/api/reports/numbers_names_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, clubFilter]);
    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => {
        const products = data.products || [];
        return {
            products: products.length,
            skus:     products.reduce((s, p) => s + (p.sku_qty_total || 0), 0),
            chars:    products.reduce((s, p) => s + (p.total_chars || 0), 0),
        };
    }, [data.products]);

    const handleExport = useCallback(() => {
        // Each card's view drives that product's rows:
        //   Name   product: Letter (chars) | Name   (whole values)
        //   Number product: Digit  (chars) | Number (whole values)
        // The 3rd column carries whatever is displayed in that card's table
        // (single letter, full name, single digit, or whole number).
        const rows = [['Product', 'Product type', 'Name/Letter/Number', 'Count']];
        const hasToggle = (t) => t === 'Name'   || t === 'Embroidered Name'
                              || t === 'Number' || t === 'Embroidered Number';
        for (const p of (data.products || [])) {
            const mode = hasToggle(p.product_type) ? (cardViews[p.product_id] || 'letter') : 'letter';
            if (mode === 'name') {
                for (const v of (p.values || [])) {
                    rows.push([p.title || '', p.product_type || '',
                               v.value || '', Number(v.count || 0)]);
                }
            } else {
                for (const c of (p.characters || [])) {
                    rows.push([p.title || '', p.product_type || '',
                               c.char || '', Number(c.count || 0)]);
                }
            }
        }
        const selected = data.selected_clubs || [];
        const slug = selected.length === 0
            ? 'all_clubs'
            : selected.length === 1
                ? selected[0].replace(/[^\w-]+/g, '_')
                : `${selected.length}_clubs`;
        downloadCsv(`numbers_names_${slug}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [data, cardViews]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Numbers & Names By Club" storageKey="numbers_names_by_club_about_open" linkLabel="About this report">
                        <Text as="p">
                            Per-product tally of digits and letters used in Name and
                            Number personalization values for a club's window.
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.products || []).length}
                        multiClub
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {(() => {
                            const sel = data.selected_clubs || [];
                            const scopeLabel = sel.length === 0
                                ? 'All clubs'
                                : sel.length === 1
                                    ? <>Club: <strong>{sel[0]}</strong></>
                                    : <>{sel.length} clubs: <strong>{sel.join(', ')}</strong></>;
                            return (
                                <Text variant="bodySm" tone="subdued">
                                    {scopeLabel}
                                    {' '}· {totals.products.toLocaleString()} products
                                    {' '}· {totals.skus.toLocaleString()} SKUs sold
                                    {' '}· {totals.chars.toLocaleString()} characters
                                </Text>
                            );
                        })()}
                    </InlineStack>

                    {loading ? null : (data.products || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No Number/Name embellishments sold in this window.
                        </div>
                    ) : (
                        <BlockStack gap="300">
                            <InlineStack gap="200" align="end" blockAlign="center">
                                <Text variant="bodySm" tone="subdued">Sort each card by</Text>
                                <ButtonGroup variant="segmented">
                                    <Button size="slim"
                                            pressed={sortMode === 'count'}
                                            onClick={() => setSortMode('count')}>
                                        Most used
                                    </Button>
                                    <Button size="slim"
                                            pressed={sortMode === 'alpha'}
                                            onClick={() => setSortMode('alpha')}>
                                        Alphabetical
                                    </Button>
                                </ButtonGroup>
                            </InlineStack>
                            {(data.products || []).map((p) => (
                                <NumbersNamesCard key={p.product_id}
                                                  product={p}
                                                  sortMode={sortMode}
                                                  viewMode={cardViews[p.product_id] || 'letter'}
                                                  onViewModeChange={(mode) => setCardView(p.product_id, mode)} />
                            ))}
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}


/* ---------- Product Analysis By Club report -------------------------- */

function ProductAnalysisByClubReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || isoDaysAgo(29));
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [clubFilter, setClubFilter] = useState(searchParams.getAll('club'));
    const [productIds, setProductIds] = useState(searchParams.getAll('product_id'));
    // Draft = uncommitted selection inside the dropdown. Only writes to
    // `productIds` when the user clicks Apply — matches the requested UX
    // ("select many, then hit Apply"). Prevents a request-per-click while
    // the user is still assembling their pick.
    const [productDraft, setProductDraft] = useState(searchParams.getAll('product_id'));
    const [productsOpen, setProductsOpen] = useState(false);
    const [productSearch, setProductSearch] = useState('');
    // Default sort: order → date descending (newest first).
    const [sortKey, setSortKey] = useState('order');
    const [sortDir, setSortDir] = useState('desc');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        available_clubs: [], available_products: [], rows: [],
        date_from: '', date_to: '', selected_club: null,
    });
    const [err, setErr] = useState(null);

    // 2026-09-25 (Eric): every picked club goes to the backend, not just the first.
    const selectedClubs = clubFilter.filter(Boolean);
    const selectedClub = selectedClubs.join(', ');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc',  l);
        for (const c of clubFilter)     p.append('club', c);
        for (const id of productIds)    p.append('product_id', id);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, clubFilter, productIds, setSearchParams]);

    // When the club changes, clear product selection — the product options are
    // club-scoped and stale IDs would produce empty results.
    const prevClubRef = useRef(selectedClub);
    useEffect(() => {
        if (prevClubRef.current !== selectedClub) {
            prevClubRef.current = selectedClub;
            setProductIds([]);
            setProductDraft([]);
        }
    }, [selectedClub]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            selectedClubs.forEach(c => p.append('clubs', c));
            productIds.forEach(id => p.append('product_ids', id));
            const res = await fetchAuth(`/api/reports/product_analysis_by_club?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter, selectedClub, productIds]);
    useEffect(() => { load(); }, [load]);

    const productMenuOptions = useMemo(() => (
        (data.available_products || []).map(p => ({
            value: p.product_id,
            label: `${p.title} (${(p.total_qty || 0).toLocaleString()} sold)`,
        }))
    ), [data.available_products]);

    const productTitleById = useMemo(() => {
        const m = {};
        for (const p of (data.available_products || [])) {
            m[p.product_id] = p.title;
        }
        return m;
    }, [data.available_products]);

    // Column keys mirror the row payload; `order` sorts by order_name.
    // `quantity` is the only numeric column; everything else is string
    // localeCompare so alphanumeric SKUs / sizes sort naturally (S/M/L via
    // custom variant order isn't worth the complexity here — alpha is close
    // enough and predictable).
    const SORTABLE_COLUMNS = useMemo(() => ([
        { key: 'order',         label: 'Order',         field: 'order_name',   defaultDir: 'desc' },
        { key: 'date',          label: 'Date',          field: 'placed_d',     defaultDir: 'desc' },
        { key: 'delivery',      label: 'Delivery',      field: 'delivery',     defaultDir: 'asc'  },
        { key: 'team',          label: 'Team',          field: 'team',         defaultDir: 'asc'  },
        { key: 'product',       label: 'Product',       field: 'product',      defaultDir: 'asc'  },
        { key: 'sku',           label: 'SKU',           field: 'sku',          defaultDir: 'asc'  },
        { key: 'variant',       label: 'Variant',       field: 'variant',      defaultDir: 'asc'  },
        { key: 'player_name',   label: 'Player Name',   field: 'player_name',  defaultDir: 'asc'  },
        { key: 'name',          label: 'Name',          field: 'name',         defaultDir: 'asc'  },
        { key: 'number',        label: 'Number',        field: 'number',       defaultDir: 'asc'  },
        { key: 'quantity',      label: 'Quantity',      field: 'quantity',     defaultDir: 'desc', numeric: true, alignRight: true },
    ]), []);

    const sortedRows = useMemo(() => {
        const col = SORTABLE_COLUMNS.find(c => c.key === sortKey) || SORTABLE_COLUMNS[0];
        const dir = sortDir === 'asc' ? 1 : -1;
        const list = (data.rows || []).slice();
        list.sort((a, b) => {
            let cmp;
            if (col.numeric) {
                cmp = (Number(a[col.field]) || 0) - (Number(b[col.field]) || 0);
            } else {
                const av = (a[col.field] || '').toString();
                const bv = (b[col.field] || '').toString();
                cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
            }
            if (cmp !== 0) return cmp * dir;
            // Tie-breaker so equal keys stay stable and readable — group by
            // order, then date, then player.
            const ta = (a.order_name || '') + '|' + (a.placed_d || '') + '|' + (a.player_name || '');
            const tb = (b.order_name || '') + '|' + (b.placed_d || '') + '|' + (b.player_name || '');
            return ta.localeCompare(tb);
        });
        return list;
    }, [data.rows, sortKey, sortDir, SORTABLE_COLUMNS]);

    const handleSort = useCallback((key) => {
        const col = SORTABLE_COLUMNS.find(c => c.key === key);
        if (!col) return;
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(col.defaultDir || 'asc');
        }
    }, [sortKey, SORTABLE_COLUMNS]);

    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const handleExport = useCallback(() => {
        const rows = [['Order','Date','Delivery','Club','Team','Product','SKU','Variant',
                       'Player Name','Name','Number','Quantity']];
        for (const r of sortedRows) {
            rows.push([r.order_name || '', r.placed_d || '', r.delivery || '',
                       r.club || '',
                       r.team || '', r.product || '', r.sku || '', r.variant || '',
                       r.player_name || '', r.name || '', r.number || '',
                       Number(r.quantity || 0)]);
        }
        const club = (selectedClub || 'club').replace(/[^\w-]+/g, '_');
        downloadCsv(`product_analysis_${club}_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [sortedRows, selectedClub, data.date_from, data.date_to]);

    const totalQty = useMemo(
        () => (data.rows || []).reduce((s, r) => s + Number(r.quantity || 0), 0),
        [data.rows]
    );

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="300">
                    <ReportColumnHelp title="Product / Player / Number Analysis By Club" storageKey="product_player_number_analysis_by_club_about_open" linkLabel="About this report">
                        <Text as="p">
                            Pick a location, club, and one or more Team Products.
                            Get a line-by-line breakdown of every player's order:
                            order number, date, product, variant, player, and quantity.
                            Name and Number show only what is printed on that line item (a Name or Number add-on
                            or attribute); an item with neither shows a dash, even when the player has a roster number.
                        </Text>
                    </ReportColumnHelp>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        availableClubs={data.available_clubs || []}
                        clubFilter={clubFilter} setClubFilter={setClubFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !(data.rows || []).length}
                        trailing={selectedClub && (productMenuOptions.length > 0) ? (
                            <Popover
                                active={productsOpen}
                                activator={
                                    <Button
                                        onClick={() => {
                                            if (!productsOpen) setProductDraft(productIds);
                                            setProductsOpen(v => !v);
                                        }}
                                        disclosure={productsOpen ? 'up' : 'down'}
                                        pressed={productIds.length > 0}
                                    >
                                        Team Products{productIds.length ? ` (${productIds.length})` : ''}
                                    </Button>
                                }
                                onClose={() => setProductsOpen(false)}
                                preferredAlignment="left"
                            >
                                <div style={{ minWidth: 360, maxWidth: 480,
                                              maxHeight: 460, padding: 10,
                                              display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <TextField labelHidden label="Search products" type="search"
                                        value={productSearch} onChange={setProductSearch}
                                        placeholder="Search products…" autoComplete="off"
                                        clearButton onClearButtonClick={() => setProductSearch('')} />
                                    <div style={{ overflowY: 'auto', flex: '1 1 auto',
                                                  minHeight: 0, maxHeight: 320 }}>
                                        <ChoiceList
                                            allowMultiple
                                            titleHidden
                                            title="Team Products"
                                            choices={productMenuOptions.filter(o =>
                                                !productSearch ||
                                                o.label.toLowerCase().includes(productSearch.toLowerCase())
                                            )}
                                            selected={productDraft}
                                            onChange={setProductDraft}
                                        />
                                    </div>
                                    <InlineStack gap="200" align="space-between">
                                        <Button variant="plain"
                                                onClick={() => setProductDraft([])}
                                                disabled={productDraft.length === 0}>
                                            Clear
                                        </Button>
                                        <InlineStack gap="200">
                                            <Button onClick={() => {
                                                setProductDraft(productIds);
                                                setProductsOpen(false);
                                            }}>Cancel</Button>
                                            <Button variant="primary" onClick={() => {
                                                setProductIds(productDraft);
                                                setProductsOpen(false);
                                            }}>Apply</Button>
                                        </InlineStack>
                                    </InlineStack>
                                </div>
                            </Popover>
                        ) : null}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'}
                        </Text>
                        {selectedClub && (
                            <Text variant="bodySm" tone="subdued">
                                Club: <strong>{selectedClub}</strong>
                            </Text>
                        )}
                        {productIds.length > 0 && (
                            <Text variant="bodySm" tone="subdued">
                                {(data.rows || []).length.toLocaleString()} rows
                                {' '}· {totalQty.toLocaleString()} units
                            </Text>
                        )}
                    </InlineStack>

                    {loading ? null : !selectedClub ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            Pick a club from the filter above to load Team Products.
                        </div>
                    ) : productIds.length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            {productMenuOptions.length
                                ? 'Pick one or more Team Products above to see per-player rows.'
                                : `No Team Products sold to ${selectedClub} in this window.`}
                        </div>
                    ) : (data.rows || []).length === 0 ? (
                        <div style={{ height: 200, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      color: '#6d7175' }}>
                            No line items matching the selected products.
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ width: '100%',
                                                                borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {SORTABLE_COLUMNS.map(col => (
                                        <th key={col.key}
                                            onClick={() => handleSort(col.key)}
                                            title={`Sort by ${col.label}`}
                                            style={{
                                                textAlign: col.alignRight ? 'right' : 'left',
                                                padding: '8px 10px',
                                                background: sortKey === col.key ? '#eef2ff' : '#f6f6f7',
                                                borderBottom: '1px solid #e1e3e5',
                                                fontSize: 11,
                                                color: sortKey === col.key ? '#3730a3' : '#6d7175',
                                                letterSpacing: 0.04,
                                                textTransform: 'uppercase',
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                            }}>
                                            {col.label}{sortArrow(col.key)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((r, i) => (
                                    <tr key={i}>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     fontFamily: 'ui-monospace, Menlo, monospace',
                                                     fontSize: 12,
                                                     whiteSpace: 'nowrap' }}>
                                            {r.order_name || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     whiteSpace: 'nowrap' }}>
                                            {r.placed_d || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     whiteSpace: 'nowrap' }}>
                                            {r.delivery || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     whiteSpace: 'nowrap' }}>
                                            {r.team || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     whiteSpace: 'nowrap' }}>
                                            {r.product || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     fontFamily: 'ui-monospace, Menlo, monospace',
                                                     fontSize: 12,
                                                     whiteSpace: 'nowrap' }}>
                                            {r.sku || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     whiteSpace: 'nowrap' }}>
                                            {r.variant || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     whiteSpace: 'nowrap' }}>
                                            {r.player_name || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     whiteSpace: 'nowrap' }}>
                                            {r.name || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     fontFamily: 'ui-monospace, Menlo, monospace',
                                                     whiteSpace: 'nowrap' }}>
                                            {r.number || '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px',
                                                     borderBottom: '1px solid #f1f2f3',
                                                     textAlign: 'right',
                                                     fontWeight: 600,
                                                     whiteSpace: 'nowrap' }}>
                                            {Number(r.quantity || 0).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}


function PPPOverviewReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || todayIso());
    const [dateTo,   setDateTo]   = useState(searchParams.get('to')   || todayIso());
    const [locationFilter, setLocationFilter] = useState(searchParams.getAll('loc'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [], date_from: '', date_to: '' });
    const [err, setErr] = useState(null);
    const [sortKey, setSortKey] = useState('name');
    const [sortDir, setSortDir] = useState('asc');

    useEffect(() => {
        const p = new URLSearchParams();
        if (dateFrom) p.set('from', dateFrom);
        if (dateTo)   p.set('to',   dateTo);
        for (const l of locationFilter) p.append('loc', l);
        setSearchParams(p, { replace: true });
    }, [dateFrom, dateTo, locationFilter, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const p = new URLSearchParams();
            if (dateFrom) p.set('date_from', dateFrom);
            if (dateTo)   p.set('date_to',   dateTo);
            locationFilter.forEach(l => p.append('locations', l));
            const res = await fetchAuth(`/api/reports/ppp_overview?${p.toString()}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [fetchAuth, dateFrom, dateTo, locationFilter]);
    useEffect(() => { load(); }, [load]);

    const sortedRows = useMemo(() => {
        const col = PPP_COLUMNS.find(c => c.key === sortKey) || PPP_COLUMNS[0];
        const rows = (data.rows || []).slice();
        rows.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const cmp = col.sortType === 'string'
                ? String(av || '').localeCompare(String(bv || ''))
                : (Number(av) || 0) - (Number(bv) || 0);
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data.rows, sortKey, sortDir]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'name' ? 'asc' : 'desc');
        }
    };

    const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const handleExport = useCallback(() => {
        const rows = [PPP_COLUMNS.map(c => c.label)];
        for (const r of sortedRows) rows.push(PPP_COLUMNS.map(c => r[c.key]));
        downloadCsv(`reporting_ppp_overview_${data.date_from}_to_${data.date_to}.csv`, rows);
    }, [sortedRows, data]);

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading} />
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">PPP Overview</Text>
                    <ReportFilters
                        dateFrom={dateFrom} setDateFrom={setDateFrom}
                        dateTo={dateTo} setDateTo={setDateTo}
                        availableLocations={availableLocations}
                        locationFilter={locationFilter} setLocationFilter={setLocationFilter}
                        onExport={handleExport}
                        exportDisabled={loading || !sortedRows.length}
                    />
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="400" wrap>
                        <Text variant="bodySm" tone="subdued">
                            Window: {data.date_from || '—'} → {data.date_to || '—'} · {sortedRows.length} user{sortedRows.length === 1 ? '' : 's'}
                        </Text>
                    </InlineStack>

                    {loading && (data.rows || []).length === 0 ? null : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="data-table" style={{ minWidth: 1100, borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        {PPP_COLUMNS.map(c => (
                                            <th key={c.key}
                                                style={{
                                                    background: c.group ? PPP_COLORS[c.group] : '#fafafa',
                                                    textAlign: c.align,
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    padding: '8px 10px',
                                                    borderBottom: '1px solid #e1e3e5',
                                                    fontWeight: 600,
                                                    fontSize: 13,
                                                }}
                                                onClick={() => handleSort(c.key)}>
                                                {c.label}{arrow(c.key)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRows.length === 0 ? (
                                        <tr><td colSpan={PPP_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: '#6d7175' }}>
                                            No PIN'd portal users in scope.
                                        </td></tr>
                                    ) : sortedRows.map(r => (
                                        <tr key={r.user_id || r.name}>
                                            {PPP_COLUMNS.map(c => (
                                                <td key={c.key}
                                                    style={{
                                                        background: c.group ? PPP_COLORS[c.group] : 'transparent',
                                                        textAlign: c.align,
                                                        padding: '6px 10px',
                                                        borderBottom: '1px solid #f1f1f2',
                                                        fontSize: 13,
                                                    }}>
                                                    {c.key === 'name'
                                                        ? r[c.key]
                                                        : (r[c.key] === 0 ? <span style={{ color: '#9ea1a3' }}>0</span> : r[c.key])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

/* ---------- top-level shell ------------------------------------------- */

export default function Reporting() {
    const { user } = useAuth();
    const { accessibleStores: _userStores } = useStore();
    const fetchAuth = useAuthFetch();
    const navigate  = useNavigate();
    const { reportSlug, storeSlug } = useParams();

    const [availableLocations, setAvailableLocations] = useState([]);
    useEffect(() => {
        fetchAuth('/api/reports/locations')
            .then(r => r.ok ? r.json() : { locations: [] })
            .then(j => setAvailableLocations(j.locations || []))
            .catch(() => setAvailableLocations([]));
    }, [fetchAuth]);

    // Hooks below MUST run every render — keep them above the early returns.
    // Filter out reports hidden for this user's role BEFORE picking a default
    // slug — otherwise a franchise user visiting /reporting with no slug set
    // would land on Today's Overview (which they can't see).
    const _reportsFlatForUser = REPORTS_FLAT.filter(
        r => _visibleReportForRole(r, user, _userStores)
    );
    const _firstReport = _reportsFlatForUser[0] || REPORTS_FLAT[0];
    // Old addresses keep working: a renamed report's former slug maps to its new one.
    const RENAMED_SLUGS = { 'store-velocity': 'store-ordering' };
    const _rawSlug = RENAMED_SLUGS[reportSlug] || reportSlug || _firstReport.slug;
    // If the URL points at a hidden report, silently redirect to the first
    // accessible one — matches how the Reporting page already handles
    // unknown slugs (falls through to the first report).
    const slug = _reportsFlatForUser.some(r => r.slug === _rawSlug)
                 ? _rawSlug
                 : _firstReport.slug;

    // Which group (if any) contains the currently-selected report — used to
    // force that group open even when the user had it collapsed.
    const activeGroupId = useMemo(() => {
        for (const r of REPORTS) {
            if (r.children && _flattenReports(r.children).some(c => c.slug === slug)) return r.groupId;
        }
        return null;
    }, [slug]);

    // Per-group open/collapsed state, persisted so the user's choice survives
    // reloads and cross-store nav. Default: all groups open on first visit.
    // 2026-09-11: report-name filter box above the list (Eric).
    const [navFilter, setNavFilter] = useState('');
    // 2026-09-24 (Eric): fold the report list away, like the main nav folds.
    // Remembered per browser; wrapped because localStorage throws in a private
    // window and the page must still render.
    const [navCollapsed, setNavCollapsed] = useState(() => {
        try { return localStorage.getItem('reporting_nav_collapsed') === '1'; } catch (_e) { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem('reporting_nav_collapsed', navCollapsed ? '1' : '0'); } catch (_e) { /* private window */ }
    }, [navCollapsed]);
    const [openGroups, setOpenGroups] = useState(() => {
        const out = {};
        for (const r of _flattenGroups(REPORTS)) {
            const stored = typeof window !== 'undefined'
                ? window.localStorage.getItem(`reporting.group.${r.groupId}`)
                : null;
            out[r.groupId] = stored === null ? true : stored === '1';
        }
        return out;
    });
    const toggleGroup = useCallback((gid) => {
        setOpenGroups(prev => {
            const next = { ...prev, [gid]: !prev[gid] };
            try { window.localStorage.setItem(`reporting.group.${gid}`, next[gid] ? '1' : '0'); } catch {}
            return next;
        });
    }, []);
    // 2026-09-13 (Eric): the group holding the selected report used to be
    // FORCED open on every render, so its header could not be collapsed while
    // one of its reports was showing. Now it is opened once when you land on
    // (or navigate to) a report inside it, and the header toggles freely after.
    const _activeGroupSeen = useRef(null);
    useEffect(() => {
        if (!activeGroupId) return;
        // First render: keep the persisted choice (a collapsed group stays
        // collapsed after a reload). Only a later navigation into the group opens it.
        if (_activeGroupSeen.current === null) { _activeGroupSeen.current = activeGroupId; return; }
        if (_activeGroupSeen.current === activeGroupId) return;
        _activeGroupSeen.current = activeGroupId;
        setOpenGroups(prev => {
            if (prev[activeGroupId]) return prev;
            try { window.localStorage.setItem(`reporting.group.${activeGroupId}`, '1'); } catch {}
            return { ...prev, [activeGroupId]: true };
        });
    }, [activeGroupId]);

    if (!user) return null;
    // Role allowlist mirrors the backend's _report_user_scope() and the
    // sidebar nav in AppShell.jsx. franchise / user / location were
    // missing previously, which is why clicking the Reporting nav link
    // bounced franchise users right back to the Orders page.
    if (!hasRole(user, ['admin', 'site_manager', 'franchise', 'user', 'crm', 'finance', 'store_manager', 'external'])) {
        return <Navigate to={`/store/${storeSlug || 'soccerpost'}/orders`} replace />;
    }

    const _matched = REPORTS_FLAT.find(r => r.slug === slug);
    const ReportComp = (_matched && _matched.component) || _firstReport.component;

    // Real <Link> (not a <button>) so right-click → "Open in new tab" and
    // cmd/ctrl-click work; react-router still intercepts plain clicks for
    // SPA navigation (2026-08-31 user request).
    const _renderReportButton = (r, indent = false) => {
        const active = r.slug === slug;
        return (
            <Link key={r.slug}
                to={`/store/${storeSlug || 'soccerpost'}/reporting/${r.slug}`}
                style={{
                    display: 'block',
                    textAlign: 'left',
                    textDecoration: 'none',
                    padding: indent === 'deep' ? '6px 10px 6px 34px' : indent ? '6px 10px 6px 22px' : '8px 10px',
                    border: 0,
                    borderRadius: 6,
                    cursor: 'pointer',
                    background: active ? '#f1f8f5' : 'transparent',
                    color: active ? '#005c2c' : '#1a1a1a',
                    fontWeight: active ? 600 : 400,
                    fontSize: indent ? 13 : 14,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                {r.label}
            </Link>
        );
    };

    return (
        <PolarisVizProvider>
            {/* No page title: the "Reporting" heading lives at the top of the
                report sidebar now (Eric, 2026-09-24). */}
            <Page fullWidth>
                <div className="reporting-outer-grid"
                     style={{ display: 'grid',
                              // `minmax(0, 1fr)` — critical: default `1fr` has
                              // min-width: auto, which lets a wide table push
                              // the column past the viewport and defeats the
                              // per-report `overflow-x: auto` container.
                              // 2026-09-24 (Eric): the report list folds away like the
                              // main nav does, so a wide report can use the whole window.
                              // Collapsed it keeps a 40px rail — a panel that vanishes
                              // with no way back is worse than one that takes a little room.
                              // Both states must be the SAME kind of value or the
                              // browser cannot interpolate and the panel snaps shut:
                              // minmax(280px, 360px) → 0px does not animate, two plain
                              // lengths do (verified frame by frame, 2026-09-24).
                              // clamp() keeps it responsive and is still a length.
                              // 2026-09-24 (Eric): the report list is a real sidebar
                              // now — flush against the main nav, top to bottom — and it
                              // minimises to the same 64px rail the main nav uses.
                              // The panel is 20px wider than its column (it reaches
                              // left past the page padding), so 28 here renders a 48px
                              // rail — narrower than the main nav's 64px, since this one
                              // carries a word rather than a column of icons.
                              gridTemplateColumns: navCollapsed
                                  ? '28px minmax(0, 1fr)'
                                  : 'clamp(210px, 18vw, 270px) minmax(0, 1fr)',
                              gap: 16, alignItems: 'start' }}>
                    {/* Inner reports sidebar — sticky on desktop; see styles.css
                        `.reporting-sidenav` for the rule + mobile collapse. */}
                    <div className={`reporting-sidenav${navCollapsed ? ' reporting-sidenav--folded' : ''}`}>
                    {navCollapsed ? (
                        <Tooltip content="Show the report list">
                            <button type="button" className="reporting-rail"
                                    aria-label="Show the report list" aria-expanded={false}
                                    onClick={() => setNavCollapsed(false)}>
                                <span className="reporting-rail__icon"><SidebarGlyph /></span>
                                <span className="reporting-rail__label">Reporting</span>
                            </button>
                        </Tooltip>
                    ) : (
                    <div className="reporting-sidenav__panel">
                        <BlockStack gap="100">
                            <InlineStack align="space-between" blockAlign="center" wrap={false}>
                                <Text variant="headingLg" as="h1">Reporting</Text>
                                <Tooltip content="Hide the report list">
                                    <button type="button" aria-label="Hide the report list" aria-expanded
                                            onClick={() => setNavCollapsed(true)}
                                            className="reporting-nav-hide">
                                        <SidebarGlyph />
                                    </button>
                                </Tooltip>
                            </InlineStack>
                            <TextField
                                labelHidden label="Filter reports" type="search"
                                value={navFilter} onChange={setNavFilter}
                                placeholder="Filter reports…" autoComplete="off"
                                clearButton onClearButtonClick={() => setNavFilter('')}
                            />
                            {REPORTS.map((r, i) => {
                                // Per-report role gate. Currently used to
                                // hide "Today's Overview" from franchise
                                // users (they don't see the fleet view).
                                if (!_visibleReportForRole(r, user, _userStores)) return null;
                                // Name filter: match on the report label; a
                                // group shows only its matching children and
                                // is forced open while a filter is typed.
                                const navQ = navFilter.trim().toLowerCase();
                                const navMatch = (x) => !navQ || String(x.label || '').toLowerCase().includes(navQ);
                                if (!r.children && !navMatch(r)) return null;
                                if (r.children) {
                                    const visibleChildren = r.children
                                        .filter(c => _visibleReportForRole(c, user, _userStores))
                                        .filter(c => c.subheader || navMatch(c));
                                    if (navQ && visibleChildren.length === 0) return null;
                                    // Force the group open when its slug is active so
                                    // the current report is always visible even if
                                    // the user had this group collapsed.
                                    const isOpen = !!navQ || !!openGroups[r.groupId];
                                    return (
                                        <React.Fragment key={r.groupId}>
                                            <button type="button"
                                                onClick={() => toggleGroup(r.groupId)}
                                                style={{
                                                    textAlign: 'left',
                                                    padding: '8px 10px',
                                                    border: 0,
                                                    borderRadius: 6,
                                                    cursor: 'pointer',
                                                    background: 'transparent',
                                                    color: '#1a1a1a',
                                                    fontWeight: 600,
                                                    fontSize: 12,
                                                    letterSpacing: '0.03em',
                                                    textTransform: 'uppercase',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}>
                                                <span style={{ whiteSpace: 'nowrap' }}>{r.label}</span>
                                                <span style={{
                                                    fontSize: 10,
                                                    color: '#6d7175',
                                                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                    transition: 'transform 120ms ease-out',
                                                    display: 'inline-block',
                                                }}>▶</span>
                                            </button>
                                            {/* 2026-08-21: hideForRoles was only enforced on
                                                top-level entries, so a role-hidden CHILD (e.g.
                                                Soccer Post Annapolis Pickup Analysis nested
                                                under Club Reports) rendered anyway for
                                                franchise. visibleChildren applies the same rule
                                                (plus the name filter). */}
                                            {isOpen && visibleChildren.map(c => {
                                                if (!c.subheader) return _renderReportButton(c, true);
                                                const subKids = c.children
                                                    .filter(k => _visibleReportForRole(k, user, _userStores))
                                                    .filter(navMatch);
                                                if (navQ && subKids.length === 0) return null;
                                                const subOpen = !!navQ || !!openGroups[c.groupId];
                                                return (
                                                    <React.Fragment key={c.label}>
                                                        <button type="button" onClick={() => toggleGroup(c.groupId)}
                                                            style={{ textAlign: 'left', padding: '10px 10px 2px 22px', border: 0, background: 'transparent', cursor: 'pointer',
                                                                fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#6d7175',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span>{c.label}</span>
                                                            <span style={{ fontSize: 10, transform: subOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms ease-out', display: 'inline-block' }}>▶</span>
                                                        </button>
                                                        {subOpen && (subKids.length === 0
                                                            ? <div style={{ padding: '2px 10px 2px 46px', fontSize: 12, color: '#8c9196' }}>No reports yet</div>
                                                            : subKids.map(k => _renderReportButton(k, 'deep')))}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                }
                                return _renderReportButton(r);
                            })}
                        </BlockStack>
                    </div>
                    )}
                    </div>
                    <div className="reporting-content">
                        <ReportComp availableLocations={availableLocations} />
                    </div>
                    <ReportCardSeam />
                </div>
            </Page>
        </PolarisVizProvider>
    );
}

/* ---------- Operator Report ---------------------------------------------
   Day-by-day, per-operator activity readout. Access: viewer sees any
   operator who did at least one event at a location the viewer can access
   on the chosen date; once the gate opens, the response contains ALL of
   that operator's activity for the day (every store, every location).
   ---------------------------------------------------------------------- */

function _fmtHourLabel(h) {
    const hour12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
    const suffix = h >= 12 ? 'p' : 'a';
    return `${hour12}${suffix}`;
}
// IANA timezone → friendly US-facing label. Falls back to the IANA name if
// unmapped (e.g. non-US zones we haven't added).
const _TZ_FRIENDLY_LABEL = {
    'America/New_York':    'Eastern Time',
    'America/Chicago':     'Central Time',
    'America/Denver':      'Mountain Time',
    'America/Phoenix':     'Arizona Time',
    'America/Los_Angeles': 'Pacific Time',
    'America/Anchorage':   'Alaska Time',
    'Pacific/Honolulu':    'Hawaii Time',
};
function _friendlyTz(iana) {
    return _TZ_FRIENDLY_LABEL[iana] || (iana || '').replace(/^America\//, '').replace(/_/g, ' ');
}

function _fmtEventTime(iso) {
    if (!iso) return '—';
    // ISO comes back "2026-07-16T14:44:44.894000" already in the operator's TZ.
    const m = /T(\d{2}):(\d{2}):(\d{2})/.exec(iso);
    if (!m) return iso;
    const hh = Number(m[1]);
    const suffix = hh < 12 ? 'AM' : 'PM';
    const h12 = (hh % 12) || 12;
    return `${h12}:${m[2]}:${m[3]} ${suffix}`;
}
function _fmtElapsed(sec) {
    sec = Math.max(0, Math.floor(sec || 0));
    if (sec < 60) return `${sec}s`;
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (s === 0) return `${m}m`;
    return `${m}m ${s}s`;
}

function OperatorReport({ availableLocations }) {
    const fetchAuth = useAuthFetch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [date, setDate]                 = useState(searchParams.get('date') || todayIso());
    const [portalUserId, setPortalUserId] = useState(searchParams.get('op') || '');
    const [candidates, setCandidates]     = useState([]);
    const [candLoading, setCandLoading]   = useState(false);
    const [candErr, setCandErr]           = useState(null);

    const [data, setData]                 = useState(null);
    const [loading, setLoading]           = useState(false);
    const [err, setErr]                   = useState(null);

    const [showOrders, setShowOrders]     = useState(false);

    // Operator popover picker — same style as the Location dropdown in
    // ReportFilters (Popover + search + scrollable list), but single-select
    // and clickable rows instead of ChoiceList checkboxes.
    const [opOpen, setOpOpen]       = useState(false);
    const [opSearch, setOpSearch]   = useState('');
    const filteredCandidates = useMemo(() => {
        const q = opSearch.trim().toLowerCase();
        if (!q) return candidates;
        return candidates.filter(o => searchMatches(o.display_name, q));
    }, [candidates, opSearch]);
    const selectedCandidate = useMemo(
        () => candidates.find(o => String(o.portal_userid) === String(portalUserId)) || null,
        [candidates, portalUserId],
    );

    // Polaris DatePicker in a Popover — matches the ReportFilters pattern
    // used by every other report in this file. Single-day (no range).
    const [dateOpen, setDateOpen] = useState(false);
    const _dateInit = useMemo(() => parseIsoLocal(date || todayIso()), [date]);
    const [{ month, year }, setMonthYear] = useState(
        () => ({ month: _dateInit.getMonth(), year: _dateInit.getFullYear() })
    );
    useEffect(() => {
        if (dateOpen) {
            const d = parseIsoLocal(date || todayIso());
            setMonthYear({ month: d.getMonth(), year: d.getFullYear() });
        }
    }, [dateOpen, date]);
    const _dateLabel = useMemo(() => {
        const d = parseIsoLocal(date || todayIso());
        return `${_MONTH_NAMES_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }, [date]);

    // Persist filters in the URL.
    useEffect(() => {
        const p = new URLSearchParams();
        if (date) p.set('date', date);
        if (portalUserId) p.set('op', String(portalUserId));
        setSearchParams(p, { replace: true });
    }, [date, portalUserId, setSearchParams]);

    // Reload the candidate list whenever the date changes.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setCandLoading(true); setCandErr(null);
            try {
                const p = new URLSearchParams({ date });
                const res = await fetchAuth(`/api/reports/operator/candidates?${p.toString()}`);
                if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(j.detail || `HTTP ${res.status}`);
                }
                const j = await res.json();
                if (cancelled) return;
                setCandidates(j.operators || []);
                // If the selected op isn't in the new candidate list, clear it.
                // Also drop any stale error banner from a previous fetch —
                // otherwise a "Couldn't load report" from the old selection
                // lingers with no active operator to explain it.
                if (portalUserId && !(j.operators || []).some(o => String(o.portal_userid) === String(portalUserId))) {
                    setPortalUserId('');
                    setData(null);
                    setErr(null);
                }
            } catch (e) {
                if (!cancelled) setCandErr(e.message || String(e));
            } finally {
                if (!cancelled) setCandLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, fetchAuth]);

    // Reload the detail whenever date + operator are both set.
    useEffect(() => {
        if (!portalUserId) { setData(null); setErr(null); return; }
        let cancelled = false;
        (async () => {
            setLoading(true); setErr(null);
            try {
                const p = new URLSearchParams({ date, portal_userid: String(portalUserId) });
                const res = await fetchAuth(`/api/reports/operator?${p.toString()}`);
                if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(j.detail || `HTTP ${res.status}`);
                }
                const j = await res.json();
                if (!cancelled) setData(j);
            } catch (e) {
                if (!cancelled) { setErr(e.message || String(e)); setData(null); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [date, portalUserId, fetchAuth]);

    const summary  = data?.summary || null;
    const buckets  = data?.buckets  || [];
    const perOrder = data?.per_order || [];
    const opTz     = data?.timezone || 'America/New_York';
    const domLoc   = data?.dominant_location || null;
    const bucketMin = data?.bucket_minutes || 20;

    // Volume bar scales to the busiest 10-minute window, minimum 1.
    const maxBucketEvents = useMemo(() => {
        let m = 0;
        for (const h of buckets) {
            const t = (h.pick || 0) + (h.press || 0) + (h.pack || 0) + (h.qc || 0);
            if (t > m) m = t;
        }
        return m || 1;
    }, [buckets]);

    const handleExport = useCallback(() => {
        if (!data) return;
        const opName = data.operator?.display_name || 'operator';
        const rows = [
            ['Section', 'Key', 'Value'],
            ['Summary', 'Operator', opName],
            ['Summary', 'Date',     data.date],
            ['Summary', 'Roles',    (data.operator?.roles || []).join(', ')],
            ['Summary', 'First event', _fmtEventTime(summary?.first_event)],
            ['Summary', 'Last event',  _fmtEventTime(summary?.last_event)],
            ['Summary', 'Elapsed',     _fmtElapsed(summary?.elapsed_seconds)],
            ['Summary', 'Distinct orders',    summary?.distinct_orders ?? 0],
            ['Summary', 'Distinct locations', summary?.distinct_locations ?? 0],
            ['Summary', 'Pick sessions (start/end)', `${summary?.sessions_start ?? 0} / ${summary?.sessions_end ?? 0}`],
            ['Summary', 'Pick events',   summary?.pick_events ?? 0],
            ['Summary', 'Pick units',    summary?.pick_units  ?? 0],
            ['Summary', 'Press bundles', summary?.press_bundles ?? 0],
            ['Summary', 'Pack events',   summary?.pack_events ?? 0],
            ['Summary', 'Pack units',    summary?.pack_units  ?? 0],
            ['Summary', 'QC fails',      summary?.qc_fails ?? 0],
            ['Summary', 'Timezone', opTz],
            [`${bucketMin}-min activity`, 'Time', 'Pick Events,Press Bundle Finishes,Pack Passes,QC Fails,Orders'],
        ];
        for (const h of buckets) {
            rows.push([`${bucketMin}-min activity`, h.label,
                `${h.pick},${h.press},${h.pack},${h.qc},${h.orders}`]);
        }
        rows.push(['Per order', 'Order,Store,Location,Club,First,Last,Events,Qty,Portals', '']);
        for (const o of perOrder) {
            rows.push(['Per order',
                `${o.order_name || o.order_id},${o.store_key},${o.location_name || o.shopify_location_id},` +
                `${o.club || ''},` +
                `${_fmtEventTime(o.first_event)},${_fmtEventTime(o.last_event)},` +
                `${o.events},${o.qty},${(o.portals || []).join('|')}`,
                '']);
        }
        downloadCsv(`operator_report_${data.date}_${opName.replace(/\s+/g, '_')}.csv`, rows);
    }, [data, summary, buckets, perOrder, opTz, bucketMin]);

    // Suppress an unused-var lint warning (component accepts availableLocations
    // to match its siblings' signature; we don't use it — filtering is server-
    // side via the viewer's own allowed_pairs, not a location picker).
    void availableLocations;

    return (
        <BlockStack gap="400">
            <DelayedSpinner loading={loading || candLoading} />
            <Card>
                <BlockStack gap="300">
                    <Text variant="headingMd" as="h2">Operator Report</Text>
                    <InlineStack gap="200" align="start" blockAlign="end" wrap>
                        <Popover
                            active={dateOpen}
                            activator={
                                <Button onClick={() => setDateOpen(v => !v)}
                                        disclosure={dateOpen ? 'up' : 'down'}>
                                    {_dateLabel}
                                </Button>
                            }
                            onClose={() => setDateOpen(false)}
                            preferredAlignment="left"
                            fluidContent
                        >
                            <div style={{ padding: 12,
                                          width: 'min(360px, calc(100vw - 40px))' }}>
                                <DatePicker
                                    month={month}
                                    year={year}
                                    onMonthChange={(m, y) => setMonthYear({ month: m, year: y })}
                                    selected={parseIsoLocal(date || todayIso())}
                                    onChange={({ start }) => {
                                        setDate(localIsoDate(start));
                                        setDateOpen(false);
                                    }}
                                />
                            </div>
                        </Popover>
                        <ButtonGroup segmented>
                            <Button onClick={() => setDate(isoDaysAgo(1))}>Yesterday</Button>
                            <Button onClick={() => setDate(todayIso())}>Today</Button>
                        </ButtonGroup>
                        <Popover
                            active={opOpen}
                            activator={
                                <Button
                                    onClick={() => setOpOpen(v => !v)}
                                    disclosure={opOpen ? 'up' : 'down'}
                                    disabled={candLoading || (!candidates.length && !portalUserId)}
                                    pressed={!!portalUserId}
                                >
                                    {selectedCandidate
                                        ? `Operator: ${selectedCandidate.display_name}`
                                        : (candLoading ? 'Loading…'
                                            : (candidates.length
                                                ? `Select operator (${candidates.length} active)`
                                                : 'No operators active on this date'))}
                                </Button>
                            }
                            onClose={() => { setOpOpen(false); setOpSearch(''); }}
                            preferredAlignment="left"
                        >
                            <div style={{ minWidth: 320, maxHeight: 460, padding: 10,
                                          display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <TextField labelHidden label="Search operators" type="search"
                                    value={opSearch} onChange={setOpSearch}
                                    placeholder="Search operators…" autoComplete="off"
                                    clearButton onClearButtonClick={() => setOpSearch('')} />
                                <button type="button" className="filter-pill-clear"
                                        disabled={!portalUserId}
                                        onClick={() => { setPortalUserId(''); setOpOpen(false); }}>
                                    Clear
                                </button>
                                <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                    {filteredCandidates.length === 0 ? (
                                        <div style={{ color: 'var(--p-color-text-secondary)', fontSize: 13,
                                                      padding: '8px 4px' }}>
                                            {opSearch
                                                ? `No matches for "${opSearch}"`
                                                : 'No operators active on this date'}
                                        </div>
                                    ) : (
                                        <div role="listbox" aria-label="Operators">
                                            {filteredCandidates.map(o => {
                                                const isSel = String(o.portal_userid) === String(portalUserId);
                                                return (
                                                    <button key={o.portal_userid} type="button"
                                                        role="option" aria-selected={isSel}
                                                        onClick={() => {
                                                            setPortalUserId(String(o.portal_userid));
                                                            setOpOpen(false);
                                                            setOpSearch('');
                                                        }}
                                                        style={{
                                                            display: 'flex', width: '100%',
                                                            padding: '8px 10px', border: 0,
                                                            borderRadius: 6, cursor: 'pointer',
                                                            textAlign: 'left', fontSize: 13,
                                                            background: isSel ? '#f1f8f5' : 'transparent',
                                                            color: isSel ? '#005c2c' : '#1a1a1a',
                                                            fontWeight: isSel ? 600 : 400,
                                                            alignItems: 'baseline',
                                                            gap: 8,
                                                        }}
                                                        onMouseEnter={e => {
                                                            if (!isSel) e.currentTarget.style.background = '#f6f6f7';
                                                        }}
                                                        onMouseLeave={e => {
                                                            if (!isSel) e.currentTarget.style.background = 'transparent';
                                                        }}
                                                    >
                                                        <span style={{ flex: 1 }}>
                                                            {o.display_name}
                                                            {!o.active && (
                                                                <span style={{ marginLeft: 6, fontSize: 11,
                                                                               color: '#8c9196' }}>(inactive)</span>
                                                            )}
                                                        </span>
                                                        <span style={{ fontSize: 11, color: '#616161',
                                                                       whiteSpace: 'nowrap' }}>
                                                            {o.event_count} events
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Popover>
                        <div style={{ marginLeft: 'auto' }}>
                            <Button
                                onClick={handleExport}
                                disabled={!data || loading}
                            >Export CSV</Button>
                        </div>
                    </InlineStack>
                    {candErr && <Banner tone="critical" title="Couldn't load operator list">{candErr}</Banner>}
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}

            {data && summary && (
                <>
                    <Card>
                        <BlockStack gap="400">
                            <InlineStack gap="300" wrap>
                                <Text variant="headingMd" as="h3">{data.operator.display_name}</Text>
                                <Text tone="subdued">
                                    Roles: {(data.operator.roles || []).join(', ') || '—'}
                                    {data.operator.active ? '' : ' · inactive'}
                                </Text>
                                <Text tone="subdued">
                                    {domLoc?.name
                                        ? `${domLoc.name} (${_friendlyTz(opTz)})`
                                        : _friendlyTz(opTz)}
                                </Text>
                            </InlineStack>
                            <div style={{ display: 'grid',
                                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                          gap: 12 }}>
                                <StatTile label="First event"       value={_fmtEventTime(summary.first_event)} />
                                <StatTile label="Last event"        value={_fmtEventTime(summary.last_event)} />
                                <StatTile label="Elapsed"           value={_fmtElapsed(summary.elapsed_seconds)} />
                                <StatTile label="Distinct orders"   value={summary.distinct_orders} />
                                <StatTile label="Distinct locations" value={summary.distinct_locations} />
                                <StatTile label="Pick sessions"     value={`${summary.sessions_start} / ${summary.sessions_end}`} sub="opened / closed" />
                                <StatTile label="Pick events"       value={summary.pick_events} />
                                <StatTile label="Pick units"        value={summary.pick_units} />
                                <StatTile label="Press bundles"     value={summary.press_bundles} />
                                <StatTile label="Pack events"       value={summary.pack_events} />
                                <StatTile label="Pack units"        value={summary.pack_units} />
                                <StatTile label="QC fails"          value={summary.qc_fails} />
                            </div>
                        </BlockStack>
                    </Card>

                    <Card>
                        <BlockStack gap="300">
                            <Text variant="headingSm" as="h4">
                                {bucketMin}-minute activity
                            </Text>
                            <Text tone="subdued" variant="bodySm">
                                <strong>Pick Events</strong> — every scan or unscan on the picking portal counts
                                as one event; a picker who scans 3 jerseys and a pair of socks fires 4 pick events.{' '}
                                <strong>Press Bundle Finishes</strong> — each time a presser marks a bundle done
                                (one finish per garment × embellishment combo); a jersey with a crest + a name
                                completes as 2 bundle finishes.{' '}
                                <strong>Pack Passes</strong> — every unit the packer marks PASS in QC.{' '}
                                <strong>QC Fails</strong> — every unit rejected in QC (kicks the unit back to
                                pressing or picking depending on the reason).
                            </Text>
                            {!buckets.length && <Text tone="subdued">No events recorded for this date.</Text>}
                            {buckets.length > 0 && (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid #e1e3e5', textAlign: 'left' }}>
                                                <th style={_thStyle}>Time</th>
                                                <th style={_thStyle}>Pick Events</th>
                                                <th style={_thStyle}>Press Bundle Finishes</th>
                                                <th style={_thStyle}>Pack Passes</th>
                                                <th style={_thStyle}>QC Fails</th>
                                                <th style={_thStyle}>Orders</th>
                                                <th style={_thStyle}>Volume</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {buckets.map(h => {
                                                const total = h.pick + h.press + h.pack + h.qc;
                                                const isEmpty = total === 0 && h.orders === 0;
                                                const pct = Math.max(4, Math.round(total * 100 / maxBucketEvents));
                                                return (
                                                    <tr key={h.bucket} style={{
                                                            borderBottom: '1px solid #f1f2f3',
                                                            color: isEmpty ? '#a0a4a8' : undefined,
                                                        }}>
                                                        <td style={_tdStyle}>{h.label}</td>
                                                        <td style={_tdStyle}>{h.pick || ''}</td>
                                                        <td style={_tdStyle}>{h.press || ''}</td>
                                                        <td style={_tdStyle}>{h.pack || ''}</td>
                                                        <td style={_tdStyle}>{h.qc || ''}</td>
                                                        <td style={_tdStyle}>{h.orders || ''}</td>
                                                        <td style={{ ..._tdStyle, minWidth: 160 }}>
                                                            {total > 0 && (
                                                                <div style={{
                                                                    background: '#e3f2ea', borderRadius: 3,
                                                                    height: 10, width: `${pct}%`,
                                                                }} title={`${total} events`} />
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </BlockStack>
                    </Card>

                    <Card>
                        <BlockStack gap="300">
                            <InlineStack align="space-between" blockAlign="center">
                                <Text variant="headingSm" as="h4">
                                    Per-order breakdown ({perOrder.length})
                                </Text>
                                <Button variant="plain" onClick={() => setShowOrders(s => !s)}>
                                    {showOrders ? 'Hide' : 'Show'}
                                </Button>
                            </InlineStack>
                            {showOrders && (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid #e1e3e5', textAlign: 'left' }}>
                                                <th style={_thStyle}>Order</th>
                                                <th style={_thStyle}>Location</th>
                                                <th style={_thStyle}>Club</th>
                                                <th style={_thStyle}>Portals</th>
                                                <th style={_thStyle}>First</th>
                                                <th style={_thStyle}>Last</th>
                                                <th style={_thStyle}>Events</th>
                                                <th style={_thStyle}>Qty</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {perOrder.map(o => (
                                                <tr key={`${o.store_key}:${o.order_id}`}
                                                    style={{ borderBottom: '1px solid #f1f2f3' }}>
                                                    <td style={_tdStyle}>
                                                        {o.order_name || o.order_id}
                                                    </td>
                                                    <td style={_tdStyle}>{o.location_name || o.shopify_location_id || '—'}</td>
                                                    <td style={_tdStyle}>{o.club || '—'}</td>
                                                    <td style={_tdStyle}>{(o.portals || []).join(', ')}</td>
                                                    <td style={_tdStyle}>{_fmtEventTime(o.first_event)}</td>
                                                    <td style={_tdStyle}>{_fmtEventTime(o.last_event)}</td>
                                                    <td style={_tdStyle}>{o.events}</td>
                                                    <td style={_tdStyle}>{o.qty || ''}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </BlockStack>
                    </Card>
                </>
            )}

            {!data && !loading && portalUserId && !err && (
                <Card><Text tone="subdued">No activity found.</Text></Card>
            )}
            {!data && !loading && !portalUserId && !candLoading && (
                <Card><Text tone="subdued">
                    Pick a date, then choose an operator to see their day.
                </Text></Card>
            )}
        </BlockStack>
    );
}

const _thStyle = { padding: '8px 10px', fontWeight: 600, fontSize: 12,
                   color: '#616161', textTransform: 'uppercase', letterSpacing: '0.03em' };
const _tdStyle = { padding: '8px 10px', fontSize: 13, verticalAlign: 'top' };

function StatTile({ label, value, sub }) {
    return (
        <div style={{ padding: '10px 12px', background: '#f7f8f9', borderRadius: 6 }}>
            <div style={{ fontSize: 11, color: '#616161', textTransform: 'uppercase',
                          letterSpacing: '0.03em', fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a', marginTop: 2 }}>
                {value ?? '—'}
            </div>
            {sub && <div style={{ fontSize: 11, color: '#8c9196' }}>{sub}</div>}
        </div>
    );
}
